import { IdentitySecurityControls } from "../../../../shared/IdentitySecurityControls";
import { useIdentityProfileForm } from "../../../../shared/useIdentityProfileForm";
import { useIdentity, useSignedInIdentity } from "../../../../shared/useIdentity";
import { useCoreNotifications } from "../../../../shared/useCoreNotifications";
import { ProgramAnnouncementPreference } from "../../../../shared/ProgramAnnouncementPreference";
import { platformEnvironment } from "../../config/environment";
import { AcademicLessonFields } from "../../../../shared/AcademicLessonFields";
import {
  useTeachingSearch,
  academicProgramIdentity,
  academicLessonIdentity,
  academicLessonRoute,
  academicCurriculumStatus,
  useTeachingVersionRecords,
  useTeachingProgramVersions,
  projectTeachingLesson,
  readAcademic,
  useTeachingContent,
  useTeachingCurricula,
  useTeachingLessons,
  useTeachingPrograms,
  type TeachingLesson,
} from "../../../../shared/useAcademicTeaching";
import { ACADEMIC_CATALOGUE_CHANGED, useAcademicRead } from "../../../../shared/useAcademicCatalogue";
import { academyRequest, createCommandSender } from "../../../../shared/academyApi";
import { requestClassHandoff } from "../../../../shared/useParticipation";
import { useInstructorAssignmentAnalytics } from "../../../../shared/useInstructorAssignmentAnalytics";
import { useInstructorAttendanceAnalytics } from "../../../../shared/useInstructorAttendanceAnalytics";
import { useInstructorReviewAnalytics } from "../../../../shared/useInstructorReviewAnalytics";
import type {
  D10AssignedCohorts,
  D10AssignmentSubmissions,
  D10Assignments,
  D10AttendanceRoster,
  D10AttendanceSessions,
  D10CohortRoster,
  D10InstructorLearnerProgress,
  D10LearningPlan,
  D10LearningPlanEnvelope,
  D10PublishedContent,
  ReviseLearningPlanRequest,
} from "../../../../shared/generated/learning";
import { checkIdentitySession } from "../../../../shared/identityClient";
import {
  assignInstructorAssessmentAssessor,
  claimInstructorAssessmentModeration,
  finalizeInstructorAssessmentEvaluation,
  finalizeInstructorAssessmentModeration,
  publishInstructorExamAssessmentAssignment,
  publishInstructorAssessmentOfficialResult,
  referInstructorAssessmentIntegrity,
  reviewInstructorScoringPolicy,
  submitInstructorScoringPolicy,
  useInstructorAssessmentAssignments,
  useInstructorAssessmentAssessorManagement,
  useInstructorAssessmentEvaluationQueue,
  useInstructorAssessmentModerationQueue,
  useInstructorAssessmentResultPublicationQueue,
  useInstructorAssessmentPolicies,
  useInstructorScoringPolicyReviews,
} from "../../../../shared/useInstructorAssessmentPolicies";
import type {
  InstructorAssessmentEvaluationQueueItem,
  InstructorAssessmentIntegrityReferralCategory,
  InstructorAssessmentModerationQueueItem,
  InstructorAssessmentOfficialResultQueueItem,
  InstructorScoringPolicyAuthoringItem,
  InstructorScoringPolicyOutcomeBand,
  InstructorScoringPolicyProposal,
} from "../../../../shared/generated/instructor-assessment";
import type {
  AcademicHistoryPage,
  AcademicProgramSummary,
  AcademicReceipt,
  CloneAcademicCurriculum,
  CreateCurriculum,
  CurriculumSummary,
  LessonCompletionRequirements,
  UpdateAcademicLesson,
} from "../../../../shared/generated/academic-catalogue";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LANGUAGE_NAMES } from "../../../../shared/languages";
import academyLogo from "../../../../shared/brand/chatoner-ai-academy-logo-landscape.png";
import academyMark from "../../../../shared/brand/chatoner-ai-academy-mark.png";
import {
  ASSIGNMENTS,
  BUILD_ROOM_MEMBERS,
  COHORTS,
  CORE_LEARNERS,
  INITIAL_BUILD_ROOM_SUBGROUPS,
  LABS,
  LAB_INSTRUCTOR_SECTIONS,
  LEARNERS,
  LESSONS,
  MESSAGE_THREADS,
  NOTIFICATIONS,
  PROJECT_REVIEWS,
  SEARCH_RECORDS,
  buildRoomMember,
  createClassCode,
  createDynamicInstructorRouteSpec,
  downloadClassCalendar,
  downloadTextFile,
  instructorLabHandoffUrl,
  instructorLabsWorkspaceUrl,
  languageCode,
  nowTime,
  openExternalHandoff,
  resolveStaticInstructorRouteSpec,
  roleCanAccessInstructorRoute,
  safeLocalStorageSet,
} from "../../data/mockData";
import type {
  BuildRoomSubgroup,
  CapstoneReview,
  ClassSession,
  Cohort,
  InstructorArea,
  InstructorModal,
  InstructorNotice,
  InstructorRouteSpec,
  LabInstructorSectionId,
  LabRecord,
  LanguageSettings,
  LearnerRecord,
  LessonRecord,
  MemberDirectoryItem,
  NotificationRecord,
  OfficeSlot,
  ProjectReview,
  TeachingRoleId,
  TeachingRoleSpec,
} from "../../data/mockData";
import {
  completeStyles,
  dropdownThemeStyles,
  instructorExtensionStyles,
  instructorFinalStyles,
  instructorStyles,
  instructorV2Styles,
  instructorV3Styles,
  integrationStyles,
} from "../../styles/theme";
import { completeIconSprite } from "../../config/icons";
import { readStorage } from "../../services/versionedStorage";
import { copyBrowserText as copyToClipboard } from "../../utils/browser";

/**
 * Chatoner AI Academy — Complete Instructor Platform v3.0.0
 *
 * Suggested application root: /instructor
 * Runtime dependency: React 18+
 * External UI dependencies: none
 *
 * The Instructor Platform uses the complete learner workspace as its interaction
 * and visual foundation, then adds cohort command, curriculum, Chatoner Class
 * hosting, persistent Build Room subgroup facilitation, independent connected AI Labs, assessment, moderation,
 * project verification, capstone decisions, office hours, analytics and audit.
 */

type RoomTab = "chat" | "plan" | "artifacts" | "preview" | "tests" | "evidence";
type InspectorTab = "details" | "agents";
type PreviewDevice = "desktop" | "tablet" | "mobile";
type DynamicMessage = {
  id: number;
  kind: "instructor" | "agent";
  text: string;
  createdAt: string;
  scopeApproved?: boolean;
};
type ToastNotice = { id: number; title: string; detail: string };

type IconProps = {
  name: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  title?: string;
};

const Icon = ({ name, size = "md", className = "", title }: IconProps) => (
  <svg
    className={`icon ${size === "md" ? "" : size} ${className}`.trim()}
    aria-hidden={title ? undefined : true}
    role={title ? "img" : undefined}
  >
    {title ? <title>{title}</title> : null}
    <use href={`#i-${name}`} />
  </svg>
);

const roomTabs: Array<{ id: RoomTab; label: string; icon: string; badge?: string }> = [
  { id: "chat", label: "Facilitation chat", icon: "message" },
  { id: "plan", label: "Shared plan", icon: "clipboard", badge: "6" },
  { id: "artifacts", label: "Artifacts", icon: "folder", badge: "8" },
  { id: "preview", label: "Live preview", icon: "eye" },
  { id: "tests", label: "Evaluations", icon: "test", badge: "18" },
  { id: "evidence", label: "Evidence", icon: "award" },
];

const channels = {
  cohort: [
    ["announcements", false],
    ["introductions", false],
    ["product-thinking", "3"],
    ["code-clinic", false],
    ["show-and-learn", "7"],
  ] as Array<[string, string | false]>,
  project: [
    ["project-brief", false, false],
    ["group-build-checkout", false, true],
    ["research-and-data", false, false],
    ["qa-and-evaluation", "1", false],
    ["instructor-review", false, false],
  ] as Array<[string, string | false, boolean]>,
};

function Reaction({ children, active = false }: { children: React.ReactNode; active?: boolean }) {
  const [selected, setSelected] = useState(active);
  return (
    <button
      type="button"
      className={`reaction ${selected ? "active" : ""}`}
      onClick={() => setSelected(!selected)}
    >
      {children}
    </button>
  );
}

function StatusChip({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "green" | "orange" | "red";
}) {
  return <span className={`status-chip ${tone === "default" ? "" : tone}`.trim()}>{children}</span>;
}

function _Rail({
  notify,
  openSearch,
}: {
  notify: (title: string, detail?: string) => void;
  openSearch: () => void;
}) {
  const items = [
    ["home", "Home"],
    ["book", "Programs"],
    ["video", "Chatoner Class"],
    ["message", "Build Rooms"],
    ["flask", "AI Labs"],
    ["folder", "Projects"],
    ["award", "Credentials"],
  ];
  return (
    <nav className="rail" aria-label="Academy applications">
      <button
        type="button"
        className="brand-mark"
        aria-label="Chatoner AI Academy home"
        title="Academy Home"
        onClick={() => notify("Academy home opened")}
      >
        <img
          className="academy-platform-mark"
          src={academyMark}
          alt=""
          width={837}
          height={765}
          decoding="async"
        />
        <span className="tip">Academy Home</span>
      </button>
      <div className="rail-nav">
        {items.map(([icon, label]) => (
          <button
            type="button"
            className={`rail-btn ${label === "Build Rooms" ? "active" : ""}`}
            key={label}
            aria-label={label}
            title={label}
            onClick={() => label !== "Build Rooms" && notify(`${label} opened`)}
          >
            <Icon name={icon} size="lg" />
            <span className="tip">{label}</span>
          </button>
        ))}
      </div>
      <div className="rail-spacer" />
      <button type="button" className="rail-btn" aria-label="Search" title="Search" onClick={openSearch}>
        <Icon name="search" size="lg" />
        <span className="tip">Search</span>
      </button>
      <button
        type="button"
        className="rail-btn"
        aria-label="Notifications"
        title="Notifications"
        onClick={() => notify("No new notifications")}
      >
        <Icon name="bell" size="lg" />
        <span className="tip">Notifications</span>
      </button>
      <button
        type="button"
        className="profile-dot"
        aria-label="Open profile"
        title="Profile"
        onClick={() => notify("Profile menu opened")}
      >
        RI
        <span className="tip">Profile</span>
      </button>
    </nav>
  );
}

function SidebarGroup({ title, children }: { title: string; children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="sidebar-section">
      <button
        type="button"
        className={`section-label ${collapsed ? "collapsed" : ""}`}
        onClick={() => setCollapsed(!collapsed)}
      >
        <Icon name="chevron" size="sm" />
        {title}
      </button>
      {!collapsed ? <div className="channel-list">{children}</div> : null}
    </section>
  );
}

function _Sidebar({
  open,
  notify,
  openSearch,
  selectTab,
}: {
  open: boolean;
  notify: (title: string, detail?: string) => void;
  openSearch: () => void;
  selectTab: (tab: RoomTab) => void;
}) {
  return (
    <aside className={`sidebar ${open ? "open" : ""}`} aria-label="Build room navigation">
      <div className="academy-platform-sidebar-brand">
        <img
          className="academy-platform-logo"
          src={academyLogo}
          alt="Chatoner AI Academy"
          width={4124}
          height={967}
          decoding="async"
        />
      </div>
      <div className="workspace-head">
        <div className="workspace-badge">CA</div>
        <div className="workspace-copy">
          <strong>AI Product Builder</strong>
          <span>October 2026 cohort</span>
        </div>
        <button
          type="button"
          className="icon-btn"
          aria-label="Workspace menu"
          onClick={() => notify("Workspace menu opened")}
        >
          <Icon name="chevron" />
        </button>
      </div>
      <div className="sidebar-search">
        <button type="button" className="search-trigger" onClick={openSearch}>
          <Icon name="search" size="sm" />
          <span>Search this learning space</span>
          <kbd className="kbd">⌘ K</kbd>
        </button>
      </div>
      <div className="sidebar-scroll">
        <SidebarGroup title="Connected learning">
          <button
            type="button"
            className="channel-row"
            onClick={() =>
              notify("Opening AI-App Builder Lab", "Your program entitlement will be checked before handoff.")
            }
          >
            <Icon name="flask" size="sm" />
            <span className="name">AI-App Builder Lab</span>
            <span className="live" />
          </button>
          <button
            type="button"
            className="channel-row"
            onClick={() => notify("Opening today's Chatoner Class studio")}
          >
            <Icon name="video" size="sm" />
            <span className="name">Live studio · 3:00 PM</span>
          </button>
          <button type="button" className="channel-row" onClick={() => selectTab("evidence")}>
            <Icon name="award" size="sm" />
            <span className="name">Credential evidence</span>
          </button>
        </SidebarGroup>

        <SidebarGroup title="Cohort channels">
          {channels.cohort.map(([name, count]) => (
            <button
              type="button"
              className="channel-row"
              key={name}
              onClick={() => notify(`#${name} opened`)}
            >
              <Icon name="hash" size="sm" />
              <span className="name">{name}</span>
              {count ? <span className="count">{count}</span> : null}
            </button>
          ))}
        </SidebarGroup>

        <SidebarGroup title="Team project · Kijani">
          {channels.project.map(([name, count, active]) => (
            <button
              type="button"
              className={`channel-row ${active ? "active" : ""}`}
              key={name}
              onClick={() => !active && notify(`#${name} opened`)}
            >
              <Icon name={name === "instructor-review" ? "lock" : "hash"} size="sm" />
              <span className="name">{name}</span>
              {count ? <span className="count">{count}</span> : null}
            </button>
          ))}
        </SidebarGroup>

        <SidebarGroup title="Direct messages">
          <button
            type="button"
            className="channel-row"
            onClick={() => notify("Amina's direct message opened")}
          >
            <span className="dm-avatar">AM</span>
            <span className="name">Amina · You</span>
            <span className="live" />
          </button>
          <button
            type="button"
            className="channel-row"
            onClick={() => notify("Daniel's direct message opened")}
          >
            <span className="dm-avatar">DM</span>
            <span className="name">Daniel · Mentor</span>
          </button>
          <button type="button" className="channel-row" onClick={() => notify("AI Builder activity opened")}>
            <span className="dm-avatar agent">AI</span>
            <span className="name">AI Builder</span>
          </button>
        </SidebarGroup>
      </div>
      <div className="sidebar-foot">
        <div className="progress-card">
          <div className="progress-top">
            <strong>Program progress</strong>
            <span>68%</span>
          </div>
          <div className="progress-bar">
            <i />
          </div>
          <p>Week 6 of 8 · Build, test and document</p>
        </div>
      </div>
    </aside>
  );
}

function DayDivider() {
  return <div className="day-divider">Today · Friday, 21 August 2026</div>;
}

function MessageShell({
  initials,
  avatarTone = "default",
  name,
  role,
  time,
  agent = false,
  children,
}: {
  initials?: string;
  avatarTone?: "default" | "instructor" | "mentor" | "ai";
  name: string;
  role: string;
  time: string;
  agent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <article className="message">
      <div className={`avatar ${avatarTone === "default" ? "" : avatarTone}`.trim()}>
        {avatarTone === "ai" ? <Icon name="bot" /> : initials}
      </div>
      <div className="message-body">
        <div className="message-meta">
          <strong>{name}</strong>
          <span className={`role-tag ${agent ? "ai" : ""}`.trim()}>{role}</span>
          <time>{time}</time>
        </div>
        {children}
      </div>
    </article>
  );
}

function AcceptanceCard({ selectTab }: { selectTab: (tab: RoomTab) => void }) {
  const criteria = [
    ["UX-04", "A learner can complete checkout without guessing the next action."],
    ["ACC-02", "Keyboard and screen-reader users receive visible, announced validation."],
    ["SEC-06", "The system never exposes payment secrets, and AI tools receive minimum access."],
    ["EVAL-03", "Include tests, a decision reflection and a human review record."],
  ];
  return (
    <div className="message-card">
      <div className="card-head">
        <span className="file-icon">
          <Icon name="clipboard" size="sm" />
        </span>
        <div className="card-head-copy">
          <strong>Checkout sprint · acceptance criteria</strong>
          <span>Academy assignment · version 3 · linked to project evidence</span>
        </div>
        <StatusChip tone="orange">Due 28 Nov</StatusChip>
      </div>
      <div className="card-body">
        <ul className="acceptance-list">
          {criteria.map(([id, text]) => (
            <li key={id}>
              <span className="check-mini">
                <Icon name="check" size="sm" />
              </span>
              <span>
                <strong>{id}:</strong> {text}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="card-actions">
        <button type="button" className="mini-btn green" onClick={() => selectTab("plan")}>
          <Icon name="clipboard" size="sm" />
          View linked plan
        </button>
        <button type="button" className="mini-btn" onClick={() => selectTab("evidence")}>
          <Icon name="award" size="sm" />
          Evidence requirements
        </button>
      </div>
    </div>
  );
}

function DefectAttachments({ notify }: { notify: (title: string, detail?: string) => void }) {
  return (
    <div className="attachment-grid">
      <button
        type="button"
        className="attachment"
        onClick={() => notify("Opening annotated accessibility capture")}
      >
        <div className="attachment-preview">
          <div className="mock-screen">
            <div className="mock-bar" />
            <div className="mock-content">
              <div>
                <div className="mock-block" />
                <div className="mock-block" />
                <div className="mock-block red-outline" />
                <div className="mock-block" />
              </div>
              <div>
                <div className="mock-block green" />
                <div className="mock-block" />
              </div>
            </div>
          </div>
          <span className="annotation-pin" style={{ left: "38%", top: "52%" }}>
            1
          </span>
        </div>
        <div className="attachment-caption">
          <Icon name="image" size="sm" />
          <div>
            <strong>coupon-error-mobile.png</strong>
            <span>Annotated screenshot · 224 KB</span>
          </div>
        </div>
      </button>
      <button type="button" className="attachment" onClick={() => notify("Opening focus-order recording")}>
        <div className="attachment-preview">
          <div className="mock-screen">
            <div className="mock-bar" />
            <div className="mock-content">
              <div>
                <div className="mock-block" />
                <div className="mock-block" />
                <div className="mock-block" />
                <div className="mock-block green" />
              </div>
              <div>
                <div className="mock-block" />
                <div className="mock-block" />
              </div>
            </div>
          </div>
          <span className="annotation-pin" style={{ right: "28%", top: "32%" }}>
            2
          </span>
          <span
            style={{
              position: "absolute",
              display: "grid",
              placeItems: "center",
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(6,16,12,.8)",
              color: "#fff",
            }}
          >
            <Icon name="play" />
          </span>
        </div>
        <div className="attachment-caption">
          <Icon name="video" size="sm" />
          <div>
            <strong>focus-order-recording.webm</strong>
            <span>00:38 · captions included</span>
          </div>
        </div>
      </button>
    </div>
  );
}

function AgentTaskCard() {
  return (
    <div className="agent-task">
      <div className="agent-task-top">
        <span className="agent-mark">
          <Icon name="bot" />
        </span>
        <div className="agent-task-copy">
          <strong>AI Builder task created by Nakato</strong>
          <p>Task CB-184 · shared with this room · activity retained as learning evidence</p>
        </div>
        <StatusChip tone="green">
          <i className="dot" />
          Completed
        </StatusChip>
      </div>
      <div className="agent-progress">
        <div className="agent-progress-bar">
          <i />
        </div>
        <span>100%</span>
      </div>
      <div className="scope-grid">
        <div className="scope-item">
          <span>Repository scope</span>
          <strong>2 files · checkout only</strong>
        </div>
        <div className="scope-item">
          <span>Environment</span>
          <strong>Isolated sandbox · internet off</strong>
        </div>
        <div className="scope-item">
          <span>Authority</span>
          <strong>Branch write · human merge</strong>
        </div>
      </div>
    </div>
  );
}

function DiffCard({
  selectTab,
  notify,
}: {
  selectTab: (tab: RoomTab) => void;
  notify: (title: string, detail?: string) => void;
}) {
  return (
    <div className="diff-card">
      <div className="diff-toolbar">
        <Icon name="code" size="sm" />
        <strong>src/features/checkout/CouponField.tsx</strong>
        <span className="diff-stat">
          <span className="plus">+14</span> <span className="minus">−3</span>
        </span>
        <button type="button" className="mini-btn" onClick={() => notify("Full diff opened")}>
          Open diff
        </button>
      </div>
      <div className="diff-code" aria-label="Code difference preview">
        <div className="diff-line remove">
          <span className="ln">42</span>
          <span className="code">- &lt;p className=&quot;error&quot;&gt;{"{error}"}&lt;/p&gt;</span>
        </div>
        <div className="diff-line add">
          <span className="ln">42</span>
          <span className="code">
            + &lt;p id=&quot;coupon-error&quot; role=&quot;status&quot; aria-live=&quot;polite&quot;&gt;
          </span>
        </div>
        <div className="diff-line add">
          <span className="ln">43</span>
          <span className="code">+ {'{error ? `Coupon not applied: ${error}` : ""}'}</span>
        </div>
        <div className="diff-line add">
          <span className="ln">44</span>
          <span className="code">+ &lt;/p&gt;</span>
        </div>
        <div className="diff-line add">
          <span className="ln">45</span>
          <span className="code">+ aria-describedby={'{error ? "coupon-error" : undefined}'}</span>
        </div>
      </div>
      <div className="card-actions">
        <button
          type="button"
          className="mini-btn green"
          onClick={() => notify("Draft pull request #184 opened")}
        >
          <Icon name="git" size="sm" />
          Review PR #184
        </button>
        <button type="button" className="mini-btn" onClick={() => selectTab("preview")}>
          <Icon name="eye" size="sm" />
          Open live preview
        </button>
        <button type="button" className="mini-btn" onClick={() => selectTab("tests")}>
          <Icon name="test" size="sm" />
          18 checks passed
        </button>
      </div>
    </div>
  );
}

function EvaluationMessageCard({ selectTab }: { selectTab: (tab: RoomTab) => void }) {
  return (
    <div className="message-card">
      <div className="card-head">
        <span className="file-icon">
          <Icon name="test" size="sm" />
        </span>
        <div className="card-head-copy">
          <strong>Evaluation run · checkout-a11y-184</strong>
          <span>Linked to ACC-02 and EVAL-03 · run by Joel</span>
        </div>
        <StatusChip tone="green">
          <i className="dot" />
          18 / 18 passed
        </StatusChip>
      </div>
      <div className="card-body">
        <div className="metric-grid">
          <div className="metric">
            <span>Unit</span>
            <strong>8/8</strong>
            <em>Passed</em>
          </div>
          <div className="metric">
            <span>Integration</span>
            <strong>4/4</strong>
            <em>Passed</em>
          </div>
          <div className="metric">
            <span>Accessibility</span>
            <strong>5/5</strong>
            <em>Passed</em>
          </div>
          <div className="metric">
            <span>Human check</span>
            <strong>1/1</strong>
            <em>Recorded</em>
          </div>
        </div>
      </div>
      <div className="card-actions">
        <button type="button" className="mini-btn green" onClick={() => selectTab("tests")}>
          <Icon name="test" size="sm" />
          Inspect run
        </button>
        <button type="button" className="mini-btn" onClick={() => selectTab("evidence")}>
          <Icon name="award" size="sm" />
          Add to evidence
        </button>
      </div>
    </div>
  );
}

function DynamicRoomMessage({
  message,
  approveScope,
  notify,
}: {
  message: DynamicMessage;
  approveScope: (id: number) => void;
  notify: (title: string, detail?: string) => void;
}) {
  if (message.kind === "instructor") {
    const parts = message.text.split(/(@AI Builder)/gi);
    return (
      <MessageShell
        initials="AM"
        avatarTone="instructor"
        name="You"
        role="Instructor · Lead facilitator"
        time={message.createdAt}
      >
        <div className="message-text">
          <p>
            {parts.map((part, index) =>
              /^@AI Builder$/i.test(part) ? (
                <span className="mention" key={index}>
                  {part}
                </span>
              ) : (
                <React.Fragment key={index}>{part}</React.Fragment>
              ),
            )}
          </p>
        </div>
      </MessageShell>
    );
  }
  return (
    <MessageShell
      avatarTone="ai"
      name="AI Builder"
      role="Permission-scoped agent"
      time={message.createdAt}
      agent
    >
      <div className="message-text">
        <p>{message.text}</p>
      </div>
      <div className="agent-task">
        <div className="agent-task-top">
          <span className="agent-mark">
            <Icon name="bot" />
          </span>
          <div className="agent-task-copy">
            <strong>Draft task awaiting instructor approval</strong>
            <p>Repository read only · no external access · no code changes started</p>
          </div>
          <StatusChip tone={message.scopeApproved ? "green" : "orange"}>
            {message.scopeApproved ? "Approved" : "Needs scope"}
          </StatusChip>
        </div>
        <div className="scope-grid">
          <div className="scope-item">
            <span>Repository scope</span>
            <strong>Not yet approved</strong>
          </div>
          <div className="scope-item">
            <span>Environment</span>
            <strong>Sandbox · internet off</strong>
          </div>
          <div className="scope-item">
            <span>Authority</span>
            <strong>Read only · no merge</strong>
          </div>
        </div>
        <div className="card-actions" style={{ border: 0, padding: "10px 0 0" }}>
          {message.scopeApproved ? (
            <StatusChip tone="green">
              <i className="dot" />
              Scope approved by instructor
            </StatusChip>
          ) : (
            <>
              <button
                type="button"
                className="mini-btn green"
                onClick={() => {
                  approveScope(message.id);
                  notify(
                    "Agent scope approved",
                    "The task can now begin within the displayed instructor-approved permission boundary.",
                  );
                }}
              >
                <Icon name="check" size="sm" />
                Approve scope
              </button>
              <button type="button" className="mini-btn" onClick={() => notify("Task editor opened")}>
                Edit task
              </button>
            </>
          )}
        </div>
      </div>
    </MessageShell>
  );
}

function ChatView({
  selectTab,
  notify,
  composer,
  setComposer,
  sendMessage,
  dynamicMessages,
  approveScope,
  languageLabel,
}: {
  selectTab: (tab: RoomTab) => void;
  notify: (title: string, detail?: string) => void;
  composer: string;
  setComposer: React.Dispatch<React.SetStateAction<string>>;
  sendMessage: () => void;
  dynamicMessages: DynamicMessage[];
  approveScope: (id: number) => void;
  languageLabel: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [dynamicMessages]);

  const insertMention = () => {
    const node = textareaRef.current;
    if (!node) return;
    const start = node.selectionStart;
    const end = node.selectionEnd;
    setComposer(current => `${current.slice(0, start)}@AI Builder ${current.slice(end)}`);
    window.requestAnimationFrame(() => {
      node.focus();
      node.selectionStart = node.selectionEnd = start + 12;
    });
  };

  return (
    <div className="view chat-view active">
      <div className="message-scroll" ref={scrollRef}>
        <div className="message-list">
          <DayDivider />
          <MessageShell
            initials="AM"
            avatarTone="instructor"
            name="You"
            role="Instructor · Lead facilitator"
            time="9:04 AM"
          >
            <div className="message-text">
              <p>
                Good morning, Kijani team. Today we are improving the checkout—not merely making the interface
                look complete. Your final submission must show why the design is safer, clearer and
                accessible.
              </p>
              <div className="translation">
                <span>Luganda translation · original preserved</span>Leero tunoonya okukola checkout ebeere
                ennyangu, etegeerekeka era ey&apos;obukuumi. Obujulizi bwammwe bulina okulaga lwaki
                enkyukakyuka zikola.
              </div>
            </div>
            <AcceptanceCard selectTab={selectTab} />
            <div className="reaction-row">
              <Reaction active>✅ 4</Reaction>
              <Reaction>👀 3</Reaction>
            </div>
          </MessageShell>

          <MessageShell
            initials="RI"
            name="Ronald Isabirye"
            role="Instructor · Lead facilitator"
            time="9:13 AM"
          >
            <div className="message-text">
              <p>
                I tested the mobile checkout with the keyboard and screen reader. The coupon error is only
                shown by colour, and focus jumps behind the delivery drawer. I marked both problems below.
              </p>
            </div>
            <DefectAttachments notify={notify} />
            <div className="reaction-row">
              <Reaction>💡 2</Reaction>
              <Reaction>🙌 3</Reaction>
            </div>
          </MessageShell>

          <MessageShell initials="NK" name="Nakato Kezia" role="Learner · Frontend" time="9:18 AM">
            <div className="message-text">
              <p>
                I reproduced both. <span className="mention">@AI Builder</span> please propose the smallest
                fix for the coupon field and drawer focus. Work only on branch{" "}
                <strong>academy/checkout-a11y</strong>, do not access the internet, and do not merge anything.
              </p>
            </div>
            <AgentTaskCard />
          </MessageShell>

          <MessageShell avatarTone="ai" name="AI Builder" role="Permission-scoped agent" time="9:22 AM" agent>
            <div className="message-text">
              <p>
                I reproduced both defects in the isolated preview. I changed two files and opened draft pull
                request <strong>#184</strong>. I did not merge or access external services.
              </p>
              <p>
                <strong>Reasoning summary:</strong> keep validation next to the coupon field, connect it with{" "}
                <code>aria-describedby</code>, announce changes through a polite live region, and return focus
                to the delivery trigger when the drawer closes.
              </p>
            </div>
            <DiffCard selectTab={selectTab} notify={notify} />
            <div className="reaction-row">
              <Reaction active>✅ 3</Reaction>
              <Reaction>🔍 2</Reaction>
            </div>
          </MessageShell>

          <MessageShell initials="DM" avatarTone="mentor" name="Daniel Mwesigwa" role="Mentor" time="9:31 AM">
            <div className="message-text">
              <p>
                The implementation is directionally good, but the team—not the agent—must own the decision.
                Before I approve this for instructor review, please add a short reflection: why is a polite
                live region appropriate here, what did you test, and what risk remains?
              </p>
            </div>
            <div className="reaction-row">
              <Reaction>📝 3</Reaction>
              <Reaction>👍 2</Reaction>
            </div>
          </MessageShell>

          <MessageShell initials="JM" name="Joel Mugisha" role="Learner · QA" time="9:39 AM">
            <div className="message-text">
              <p>
                I reviewed the diff and ran the linked test set. A polite announcement does not interrupt the
                user&apos;s current reading, which fits a coupon response. I verified keyboard order, visible
                focus, invalid and valid coupon states, closing with Escape, and focus return. Remaining risk:
                third-party payment widgets still need a separate screen-reader check in staging.
              </p>
            </div>
            <EvaluationMessageCard selectTab={selectTab} />
          </MessageShell>

          <MessageShell
            initials="AM"
            avatarTone="instructor"
            name="You"
            role="Instructor · Lead facilitator"
            time="9:46 AM"
          >
            <div className="message-text">
              <p>
                Good evidence trail. I have approved the change for a learner merge, with one condition:
                complete the staging check Joel identified and attach the outcome. The AI output supports the
                work; it is not the final assessment.
              </p>
            </div>
            <div className="agent-task">
              <div className="agent-task-top">
                <span
                  className="agent-mark"
                  style={{ background: "var(--orange-soft)", color: "var(--orange)" }}
                >
                  <Icon name="shield" />
                </span>
                <div className="agent-task-copy">
                  <strong>Human checkpoint approved with condition</strong>
                  <p>Decision by Amina · PR #184 may be merged by a learner after staging verification</p>
                </div>
                <StatusChip tone="orange">1 condition</StatusChip>
              </div>
              <div className="scope-grid">
                <div className="scope-item">
                  <span>Next owner</span>
                  <strong>Joel · staging check</strong>
                </div>
                <div className="scope-item">
                  <span>Merge authority</span>
                  <strong>Nakato or Ronald</strong>
                </div>
                <div className="scope-item">
                  <span>Evidence status</span>
                  <strong>Provisional · review pending</strong>
                </div>
              </div>
            </div>
          </MessageShell>

          {dynamicMessages.map(message => (
            <DynamicRoomMessage
              key={message.id}
              message={message}
              approveScope={approveScope}
              notify={notify}
            />
          ))}
        </div>
      </div>

      <div className="composer-wrap">
        <div className="composer">
          <div className="composer-top">
            <span className="composer-context">
              <Icon name="hash" size="sm" />
              group-build-checkout
            </span>
          </div>
          <textarea
            ref={textareaRef}
            rows={2}
            aria-label="Message group-build-checkout"
            value={composer}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setComposer(event.target.value)}
            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Guide the team, record a decision or type @ to invite a permission-scoped AI agent…"
          />
          <div className="composer-bottom">
            <button
              type="button"
              className="tool-btn"
              aria-label="Add attachment"
              onClick={() => notify("Attachment picker opened")}
            >
              <Icon name="plus" size="sm" />
            </button>
            <button
              type="button"
              className="tool-btn"
              aria-label="Attach file"
              onClick={() => notify("File picker opened")}
            >
              <Icon name="paperclip" size="sm" />
            </button>
            <button
              type="button"
              className="tool-btn"
              aria-label="Add emoji"
              onClick={() => notify("Emoji picker opened")}
            >
              <Icon name="smile" size="sm" />
            </button>
            <button
              type="button"
              className="tool-btn"
              aria-label="Record voice note"
              onClick={() => notify("Voice note ready")}
            >
              <Icon name="mic" size="sm" />
            </button>
            <button
              type="button"
              className="tool-btn"
              aria-label="Mention a person or agent"
              onClick={insertMention}
            >
              <Icon name="at" size="sm" />
            </button>
            <button
              type="button"
              className="tool-btn language"
              aria-label="Message language"
              onClick={() => notify("Language menu opened")}
            >
              <Icon name="globe" size="sm" />
              {languageLabel} <Icon name="chevron" size="sm" />
            </button>
            <span className="composer-spacer" />
            <button type="button" className="send-btn" onClick={sendMessage} disabled={!composer.trim()}>
              <Icon name="send" size="sm" />
              Send
            </button>
          </div>
        </div>
        <div className="composer-hint">
          Enter to send · Shift + Enter for a new line · Instructor decisions are attributable · AI agents
          cannot grade or merge
        </div>
      </div>
    </div>
  );
}

function ViewHeading({
  icon,
  title,
  description,
  action,
}: {
  icon: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="view-heading">
      <span className="heading-icon">
        <Icon name={icon} size="lg" />
      </span>
      <div className="view-heading-copy">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function PlanView({ notify }: { notify: (title: string, detail?: string) => void }) {
  const steps = [
    {
      title: "Reproduce and document the defects",
      description: "Keyboard, screen-reader and mobile evidence attached in the room.",
      owner: "Ronald",
      initials: "RI",
      status: "done",
    },
    {
      title: "Agree the smallest safe implementation",
      description: "Scope limited to coupon feedback and drawer focus restoration.",
      owner: "Nakato",
      initials: "NK",
      status: "done",
    },
    {
      title: "Generate a draft patch in an isolated branch",
      description: "AI Builder used two files, internet off and no merge authority.",
      owner: "AI Builder",
      initials: "AI",
      status: "done",
    },
    {
      title: "Run automated and manual evaluation",
      description: "18 checks passed; human keyboard and announcement check recorded.",
      owner: "Joel",
      initials: "JM",
      status: "done",
    },
    {
      title: "Verify the third-party payment widget in staging",
      description: "Instructor condition before learner merge.",
      owner: "",
      initials: "",
      status: "review",
    },
    {
      title: "Learner merge, reflection and evidence submission",
      description: "Record decision owner, residual risk and proof of the final state.",
      owner: "",
      initials: "",
      status: "pending",
    },
  ];
  return (
    <div className="view canvas-view active">
      <div className="canvas-inner">
        <ViewHeading
          icon="clipboard"
          title="Shared build plan"
          description="The team and AI agents work from the same visible plan. Learners can edit it; agents can propose changes but cannot approve scope."
          action={
            <button type="button" className="primary-btn" onClick={() => notify("New plan step added")}>
              <Icon name="plus" size="sm" />
              Add step
            </button>
          }
        />
        <div className="panel-grid">
          <section className="panel full">
            <div className="panel-head">
              <Icon name="clipboard" size="sm" />
              <strong>Checkout accessibility sprint</strong>
              <span>4 complete · 1 in review · 1 pending</span>
            </div>
            <div className="panel-body">
              {steps.map((step, index) => (
                <div className="plan-step" key={step.title}>
                  <span
                    className={`step-number ${step.status === "done" ? "done" : ""}`}
                    style={
                      step.status === "review"
                        ? {
                            borderColor: "rgba(247,127,0,.28)",
                            background: "var(--orange-soft)",
                            color: "var(--orange)",
                          }
                        : undefined
                    }
                  >
                    {step.status === "done" ? <Icon name="check" size="sm" /> : index + 1}
                  </span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.description}</p>
                  </div>
                  {step.owner ? (
                    <span className="owner">
                      <i
                        style={
                          step.initials === "AI"
                            ? { background: "var(--green-soft)", color: "var(--green)" }
                            : undefined
                        }
                      >
                        {step.initials}
                      </i>
                      {step.owner}
                    </span>
                  ) : (
                    <StatusChip tone={step.status === "review" ? "orange" : "default"}>
                      {step.status === "review" ? "In review" : "Pending"}
                    </StatusChip>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <Icon name="shield" size="sm" />
              <strong>Working agreement</strong>
            </div>
            <div className="panel-body">
              <InfoRow
                icon="check"
                title="Human-owned decisions"
                text="Learners approve plans and merges; instructors make assessment decisions."
              />
              <InfoRow
                icon="check"
                title="Visible AI work"
                text="Plans, prompts, changes, tests and stops remain in the room record."
              />
              <InfoRow
                icon="check"
                title="Minimum access"
                text="Agents inherit the invoker's rights and are further limited by task scope."
              />
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <Icon name="globe" size="sm" />
              <strong>Language and inclusion</strong>
            </div>
            <div className="panel-body">
              <InfoRow
                icon="message"
                title="English + Luganda"
                text="Translations are optional and the original message is always retained."
              />
              <InfoRow
                icon="eye"
                title="Accessible artifacts"
                text="Images require descriptions; recordings carry captions or transcripts."
              />
              <InfoRow
                icon="users"
                title="Participation evidence"
                text="Contribution is shown by decisions and artifacts—not message volume."
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="info-row">
      <Icon name={icon} size="sm" />
      <div className="info-row-copy">
        <strong>{title}</strong>
        <span>{text}</span>
      </div>
    </div>
  );
}

function ArtifactRow({
  icon,
  tone = "default",
  title,
  detail,
  action,
}: {
  icon: string;
  tone?: "default" | "green" | "orange";
  title: string;
  detail: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="artifact-row">
      <span className={`artifact-icon ${tone === "default" ? "" : tone}`.trim()}>
        <Icon name={icon} size="sm" />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{detail}</p>
      </div>
      {action}
    </div>
  );
}

function ArtifactsView({
  selectTab,
  notify,
}: {
  selectTab: (tab: RoomTab) => void;
  notify: (title: string, detail?: string) => void;
}) {
  return (
    <div className="view canvas-view active">
      <div className="canvas-inner">
        <ViewHeading
          icon="folder"
          title="Room artifacts"
          description="Designs, files, code changes, recordings and decisions are connected to the assignment rather than buried in a chat history."
          action={
            <button type="button" className="primary-btn" onClick={() => notify("Artifact upload opened")}>
              <Icon name="plus" size="sm" />
              Add artifact
            </button>
          }
        />
        <div className="panel-grid">
          <section className="panel">
            <div className="panel-head">
              <Icon name="folder" size="sm" />
              <strong>Design and research</strong>
              <span>3 items</span>
            </div>
            <div className="panel-body">
              <ArtifactRow
                icon="image"
                tone="orange"
                title="Kijani checkout · Figma design"
                detail="Connected frame · modified today by Nakato"
                action={
                  <button type="button" className="mini-btn" onClick={() => notify("Design opened")}>
                    Open
                  </button>
                }
              />
              <ArtifactRow
                icon="image"
                title="coupon-error-mobile.png"
                detail="Annotated defect · Ronald"
                action={
                  <button type="button" className="mini-btn" onClick={() => notify("Screenshot opened")}>
                    View
                  </button>
                }
              />
              <ArtifactRow
                icon="video"
                title="focus-order-recording.webm"
                detail="38 seconds · captions included"
                action={
                  <button type="button" className="mini-btn" onClick={() => notify("Recording opened")}>
                    Play
                  </button>
                }
              />
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <Icon name="code" size="sm" />
              <strong>Code and evaluation</strong>
              <span>4 items</span>
            </div>
            <div className="panel-body">
              <ArtifactRow
                icon="git"
                tone="green"
                title="Draft pull request #184"
                detail="2 files · +27 −6 · human merge required"
                action={<StatusChip tone="orange">Review</StatusChip>}
              />
              <ArtifactRow
                icon="test"
                tone="green"
                title="checkout-a11y-184 evaluation"
                detail="18/18 passed · automated + human"
                action={<StatusChip tone="green">Passed</StatusChip>}
              />
              <ArtifactRow
                icon="file"
                title="Agent activity record CB-184"
                detail="Prompt, permissions, steps and output"
                action={
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={() => notify("Agent activity record opened")}
                  >
                    Inspect
                  </button>
                }
              />
              <ArtifactRow
                icon="file"
                title="Team reflection draft"
                detail="Decision, tests and remaining risk"
                action={<StatusChip>Draft</StatusChip>}
              />
            </div>
          </section>
          <section className="panel full">
            <div className="panel-head">
              <Icon name="clock" size="sm" />
              <strong>Decision record</strong>
              <span>Searchable and retained with the project</span>
            </div>
            <div className="panel-body">
              <ArtifactRow
                icon="shield"
                tone="orange"
                title="DR-012 · Use a polite announcement for coupon feedback"
                detail="Proposed by AI Builder · verified by Joel · conditionally approved by Amina · rationale captured in chat"
                action={
                  <button type="button" className="mini-btn green" onClick={() => selectTab("chat")}>
                    See discussion
                  </button>
                }
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function PreviewView({
  device,
  setDevice,
  notify,
}: {
  device: PreviewDevice;
  setDevice: React.Dispatch<React.SetStateAction<PreviewDevice>>;
  notify: (title: string, detail?: string) => void;
}) {
  const [basketSize, setBasketSize] = useState("4–5");
  const [delivery, setDelivery] = useState("Home");
  return (
    <div className="view canvas-view active">
      <div className="canvas-inner">
        <ViewHeading
          icon="eye"
          title="Live build preview"
          description="Everyone in the room can inspect the same isolated branch, switch devices and comment on the exact state under review."
          action={
            <button
              type="button"
              className="primary-btn orange"
              onClick={() => notify("Preview shared in the room")}
            >
              <Icon name="message" size="sm" />
              Share preview
            </button>
          }
        />
        <div className="preview-toolbar">
          <div className="segmented" aria-label="Preview device">
            {(["desktop", "tablet", "mobile"] as PreviewDevice[]).map(option => (
              <button
                type="button"
                className={device === option ? "active" : ""}
                onClick={() => setDevice(option)}
                key={option}
                aria-label={option}
              >
                <Icon name={option === "desktop" ? "monitor" : option} size="sm" />
              </button>
            ))}
          </div>
          <StatusChip tone="green">
            <i className="dot" />
            Branch preview ready
          </StatusChip>
          <StatusChip>
            <Icon name="git" size="sm" />
            academy/checkout-a11y
          </StatusChip>
          <StatusChip>
            <Icon name="shield" size="sm" />
            Sandbox · test data
          </StatusChip>
          <span style={{ flex: 1 }} />
          <button type="button" className="mini-btn" onClick={() => notify("Preview refreshed")}>
            <Icon name="refresh" size="sm" />
            Refresh
          </button>
          <button type="button" className="mini-btn" onClick={() => notify("Comment mode enabled")}>
            <Icon name="message" size="sm" />
            Comment
          </button>
        </div>
        <div className="preview-stage">
          <div className={`device-frame ${device === "desktop" ? "" : device}`.trim()}>
            <div className="browser-bar">
              <i />
              <i />
              <i />
              <span className="browser-url">preview.kijani.test/checkout</span>
            </div>
            <div className="shop-preview">
              <div className="shop-nav">
                <span className="shop-logo">Kijani.</span>
                <div className="shop-links">
                  <span>Shop</span>
                  <span>Stories</span>
                  <span>Support</span>
                </div>
                <span className="shop-cart">
                  <Icon name="folder" size="sm" />
                </span>
              </div>
              <div className="shop-body">
                <div className="product-gallery">
                  <div className="product-main">
                    <div className="product-orb">K</div>
                  </div>
                  <div className="thumbs">
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <div className="product-copy">
                  <span className="eyebrow">Kijani essentials</span>
                  <h3>Everyday Market Basket</h3>
                  <div className="rating">
                    ★★★★★ <span style={{ color: "#68786f" }}>4.9 · 126 reviews</span>
                  </div>
                  <div className="price">UGX 89,000</div>
                  <p>
                    A thoughtfully selected household basket sourced from local producers, with delivery
                    options chosen at checkout.
                  </p>
                  <div className="option-label">Basket size</div>
                  <div className="options">
                    {["2–3", "4–5", "6+"].map(option => (
                      <button
                        type="button"
                        key={option}
                        className={basketSize === option ? "active" : ""}
                        onClick={() => {
                          setBasketSize(option);
                          notify(`Basket size set to ${option}`);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <div className="option-label">Delivery</div>
                  <div className="options">
                    {["Home", "Pick up"].map(option => (
                      <button
                        type="button"
                        key={option}
                        className={delivery === option ? "active" : ""}
                        onClick={() => {
                          setDelivery(option);
                          notify(`Delivery set to ${option}`);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="add-cart"
                    onClick={() => notify("Accessible checkout continued")}
                  >
                    Continue to accessible checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestRow({ title, description, code }: { title: string; description: string; code: string }) {
  return (
    <div className="test-row">
      <span className="test-status">
        <Icon name="check" size="sm" />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <code>{code}</code>
    </div>
  );
}

function TestsView({
  selectTab,
  notify,
}: {
  selectTab: (tab: RoomTab) => void;
  notify: (title: string, detail?: string) => void;
}) {
  return (
    <div className="view canvas-view active">
      <div className="canvas-inner">
        <ViewHeading
          icon="test"
          title="Evaluation and test evidence"
          description="Automated checks are combined with human observation. Passing tests do not replace instructor judgment or the learner's explanation."
          action={
            <button type="button" className="primary-btn" onClick={() => notify("Evaluation run started")}>
              <Icon name="play" size="sm" />
              Run evaluation
            </button>
          }
        />
        <div className="metric-grid" style={{ marginBottom: 12 }}>
          <div className="metric">
            <span>Total checks</span>
            <strong>18</strong>
            <em>All complete</em>
          </div>
          <div className="metric">
            <span>Automated</span>
            <strong>17</strong>
            <em>17 passed</em>
          </div>
          <div className="metric">
            <span>Human checks</span>
            <strong>1</strong>
            <em>Recorded</em>
          </div>
          <div className="metric">
            <span>Coverage</span>
            <strong>4/4</strong>
            <em>Requirements linked</em>
          </div>
        </div>
        <div className="panel-grid">
          <section className="panel full">
            <div className="panel-head">
              <Icon name="test" size="sm" />
              <strong>checkout-a11y-184 · latest run</strong>
              <span>Completed today at 9:36 AM · 42 seconds</span>
            </div>
            <div className="panel-body">
              <TestRow
                title="Coupon feedback is connected to the input"
                description="Invalid state exposes descriptive text through the accessibility tree."
                code="ACC-02 · 38ms"
              />
              <TestRow
                title="Coupon status is announced without interrupting reading"
                description="Polite live region updates on valid and invalid responses."
                code="ACC-02 · 51ms"
              />
              <TestRow
                title="Closing the delivery drawer restores focus"
                description="Escape and close-button paths return focus to the opening control."
                code="UX-04 · 62ms"
              />
              <TestRow
                title="Keyboard-only checkout path"
                description="Human observation by Joel; recording and notes attached."
                code="EVAL-03 · human"
              />
              <TestRow
                title="Payment secrets excluded from logs and agent context"
                description="Redaction and fixture-data assertions passed."
                code="SEC-06 · 44ms"
              />
            </div>
            <div className="card-actions">
              <button
                type="button"
                className="mini-btn"
                onClick={() => notify("Evaluation report downloaded")}
              >
                <Icon name="file" size="sm" />
                Download report
              </button>
              <button
                type="button"
                className="mini-btn"
                onClick={() => notify("Human check recording opened")}
              >
                <Icon name="video" size="sm" />
                Watch human check
              </button>
              <button type="button" className="mini-btn green" onClick={() => selectTab("evidence")}>
                <Icon name="award" size="sm" />
                Use as evidence
              </button>
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <Icon name="shield" size="sm" />
              <strong>Required staging check</strong>
            </div>
            <div className="panel-body">
              <InfoRow
                icon="clock"
                title="Third-party payment widget"
                text="Pending manual screen-reader verification in staging. This is the instructor's condition before learner merge."
              />
              <button
                type="button"
                className="mini-btn orange"
                style={{ marginTop: 5 }}
                onClick={() => notify("Staging verification checklist opened")}
              >
                Start verification
              </button>
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <Icon name="evidence" size="sm" />
              <strong>Requirement traceability</strong>
            </div>
            <div className="panel-body">
              <InfoRow
                icon="check"
                title="UX-04 · 5 checks"
                text="Flow clarity, focus order and focus restoration."
              />
              <InfoRow
                icon="check"
                title="ACC-02 · 8 checks"
                text="Announcements, labels, contrast and keyboard access."
              />
              <InfoRow
                icon="check"
                title="SEC-06 · 5 checks"
                text="Data isolation, redaction and permission boundaries."
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function EvidenceItem({ title, text, pending = false }: { title: string; text: string; pending?: boolean }) {
  return (
    <div className="evidence-item" style={pending ? { borderColor: "rgba(247,127,0,.26)" } : undefined}>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}

function EvidenceView({ notify }: { notify: (title: string, detail?: string) => void }) {
  return (
    <div className="view canvas-view active">
      <div className="canvas-inner">
        <ViewHeading
          icon="award"
          title="Learning and credential evidence"
          description="Evidence is organised around what the learner understood, what the team built and how the result was proven—not around AI-generated output alone."
          action={
            <button
              type="button"
              className="primary-btn orange"
              onClick={() => notify("Evidence submission preview opened")}
            >
              <Icon name="eye" size="sm" />
              Preview submission
            </button>
          }
        />
        <div className="evidence-map">
          <section className="evidence-column">
            <div className="evidence-column-head">
              <Icon name="book" size="sm" />
              <strong>Skill</strong>
              <StatusChip tone="green">3 items</StatusChip>
            </div>
            <div className="evidence-column-body">
              <EvidenceItem
                title="Accessibility defect analysis"
                text="Annotated screenshot and screen-reader observation by Ronald."
              />
              <EvidenceItem
                title="Decision reflection"
                text="Joel explains the announcement strategy, tests and residual risk."
              />
              <EvidenceItem
                title="Peer review participation"
                text="Nakato constrained the AI task and reviewed the draft change."
              />
            </div>
          </section>
          <section className="evidence-column">
            <div className="evidence-column-head">
              <Icon name="code" size="sm" />
              <strong>System</strong>
              <StatusChip tone="green">3 items</StatusChip>
            </div>
            <div className="evidence-column-body">
              <EvidenceItem
                title="Draft pull request #184"
                text="Accessible coupon feedback and drawer focus restoration."
              />
              <EvidenceItem
                title="Connected live preview"
                text="Responsive checkout state on a permission-scoped branch."
              />
              <EvidenceItem
                title="Agent activity record"
                text="Task scope, access boundary, actions and generated patch."
              />
            </div>
          </section>
          <section className="evidence-column">
            <div className="evidence-column-head">
              <Icon name="shield" size="sm" />
              <strong>Proof</strong>
              <StatusChip tone="orange">1 pending</StatusChip>
            </div>
            <div className="evidence-column-body">
              <EvidenceItem
                title="18-check evaluation run"
                text="Automated and human checks mapped to requirements."
              />
              <EvidenceItem
                title="Instructor checkpoint"
                text="Conditional approval by Amina with named next action."
              />
              <EvidenceItem
                pending
                title="Staging assistive-technology check"
                text="Pending. Required before the evidence set is ready for final review."
              />
            </div>
          </section>
        </div>
        <div className="evidence-footer">
          <Icon name="info" size="lg" />
          <p>
            <strong>Human review remains final.</strong> AI may help organise evidence and identify gaps, but
            an authorised Academy educator decides whether the work meets the credential standard.
          </p>
          <button
            type="button"
            className="mini-btn orange"
            onClick={() => notify("Instructor review room opened")}
          >
            Open review room
          </button>
        </div>
        <div className="panel-grid" style={{ marginTop: 12 }}>
          <section className="panel">
            <div className="panel-head">
              <Icon name="users" size="sm" />
              <strong>Contribution record</strong>
            </div>
            <div className="panel-body">
              <MemberRow
                initials="RI"
                name="Ronald Isabirye"
                role="Defect analysis · product reasoning · team coordination"
                status={<StatusChip tone="green">Ready</StatusChip>}
              />
              <MemberRow
                initials="NK"
                name="Nakato Kezia"
                role="Task scoping · frontend review · merge owner"
                status={<StatusChip tone="green">Ready</StatusChip>}
              />
              <MemberRow
                initials="JM"
                name="Joel Mugisha"
                role="Evaluation · reflection · staging verification"
                status={<StatusChip tone="orange">Action</StatusChip>}
              />
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <Icon name="clock" size="sm" />
              <strong>Evidence timeline</strong>
            </div>
            <div className="panel-body">
              <InfoRow
                icon="check"
                title="9:13 · Defects documented"
                text="Original media retained with descriptions and captions."
              />
              <InfoRow
                icon="check"
                title="9:22 · Draft patch produced"
                text="Agent permissions and code difference recorded."
              />
              <InfoRow
                icon="check"
                title="9:46 · Human checkpoint"
                text="Conditional decision linked to one remaining verification."
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function MemberRow({
  initials,
  name,
  role,
  status,
}: {
  initials: string;
  name: string;
  role: string;
  status?: React.ReactNode;
}) {
  return (
    <div className="member-row">
      <span className="member-avatar">{initials}</span>
      <div className="member-copy">
        <strong>{name}</strong>
        <span>{role}</span>
      </div>
      {status ?? <i className="online-dot" />}
    </div>
  );
}

function Inspector({
  open,
  tab,
  setTab,
  close,
  notify,
  agentStopped,
  stopAgent,
  openMembers,
  manageGroups,
  openLab,
  openClass,
  subgroups,
}: {
  open: boolean;
  tab: InspectorTab;
  setTab: React.Dispatch<React.SetStateAction<InspectorTab>>;
  close: () => void;
  notify: (title: string, detail?: string) => void;
  agentStopped: boolean;
  stopAgent: () => void;
  openMembers: () => void;
  manageGroups: () => void;
  openLab: () => void;
  openClass: () => void;
  subgroups: BuildRoomSubgroup[];
}) {
  const assignedLearners = new Set(subgroups.flatMap(group => group.memberIds)).size;
  const reviewCount = subgroups.reduce((sum, group) => sum + group.reviewCount, 0);
  return (
    <aside className={`inspector ${open ? "open" : "closed"}`} aria-label="Room details and agents">
      <div className="inspector-head">
        <strong>Build room</strong>
        <StatusChip tone="green">
          <i className="dot" />
          Active
        </StatusChip>
        <button type="button" className="icon-btn" onClick={close} aria-label="Close inspector">
          <Icon name="x" />
        </button>
      </div>
      <div className="inspector-tabs">
        <button
          type="button"
          className={`inspector-tab ${tab === "details" ? "active" : ""}`}
          onClick={() => setTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`inspector-tab ${tab === "agents" ? "active" : ""}`}
          onClick={() => setTab("agents")}
        >
          Agents · 2
        </button>
      </div>
      <div className="inspector-scroll">
        {tab === "details" ? (
          <div className="inspector-pane active">
            <section className="info-block">
              <div className="info-title">
                <Icon name="info" size="sm" />
                About this room
              </div>
              <InfoRow
                icon="message"
                title="Purpose"
                text="Build, test and explain Kijani Market's checkout improvement as a visible team."
              />
              <InfoRow
                icon="lock"
                title="Access"
                text="October 2026 cohort, assigned mentors and authorised Academy staff."
              />
              <InfoRow
                icon="clock"
                title="Retention"
                text="Messages, decisions and selected artifacts are retained with project evidence."
              />
            </section>
            <section className="info-block">
              <div className="info-title">
                <Icon name="users" size="sm" />
                Small groups · {subgroups.length}
              </div>
              <InfoRow
                icon="users"
                title={`${assignedLearners} learners assigned`}
                text="Learners keep the shared parent room and receive one persistent small-group workspace."
              />
              <InfoRow
                icon="shield"
                title="Private by default"
                text="Group messages and drafts are visible only to assigned members, instructors and authorised reviewers."
              />
              <InfoRow
                icon="alert"
                title={`${reviewCount} instructor reviews`}
                text="The parent overview shows progress and checkpoints without exposing private group conversations."
              />
              <button type="button" className="agent-catalog" onClick={manageGroups}>
                <Icon name="grid" size="sm" /> Manage small groups
              </button>
            </section>
            <section className="info-block">
              <div className="info-title">
                <Icon name="shield" size="sm" />
                Room controls
              </div>
              <InfoRow
                icon="check"
                title="Human merge required"
                text="AI agents cannot approve or merge code."
              />
              <InfoRow
                icon="check"
                title="Group-scoped AI access"
                text="Every small group receives a separate branch, files and agent permission record."
              />
              <InfoRow
                icon="check"
                title="Test data only"
                text="No learner or customer payment data enters a group preview or Lab handoff."
              />
            </section>
            <section className="info-block">
              <div className="info-title">
                <Icon name="users" size="sm" />
                People · 124
              </div>
              <MemberRow initials="AM" name="Amina Mutesi" role="Instructor · final human reviewer" />
              <MemberRow initials="DM" name="Daniel Mwesigwa" role="Mentor · product and accessibility" />
              <MemberRow initials="RI" name="Ronald Isabirye" role="Learner · product lead" />
              <MemberRow initials="NK" name="Nakato Kezia" role="Learner · frontend" />
              <MemberRow initials="JM" name="Joel Mugisha" role="Learner · evaluation" />
              <button type="button" className="agent-catalog" onClick={openMembers}>
                Search all 124 members
              </button>
            </section>
            <section className="info-block">
              <div className="info-title">
                <Icon name="flask" size="sm" />
                Connected Academy tools
              </div>
              <ArtifactRow
                icon="flask"
                tone="green"
                title="AI-App Builder Lab"
                detail="Independent workspace on labs.chatoner.com"
                action={
                  <button type="button" className="mini-btn" onClick={openLab}>
                    Open
                    <Icon name="external" size="sm" />
                  </button>
                }
              />
              <ArtifactRow
                icon="video"
                title="Chatoner Class"
                detail="Live studio and group huddles"
                action={
                  <button type="button" className="mini-btn" onClick={openClass}>
                    Open
                    <Icon name="external" size="sm" />
                  </button>
                }
              />
            </section>
          </div>
        ) : (
          <div className="inspector-pane active">
            <section className="info-block" style={{ paddingTop: 0 }}>
              <div className="info-title">
                <Icon name="bot" size="sm" />
                Active in this room
              </div>
              <article className={`agent-card ${agentStopped ? "" : "running"}`}>
                <div className="agent-card-top">
                  <span className="agent-card-icon">
                    <Icon name="bot" />
                  </span>
                  <div className="agent-card-copy">
                    <strong>AI Builder</strong>
                    <span>
                      {agentStopped
                        ? "Stopped by Amina · activity retained"
                        : "Completed task CB-184 · watching review"}
                    </span>
                  </div>
                  {agentStopped ? (
                    <StatusChip tone="red">Stopped</StatusChip>
                  ) : (
                    <button
                      type="button"
                      className="stop-btn"
                      onClick={stopAgent}
                      aria-label="Stop AI Builder"
                    >
                      <Icon name="stop" size="sm" />
                    </button>
                  )}
                </div>
                <div className="agent-card-body">
                  <p>Drafted the smallest scoped accessibility patch and opened PR #184.</p>
                  <div className="agent-mini-meta">
                    <span>2 files</span>
                    <span>Internet off</span>
                    <span>Branch write</span>
                    <span>No merge</span>
                  </div>
                </div>
              </article>
              <article className="agent-card">
                <div className="agent-card-top">
                  <span
                    className="agent-card-icon"
                    style={{ background: "rgba(104,168,255,.1)", color: "var(--blue)" }}
                  >
                    <Icon name="test" />
                  </span>
                  <div className="agent-card-copy">
                    <strong>Evaluation Agent</strong>
                    <span>Idle · available to run approved checks</span>
                  </div>
                  <StatusChip>Idle</StatusChip>
                </div>
                <div className="agent-card-body">
                  <p>Runs the room&apos;s linked unit, integration, accessibility and safety evaluations.</p>
                  <div className="agent-mini-meta">
                    <span>Read + test</span>
                    <span>Fixtures only</span>
                    <span>No code writes</span>
                  </div>
                </div>
              </article>
              <button
                type="button"
                className="agent-catalog"
                onClick={() => notify("Agent catalogue opened")}
              >
                <Icon name="plus" size="sm" /> Invite an approved agent
              </button>
            </section>
            <section className="info-block">
              <div className="info-title">
                <Icon name="shield" size="sm" />
                How agents work here
              </div>
              <InfoRow
                icon="users"
                title="Invoked by a person"
                text="The activity record names who requested the task and which group scope was inherited."
              />
              <InfoRow
                icon="eye"
                title="Visible to the group"
                text="Plans, progress, results, failures and stop actions appear in the assigned group's conversation."
              />
              <InfoRow
                icon="shield"
                title="Bounded authority"
                text="Agents cannot cross group branches, make final learning decisions or merge protected work."
              />
            </section>
          </div>
        )}
      </div>
    </aside>
  );
}

// Preserved legacy room overlay; the v3 shell uses the newer global search implementation.
// eslint-disable-next-line unused-imports/no-unused-vars
function SearchModal({
  open,
  query,
  setQuery,
  close,
  selectTab,
}: {
  open: boolean;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  close: () => void;
  selectTab: (tab: RoomTab) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(
    () =>
      [
        {
          tab: "chat" as RoomTab,
          icon: "message",
          title: "Coupon accessibility decision",
          detail: "#group-build-checkout · polite live region, focus restoration and PR #184",
          label: "Message",
          tone: "default" as const,
        },
        {
          tab: "preview" as RoomTab,
          icon: "eye",
          title: "Kijani checkout branch preview",
          detail: "academy/checkout-a11y · isolated test-data environment",
          label: "Preview",
          tone: "default" as const,
        },
        {
          tab: "tests" as RoomTab,
          icon: "test",
          title: "Evaluation run checkout-a11y-184",
          detail: "18 of 18 checks passed · one staging condition remains",
          label: "Passed",
          tone: "green" as const,
        },
        {
          tab: "evidence" as RoomTab,
          icon: "award",
          title: "Credential evidence set",
          detail: "Skill, system and proof artifacts for this sprint",
          label: "1 pending",
          tone: "orange" as const,
        },
      ].filter(item => `${item.title} ${item.detail}`.toLowerCase().includes(query.toLowerCase())),
    [query],
  );

  useEffect(() => {
    if (open) window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Search Academy space"
      onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="search-modal">
        <div className="search-input-wrap">
          <Icon name="search" />
          <input
            ref={inputRef}
            autoComplete="off"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            placeholder="Search messages, decisions, artifacts and people…"
          />
          <button type="button" className="icon-btn" onClick={close} aria-label="Close search">
            <Icon name="x" />
          </button>
        </div>
        <div className="search-results">
          {results.length ? (
            results.map(item => (
              <button
                type="button"
                className="search-result"
                key={item.title}
                onClick={() => selectTab(item.tab)}
              >
                <span className="result-icon">
                  <Icon name={item.icon} size="sm" />
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </div>
                <StatusChip tone={item.tone}>{item.label}</StatusChip>
              </button>
            ))
          ) : (
            <div style={{ padding: 22, color: "var(--muted)", textAlign: "center", fontSize: 14 }}>
              No matching room records.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function _ToastStack({ notices }: { notices: ToastNotice[] }) {
  return (
    <div className="toast-stack" aria-live="polite">
      {notices.map(notice => (
        <div className="toast" key={notice.id}>
          <Icon name="check" size="sm" />
          <div>
            <strong>{notice.title}</strong>
            <span>{notice.detail}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RoomHeader({
  openSidebar,
  openInspector,
  openSearch,
  openMembers,
  manageGroups,
  inspectorOpen,
  notify,
  startHuddle,
  subgroupCount,
}: {
  openSidebar: () => void;
  openInspector: () => void;
  openSearch: () => void;
  openMembers: () => void;
  manageGroups: () => void;
  inspectorOpen: boolean;
  notify: (title: string, detail?: string) => void;
  startHuddle?: () => void;
  subgroupCount: number;
}) {
  return (
    <header className="room-head">
      <button type="button" className="icon-btn mobile-menu" onClick={openSidebar} aria-label="Open channels">
        <Icon name="menu" />
      </button>
      <div className="room-title">
        <h1>
          <Icon name="hash" size="sm" />
          <span>group-build-checkout</span>
          <StatusChip tone="green">
            <i className="dot" /> Live build
          </StatusChip>
        </h1>
        <p>
          Kijani Market · Product checkout sprint · 34 learners in {subgroupCount} small groups · 124
          connected
        </p>
      </div>
      <button
        type="button"
        className="member-stack-button"
        onClick={openMembers}
        aria-label="Open all 124 Build Room members"
      >
        <span className="member-stack" aria-hidden="true">
          <span>RI</span>
          <span>NK</span>
          <span>JM</span>
          <span>AM</span>
          <span className="ai">AI</span>
          <span className="overflow-count">+119</span>
        </span>
        <span className="member-stack-label">124</span>
      </button>
      <div className="room-actions">
        <button
          type="button"
          className="pill-btn group-pill"
          onClick={manageGroups}
          aria-label={`Manage ${subgroupCount} small groups`}
        >
          <Icon name="grid" size="sm" />
          Groups · {subgroupCount}
        </button>
        <button
          type="button"
          className="pill-btn"
          onClick={() => (startHuddle ? startHuddle() : notify("Chatoner Class huddle ready"))}
          aria-label="Start this Build Room huddle in Chatoner Class"
        >
          <Icon name="video" size="sm" />
          Start huddle
        </button>
        <button type="button" className="icon-btn" onClick={openSearch} aria-label="Search room">
          <Icon name="search" />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={openInspector}
          aria-label={inspectorOpen ? "Hide room details" : "Open room details"}
          aria-pressed={inspectorOpen}
        >
          <Icon name="panel" />
        </button>
      </div>
    </header>
  );
}

function ContextStrip({
  openBrief,
  openLanguage,
  languageLabel,
}: {
  openBrief: () => void;
  openLanguage: () => void;
  languageLabel: string;
}) {
  return (
    <section className="context-strip" aria-label="Connected assignment">
      <div className="context-icon">
        <Icon name="clipboard" size="lg" />
      </div>
      <div className="context-copy">
        <strong>Connected assignment: Build an accessible, trustworthy checkout</strong>
        <span>Requirement IDs UX-04 · ACC-02 · SEC-06 · Evaluation due 28 Nov 2026</span>
      </div>
      <div className="context-meta">
        <StatusChip tone="green">
          <Icon name="shield" size="sm" />
          Human merge only
        </StatusChip>
        <button
          type="button"
          className="language-context-btn"
          onClick={openLanguage}
          aria-label={`Open room language settings: ${languageLabel}`}
        >
          <Icon name="globe" size="sm" />
          {languageLabel}
          <Icon name="chevron" size="sm" />
        </button>
        <button
          type="button"
          className="mini-btn"
          onClick={openBrief}
          aria-label="Open connected assignment brief"
        >
          Open brief <Icon name="external" size="sm" />
        </button>
      </div>
    </section>
  );
}

function RoomTabs({ tab, selectTab }: { tab: RoomTab; selectTab: (tab: RoomTab) => void }) {
  return (
    <nav className="room-tabs" aria-label="Room views">
      {roomTabs.map(item => (
        <button
          type="button"
          className={`tab-btn ${tab === item.id ? "active" : ""}`}
          onClick={() => selectTab(item.id)}
          key={item.id}
        >
          <Icon name={item.icon} size="sm" />
          {item.label}
          {item.badge ? <span className="badge">{item.badge}</span> : null}
        </button>
      ))}
    </nav>
  );
}

function MobileBottom({
  tab,
  selectTab,
  openSidebar,
  openInspector,
}: {
  tab: RoomTab;
  selectTab: (tab: RoomTab) => void;
  openSidebar: () => void;
  openInspector: () => void;
}) {
  return (
    <nav className="mobile-bottom" aria-label="Mobile Academy navigation">
      <button type="button" onClick={openSidebar}>
        <Icon name="menu" />
        Channels
      </button>
      <button type="button" className={tab === "chat" ? "active" : ""} onClick={() => selectTab("chat")}>
        <Icon name="message" />
        Room
      </button>
      <button
        type="button"
        className={tab === "preview" ? "active" : ""}
        onClick={() => selectTab("preview")}
      >
        <Icon name="eye" />
        Preview
      </button>
      <button
        type="button"
        className={tab === "evidence" ? "active" : ""}
        onClick={() => selectTab("evidence")}
      >
        <Icon name="award" />
        Evidence
      </button>
      <button type="button" onClick={openInspector}>
        <Icon name="panel" />
        Details
      </button>
    </nav>
  );
}

const INSTRUCTOR_ROLES: Record<TeachingRoleId, TeachingRoleSpec> = {
  "instructor-lead": {
    id: "instructor-lead",
    name: "Instructor Lead",
    shortName: "Lead Instructor",
    description:
      "Leads teaching delivery, instructor assignments, publishing, moderation and final academic escalation.",
    authorities: ["view", "facilitate", "teach", "assess", "approve", "lead"],
    scope: "Assigned programs, cohorts, teaching teams, moderation and delegated final approvals",
  },
  "instructor-assessor": {
    id: "instructor-assessor",
    name: "Instructor & Assessor",
    shortName: "Instructor & Assessor",
    description: "Teaches assigned cohorts and conducts human assessment, grading and evidence review.",
    authorities: ["view", "facilitate", "teach", "assess"],
    scope:
      "Assigned cohorts, assessments, Labs evidence and project reviews; no final moderation or access administration",
  },
  instructor: {
    id: "instructor",
    name: "Instructor",
    shortName: "Instructor",
    description: "Plans lessons, hosts Classes, facilitates Build Rooms and supports assigned learners.",
    authorities: ["view", "facilitate", "teach"],
    scope: "Assigned programs, cohorts, Classes, Build Rooms and teaching content",
  },
  mentor: {
    id: "mentor",
    name: "Mentor",
    shortName: "Mentor",
    description:
      "Supports learners, facilitates discussions and gives formative feedback without grading or final decisions.",
    authorities: ["view", "facilitate"],
    scope: "Explicitly assigned learners, office hours, Build Rooms and project support",
  },
};

const AREA_CONFIG: Record<
  InstructorArea,
  { label: string; short: string; icon: string; description: string; defaultPage: string }
> = {
  command: {
    label: "Instructor Command",
    short: "IC",
    icon: "home",
    description: "Priorities, cohort health, teaching schedule and review workload",
    defaultPage: "dashboard",
  },
  cohorts: {
    label: "Cohorts & Learners",
    short: "CL",
    icon: "users",
    description: "Cohorts, learner progress, support signals and completion",
    defaultPage: "cohorts",
  },
  teaching: {
    label: "Curriculum & Teaching",
    short: "CT",
    icon: "book",
    description: "Programs, lessons, resources, accessibility and publishing",
    defaultPage: "curriculum",
  },
  class: {
    label: "Chatoner Class",
    short: "CC",
    icon: "video",
    description: "Schedule and host live teaching at class.chatoner.com",
    defaultPage: "schedule",
  },
  buildRooms: {
    label: "Build Rooms",
    short: "BR",
    icon: "message",
    description: "Facilitate group work, AI activity, evidence and human checkpoints",
    defaultPage: "overview",
  },
  labs: {
    label: "Connected AI Labs",
    short: "AL",
    icon: "flask",
    description: "Coordinate Academy assignments and open the independent Labs instructor workspace",
    defaultPage: "overview",
  },
  assessment: {
    label: "Assessment & Grading",
    short: "AG",
    icon: "clipboard",
    description: "Assignments, grading, assessments, questions, rubrics and moderation",
    defaultPage: "grading",
  },
  projects: {
    label: "Projects & Credentials",
    short: "PC",
    icon: "award",
    description: "Project verification, capstones, evidence and credential recommendations",
    defaultPage: "reviews",
  },
  engagement: {
    label: "Engagement",
    short: "EN",
    icon: "mail",
    description: "Office hours, messages, announcements and learner support",
    defaultPage: "office-hours",
  },
  analytics: {
    label: "Instructor Analytics",
    short: "AN",
    icon: "chart",
    description: "Outcomes, attendance, engagement, review SLA and workload",
    defaultPage: "overview",
  },
  search: {
    label: "Academy Search",
    short: "SE",
    icon: "search",
    description: "Find learners, classes, work, evidence and decisions",
    defaultPage: "all",
  },
  notifications: {
    label: "Notifications",
    short: "NT",
    icon: "bell",
    description: "Reviews, classes, support signals and system updates",
    defaultPage: "all",
  },
  settings: {
    label: "Instructor Settings",
    short: "ST",
    icon: "settings",
    description: "Profile, role access, languages, integrations and privacy",
    defaultPage: "profile",
  },
};

const RAIL_AREAS: InstructorArea[] = [
  "command",
  "cohorts",
  "teaching",
  "class",
  "buildRooms",
  "labs",
  "assessment",
  "projects",
  "engagement",
  "analytics",
];
const UTILITY_AREAS: InstructorArea[] = ["search", "notifications", "settings"];

const CAPSTONES: CapstoneReview[] = [
  {
    id: "capstone-grace",
    learner: "Grace Nwosu",
    initials: "GN",
    title: "Consulting Knowledge Pack and Delivery Copilot",
    program: "Prompt & Context Engineering",
    status: "Panel review",
    evidenceComplete: 100,
    defenseDate: "15 Nov · 3:00 PM",
    responsibleUse: "Pass",
    recommendation: "Pending",
  },
  {
    id: "capstone-hugo",
    learner: "Hugo Mendes",
    initials: "HM",
    title: "Operations Prompt Library",
    program: "Prompt & Context Engineering",
    status: "Revision",
    evidenceComplete: 67,
    defenseDate: "18 Nov · 11:00 AM",
    responsibleUse: "Review",
    recommendation: "Hold",
  },
  {
    id: "capstone-ibrahim",
    learner: "Ibrahim Yusuf",
    initials: "IY",
    title: "Policy Research Context System",
    program: "Prompt & Context Engineering",
    status: "Recommended",
    evidenceComplete: 100,
    defenseDate: "Completed",
    responsibleUse: "Pass",
    recommendation: "Recommend",
  },
];

const OFFICE_HOURS: OfficeSlot[] = [
  {
    id: "slot-mon-1600",
    day: "Monday",
    start: "16:00",
    end: "18:00",
    timezone: "Africa/Kampala",
    type: "Learner coaching · 25 minutes",
    capacity: 4,
    bookings: 3,
    enabled: true,
  },
  {
    id: "slot-wed-1200",
    day: "Wednesday",
    start: "12:00",
    end: "14:00",
    timezone: "Africa/Kampala",
    type: "Project architecture review · 30 minutes",
    capacity: 3,
    bookings: 2,
    enabled: true,
  },
  {
    id: "slot-fri-1500",
    day: "Friday",
    start: "15:00",
    end: "17:00",
    timezone: "Africa/Kampala",
    type: "Assessment clarification · 20 minutes",
    capacity: 4,
    bookings: 1,
    enabled: true,
  },
  {
    id: "slot-sat-1000",
    day: "Saturday",
    start: "10:00",
    end: "11:30",
    timezone: "Africa/Kampala",
    type: "Cohort open clinic · 20 minutes",
    capacity: 4,
    bookings: 4,
    enabled: false,
  },
];

function AcademyPanel({
  icon,
  title,
  meta,
  children,
  className = "",
  action,
}: {
  icon: string;
  title: string;
  meta?: string;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}) {
  return (
    <section className={`academy-panel ${className}`.trim()}>
      <header className="academy-panel-head">
        <Icon name={icon} size="sm" className="head-icon" />
        <strong>{title}</strong>
        {meta ? <span>{meta}</span> : null}
        {action}
      </header>
      <div className="academy-panel-body">{children}</div>
    </section>
  );
}

function TonePill({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "green" | "orange" | "red" | "purple" | "blue";
}) {
  return <span className={`tag-pill ${tone === "default" ? "" : tone}`.trim()}>{children}</span>;
}

function RiskPill({ risk }: { risk: LearnerRecord["risk"] }) {
  const label = risk === "on-track" ? "On track" : risk === "watch" ? "Watch" : "Intervention";
  return <span className={`risk-pill ${risk}`}>{label}</span>;
}

function ProgressMeter({
  value,
  tone = "default",
}: {
  value: number;
  tone?: "default" | "green" | "orange";
}) {
  return (
    <div className={`mini-progress`}>
      <div className={`progress-meter ${tone === "default" ? "" : tone}`}>
        <i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
      <span>{value}%</span>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  detail,
  tone = "purple",
}: {
  icon: string;
  label: string;
  value: string | number;
  detail: string;
  tone?: "purple" | "blue" | "cyan" | "orange";
}) {
  return (
    <article className={`stat-tile ${tone === "purple" ? "" : tone}`.trim()}>
      <div className="stat-top">
        <span>{label}</span>
        <Icon name={icon} size="sm" />
      </div>
      <strong>{value}</strong>
      <small className={tone === "orange" ? "warning" : ""}>{detail}</small>
    </article>
  );
}

function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">
        <Icon name={icon} size="lg" />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

function Switch({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      className={`toggle ${value ? "on" : ""}`}
      aria-label={label}
      aria-checked={value}
      aria-pressed={value}
      onClick={() => onChange(!value)}
    />
  );
}

function Avatar({
  initials,
  tone = "purple",
  small = false,
}: {
  initials: string;
  tone?: "purple" | "green" | "orange" | "red";
  small?: boolean;
}) {
  return (
    <span className={`avatar-chip ${small ? "small" : ""} ${tone === "purple" ? "" : tone}`.trim()}>
      {initials}
    </span>
  );
}

function InstructorHero({
  eyebrow,
  title,
  text,
  actions,
  side,
}: {
  eyebrow: string;
  title: string;
  text: string;
  actions?: React.ReactNode;
  side?: React.ReactNode;
}) {
  return (
    <section className="instructor-hero">
      <div className="instructor-hero-copy">
        <div className="hero-eyebrow">
          <Icon name="sparkles" size="sm" />
          {eyebrow}
        </div>
        <h2>{title}</h2>
        <p>{text}</p>
        {actions ? <div className="hero-actions">{actions}</div> : null}
      </div>
      <div className="instructor-hero-side">{side}</div>
    </section>
  );
}

function HeroSideCard({
  icon,
  title,
  detail,
  value,
}: {
  icon: string;
  title: string;
  detail: string;
  value: string | number;
}) {
  return (
    <article className="hero-side-card">
      <span className="hero-side-icon">
        <Icon name={icon} />
      </span>
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <em>{value}</em>
    </article>
  );
}

function PageTitle({ title, text, actions }: { title: string; text: string; actions?: React.ReactNode }) {
  return (
    <div className="section-title-row">
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      {actions ? <div className="section-actions">{actions}</div> : null}
    </div>
  );
}

function InstructorRouteFrame({
  spec,
  roleId,
  openRoleSwitch,
  children,
}: {
  spec: InstructorRouteSpec;
  roleId: TeachingRoleId;
  openRoleSwitch: () => void;
  children: React.ReactNode;
}) {
  const role = INSTRUCTOR_ROLES[roleId];
  return (
    <div
      className="route-frame"
      data-route-id={spec.id}
      data-route-title={spec.title}
      data-operational-model={spec.model}
      data-layout-family={spec.layout}
      data-route-signature={spec.signature}
      data-required-authority={spec.authority}
      data-active-role={roleId}
    >
      <span className="route-signature">{spec.signature}</span>
      <div className="route-operational-ribbon">
        <div className="route-model">
          <span>
            <Icon name={spec.icon} size="sm" />
            Operational model
          </span>
          <strong>{spec.model}</strong>
        </div>
        <div className="route-purpose">
          <strong>{spec.primaryAction}</strong>
          <p>{spec.purpose}</p>
        </div>
        <div className="route-ribbon-actions">
          <span className="route-backend-chip">
            <i />
            {spec.backendDomain}
          </span>
          <TonePill
            tone={
              spec.authority === "approve" || spec.authority === "lead"
                ? "orange"
                : spec.authority === "assess"
                  ? "blue"
                  : "green"
            }
          >
            {spec.authority}
          </TonePill>
          <button type="button" className="role-preview-btn" onClick={openRoleSwitch}>
            <Icon name="users" size="sm" />
            {role.shortName}
            <Icon name="chevron" size="sm" />
          </button>
        </div>
      </div>
      <div className="route-workflow-line">
        <Icon name="workflow" size="sm" />
        <span>
          <strong>Workflow:</strong> {spec.workflow}
        </span>
      </div>
      {children}
    </div>
  );
}

function InstructorAccessBoundary({
  spec,
  roleId,
  openRoleSwitch,
  navigate,
}: {
  spec: InstructorRouteSpec;
  roleId: TeachingRoleId;
  openRoleSwitch: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const role = INSTRUCTOR_ROLES[roleId];
  return (
    <div className="access-boundary-view" data-access-boundary="true">
      <span>
        <Icon name="lock" size="xl" />
      </span>
      <h2>{role.name} access boundary</h2>
      <p>
        This destination requires <strong>{spec.authority}</strong> authority. {role.name} is scoped to{" "}
        {role.scope}. Viewing or changing protected grading, moderation, credential, access or privacy records
        requires a separately assigned and audited role.
      </p>
      <div className="hero-actions">
        <button type="button" className="secondary-btn" onClick={openRoleSwitch}>
          <Icon name="users" size="sm" />
          Preview another teaching role
        </button>
        <button type="button" className="primary-btn" onClick={() => navigate("command", "dashboard")}>
          <Icon name="home" size="sm" />
          Return to command
        </button>
      </div>
    </div>
  );
}

function UnknownInstructorRoute({
  area,
  page,
  navigate,
}: {
  area: InstructorArea;
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  return (
    <div className="unknown-route-view" data-unknown-route="true">
      <span>
        <Icon name="alert" size="xl" />
      </span>
      <h2>Unregistered Instructor destination</h2>
      <p>
        The route{" "}
        <code>
          {area}/{page}
        </code>{" "}
        is not registered in the v3 operational catalogue. The platform fails closed instead of displaying a
        plausible generic page under a different title.
      </p>
      <button type="button" className="primary-btn" onClick={() => navigate("command", "dashboard")}>
        <Icon name="home" size="sm" />
        Open Instructor Command
      </button>
    </div>
  );
}

function InstructorSidebarGroup({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="sidebar-section">
      <button
        type="button"
        className={`section-label ${collapsed ? "collapsed" : ""}`}
        onClick={() => setCollapsed(current => !current)}
      >
        <Icon name="chevron" size="sm" />
        {title}
        {action ? (
          <span
            className="section-add"
            role="button"
            aria-label={`Add to ${title}`}
            onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
              event.stopPropagation();
              action();
            }}
          >
            <Icon name="plus" size="sm" />
          </span>
        ) : null}
      </button>
      {!collapsed ? <div className="channel-list">{children}</div> : null}
    </section>
  );
}

function SidebarRow({
  icon,
  label,
  subline,
  badge,
  active,
  onClick,
  locked = false,
}: {
  icon: string;
  label: string;
  subline?: string;
  badge?: string | number;
  active?: boolean;
  onClick: () => void;
  locked?: boolean;
}) {
  return (
    <button
      type="button"
      className={`channel-row ${subline ? "two-line" : ""} ${active ? "active" : ""}`}
      onClick={onClick}
    >
      <Icon name={locked ? "lock" : icon} size="sm" />
      {subline ? (
        <span className="row-copy">
          <span className="name">{label}</span>
          <span className="subline">{subline}</span>
        </span>
      ) : (
        <span className="name">{label}</span>
      )}
      {badge !== undefined && badge !== "" ? <span className="count">{badge}</span> : null}
    </button>
  );
}

function InstructorRail({
  area,
  navigate,
  unread,
  openProfile,
}: {
  area: InstructorArea;
  navigate: (area: InstructorArea, page?: string) => void;
  unread: number;
  openProfile: () => void;
}) {
  const identity = useSignedInIdentity();
  return (
    <nav className="rail" aria-label="Instructor Platform applications">
      <button
        type="button"
        className="brand-mark"
        aria-label="Open Instructor Command"
        title="Instructor Command"
        onClick={() => navigate("command")}
      >
        <img
          className="academy-platform-mark"
          src={academyMark}
          alt=""
          width={837}
          height={765}
          decoding="async"
        />
        <span className="tip">Instructor Command</span>
      </button>
      <div className="rail-nav">
        {RAIL_AREAS.map(item => (
          <button
            type="button"
            className={`rail-btn ${area === item ? "active" : ""}`}
            key={item}
            onClick={() => navigate(item)}
            aria-label={AREA_CONFIG[item].label}
            title={AREA_CONFIG[item].label}
          >
            <Icon name={AREA_CONFIG[item].icon} size="lg" />
            <span className="tip">{AREA_CONFIG[item].label}</span>
            {item === "assessment" ? <span className="rail-badge">11</span> : null}
          </button>
        ))}
      </div>
      <div className="rail-spacer" />
      <div className="rail-nav">
        {UTILITY_AREAS.map(item => (
          <button
            type="button"
            className={`rail-btn ${area === item ? "active" : ""}`}
            key={item}
            onClick={() => navigate(item)}
            aria-label={AREA_CONFIG[item].label}
            title={AREA_CONFIG[item].label}
          >
            <Icon name={AREA_CONFIG[item].icon} size="lg" />
            <span className="tip">{AREA_CONFIG[item].label}</span>
            {item === "notifications" && unread ? <span className="rail-badge">{unread}</span> : null}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="profile-dot"
        aria-label="Open instructor profile"
        title="Instructor profile"
        onClick={openProfile}
      >
        {identity.initials}
        <span className="tip">Instructor profile</span>
      </button>
    </nav>
  );
}

function InstructorSidebar({
  open,
  area,
  page,
  navigate,
  close,
  openSearch,
  openModal,
  customChannels,
  subgroups,
  sessions,
  sessionsLoading,
  sessionsError,
}: {
  open: boolean;
  area: InstructorArea;
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  close: () => void;
  openSearch: () => void;
  openModal: (modal: InstructorModal) => void;
  customChannels: string[];
  subgroups: BuildRoomSubgroup[];
  sessions: ClassSession[];
  sessionsLoading: boolean;
  sessionsError: string;
}) {
  const identity = useSignedInIdentity();
  const teachingPrograms = useTeachingPrograms(area === "teaching");
  const [workspaceMenu, setWorkspaceMenu] = useState(false);
  const select = (nextPage: string) => {
    navigate(area, nextPage);
    setWorkspaceMenu(false);
    close();
  };
  const jump = (nextArea: InstructorArea, nextPage?: string) => {
    navigate(nextArea, nextPage);
    setWorkspaceMenu(false);
    close();
  };
  const row = (
    icon: string,
    label: string,
    target: string,
    subline?: string,
    badge?: string | number,
    locked?: boolean,
  ) => (
    <SidebarRow
      key={`${area}-${target}`}
      icon={icon}
      label={label}
      subline={subline}
      badge={badge}
      active={page === target || page.startsWith(`${target}-`)}
      onClick={() => select(target)}
      locked={locked}
    />
  );

  let content: React.ReactNode;
  if (area === "command")
    content = (
      <>
        <InstructorSidebarGroup title="Instructor Command">
          {row("home", "Dashboard", "dashboard", "Priorities and cohort health")}
          {row("clipboard", "Priority queue", "priority-queue", "Reviews, risks and deadlines", 11)}
          {row("calendar", "My calendar", "calendar", "Classes, reviews and office hours", 4)}
          {row("activity", "Recent activity", "activity", "Attributable changes and decisions")}
          {row("bookmark", "Saved views", "saved", "Reusable filters and reports")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Quick access">
          <SidebarRow
            icon="video"
            label="Host today's class"
            subline="3:00 PM · 34 learners"
            badge="Live"
            active={false}
            onClick={() => jump("class", "schedule")}
          />
          <SidebarRow
            icon="clipboard"
            label="Review Maya's context pack"
            subline="4 evidence artifacts"
            active={false}
            onClick={() => jump("assessment", "submission-maya-context")}
          />
          <SidebarRow
            icon="alert"
            label="Support Jamal"
            subline="Intervention recommended"
            active={false}
            onClick={() => jump("cohorts", "learner-jamal-thompson")}
          />
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "cohorts")
    content = (
      <>
        <InstructorSidebarGroup title="Cohorts" action={() => openModal({ type: "new-cohort" })}>
          {row("users", "All cohorts", "cohorts", "Active, upcoming and completed", 4)}
          {row("activity", "Active cohorts", "active", "Delivery now", 2)}
          {row("calendar", "Upcoming cohorts", "upcoming", "Planning and enrolment", 1)}
          {row("check-circle", "Completed cohorts", "completed", "Outcomes and archive", 1)}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Learners">
          {row("users", "Learner directory", "learners", "Progress, evidence and support", LEARNERS.length)}
          {row("alert", "Support signals", "support", "Watch and intervention", 9)}
          {row("award", "Credential readiness", "readiness", "Proof completion and gaps")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Assigned cohorts">
          {COHORTS.slice(0, 3).map(cohort => (
            <SidebarRow
              key={cohort.id}
              icon="users"
              label={cohort.title.split(" · ")[0]}
              subline={
                cohort.status === "active"
                  ? `${cohort.learners} learners · ${cohort.progress}%`
                  : cohort.intake
              }
              badge={cohort.reviewQueue || undefined}
              active={page === `cohort-${cohort.id}`}
              onClick={() => select(`cohort-${cohort.id}`)}
            />
          ))}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "teaching")
    content = (
      <>
        <InstructorSidebarGroup title="Teaching">
          {row("book", "Curriculum", "curriculum", "Programs, modules and outcomes")}
          {row("book", "Lessons", "lessons", "Authoring, versions and quality")}
          {row("folder", "Teaching resources", "resources", "Slides, guides and examples")}
          {row("shield", "Content quality", "quality", "Accessibility and readiness")}
          {row("activity", "Version history", "versions", "Published and draft changes")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Assigned programs">
          {teachingPrograms.loading && <p role="status">Loading assigned Programs…</p>}
          {teachingPrograms.error && <p role="alert">{teachingPrograms.error}</p>}
          {teachingPrograms.data?.map(program =>
            row("book", program.name, `program-${program.id}`, program.code),
          )}
          {!teachingPrograms.loading && !teachingPrograms.error && !teachingPrograms.data?.length && (
            <p>No assigned Programs.</p>
          )}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "class")
    content = (
      <>
        <InstructorSidebarGroup title="Chatoner Class" action={() => openModal({ type: "create-class" })}>
          {row("calendar", "Class schedule", "schedule", "Every session has a link and code")}
          {row("video", "Live now", "live", "Host and waiting room", 1)}
          {row("plus", "Create class", "create", "Schedule a new Class session")}
          {row("play", "Recordings", "recordings", "Published and processing", 1)}
          {row("users", "Attendance", "attendance", "Participation and corrections", 2)}
          {row("message", "Questions & polls", "questions", "Prepare and moderate", 9)}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Upcoming sessions">
          {sessionsLoading && <p role="status">Loading current Class sessions…</p>}
          {sessionsError && <p role="alert">{sessionsError}</p>}
          {sessions
            .filter(session => session.status !== "completed")
            .map(session => (
              <SidebarRow
                key={session.id}
                icon="video"
                label={session.title}
                subline={`${session.dateLabel} · ${session.timeLabel}`}
                badge={session.status === "live" ? "Live" : undefined}
                active={page === `session-${session.id}`}
                onClick={() => select(`session-${session.id}`)}
              />
            ))}
          {!sessionsLoading &&
            !sessionsError &&
            !sessions.some(session => session.status !== "completed") && <p>No upcoming Class sessions.</p>}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Class settings">
          {row("globe", "Languages & captions", "languages", "Interpretation and original retention")}
          {row("settings", "Studio preferences", "preferences", "Devices, recording and safety")}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "buildRooms")
    content = (
      <>
        <InstructorSidebarGroup title="Build Rooms" action={() => openModal({ type: "new-channel" })}>
          {row("grid", "Room overview", "overview", "Active rooms and checkpoints")}
          {row(
            "users",
            "Small groups",
            "small-groups",
            "Persistent teams inside one class",
            subgroups.length,
          )}
          {row("activity", "Needs instructor review", "review-queue", "Human decisions pending", 5)}
          {row("message", "All rooms", "rooms", "Cohort and project rooms", 12)}
          {row("folder", "Archived rooms", "archived", "Completed evidence spaces")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="AI Product Builder · Oct 2026">
          <SidebarRow
            icon="hash"
            label="announcements"
            active={page === "announcements"}
            onClick={() => select("announcements")}
          />
          <SidebarRow
            icon="hash"
            label="product-thinking"
            badge="3"
            active={page === "product-thinking"}
            onClick={() => select("product-thinking")}
          />
          <SidebarRow
            icon="hash"
            label="code-clinic"
            active={page === "code-clinic"}
            onClick={() => select("code-clinic")}
          />
          <SidebarRow
            icon="hash"
            label="show-and-learn"
            badge="7"
            active={page === "show-and-learn"}
            onClick={() => select("show-and-learn")}
          />
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Kijani project">
          <SidebarRow
            icon="hash"
            label="project-brief"
            active={page === "project-brief"}
            onClick={() => select("project-brief")}
          />
          <SidebarRow
            icon="hash"
            label="group-build-checkout"
            badge="Live"
            active={page === "group-build-checkout"}
            onClick={() => select("group-build-checkout")}
          />
          <SidebarRow
            icon="hash"
            label="qa-and-evaluation"
            badge="1"
            active={page === "qa-and-evaluation"}
            onClick={() => select("qa-and-evaluation")}
          />
          <SidebarRow
            icon="lock"
            label="instructor-review"
            active={page === "instructor-review"}
            onClick={() => select("instructor-review")}
            locked
          />
          {customChannels.map(channel => (
            <SidebarRow
              key={channel}
              icon="hash"
              label={channel}
              active={page === channel}
              onClick={() => select(channel)}
            />
          ))}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup
          title="Kijani small groups"
          action={() => openModal({ type: "manage-groups" })}
        >
          {subgroups.map(group => (
            <SidebarRow
              key={group.id}
              icon="users"
              label={group.channel.replace(/^subgroup-/, "")}
              subline={`${group.memberIds.length} learners · ${group.status}`}
              badge={group.reviewCount || undefined}
              active={page === group.channel}
              onClick={() => select(group.channel)}
            />
          ))}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "labs")
    content = (
      <>
        <InstructorSidebarGroup title="Academy ↔ Labs" action={() => openModal({ type: "assign-lab" })}>
          {row("link", "Connection overview", "overview", "Independent but interconnected")}
          {row("clipboard", "Lab assignments", "assignments", "Academy outcomes and entitlements", 5)}
          {row("flask", "Intended Lab catalogue", "catalogue", "Open exact environments")}
          {row(
            "external",
            "Instructor Lab workspace",
            "workspace",
            "Create, guide and grade on labs.chatoner.com",
          )}
          {row("activity", "Synced cohort runs", "runs", "Live, paused and completed", 52)}
          {row("award", "Returned evidence", "evidence", "Selected Lab proof awaiting review", 8)}
          {row("chart", "Synced analytics", "analytics", "Completion, time and evidence")}
          {row("shield", "Access & safety sync", "safety", "View policies; manage in Labs")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Assigned intended Labs">
          {LABS.filter(lab => lab.assignedCohorts > 0).map(lab => (
            <SidebarRow
              key={lab.id}
              icon={lab.icon}
              label={lab.title}
              subline={`${lab.activeRuns} synced runs · ${lab.completion}% complete`}
              active={page === `lab-${lab.id}`}
              onClick={() => select(`lab-${lab.id}`)}
            />
          ))}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "assessment")
    content = (
      <>
        <InstructorSidebarGroup title="Assessment" action={() => openModal({ type: "create-assignment" })}>
          {row("clipboard", "Assignments", "assignments", "Briefs, deadlines and cohorts")}
          {row("check-circle", "Grading queue", "grading", "Review, feedback and decisions", 11)}
          {row("test", "Assessments", "assessments", "Quiz, practical, scenario and defense")}
          {row("book", "Question bank", "questions", "Reusable aligned items")}
          {row("evidence", "Rubrics", "rubrics", "Criteria, levels and calibration", 3)}
          {row("shield", "Moderation", "moderation", "Second marking and exceptions", 2)}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Saved queues">
          {row("clock", "Older than 24 hours", "queue-older-24h", "Review SLA at risk", 4)}
          {row("alert", "High-risk evidence", "queue-high-risk", "Integrity or safety review", 2)}
          {row("award", "Credential evidence", "queue-credential", "Final proof verification", 3)}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "projects")
    content = (
      <>
        <InstructorSidebarGroup title="Projects & Proof">
          {row("folder", "Project reviews", "reviews", "Architecture, evidence and verification", 4)}
          {row("award", "Capstone reviews", "capstones", "Panel, defense and recommendation", 2)}
          {row("evidence", "Evidence verification", "evidence", "Skill, System and Proof")}
          {row("shield", "Credential decisions", "credentials", "Human recommendation and audit")}
          {row("users", "Review panels", "panels", "Membership, calibration and schedule")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Priority reviews">
          <SidebarRow
            icon="folder"
            label="Kijani Market Checkout"
            subline="Conditional checkpoint"
            badge="1"
            active={page === "project-ronald"}
            onClick={() => select("project-ronald")}
          />
          <SidebarRow
            icon="award"
            label="Grace Nwosu capstone"
            subline="Panel review · 15 Nov"
            active={page === "capstone-grace"}
            onClick={() => select("capstone-grace")}
          />
          <SidebarRow
            icon="alert"
            label="Jamal project"
            subline="Consent boundary missing"
            badge="High"
            active={page === "project-jamal"}
            onClick={() => select("project-jamal")}
          />
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "engagement")
    content = (
      <>
        <InstructorSidebarGroup title="Engagement" action={() => openModal({ type: "office-hours" })}>
          {row("calendar", "Office hours", "office-hours", "Availability and bookings")}
          {row("mail", "Messages", "messages", "Learner, cohort and staff", 3)}
          {row("message", "Announcements", "announcements", "Cohort communications")}
          {row("users", "Mentoring notes", "notes", "Private support records")}
          {row("alert", "Help requests", "help-requests", "Learners waiting for support", 4)}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Recent conversations">
          {MESSAGE_THREADS.map(thread => (
            <SidebarRow
              key={thread.id}
              icon="mail"
              label={thread.participant}
              subline={thread.subject}
              badge={thread.unread || undefined}
              active={page === `thread-${thread.id}`}
              onClick={() => select(`thread-${thread.id}`)}
            />
          ))}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "analytics")
    content = (
      <>
        <InstructorSidebarGroup title="Analytics">
          {row("chart", "Overview", "overview", "Cohort and instructor performance")}
          {row("users", "Learner outcomes", "outcomes", "Progress, completion and readiness")}
          {row("activity", "Engagement", "engagement", "Weekly activity and participation")}
          {row("calendar", "Attendance", "attendance", "Trends and interventions")}
          {row("clipboard", "Review performance", "reviews", "SLA, turnaround and decisions")}
          {row("book", "Content quality", "content", "Accessibility and lesson engagement")}
          {row("download", "Data exports", "exports", "Privacy-aware reporting")}
        </InstructorSidebarGroup>
        <InstructorSidebarGroup title="Saved reports">
          {row("bookmark", "Weekly delivery brief", "report-weekly", "Cohort leads and operations")}
          {row("bookmark", "Credential readiness", "report-readiness", "Evidence gaps by cohort")}
          {row("bookmark", "Review SLA", "report-sla", "Queue age and workload")}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "search")
    content = (
      <>
        <InstructorSidebarGroup title="Search scope">
          {row("search", "Everything", "all")}
          {row("users", "Learners & cohorts", "people")}
          {row("video", "Classes & recordings", "classes")}
          {row("clipboard", "Submissions & reviews", "reviews")}
          {row("folder", "Projects & evidence", "projects")}
          {row("book", "Curriculum & lessons", "content")}
        </InstructorSidebarGroup>
      </>
    );
  else if (area === "notifications")
    content = (
      <>
        <InstructorSidebarGroup title="Notifications">
          {row(
            "bell",
            "All notifications",
            "all",
            "Every update",
            NOTIFICATIONS.filter(item => item.unread).length,
          )}
          {row("clipboard", "Review work", "reviews", "Submissions and moderation")}
          {row("video", "Classes", "classes", "Sessions, questions and recordings")}
          {row("alert", "Learner support", "learners", "Risk and accommodations")}
          {row("settings", "System", "system", "Content and integration updates")}
        </InstructorSidebarGroup>
      </>
    );
  else
    content = (
      <>
        <InstructorSidebarGroup title="Instructor settings">
          {row("users", "Profile", "profile", "Identity and instructor biography")}
          {row("shield", "Role & access", "access", "Programs, cohorts and decision authority")}
          {row("settings", "Preferences", "preferences", "Workspace and schedule defaults")}
          {row("info", "Accessibility", "accessibility", "Display, keyboard and motion")}
          {row("globe", "Languages", "languages", "Writing, translation and captions")}
          {row("link", "Integrations", "integrations", "Class, Labs, calendar and repository")}
          {row("bell", "Notification rules", "notification-rules", "Channels and quiet hours")}
          {row("lock", "Privacy & data", "privacy", "Retention, consent and exports")}
        </InstructorSidebarGroup>
      </>
    );

  return (
    <aside className={`sidebar ${open ? "open" : ""}`} aria-label={`${AREA_CONFIG[area].label} navigation`}>
      <div className="academy-platform-sidebar-brand">
        <img
          className="academy-platform-logo"
          src={academyLogo}
          alt="Chatoner AI Academy"
          width={4124}
          height={967}
          decoding="async"
        />
      </div>
      <div className="workspace-head">
        <div className="workspace-badge">AI</div>
        <div className="workspace-copy">
          <strong>Instructor Platform</strong>
          <span>
            {identity.name} · {identity.role || "No assigned role"}
          </span>
        </div>
        <div className="sidebar-top-actions">
          <button
            type="button"
            className="icon-btn mobile-menu sidebar-close"
            aria-label="Close sidebar"
            onClick={close}
          >
            <Icon name="x" />
          </button>
          <button
            type="button"
            className={`icon-btn ${workspaceMenu ? "active" : ""}`}
            aria-label="Switch workspace"
            onClick={() => setWorkspaceMenu(current => !current)}
          >
            <Icon name="chevron" />
          </button>
        </div>
        {workspaceMenu ? (
          <div className="workspace-switcher-popover">
            <button type="button" className="active" onClick={() => setWorkspaceMenu(false)}>
              <span className="workspace-option-icon">
                <Icon name="users" size="sm" />
              </span>
              <span>
                <strong>Instructor Platform</strong>
                <span>Teaching and assessment workspace</span>
              </span>
              <Icon name="check" size="sm" />
            </button>
            <button
              type="button"
              onClick={() => {
                setWorkspaceMenu(false);
                openExternalHandoff(
                  "https://instructors.academy.chatoner.com/#/instructor/command/dashboard",
                );
              }}
            >
              <span className="workspace-option-icon">
                <Icon name="book" size="sm" />
              </span>
              <span>
                <strong>Learner Workspace</strong>
                <span>Preview the learner experience</span>
              </span>
              <Icon name="external" size="sm" />
            </button>
            <button
              type="button"
              onClick={() => {
                setWorkspaceMenu(false);
                openExternalHandoff(
                  "https://instructors.academy.chatoner.com/#/instructor/settings/role-access",
                );
              }}
            >
              <span className="workspace-option-icon">
                <Icon name="shield" size="sm" />
              </span>
              <span>
                <strong>Academic Operations</strong>
                <span>Requires operations permission</span>
              </span>
              <Icon name="lock" size="sm" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="sidebar-search">
        <button type="button" className="search-trigger" onClick={openSearch}>
          <Icon name="search" size="sm" />
          <span>Search Instructor Platform</span>
          <kbd className="kbd">⌘ K</kbd>
        </button>
      </div>
      <div className="sidebar-scroll">{content}</div>
      <div className="sidebar-foot">
        <div className="progress-card">
          <div className="progress-top">
            <strong>Weekly delivery</strong>
            <span>74%</span>
          </div>
          <div className="progress-bar">
            <i style={{ width: "74%" }} />
          </div>
          <p>3 classes · 11 reviews · 4 office-hour bookings</p>
        </div>
      </div>
    </aside>
  );
}

function pageTitle(area: InstructorArea, page: string) {
  const registered = resolveStaticInstructorRouteSpec(area, page);
  if (registered) return registered.title;
  if (page === "overview") return area === "labs" ? "Academy ↔ Labs Connection" : AREA_CONFIG[area].label;
  if (page === "assignments") return area === "labs" ? "Lab Assignments" : "Assignments";
  if (page === "evidence")
    return area === "labs"
      ? "Returned Lab Evidence"
      : area === "projects"
        ? "Evidence Verification"
        : "Evidence";
  if (page === "analytics") return area === "labs" ? "Synced Lab Analytics" : "Analytics";
  if (page === "reviews") return area === "projects" ? "Project Reviews" : "Review Analytics";
  const details: Record<string, string> = {
    dashboard: "Instructor Command",
    "priority-queue": "Priority Queue",
    calendar: "Instructor Calendar",
    activity: "Recent Activity",
    saved: "Saved Views",
    cohorts: "Cohorts",
    active: "Active Cohorts",
    upcoming: "Upcoming Cohorts",
    completed: "Completed Cohorts",
    learners: "Learner Directory",
    support: "Learner Support Signals",
    readiness: "Credential Readiness",
    curriculum: "Curriculum",
    lessons: "Lessons",
    resources: "Teaching Resources",
    quality: "Content Quality",
    versions: "Version History",
    schedule: "Chatoner Class Schedule",
    live: "Live Classes",
    create: "Create Class",
    recordings: "Class Recordings",
    attendance: "Attendance",
    questions: "Questions & Polls",
    languages: "Languages & Captions",
    preferences: "Studio Preferences",
    "review-queue": "Instructor Review Queue",
    rooms: "All Build Rooms",
    archived: "Archived Build Rooms",
    workspace: "Instructor Lab Workspace",
    catalogue: "Intended AI Lab Catalogue",
    runs: "Synced Cohort Lab Runs",
    safety: "Access & Safety Sync",
    "small-groups": "Build Room Small Groups",
    grading: "Grading Queue",
    assessments: "Assessments",
    rubrics: "Rubrics",
    moderation: "Moderation",
    capstones: "Capstone Reviews",
    credentials: "Credential Decisions",
    panels: "Review Panels",
    "office-hours": "Office Hours",
    messages: "Messages",
    announcements: "Announcements",
    notes: "Mentoring Notes",
    "help-requests": "Help Requests",
    outcomes: "Learner Outcomes",
    engagement: "Engagement Analytics",
    content: "Content Analytics",
    exports: "Data Exports",
    all: area === "search" ? "Search Everything" : "All Notifications",
    people: "People Search",
    classes: "Class Search",
    projects: "Project Search",
    system: "System Notifications",
    profile: "Instructor Profile",
    access: "Role & Access",
    accessibility: "Accessibility",
    integrations: "Integrations",
    "notification-rules": "Notification Rules",
    privacy: "Privacy & Data",
  };
  if (page.startsWith("cohort-")) return "Cohort Detail";
  if (page.startsWith("learner-")) return "Learner Detail";
  if (page.startsWith("lesson-")) return "Lesson Authoring";
  if (page.startsWith("session-")) return "Class Session";
  if (page.startsWith("lab-")) return "Connected AI Lab";
  if (page.startsWith("submission-")) return "Submission Review";
  if (page.startsWith("project-")) return "Project Review";
  if (page.startsWith("capstone-")) return "Capstone Review";
  if (page.startsWith("thread-")) return "Message Thread";
  return details[page] || AREA_CONFIG[area].label;
}

function InstructorHeader({
  area,
  page,
  openSidebar,
  openSearch,
  openNotifications,
  openProfile,
  unread,
  primaryAction,
}: {
  area: InstructorArea;
  page: string;
  openSidebar: () => void;
  openSearch: () => void;
  openNotifications: () => void;
  openProfile: () => void;
  unread: number;
  primaryAction?: React.ReactNode;
}) {
  const identity = useSignedInIdentity();
  return (
    <header className="academy-head">
      <button
        type="button"
        className="icon-btn mobile-menu"
        aria-label="Open navigation"
        onClick={openSidebar}
      >
        <Icon name="menu" />
      </button>
      <span className="academy-head-icon">
        <Icon name={AREA_CONFIG[area].icon} />
      </span>
      <div className="academy-head-copy">
        <h1>{pageTitle(area, page)}</h1>
        <p>{AREA_CONFIG[area].description}</p>
      </div>
      <div className="academy-head-actions">
        {primaryAction}
        <button
          type="button"
          className="icon-btn"
          aria-label="Search Instructor Platform"
          onClick={openSearch}
        >
          <Icon name="search" />
        </button>
        <button
          type="button"
          className="icon-btn head-notification"
          aria-label={`Open notifications${unread ? `, ${unread} unread` : ""}`}
          onClick={openNotifications}
        >
          <Icon name="bell" />
          {unread ? <i /> : null}
        </button>
        <button type="button" className="profile-menu-button" onClick={openProfile}>
          <Avatar initials={identity.initials} small />
          <span>
            <strong>{identity.name}</strong>
            <small>{identity.role || "No assigned role"}</small>
          </span>
          <Icon name="chevron" size="sm" />
        </button>
      </div>
    </header>
  );
}

function InstructorMobileBottom({
  area,
  navigate,
  openSidebar,
}: {
  area: InstructorArea;
  navigate: (area: InstructorArea, page?: string) => void;
  openSidebar: () => void;
}) {
  const items: Array<[InstructorArea, string, string]> = [
    ["command", "home", "Command"],
    ["class", "video", "Class"],
    ["assessment", "clipboard", "Grade"],
    ["buildRooms", "message", "Rooms"],
  ];
  return (
    <nav className="mobile-bottom academy-mobile-bottom" aria-label="Mobile Instructor Platform navigation">
      <button type="button" onClick={openSidebar}>
        <Icon name="menu" />
        Menu
      </button>
      {items.map(([target, icon, label]) => (
        <button
          type="button"
          className={area === target ? "active" : ""}
          key={target}
          onClick={() => navigate(target)}
        >
          <Icon name={icon} />
          {label}
        </button>
      ))}
    </nav>
  );
}

function CommandView({
  page,
  navigate,
  notify,
  openModal,
  openSearch,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
  openSearch: () => void;
}) {
  if (page === "priority-queue") {
    const priorities = [
      {
        icon: "clipboard",
        tone: "purple",
        title: "Review Maya Chen · Context Pack v2",
        detail: "4 evidence artifacts · due within 21 hours",
        label: "Review",
        area: "assessment" as InstructorArea,
        page: "submission-maya-context",
      },
      {
        icon: "alert",
        tone: "red",
        title: "Contact Jamal Thompson",
        detail: "Attendance 66% · 4 missing or late submissions",
        label: "Intervention",
        area: "cohorts" as InstructorArea,
        page: "learner-jamal-thompson",
      },
      {
        icon: "video",
        tone: "cyan",
        title: "Prepare Accessible Product Decisions",
        detail: "Starts today at 3:00 PM · 9 learner questions",
        label: "Class",
        area: "class" as InstructorArea,
        page: "schedule",
      },
      {
        icon: "shield",
        tone: "orange",
        title: "Complete capstone rubric calibration",
        detail: "Panel meets 15 Nov · version 2.5 pending",
        label: "Calibration",
        area: "projects" as InstructorArea,
        page: "panels",
      },
      {
        icon: "book",
        tone: "orange",
        title: "Resolve API Boundaries accessibility blockers",
        detail: "Captions and code-description checks are incomplete",
        label: "Content",
        area: "teaching" as InstructorArea,
        page: "lesson-api-boundaries",
      },
      {
        icon: "users",
        tone: "purple",
        title: "Review two attendance correction requests",
        detail: "Context Architecture Clinic · learner evidence attached",
        label: "Attendance",
        area: "class" as InstructorArea,
        page: "attendance",
      },
    ];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Priority queue"
          text="One accountable queue across teaching, learner support, assessment and academic quality."
          actions={
            <>
              <select
                className="select-field"
                defaultValue="All priorities"
                onChange={() => notify("Priority filter applied")}
              >
                <option>All priorities</option>
                <option>Due today</option>
                <option>High risk</option>
                <option>Waiting on me</option>
              </select>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "export", payload: "priority-queue" })}
              >
                <Icon name="download" size="sm" />
                Export queue
              </button>
            </>
          }
        />
        <AcademyPanel
          icon="command"
          title="Accountable work queue"
          meta={`${priorities.length} visible priorities`}
        >
          <div className="priority-list">
            {priorities.map(item => (
              <button
                type="button"
                className="priority-item"
                key={item.title}
                onClick={() => navigate(item.area, item.page)}
              >
                <span className={`priority-icon ${item.tone}`}>
                  <Icon name={item.icon} />
                </span>
                <span>
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                </span>
                <span className="priority-meta">
                  <TonePill
                    tone={
                      item.tone === "red"
                        ? "red"
                        : item.tone === "orange"
                          ? "orange"
                          : item.tone === "cyan"
                            ? "blue"
                            : "purple"
                    }
                  >
                    {item.label}
                  </TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }

  if (page === "calendar") {
    const events = [
      ["09:00", "Review block", "Maya context pack · grading", "assessment", "submission-maya-context"],
      ["11:30", "Office hour", "Jamal Thompson · consent and retention", "engagement", "office-hours"],
      ["15:00", "Accessible Product Decisions", "Chatoner Class · 34 learners", "class", "schedule"],
      ["17:00", "Kijani checkpoint", "Build Room human review", "buildRooms", "group-build-checkout"],
    ] as Array<[string, string, string, InstructorArea, string]>;
    return (
      <div className="instructor-page">
        <PageTitle
          title="Instructor calendar"
          text="Classes, reviews, office hours and deadlines in one delivery-aware view."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() =>
                  notify(
                    "Calendar connection refreshed",
                    "Google Calendar and Chatoner Class availability have been checked.",
                  )
                }
              >
                <Icon name="refresh" size="sm" />
                Refresh
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => openModal({ type: "create-class" })}
              >
                <Icon name="plus" size="sm" />
                Schedule class
              </button>
            </>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="calendar" title="Wednesday · 4 November 2026" meta="Africa/Kampala">
            <div className="schedule-list">
              {events.map(event => (
                <button
                  type="button"
                  className="priority-item"
                  key={`${event[0]}-${event[1]}`}
                  onClick={() => navigate(event[3], event[4])}
                >
                  <span className="schedule-time">{event[0]}</span>
                  <span>
                    <strong>{event[1]}</strong>
                    <p>{event[2]}</p>
                  </span>
                  <Icon name="arrow-right" size="sm" />
                </button>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="clock" title="Capacity today" meta="7h 15m scheduled">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Deep work</strong>
                  <p>Review and lesson preparation</p>
                </div>
                <TonePill tone="purple">3h 10m</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Live delivery</strong>
                  <p>Class and Build Room checkpoint</p>
                </div>
                <TonePill tone="blue">2h</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Learner support</strong>
                  <p>Office hour and follow-up</p>
                </div>
                <TonePill tone="green">1h 5m</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Protected break</strong>
                  <p>No meetings</p>
                </div>
                <TonePill>1h</TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  }

  if (page === "activity") {
    const activity = [
      [
        "check",
        "Submission review completed",
        "Ibrahim Yusuf · Prompt Library · 96% · moderation requested",
        "42 minutes ago",
        "assessment",
        "submission-ibrahim-prompts",
      ],
      [
        "alert",
        "Learner support flag created",
        "Jamal Thompson · attendance and assignment pace",
        "1 hour ago",
        "cohorts",
        "learner-jamal-thompson",
      ],
      [
        "video",
        "Class agenda updated",
        "Accessible Product Decisions · 4 agenda items",
        "2 hours ago",
        "class",
        "schedule",
      ],
      [
        "book",
        "Lesson moved to review",
        "APIs, Data Boundaries and Server Actions · v1.4",
        "Yesterday",
        "teaching",
        "lesson-api-boundaries",
      ],
      [
        "shield",
        "Human checkpoint approved with condition",
        "Kijani checkout · staging verification still required",
        "Yesterday",
        "buildRooms",
        "group-build-checkout",
      ],
      [
        "flask",
        "Lab assignment published",
        "AI-App Builder Lab · Builder October 2026",
        "2 days ago",
        "labs",
        "lab-ai-app-builder",
      ],
    ] as Array<[string, string, string, string, InstructorArea, string]>;
    return (
      <div className="instructor-page">
        <PageTitle
          title="Recent activity"
          text="Every teaching, review, support and AI-related action remains attributable and searchable."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "activity-audit" })}
            >
              <Icon name="download" size="sm" />
              Export audit
            </button>
          }
        />
        <AcademyPanel icon="activity" title="Instructor activity trail" meta="Last 30 days">
          <div className="timeline">
            {activity.map(item => (
              <button
                type="button"
                className="timeline-item"
                key={item[1]}
                onClick={() => navigate(item[4], item[5])}
                style={{
                  border: 0,
                  width: "100%",
                  background: "transparent",
                  color: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>{item[3]}</time>
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }

  if (page === "saved") {
    const views = [
      [
        "My overdue reviews",
        "Submission age over 24 hours and assigned to me",
        "4 records",
        "assessment",
        "queue-older-24h",
      ],
      [
        "Builder cohort support",
        "Watch and intervention learners in October cohort",
        "8 learners",
        "cohorts",
        "support",
      ],
      [
        "Credential evidence gaps",
        "Projects with one or more Proof items missing",
        "6 projects",
        "projects",
        "evidence",
      ],
      ["Content readiness", "Lessons blocked by accessibility or review", "2 lessons", "teaching", "quality"],
    ] as Array<[string, string, string, InstructorArea, string]>;
    return (
      <div className="instructor-page">
        <PageTitle
          title="Saved views"
          text="Reusable delivery filters for the questions you need to answer repeatedly."
          actions={
            <button
              type="button"
              className="primary-btn"
              onClick={() =>
                notify("New saved view started", "Apply filters on any list and choose Save view.")
              }
            >
              <Icon name="plus" size="sm" />
              New view
            </button>
          }
        />
        <div className="content-grid equal">
          {views.map(view => (
            <AcademyPanel icon="bookmark" title={view[0]} meta={view[2]} key={view[0]}>
              <p style={{ margin: 0, color: "var(--muted)", fontSize: 14 }}>{view[1]}</p>
              <div className="hero-actions">
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => navigate(view[3], view[4])}
                >
                  Open view
                  <Icon name="arrow-right" size="sm" />
                </button>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("View options opened", view[0])}
                >
                  <Icon name="more" size="sm" />
                  Options
                </button>
              </div>
            </AcademyPanel>
          ))}
        </div>
      </div>
    );
  }

  const priorityItems = [
    {
      icon: "clipboard",
      tone: "",
      title: "Review Maya Chen · Context Pack v2",
      detail: "4 evidence artifacts · learner waiting 3 hours",
      label: "Due today",
      action: () => navigate("assessment", "submission-maya-context"),
    },
    {
      icon: "alert",
      tone: "red",
      title: "Support Jamal Thompson",
      detail: "Attendance 66% · intervention recommended",
      label: "High",
      action: () => navigate("cohorts", "learner-jamal-thompson"),
    },
    {
      icon: "video",
      tone: "cyan",
      title: "Prepare today’s live class",
      detail: "Accessible Product Decisions · 9 questions",
      label: "3:00 PM",
      action: () => navigate("class", "schedule"),
    },
    {
      icon: "shield",
      tone: "orange",
      title: "Complete capstone calibration",
      detail: "Panel rubric v2.5 · due before 15 Nov",
      label: "4 days",
      action: () => navigate("projects", "panels"),
    },
  ];
  const schedule = [
    ["09:00", "Review block", "Maya context pack", "assessment", "submission-maya-context"],
    ["11:30", "Office hour", "Jamal · consent and retention", "engagement", "office-hours"],
    ["15:00", "Live Class", "Accessible Product Decisions", "class", "schedule"],
    ["17:00", "Build Room", "Kijani checkpoint", "buildRooms", "group-build-checkout"],
  ] as Array<[string, string, string, InstructorArea, string]>;

  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Instructor Command · Wednesday, 4 November"
        title="Good afternoon, Amina. Four decisions need you today."
        text="See cohort health, live teaching, learner support, assessment and evidence work without losing the human decision boundary."
        actions={
          <>
            <button type="button" className="primary-btn" onClick={() => navigate("class", "schedule")}>
              <Icon name="video" size="sm" />
              Host today’s class
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("assessment", "grading")}>
              <Icon name="clipboard" size="sm" />
              Open grading queue
            </button>
            <button type="button" className="ghost-btn" onClick={openSearch}>
              <Icon name="search" size="sm" />
              Search Studio
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard
              icon="video"
              title="Next live class"
              detail="Accessible Product Decisions"
              value="3:00 PM"
            />
            <HeroSideCard icon="clipboard" title="Review SLA" detail="Median turnaround" value="18.4h" />
            <HeroSideCard
              icon="users"
              title="Learners needing support"
              detail="Across 2 active cohorts"
              value="9"
            />
          </>
        }
      />
      <div className="stat-grid">
        <StatTile icon="users" label="Active learners" value="62" detail="58 active this week" />
        <StatTile
          icon="clipboard"
          label="Review queue"
          value="11"
          detail="4 due within 24 hours"
          tone="blue"
        />
        <StatTile
          icon="calendar"
          label="Attendance"
          value="92%"
          detail="+2.1 points from last week"
          tone="cyan"
        />
        <StatTile
          icon="alert"
          label="Interventions"
          value="3"
          detail="2 require action today"
          tone="orange"
        />
      </div>
      <div className="content-grid">
        <AcademyPanel icon="command" title="Priority action centre" meta="4 actions">
          <div className="priority-list">
            {priorityItems.map(item => (
              <button type="button" className="priority-item" key={item.title} onClick={item.action}>
                <span className={`priority-icon ${item.tone}`}>
                  <Icon name={item.icon} />
                </span>
                <span>
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                </span>
                <span className="priority-meta">
                  <TonePill
                    tone={
                      item.tone === "red"
                        ? "red"
                        : item.tone === "orange"
                          ? "orange"
                          : item.tone === "cyan"
                            ? "blue"
                            : "purple"
                    }
                  >
                    {item.label}
                  </TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="calendar" title="Today’s schedule" meta="Africa/Kampala">
          <div className="schedule-list">
            {schedule.map(item => (
              <button
                type="button"
                className="schedule-entry"
                key={`${item[0]}-${item[1]}`}
                onClick={() => navigate(item[3], item[4])}
                style={{ width: "100%", color: "inherit", textAlign: "left", cursor: "pointer" }}
              >
                <span className="schedule-time">
                  {item[0]}
                  <small>EAT</small>
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <Icon name="arrow-right" size="sm" />
              </button>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="users" title="Cohort momentum" meta="2 active cohorts">
          <div className="cohort-grid">
            {COHORTS.filter(cohort => cohort.status === "active").map(cohort => (
              <button
                type="button"
                className="cohort-card"
                key={cohort.id}
                onClick={() => navigate("cohorts", `cohort-${cohort.id}`)}
                style={{ color: "inherit", textAlign: "left", cursor: "pointer" }}
              >
                <div className="cohort-card-top">
                  <span className="cohort-card-icon">
                    <Icon name="users" />
                  </span>
                  <div className="cohort-card-copy">
                    <h3>{cohort.title}</h3>
                    <p>
                      {cohort.learners} learners · next: {cohort.nextSession}
                    </p>
                  </div>
                  <TonePill tone="green">Active</TonePill>
                </div>
                <ProgressMeter value={cohort.progress} />
                <div className="cohort-metrics">
                  <div className="cohort-metric">
                    <span>Attendance</span>
                    <strong>{cohort.attendance}%</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Forecast</span>
                    <strong>{cohort.completionForecast}%</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Watch</span>
                    <strong>{cohort.watch}</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Reviews</span>
                    <strong>{cohort.reviewQueue}</strong>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="chart" title="Assignment performance" meta="Current program">
          <div className="action-list">
            {[
              ["Problem framing", 91, "34 learners"],
              ["Prompt contracts", 87, "31 graded"],
              ["Context systems", 84, "29 graded"],
              ["Interface trust", 79, "16 graded"],
            ].map(([label, value, meta]) => (
              <div className="setting-row" key={String(label)}>
                <div style={{ flex: 1 }}>
                  <strong>{label}</strong>
                  <p>{meta}</p>
                  <ProgressMeter value={Number(value)} />
                </div>
                <TonePill tone={Number(value) >= 85 ? "green" : "orange"}>{value}%</TonePill>
              </div>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel
          icon="activity"
          title="Recent instructor activity"
          meta="Auditable"
          className="span-all"
        >
          <div className="timeline">
            {[
              ["check", "Review completed", "Ibrahim Yusuf · Prompt Library · 96%", "42m"],
              ["alert", "Learner flag created", "Jamal Thompson · attendance and pace", "1h"],
              ["video", "Session agenda updated", "Accessible Product Decisions · 4 items", "2h"],
              ["book", "Lesson moved to review", "API Boundaries · v1.4", "Yesterday"],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>{item[3]}</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      </div>
    </div>
  );
}

function CohortDetailView({
  cohort,
  navigate,
  openModal,
  sessions,
  sessionsLoading,
  sessionsError,
}: {
  cohort: Cohort;
  navigate: (area: InstructorArea, page?: string) => void;
  openModal: (modal: InstructorModal) => void;
  sessions: ClassSession[];
  sessionsLoading: boolean;
  sessionsError: string;
}) {
  const [tab, setTab] = useState("overview");
  const cohortLearners = LEARNERS.filter(learner => learner.cohortId === cohort.id);
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("cohorts", "cohorts")}>
        <Icon name="arrow-left" size="sm" />
        Back to cohorts
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar
            initials={cohort.title
              .split(" ")
              .slice(0, 2)
              .map(word => word[0])
              .join("")}
          />
          <div>
            <div className="hero-eyebrow">{cohort.status} cohort</div>
            <h2>{cohort.title}</h2>
            <p>
              {cohort.program} · {cohort.intake} · {cohort.learners}/{cohort.capacity} seats
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <button type="button" className="secondary-btn" onClick={() => openModal({ type: "announcement" })}>
            <Icon name="message" size="sm" />
            Announce
          </button>
          <button type="button" className="secondary-btn" onClick={() => navigate("class", "create")}>
            <Icon name="video" size="sm" />
            Schedule class
          </button>
          <button type="button" className="primary-btn" onClick={() => navigate("buildRooms", "overview")}>
            <Icon name="message" size="sm" />
            Open Build Rooms
          </button>
        </div>
      </section>
      <nav className="detail-tabs" aria-label="Cohort detail sections">
        {["overview", "roster", "schedule", "attendance", "assignments", "announcements", "outcomes"].map(
          item => (
            <button
              type="button"
              className={tab === item ? "active" : ""}
              key={item}
              onClick={() => setTab(item)}
            >
              {item[0].toUpperCase() + item.slice(1)}
            </button>
          ),
        )}
      </nav>
      {tab === "overview" ? (
        <>
          <div className="stat-grid">
            <StatTile
              icon="users"
              label="Learners"
              value={cohort.learners}
              detail={`${cohort.capacity - cohort.learners} seats available`}
            />
            <StatTile
              icon="activity"
              label="Program progress"
              value={`${cohort.progress}%`}
              detail="On schedule"
              tone="blue"
            />
            <StatTile
              icon="calendar"
              label="Attendance"
              value={`${cohort.attendance}%`}
              detail="Target is 90%"
              tone="cyan"
            />
            <StatTile
              icon="award"
              label="Completion forecast"
              value={`${cohort.completionForecast}%`}
              detail="Based on current evidence"
              tone="orange"
            />
          </div>
          <div className="content-grid">
            <AcademyPanel icon="activity" title="Cohort health" meta="Current signals">
              <div className="donut-wrap">
                <div style={{ position: "relative" }}>
                  <div
                    className="donut"
                    style={{
                      background: `conic-gradient(var(--green) 0 ${(cohort.onTrack / cohort.learners) * 100}%,var(--orange) ${(cohort.onTrack / cohort.learners) * 100}% ${((cohort.onTrack + cohort.watch) / cohort.learners) * 100}%,var(--red) ${((cohort.onTrack + cohort.watch) / cohort.learners) * 100}% 100%)`,
                    }}
                  />
                  <div className="donut-center">
                    <strong>{cohort.learners}</strong>
                    <span>learners</span>
                  </div>
                </div>
                <div className="legend-list">
                  <div className="legend-row">
                    <i style={{ background: "var(--green)" }} />
                    <strong>On track</strong>
                    <span>{cohort.onTrack}</span>
                  </div>
                  <div className="legend-row">
                    <i style={{ background: "var(--orange)" }} />
                    <strong>Watch</strong>
                    <span>{cohort.watch}</span>
                  </div>
                  <div className="legend-row">
                    <i style={{ background: "var(--red)" }} />
                    <strong>Intervention</strong>
                    <span>{cohort.intervention}</span>
                  </div>
                </div>
              </div>
            </AcademyPanel>
            <AcademyPanel icon="command" title="Next delivery actions" meta={`${cohort.reviewQueue} reviews`}>
              <div className="priority-list">
                <button
                  type="button"
                  className="priority-item"
                  onClick={() => navigate("assessment", "grading")}
                >
                  <span className="priority-icon">
                    <Icon name="clipboard" />
                  </span>
                  <span>
                    <strong>Review current submissions</strong>
                    <p>{cohort.reviewQueue} submissions are assigned to your queue</p>
                  </span>
                  <Icon name="arrow-right" size="sm" />
                </button>
                <button
                  type="button"
                  className="priority-item"
                  onClick={() => navigate("cohorts", "support")}
                >
                  <span className="priority-icon red">
                    <Icon name="alert" />
                  </span>
                  <span>
                    <strong>Resolve support signals</strong>
                    <p>{cohort.watch + cohort.intervention} learners need attention</p>
                  </span>
                  <Icon name="arrow-right" size="sm" />
                </button>
                <button
                  type="button"
                  className="priority-item"
                  onClick={() => openModal({ type: "announcement" })}
                >
                  <span className="priority-icon cyan">
                    <Icon name="message" />
                  </span>
                  <span>
                    <strong>Send weekly update</strong>
                    <p>Share priorities, deadlines and class preparation</p>
                  </span>
                  <Icon name="arrow-right" size="sm" />
                </button>
              </div>
            </AcademyPanel>
          </div>
        </>
      ) : null}
      {tab === "roster" ? (
        <AcademyPanel icon="users" title="Cohort roster" meta={`${cohortLearners.length} loaded`}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Progress</th>
                  <th>Attendance</th>
                  <th>Average</th>
                  <th>Risk</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {cohortLearners.map(learner => (
                  <tr key={learner.id} onClick={() => navigate("cohorts", `learner-${learner.id}`)}>
                    <td>
                      <div className="person-cell">
                        <Avatar initials={learner.initials} small />
                        <span>
                          <strong>{learner.name}</strong>
                          <span>{learner.project}</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <ProgressMeter value={learner.progress} />
                    </td>
                    <td>{learner.attendance}%</td>
                    <td>{learner.average}%</td>
                    <td>
                      <RiskPill risk={learner.risk} />
                    </td>
                    <td>{learner.credentialReadiness}% ready</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "schedule" ? (
        <div className="content-grid equal">
          {sessionsLoading && <p role="status">Loading current Cohort sessions…</p>}
          {sessionsError && <p role="alert">{sessionsError}</p>}
          {sessions
            .filter(session => session.cohort.includes(cohort.title.split(" · ")[0]))
            .slice(0, 3)
            .map(session => (
              <AcademyPanel key={session.id} icon="video" title={session.title} meta={session.dateLabel}>
                <p style={{ color: "var(--muted)", fontSize: 14 }}>
                  {session.timeLabel} · {session.duration}
                </p>
                <div className="hero-actions">
                  <button
                    type="button"
                    className="primary-btn button-sm"
                    onClick={() => navigate("class", `session-${session.id}`)}
                  >
                    Open session
                  </button>
                </div>
              </AcademyPanel>
            ))}
          {!sessionsLoading &&
            !sessionsError &&
            !sessions.some(session => session.cohort.includes(cohort.title.split(" · ")[0])) && (
              <p>No current Class sessions are assigned to this Cohort.</p>
            )}
        </div>
      ) : null}
      {tab === "attendance" ? (
        <AcademyPanel icon="calendar" title="Attendance overview" meta={`${cohort.attendance}% average`}>
          <div className="stat-grid" style={{ marginTop: 0 }}>
            <StatTile icon="check" label="Present" value="29" detail="Most recent session" />
            <StatTile icon="clock" label="Late" value="3" detail="Follow up if repeated" tone="orange" />
            <StatTile icon="x" label="Absent" value="2" detail="2 correction requests" tone="orange" />
            <StatTile
              icon="activity"
              label="Participation"
              value="87%"
              detail="Discussion and polls"
              tone="cyan"
            />
          </div>
          <div className="hero-actions">
            <button type="button" className="primary-btn" onClick={() => navigate("class", "attendance")}>
              Open attendance register
            </button>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "assignments" ? (
        <AcademyPanel
          icon="clipboard"
          title="Cohort assignments"
          meta={`${ASSIGNMENTS.filter(a => a.cohorts.some(c => cohort.title.includes(c.split(" ")[0]))).length || 3} active`}
        >
          <div className="action-list">
            {ASSIGNMENTS.slice(0, 4).map(assignment => (
              <button
                type="button"
                className="action-card"
                key={assignment.id}
                onClick={() => navigate("assessment", `assignment-${assignment.id}`)}
              >
                <span className="action-icon">
                  <Icon name="clipboard" />
                </span>
                <span>
                  <strong>{assignment.title}</strong>
                  <span>
                    {assignment.due} · {assignment.submissions} submissions · {assignment.graded} graded
                  </span>
                </span>
                <TonePill tone={assignment.status === "Published" ? "green" : "default"}>
                  {assignment.status}
                </TonePill>
              </button>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "announcements" ? (
        <AcademyPanel icon="message" title="Cohort announcements" meta="3 this week">
          <div className="timeline">
            <div className="timeline-item">
              <span className="timeline-dot">
                <Icon name="message" size="sm" />
              </span>
              <span>
                <strong>Week 6: evidence before claims</strong>
                <p>Posted by Amina · links to class, Lab and assignment included.</p>
              </span>
              <time>Today</time>
            </div>
            <div className="timeline-item">
              <span className="timeline-dot">
                <Icon name="calendar" size="sm" />
              </span>
              <span>
                <strong>Class time confirmed</strong>
                <p>Accessible Product Decisions · 3:00 PM EAT.</p>
              </span>
              <time>Yesterday</time>
            </div>
          </div>
          <div className="hero-actions">
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "announcement" })}>
              <Icon name="plus" size="sm" />
              New announcement
            </button>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "outcomes" ? (
        <div className="content-grid equal">
          <AcademyPanel icon="award" title="Completion forecast" meta={`${cohort.completionForecast}%`}>
            <div className="line-chart">
              {[72, 76, 79, 82, 85, 86, 88, cohort.completionForecast].map((value, index) => (
                <div className="line-column" key={index}>
                  <i style={{ height: `${value}%` }} />
                  <span>W{index + 1}</span>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="evidence" title="Credential evidence" meta="Skill · System · Proof">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Skill</strong>
                  <p>Knowledge and applied practice</p>
                </div>
                <TonePill tone="green">84%</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>System</strong>
                  <p>Working project and technical evidence</p>
                </div>
                <TonePill tone="purple">72%</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Proof</strong>
                  <p>Evaluation, reflection and human review</p>
                </div>
                <TonePill tone="orange">63%</TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
    </div>
  );
}

function LearnerDetailView({
  learner,
  navigate,
  notify,
  openModal,
}: {
  learner: LearnerRecord;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [tab, setTab] = useState("overview");
  const [risk, setRisk] = useState<LearnerRecord["risk"]>(learner.risk);
  const [note, setNote] = useState(
    learner.risk === "intervention"
      ? "Follow up on consent evidence, attendance and Wednesday office hours."
      : "",
  );
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("cohorts", "learners")}>
        <Icon name="arrow-left" size="sm" />
        Back to learners
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar
            initials={learner.initials}
            tone={risk === "intervention" ? "red" : risk === "watch" ? "orange" : "purple"}
          />
          <div>
            <div className="hero-eyebrow">Learner profile · consent-aware</div>
            <h2>{learner.name}</h2>
            <p>
              {learner.email} · {learner.cohort} · {learner.languages}
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("engagement", `thread-thread-${learner.id}`)}
          >
            <Icon name="mail" size="sm" />
            Message
          </button>
          <button type="button" className="secondary-btn" onClick={() => openModal({ type: "office-hours" })}>
            <Icon name="calendar" size="sm" />
            Book support
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() =>
              navigate("projects", learner.project.includes("Kijani") ? "project-ronald" : "reviews")
            }
          >
            <Icon name="folder" size="sm" />
            Open project
          </button>
        </div>
      </section>
      <nav className="detail-tabs">
        {[
          "overview",
          "progress",
          "assignments",
          "assessments",
          "projects",
          "feedback",
          "support",
          "timeline",
        ].map(item => (
          <button
            type="button"
            className={tab === item ? "active" : ""}
            key={item}
            onClick={() => setTab(item)}
          >
            {item[0].toUpperCase() + item.slice(1)}
          </button>
        ))}
      </nav>
      {tab === "overview" ? (
        <>
          <div className="stat-grid">
            <StatTile
              icon="activity"
              label="Progress"
              value={`${learner.progress}%`}
              detail={learner.submissions + " submissions"}
            />
            <StatTile
              icon="calendar"
              label="Attendance"
              value={`${learner.attendance}%`}
              detail="Across live sessions"
              tone="cyan"
            />
            <StatTile
              icon="award"
              label="Average score"
              value={`${learner.average}%`}
              detail="Human-reviewed work"
              tone="blue"
            />
            <StatTile
              icon="evidence"
              label="Credential readiness"
              value={`${learner.credentialReadiness}%`}
              detail="Skill · System · Proof"
              tone="orange"
            />
          </div>
          <div className="content-grid">
            <AcademyPanel icon="activity" title="Current learning position" meta={learner.program}>
              <div className="action-list">
                <div className="setting-row">
                  <div style={{ flex: 1 }}>
                    <strong>Program progress</strong>
                    <p>Current module and assignments</p>
                    <ProgressMeter value={learner.progress} />
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Current project</strong>
                    <p>{learner.project}</p>
                  </div>
                  <TonePill tone="purple">In progress</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Last active</strong>
                    <p>{learner.lastActive}</p>
                  </div>
                  <TonePill tone="green">Connected</TonePill>
                </div>
              </div>
            </AcademyPanel>
            <AcademyPanel icon="shield" title="Support and visibility boundary" meta="Private">
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Current risk</strong>
                    <p>Update only with evidence and an accountable note.</p>
                  </div>
                  <select
                    className="select-field"
                    value={risk}
                    onChange={event => setRisk(event.target.value as LearnerRecord["risk"])}
                  >
                    <option value="on-track">On track</option>
                    <option value="watch">Watch</option>
                    <option value="intervention">Intervention</option>
                  </select>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Accommodation visibility</strong>
                    <p>{learner.accommodations}</p>
                  </div>
                  <TonePill>Need-to-know</TonePill>
                </div>
                <div className="form-field">
                  <label>Private support note</label>
                  <textarea value={note} onChange={event => setNote(event.target.value)} />
                </div>
                <div className="hero-actions">
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() =>
                      notify(
                        "Support record saved",
                        `${learner.name} is now marked ${risk.replace("-", " ")}.`,
                      )
                    }
                  >
                    <Icon name="save" size="sm" />
                    Save support record
                  </button>
                </div>
              </div>
            </AcademyPanel>
          </div>
        </>
      ) : null}
      {tab === "progress" ? (
        <div className="content-grid equal">
          <AcademyPanel icon="book" title="Module progress" meta="8 modules">
            <div className="action-list">
              {[84, 92, 89, 76, 68, 55, 30, 0].map((value, index) => (
                <div className="setting-row" key={index}>
                  <div style={{ flex: 1 }}>
                    <strong>Module {index + 1}</strong>
                    <p>
                      {
                        [
                          "Foundations",
                          "Problem framing",
                          "Prompt contracts",
                          "Context systems",
                          "AI applications",
                          "Trustworthy interfaces",
                          "Evaluation",
                          "Proof",
                        ][index]
                      }
                    </p>
                    <ProgressMeter value={value} />
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="award" title="Skill profile" meta="Verified evidence">
            <div className="action-list">
              {[
                ["Problem framing", 91],
                ["Prompt engineering", 88],
                ["Context systems", 84],
                ["Evaluation", learner.average],
                ["Responsible use", 79],
              ].map(([label, value]) => (
                <div className="setting-row" key={String(label)}>
                  <div style={{ flex: 1 }}>
                    <strong>{label}</strong>
                    <ProgressMeter value={Number(value)} />
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "assignments" ? (
        <AcademyPanel icon="clipboard" title="Assignment record" meta={learner.submissions}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Score</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {ASSIGNMENTS.slice(0, 4).map((assignment, index) => (
                  <tr
                    key={assignment.id}
                    onClick={() =>
                      navigate(
                        "assessment",
                        index === 0 ? "submission-ronald-checkout" : `assignment-${assignment.id}`,
                      )
                    }
                  >
                    <td>{assignment.title}</td>
                    <td>
                      <TonePill tone={index < 2 ? "green" : "purple"}>
                        {index < 3 ? "Submitted" : "In progress"}
                      </TonePill>
                    </td>
                    <td>{index < 3 ? ["Today", "2 Nov", "15 Oct"][index] : "—"}</td>
                    <td>{index < 3 ? `${Math.max(64, learner.average - index * 3)}%` : "—"}</td>
                    <td>{index < 3 ? `${4 + index} items` : "0"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "assessments" ? (
        <AcademyPanel icon="test" title="Assessment attempts" meta="3 completed">
          <div className="action-list">
            {[
              ["AI product foundations", 94, "Passed"],
              ["Prompt contracts practical", 88, "Passed"],
              ["Responsible-use scenario", 76, "Passed"],
            ].map(item => (
              <div className="action-card" key={String(item[0])}>
                <span className="action-icon">
                  <Icon name="test" />
                </span>
                <span>
                  <strong>{item[0]}</strong>
                  <span>Score {item[1]}% · human-reviewed exceptions retained</span>
                </span>
                <TonePill tone="green">{item[2]}</TonePill>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "projects" ? (
        <AcademyPanel icon="folder" title="Project evidence" meta={learner.project}>
          <div className="review-score-grid">
            <div>
              <span>Architecture</span>
              <strong>{learner.average - 2}</strong>
            </div>
            <div>
              <span>Evidence</span>
              <strong>{learner.credentialReadiness}</strong>
            </div>
            <div>
              <span>Verification</span>
              <strong>{Math.max(50, learner.credentialReadiness - 3)}</strong>
            </div>
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="primary-btn"
              onClick={() =>
                navigate("projects", learner.id === "ronald-isabirye" ? "project-ronald" : "reviews")
              }
            >
              Open project review
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate("buildRooms", "overview")}
            >
              Open Build Room
            </button>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "feedback" ? (
        <AcademyPanel icon="message" title="Feedback history" meta="Visible to learner">
          <div className="timeline">
            {[
              ["check", "Problem framing approved", "Clear decision boundary and user evidence.", "15 Oct"],
              [
                "clipboard",
                "Context pack revision requested",
                "Add source freshness and deletion trigger.",
                "28 Oct",
              ],
              ["message", "Revision accepted", "Evidence now connects each claim to a source.", "31 Oct"],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>{item[3]}</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "support" ? (
        <div className="content-grid equal">
          <AcademyPanel icon="shield" title="Support plan" meta="Private instructor view">
            <div className="form-field">
              <label>Next support action</label>
              <select
                defaultValue={risk === "intervention" ? "Office hours" : "Monitor"}
                onChange={() => notify("Support action updated")}
              >
                <option>Monitor</option>
                <option>Office hours</option>
                <option>Mentor follow-up</option>
                <option>Academic Operations referral</option>
              </select>
            </div>
            <div className="form-field" style={{ marginTop: 10 }}>
              <label>Private note</label>
              <textarea value={note} onChange={event => setNote(event.target.value)} />
            </div>
            <div className="hero-actions">
              <button type="button" className="primary-btn" onClick={() => notify("Support plan saved")}>
                Save plan
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="info" title="Consent and boundaries" meta="Minimum necessary visibility">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Academic progress</strong>
                  <p>Visible to assigned teaching team</p>
                </div>
                <TonePill tone="green">Allowed</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Accommodation detail</strong>
                  <p>Only functional support needs are displayed</p>
                </div>
                <TonePill tone="orange">Limited</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Private support note</strong>
                  <p>Not visible in cohort or learner exports</p>
                </div>
                <TonePill tone="purple">Restricted</TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "timeline" ? (
        <AcademyPanel icon="activity" title="Learner timeline" meta="Attributable">
          <div className="timeline">
            {[
              [
                "video",
                "Joined Accessible Product Decisions",
                "Class identity and language context applied",
                "Today",
              ],
              ["clipboard", "Submitted checkout evidence", "8 artifacts · AI disclosure included", "Today"],
              ["flask", "Completed AI-App Builder Lab run", "42 minutes · evidence attached", "Yesterday"],
              ["message", "Received instructor feedback", "One staging condition remains", "Yesterday"],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>{item[3]}</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
    </div>
  );
}

function InstructorLearningDirectory() {
  const [cohorts, setCohorts] = useState<D10AssignedCohorts["items"]>([]);
  const [cohortId, setCohortId] = useState("");
  const [learners, setLearners] = useState<D10CohortRoster["items"]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [progressByLearner, setProgressByLearner] = useState<
    Record<
      string,
      {
        cohortId: string;
        loading?: boolean;
        data?: D10InstructorLearnerProgress;
        error?: string;
      }
    >
  >({});
  const [selectedPlanEnrollment, setSelectedPlanEnrollment] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<D10LearningPlan | null>(null);
  const [planError, setPlanError] = useState("");
  const [planBusy, setPlanBusy] = useState(false);
  const [planDraft, setPlanDraft] = useState({
    goals: "",
    milestones: "",
    supportActions: "",
    mentorActions: "",
    reviewAt: "",
    familyVisibleSummary: "",
    reason: "",
  });
  const planSender = useRef(createCommandSender({ headerOnly: true, context: "d10-learning-plan" }));
  const loadPlan = (enrollmentId: string) => {
    setSelectedPlanEnrollment(enrollmentId);
    setSelectedPlan(null);
    setPlanError("");
    void academyRequest<D10LearningPlanEnvelope>(
      `instructor/learning/cohorts/${cohortId}/learners/${enrollmentId}/plan`,
      undefined,
      "GET",
    )
      .then(result => {
        setSelectedPlan(result.plan);
        if (result.plan)
          setPlanDraft({
            goals: result.plan.goals.join("\n"),
            milestones: result.plan.milestones.join("\n"),
            supportActions: result.plan.supportActions.join("\n"),
            mentorActions: result.plan.mentorActions.join("\n"),
            reviewAt: "",
            familyVisibleSummary: result.plan.familyVisibleSummary ?? "",
            reason: "",
          });
      })
      .catch(cause => setPlanError(cause instanceof Error ? cause.message : "Learning Plan unavailable"));
  };
  const revisePlan = async () => {
    if (!selectedPlan) return;
    setPlanBusy(true);
    setPlanError("");
    try {
      const lines = (value: string) =>
        value
          .split("\n")
          .map(item => item.trim())
          .filter(Boolean);
      const input: ReviseLearningPlanRequest = {
        planId: selectedPlan.id,
        expectedVersion: selectedPlan.version,
        state: "active",
        goals: lines(planDraft.goals),
        milestones: lines(planDraft.milestones),
        supportActions: lines(planDraft.supportActions),
        mentorActions: lines(planDraft.mentorActions),
        reason: planDraft.reason.trim(),
      };
      if (planDraft.reviewAt) input.reviewAt = new Date(planDraft.reviewAt).toISOString();
      if (planDraft.familyVisibleSummary.trim())
        input.familyVisibleSummary = planDraft.familyVisibleSummary.trim();
      const updated = await planSender.current<D10LearningPlan>("instructor/learning/plans/revise", input);
      setSelectedPlan(updated);
      setPlanDraft(current => ({ ...current, reason: "" }));
    } catch (cause) {
      setPlanError(cause instanceof Error ? cause.message : "Learning Plan revision unavailable");
    } finally {
      setPlanBusy(false);
    }
  };
  const loadProgress = (enrollmentId: string) => {
    setProgressByLearner(current => ({ ...current, [enrollmentId]: { cohortId, loading: true } }));
    void academyRequest<D10InstructorLearnerProgress>(
      `instructor/learning/cohorts/${cohortId}/learners/${enrollmentId}/progress`,
      undefined,
      "GET",
    )
      .then(data => setProgressByLearner(current => ({ ...current, [enrollmentId]: { cohortId, data } })))
      .catch(cause =>
        setProgressByLearner(current => ({
          ...current,
          [enrollmentId]: {
            cohortId,
            error: cause instanceof Error ? cause.message : "Learning progress unavailable",
          },
        })),
      );
  };
  useEffect(() => {
    let active = true;
    void academyRequest<D10AssignedCohorts>("instructor/learning/cohorts", undefined, "GET")
      .then(result => {
        if (active) {
          setCohorts(result.items);
          setCohortId(result.items[0]?.id || "");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Teaching scope unavailable");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setSelectedPlanEnrollment("");
    setSelectedPlan(null);
    if (!cohortId) {
      setLearners([]);
      return;
    }
    let active = true;
    void academyRequest<D10CohortRoster>(`instructor/learning/cohorts/${cohortId}/roster`, undefined, "GET")
      .then(result => {
        if (active) {
          setLearners(result.items);
          setError("");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Cohort roster unavailable");
      });
    return () => {
      active = false;
    };
  }, [cohortId]);
  const visible = learners.filter(learner => learner.displayName.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="instructor-page">
      <PageTitle title="Learner directory" text="Current learners in your assigned Cohort teaching scope." />
      <AcademyPanel icon="users" title="Current Cohort learners" meta={`${visible.length} matching`}>
        {error && <p role="alert">{error}</p>}
        {!error && !cohorts.length && <p>No current assigned Cohorts are available.</p>}
        {!!cohorts.length && (
          <>
            <div className="form-field">
              <label htmlFor="d10-directory-cohort">Assigned Cohort</label>
              <select
                id="d10-directory-cohort"
                className="select-field"
                value={cohortId}
                onChange={event => setCohortId(event.target.value)}
              >
                {cohorts.map(cohort => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="d10-directory-search">Search learner</label>
              <input
                id="d10-directory-search"
                value={query}
                onChange={event => setQuery(event.target.value)}
              />
            </div>
            {!!visible.length && (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Learning progress</th>
                      <th>Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(learner => (
                      <tr key={learner.enrollmentId}>
                        <td>
                          {learner.displayName}
                          <button
                            type="button"
                            className="mini-btn"
                            onClick={() => loadPlan(learner.enrollmentId)}
                          >
                            Learning Plan
                          </button>
                        </td>
                        <td>
                          {(() => {
                            const current = progressByLearner[learner.enrollmentId];
                            const projection = current?.cohortId === cohortId ? current : undefined;
                            if (projection?.loading) return "Loading learning progress…";
                            if (projection?.error) return <span role="alert">{projection.error}</span>;
                            if (projection?.data)
                              return projection.data.state === "NOT_INITIALIZED"
                                ? "Learning record not started"
                                : projection.data.progress?.percent === null
                                  ? "Progress unavailable"
                                  : `${Math.round(projection.data.progress?.percent ?? 0)}% learning progress`;
                            return (
                              <button
                                type="button"
                                className="mini-btn"
                                onClick={() => loadProgress(learner.enrollmentId)}
                              >
                                View learning progress
                              </button>
                            );
                          })()}
                        </td>
                        <td>See Academy attendance</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!visible.length && <p>No current learners match this Cohort and search.</p>}
            {!!selectedPlanEnrollment && (
              <div className="module-list">
                <h3>Official Learning Plan</h3>
                {planError && <p role="alert">{planError}</p>}
                {!selectedPlan && !planError && (
                  <p>This learner has not proposed an official Learning Plan.</p>
                )}
                {selectedPlan && (
                  <>
                    <p>
                      {selectedPlan.state} · version {selectedPlan.version}
                    </p>
                    <div className="form-field">
                      <label htmlFor="d10-plan-goals">Goals, one per line</label>
                      <textarea
                        id="d10-plan-goals"
                        value={planDraft.goals}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, goals: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-milestones">Milestones, one per line</label>
                      <textarea
                        id="d10-plan-milestones"
                        value={planDraft.milestones}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, milestones: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-support">Academic support actions</label>
                      <textarea
                        id="d10-plan-support"
                        value={planDraft.supportActions}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, supportActions: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-mentor">Mentor actions</label>
                      <textarea
                        id="d10-plan-mentor"
                        value={planDraft.mentorActions}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, mentorActions: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-review">Next review</label>
                      <input
                        id="d10-plan-review"
                        type="datetime-local"
                        value={planDraft.reviewAt}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, reviewAt: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-family">Family-visible summary</label>
                      <textarea
                        id="d10-plan-family"
                        value={planDraft.familyVisibleSummary}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, familyVisibleSummary: event.target.value }))
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="d10-plan-reason">Reason for review</label>
                      <input
                        id="d10-plan-reason"
                        value={planDraft.reason}
                        onChange={event =>
                          setPlanDraft(current => ({ ...current, reason: event.target.value }))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="primary-btn"
                      disabled={
                        planBusy ||
                        planDraft.reason.trim().length < 10 ||
                        !planDraft.goals.trim() ||
                        !planDraft.milestones.trim()
                      }
                      onClick={() => void revisePlan()}
                    >
                      {selectedPlan.state === "proposed"
                        ? "Activate Learning Plan"
                        : "Save reviewed revision"}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </AcademyPanel>
    </div>
  );
}

function CohortsView({
  page,
  navigate,
  notify,
  openModal,
  sessions,
  sessionsLoading,
  sessionsError,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
  sessions: ClassSession[];
  sessionsLoading: boolean;
  sessionsError: string;
}) {
  const [query, setQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState("All risks");
  const [cohortFilter, setCohortFilter] = useState("All cohorts");
  const [pageNumber, setPageNumber] = useState(1);

  if (page.startsWith("cohort-")) {
    const cohort = COHORTS.find(item => `cohort-${item.id}` === page) || COHORTS[0];
    return (
      <CohortDetailView
        cohort={cohort}
        navigate={navigate}
        openModal={openModal}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        sessionsError={sessionsError}
      />
    );
  }
  if (page.startsWith("learner-")) {
    const id = page.replace("learner-", "");
    const learner =
      LEARNERS.find(item => item.id === id) ||
      LEARNERS.find(item => item.id.includes(id)) ||
      CORE_LEARNERS[0];
    return <LearnerDetailView learner={learner} navigate={navigate} notify={notify} openModal={openModal} />;
  }

  const pageSize = 12;

  if (page === "learners") return <InstructorLearningDirectory />;

  if (["support", "readiness"].includes(page)) {
    let filtered = LEARNERS.filter(learner =>
      `${learner.name} ${learner.email} ${learner.project} ${learner.cohort}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    );
    if (riskFilter !== "All risks") filtered = filtered.filter(learner => learner.risk === riskFilter);
    if (cohortFilter !== "All cohorts")
      filtered = filtered.filter(learner => learner.cohortId === cohortFilter);
    if (page === "support") filtered = filtered.filter(learner => learner.risk !== "on-track");
    if (page === "readiness")
      filtered = filtered.sort((a, b) => a.credentialReadiness - b.credentialReadiness);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(pageNumber, pages);
    const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
    return (
      <div className="instructor-page">
        <PageTitle
          title={
            page === "learners"
              ? "Learner directory"
              : page === "support"
                ? "Learner support signals · Preview"
                : "Credential readiness · Preview"
          }
          text={
            page === "learners"
              ? "Search progress, attendance, work and support without exposing unnecessary personal information."
              : page === "support"
                ? "Prioritise evidence-backed support and keep private notes within assigned teaching roles."
                : "See Skill, System and Proof evidence gaps before final assessment."
          }
          actions={
            <button
              type="button"
              className="secondary-btn"
              disabled
              title="Preview data cannot be exported as an academic record."
            >
              <Icon name="download" size="sm" />
              Export view
            </button>
          }
        />
        <AcademyPanel icon="users" title="Preview learners" meta={`${filtered.length} sample records`}>
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input
                value={query}
                onChange={event => {
                  setQuery(event.target.value);
                  setPageNumber(1);
                }}
                placeholder="Search learners, projects or email…"
              />
            </div>
            <select
              className="select-field"
              value={cohortFilter}
              onChange={event => {
                setCohortFilter(event.target.value);
                setPageNumber(1);
              }}
            >
              <option>All cohorts</option>
              {COHORTS.map(cohort => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.title}
                </option>
              ))}
            </select>
            <select
              className="select-field"
              value={riskFilter}
              onChange={event => {
                setRiskFilter(event.target.value);
                setPageNumber(1);
              }}
            >
              <option>All risks</option>
              <option value="on-track">On track</option>
              <option value="watch">Watch</option>
              <option value="intervention">Intervention</option>
            </select>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                setQuery("");
                setRiskFilter("All risks");
                setCohortFilter("All cohorts");
                setPageNumber(1);
              }}
            >
              <Icon name="refresh" size="sm" />
              Reset
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Cohort</th>
                  <th>Progress</th>
                  <th>Attendance</th>
                  <th>Average</th>
                  <th>Risk</th>
                  <th>Credential</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(learner => (
                  <tr key={learner.id}>
                    <td>
                      <div className="person-cell">
                        <Avatar
                          initials={learner.initials}
                          small
                          tone={
                            learner.risk === "intervention"
                              ? "red"
                              : learner.risk === "watch"
                                ? "orange"
                                : "purple"
                          }
                        />
                        <span>
                          <strong>{learner.name}</strong>
                          <span>{learner.project}</span>
                        </span>
                      </div>
                    </td>
                    <td>{learner.cohort}</td>
                    <td>
                      <ProgressMeter value={learner.progress} />
                    </td>
                    <td>{learner.attendance}%</td>
                    <td>{learner.average}%</td>
                    <td>
                      <RiskPill risk={learner.risk} />
                    </td>
                    <td>{learner.credentialReadiness}%</td>
                    <td>{learner.lastActive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel-footer">
            <span>
              Page {safePage} of {pages} · showing {(safePage - 1) * pageSize + (visible.length ? 1 : 0)}–
              {Math.min(safePage * pageSize, filtered.length)}
            </span>
            <div className="row-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                disabled={safePage <= 1}
                onClick={() => setPageNumber(current => Math.max(1, current - 1))}
              >
                <Icon name="arrow-left" size="sm" />
                Previous
              </button>
              <button
                type="button"
                className="secondary-btn button-sm"
                disabled={safePage >= pages}
                onClick={() => setPageNumber(current => Math.min(pages, current + 1))}
              >
                Next
                <Icon name="arrow-right" size="sm" />
              </button>
            </div>
          </div>
        </AcademyPanel>
      </div>
    );
  }

  const status =
    page === "active"
      ? "active"
      : page === "upcoming"
        ? "upcoming"
        : page === "completed"
          ? "completed"
          : null;
  const cohorts = status ? COHORTS.filter(cohort => cohort.status === status) : COHORTS;
  return (
    <div className="instructor-page">
      <p role="status">
        Preview cohort metrics and cards are sample data. The Learner directory uses the current D07/D09
        Cohort roster.
      </p>
      <InstructorHero
        eyebrow="Cohorts & learners"
        title="Deliver every cohort with visible momentum and accountable support."
        text="Move from cohort-level signals into the learner, class, assignment, Lab, project or evidence record that explains them."
        actions={
          <>
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "new-cohort" })}>
              <Icon name="plus" size="sm" />
              Create cohort
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("cohorts", "learners")}>
              <Icon name="users" size="sm" />
              Learner directory
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard
              icon="users"
              title="Active cohorts"
              detail="Builder and Prompt programs"
              value="2"
            />
            <HeroSideCard icon="alert" title="Support signals" detail="Watch + intervention" value="9" />
            <HeroSideCard
              icon="award"
              title="Completion forecast"
              detail="Weighted active cohorts"
              value="90%"
            />
          </>
        }
      />
      <div className="stat-grid">
        <StatTile icon="users" label="Assigned learners" value="62" detail="Across 2 active cohorts" />
        <StatTile icon="calendar" label="Average attendance" value="92%" detail="Target ≥90%" tone="cyan" />
        <StatTile
          icon="clipboard"
          label="Review workload"
          value="17"
          detail="11 in current queue"
          tone="blue"
        />
        <StatTile icon="alert" label="Interventions" value="3" detail="2 actions due today" tone="orange" />
      </div>
      <PageTitle
        title={status ? `${status[0].toUpperCase() + status.slice(1)} cohorts` : "All cohorts"}
        text="Capacity, progress, attendance, risk distribution and completion forecast."
        actions={
          <>
            <select
              className="select-field"
              defaultValue="All programs"
              onChange={() => notify("Program filter applied")}
            >
              <option>All programs</option>
              <option>AI Product Builder</option>
              <option>Prompt & Context Engineering</option>
              <option>AI Leadership</option>
            </select>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "cohorts" })}
            >
              <Icon name="download" size="sm" />
              Export
            </button>
          </>
        }
      />
      <div className="cohort-grid">
        {cohorts.map(cohort => (
          <button
            type="button"
            className="cohort-card"
            key={cohort.id}
            onClick={() => navigate("cohorts", `cohort-${cohort.id}`)}
            style={{ color: "inherit", textAlign: "left", cursor: "pointer" }}
          >
            <div className="cohort-card-top">
              <span className="cohort-card-icon">
                <Icon name="users" />
              </span>
              <div className="cohort-card-copy">
                <h3>{cohort.title}</h3>
                <p>
                  {cohort.intake} · {cohort.learners}/{cohort.capacity} learners
                </p>
              </div>
              <TonePill
                tone={
                  cohort.status === "active" ? "green" : cohort.status === "upcoming" ? "blue" : "default"
                }
              >
                {cohort.status}
              </TonePill>
            </div>
            <ProgressMeter value={cohort.progress} />
            <div className="cohort-metrics">
              <div className="cohort-metric">
                <span>Attendance</span>
                <strong>
                  {cohort.attendance || "—"}
                  {cohort.attendance ? "%" : ""}
                </strong>
              </div>
              <div className="cohort-metric">
                <span>Forecast</span>
                <strong>{cohort.completionForecast}%</strong>
              </div>
              <div className="cohort-metric">
                <span>Support</span>
                <strong>{cohort.watch + cohort.intervention}</strong>
              </div>
              <div className="cohort-metric">
                <span>Reviews</span>
                <strong>{cohort.reviewQueue}</strong>
              </div>
            </div>
            <div className="risk-distribution">
              <i className="good" style={{ flex: cohort.onTrack }} />
              <i className="watch" style={{ flex: Math.max(1, cohort.watch) }} />
              <i className="bad" style={{ flex: Math.max(1, cohort.intervention) }} />
            </div>
            <div className="panel-footer" style={{ padding: 0, border: 0 }}>
              <span>{cohort.nextSession}</span>
              <span>
                Open cohort <Icon name="arrow-right" size="sm" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LessonDetailView({
  lesson,
  navigate,
  notify,
}: {
  lesson: LessonRecord | TeachingLesson;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [tab, setTab] = useState("content");
  const academic = "academic" in lesson ? lesson.academic : undefined;
  const identity = useIdentity();
  const [reason, setReason] = useState(""),
    [saveError, setSaveError] = useState(""),
    [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState(academic?.lesson.activities.join("\n\n") || "");
  const [completionRules, setCompletionRules] = useState(() =>
    (academic?.lesson.activities || []).map((text, index) => {
      const existing = academic?.lesson.completionRequirements?.activities[index];
      return {
        activityId: existing?.activityId || crypto.randomUUID(),
        text,
        required: existing?.required ?? true,
        progressWeight: existing?.progressWeight ?? 1,
        method: existing?.completionRequirement.method || "",
        parameters: existing?.completionRequirement.parameters || ({} as Record<string, unknown>),
      };
    }),
  );
  const [expectedVersion, setExpectedVersion] = useState(academic?.curriculum.version || 0);
  const sender = useRef(createCommandSender({ headerOnly: true })),
    savingRef = useRef(false);
  const history = useAcademicRead(`lesson-history/${academic?.programId || ""}`, !!academic, signal =>
    readAcademic<AcademicHistoryPage>(`program/${academic?.programId}/history`, signal),
  );
  const [title, setTitle] = useState(lesson.title);
  const [objective, setObjective] = useState(
    academic
      ? academic.lesson.objectives.join("\n")
      : "Learners can identify safe API boundaries, explain data ownership and design server actions with explicit human authority.",
  );
  const [notes, setNotes] = useState(
    academic
      ? ""
      : "Use the Kijani checkout example. Pause after each boundary decision and invite one learner to name the data owner, retention rule and failure path.",
  );
  const [checks, setChecks] = useState({
    transcript: true,
    captions: lesson.accessibility !== null && lesson.accessibility >= 90,
    descriptions: lesson.accessibility !== null && lesson.accessibility >= 95,
    keyboard: true,
    contrast: true,
  });
  const toggleCheck = (key: keyof typeof checks) =>
    setChecks(current => ({ ...current, [key]: !current[key] }));
  const readiness = Math.round(
    (Object.values(checks).filter(Boolean).length / Object.keys(checks).length) * 100,
  );
  const activityTexts = activities
    .split(/\n\s*\n/)
    .map(item => item.trim())
    .filter(Boolean);
  const updateActivities = (value: string) => {
    setActivities(value);
    const texts = value
      .split(/\n\s*\n/)
      .map(item => item.trim())
      .filter(Boolean);
    setCompletionRules(current =>
      texts.map((text, index) => ({
        ...(current[index] || {
          activityId: crypto.randomUUID(),
          required: true,
          progressWeight: 1,
          method: "",
          parameters: {} as Record<string, unknown>,
        }),
        text,
      })),
    );
  };
  const updateCompletionRule = (index: number, change: Partial<(typeof completionRules)[number]>) =>
    setCompletionRules(current =>
      current.map((item, position) => (position === index ? { ...item, ...change } : item)),
    );
  const saveAcademicDraft = async () => {
    if (!academic || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const actual = await checkIdentitySession();
      if (
        actual.workspace.id !== identity.session?.workspace.id ||
        actual.user.id !== identity.session?.user.id ||
        actual.tenantId !== identity.session?.tenantId
      )
        throw new Error("Your workspace changed. Reopen this lesson in its current scope.");
      const completeRules =
        completionRules.length === activityTexts.length && completionRules.every(item => item.method);
      if (
        !completeRules &&
        (completionRules.some(item => item.method) || academic.lesson.completionRequirements)
      )
        throw new Error(
          "Choose an explicit completion method for every activity before saving these requirements.",
        );
      const completionRequirements: LessonCompletionRequirements | undefined = completeRules
        ? {
            policyId: "REQUIRED_WEIGHTED_COMPLETION_V1",
            policyVersion: "1",
            activities: completionRules.map(item => ({
              activityId: item.activityId,
              text: item.text,
              required: item.required,
              progressWeight: item.progressWeight,
              completionRequirement: {
                method:
                  item.method as LessonCompletionRequirements["activities"][number]["completionRequirement"]["method"],
                parameters: item.parameters,
                policyVersion: "1",
              },
            })),
          }
        : undefined;
      const input: UpdateAcademicLesson = {
        title: title.trim(),
        objectives: objective
          .split("\n")
          .map(item => item.trim())
          .filter(Boolean),
        activities: activities
          .split(/\n\s*\n/)
          .map(item => item.trim())
          .filter(Boolean),
        completionRequirements,
        expectedVersion,
        reason: reason.trim(),
      };
      const receipt = await sender.current<AcademicReceipt>(
        `academic-catalogue/program/${academic.programId}/curricula/${academic.curriculum.id}/modules/${academic.lesson.moduleId}/lessons/${academic.lesson.id}/commands/update`,
        input,
      );
      setExpectedVersion(receipt.version);
      setReason("");
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      notify("Draft saved", title);
    } catch (failure) {
      setSaveError(failure instanceof Error ? failure.message : "The lesson was not saved.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("teaching", "lessons")}>
        <Icon name="arrow-left" size="sm" />
        Back to lessons
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <span className="simple-card-icon">
            <Icon name="book" />
          </span>
          <div>
            <div className="hero-eyebrow">
              {lesson.module} · {lesson.version}
            </div>
            <h2>{title}</h2>
            <p>
              {lesson.program} · {lesson.format} · {lesson.duration}
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <button
            type="button"
            className="secondary-btn"
            disabled={!!academic}
            onClick={() =>
              notify("Learner preview opened", "The lesson is shown exactly as a learner will receive it.")
            }
          >
            <Icon name="eye" size="sm" />
            Preview
          </button>
          <button
            type="button"
            className="secondary-btn"
            disabled={
              !!academic &&
              (saving ||
                !identity.session?.permissions.includes("curriculum.manage") ||
                academic.curriculum.state !== "draft" ||
                reason.trim().length < 10 ||
                title.trim().length < 3 ||
                !objective.trim())
            }
            onClick={() =>
              academic
                ? void saveAcademicDraft()
                : notify("Draft saved", `${title} was saved as a new attributable draft.`)
            }
          >
            <Icon name="save" size="sm" />
            Save draft
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={!!academic || readiness < 100}
            onClick={() => notify("Lesson published", `${title} is now available to assigned cohorts.`)}
          >
            <Icon name="upload" size="sm" />
            Publish
          </button>
        </div>
      </section>
      {saveError && <p role="alert">{saveError}</p>}
      <nav className="detail-tabs" aria-label="Lesson editor sections">
        {["content", "media", "interactions", "accessibility", "teaching-notes", "versions"].map(item => (
          <button
            type="button"
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item
              .split("-")
              .map(word => word[0].toUpperCase() + word.slice(1))
              .join(" ")}
          </button>
        ))}
      </nav>
      {tab === "content" ? (
        <div className="builder-layout">
          <div className="builder-stage">
            <AcademyPanel
              icon="edit"
              title="Lesson content"
              meta={academic ? "Save draft to record changes" : "Autosaved locally"}
            >
              <div className="form-stack">
                <div className="form-field">
                  <label>Lesson title</label>
                  <input
                    aria-label="Lesson title"
                    value={title}
                    disabled={!!academic && (saving || academic.curriculum.state !== "draft")}
                    onChange={event => setTitle(event.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Learning outcome</label>
                  <textarea
                    aria-label="Learning outcome"
                    value={objective}
                    disabled={!!academic && (saving || academic.curriculum.state !== "draft")}
                    onChange={event => setObjective(event.target.value)}
                  />
                </div>
                {academic && (
                  <div className="form-field">
                    <label htmlFor="lesson-change-reason">Reason for change</label>
                    <textarea
                      id="lesson-change-reason"
                      value={reason}
                      maxLength={2000}
                      disabled={saving || academic.curriculum.state !== "draft"}
                      onChange={event => setReason(event.target.value)}
                    />
                  </div>
                )}
                <div>
                  <div className="editor-toolbar" inert={academic ? true : undefined}>
                    <button type="button" onClick={() => notify("Heading block added")}>
                      H2
                    </button>
                    <button type="button" onClick={() => notify("Bold formatting selected")}>
                      B
                    </button>
                    <button type="button" onClick={() => notify("List block added")}>
                      List
                    </button>
                    <button type="button" onClick={() => notify("Code block added")}>
                      <Icon name="code" size="sm" />
                    </button>
                    <button type="button" onClick={() => notify("Image block added")}>
                      <Icon name="image" size="sm" />
                    </button>
                    <button type="button" onClick={() => notify("Knowledge check added")}>
                      <Icon name="test" size="sm" />
                    </button>
                  </div>
                  {academic ? (
                    <textarea
                      className="rich-editor"
                      aria-label="Lesson activities"
                      value={activities}
                      disabled={saving || academic.curriculum.state !== "draft"}
                      onChange={event => updateActivities(event.target.value)}
                      placeholder="Write learning activities. Separate activities with a blank line."
                    />
                  ) : (
                    <div className="rich-editor" contentEditable suppressContentEditableWarning>
                      <h3>Why boundaries are product decisions</h3>
                      <p>
                        Every AI-enabled product must make ownership, permissions, retention, failure handling
                        and human authority visible. Learners compare a direct browser call with a server
                        action and identify what changes.
                      </p>
                      <ul>
                        <li>Name the data owner.</li>
                        <li>Limit the action to the minimum required scope.</li>
                        <li>Keep a visible human decision point.</li>
                        <li>Record failure evidence before changing the solution.</li>
                      </ul>
                    </div>
                  )}
                  {academic && activityTexts.length > 0 && (
                    <div className="action-list" aria-label="Lesson completion requirements">
                      <strong>Activity completion requirements</strong>
                      <p>Choose how each activity is completed. A page view never completes a lesson.</p>
                      {completionRules.map((item, index) => (
                        <div className="setting-row" key={item.activityId}>
                          <div className="form-field">
                            <strong>{item.text}</strong>
                            <label>
                              <input
                                type="checkbox"
                                checked={item.required}
                                disabled={saving || academic.curriculum.state !== "draft"}
                                onChange={event =>
                                  updateCompletionRule(index, { required: event.target.checked })
                                }
                              />
                              Required for lesson completion
                            </label>
                            <label htmlFor={`completion-method-${index}`}>Completion method</label>
                            <select
                              id={`completion-method-${index}`}
                              value={item.method}
                              disabled={saving || academic.curriculum.state !== "draft"}
                              onChange={event =>
                                updateCompletionRule(index, { method: event.target.value, parameters: {} })
                              }
                            >
                              <option value="">Select an explicit rule</option>
                              <option value="LEARNER_ACKNOWLEDGEMENT">Learner acknowledgement</option>
                              <option value="CONTENT_CONSUMPTION">Verified content consumption</option>
                              <option value="ATTENDANCE">Verified attendance</option>
                              <option value="SUBMISSION">Learning submission</option>
                              <option value="LAB_EVIDENCE">Verified Lab evidence</option>
                              <option value="ASSESSMENT_OUTCOME">Assessment outcome</option>
                              <option value="HUMAN_CONFIRMATION">Human confirmation</option>
                              <option value="EXTERNAL_EVIDENCE">Verified external evidence</option>
                            </select>
                            {item.required && (
                              <label>
                                Progress weight
                                <input
                                  type="number"
                                  min="0.000001"
                                  step="0.1"
                                  value={item.progressWeight}
                                  disabled={saving || academic.curriculum.state !== "draft"}
                                  onChange={event =>
                                    updateCompletionRule(index, {
                                      progressWeight: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                            )}
                            {item.method === "CONTENT_CONSUMPTION" && (
                              <label>
                                Verified consumption threshold (0–1)
                                <input
                                  type="number"
                                  min="0.000001"
                                  max="1"
                                  step="0.01"
                                  value={
                                    typeof item.parameters.threshold === "number"
                                      ? item.parameters.threshold
                                      : ""
                                  }
                                  disabled={saving || academic.curriculum.state !== "draft"}
                                  onChange={event =>
                                    updateCompletionRule(index, {
                                      parameters: { threshold: Number(event.target.value) },
                                    })
                                  }
                                />
                              </label>
                            )}
                            {item.method === "ATTENDANCE" && (
                              <fieldset>
                                <legend>Attendance statuses satisfying this Activity</legend>
                                {(["PRESENT", "LATE", "EXCUSED"] as const).map(status => {
                                  const accepted = Array.isArray(item.parameters.acceptedStatuses)
                                    ? (item.parameters.acceptedStatuses as string[])
                                    : [];
                                  return (
                                    <label key={status}>
                                      <input
                                        type="checkbox"
                                        checked={accepted.includes(status)}
                                        disabled={saving || academic.curriculum.state !== "draft"}
                                        onChange={event =>
                                          updateCompletionRule(index, {
                                            parameters: {
                                              acceptedStatuses: event.target.checked
                                                ? [...accepted, status]
                                                : accepted.filter(value => value !== status),
                                            },
                                          })
                                        }
                                      />
                                      {status.toLowerCase()}
                                    </label>
                                  );
                                })}
                              </fieldset>
                            )}
                            {item.method === "SUBMISSION" && (
                              <label>
                                Satisfying submission state
                                <select
                                  value={String(item.parameters.requiredState || "")}
                                  disabled={saving || academic.curriculum.state !== "draft"}
                                  onChange={event =>
                                    updateCompletionRule(index, {
                                      parameters: { requiredState: event.target.value },
                                    })
                                  }
                                >
                                  <option value="">Select state</option>
                                  <option value="SUBMITTED">Submitted</option>
                                  <option value="REVISION_ACCEPTED">Revision accepted</option>
                                </select>
                              </label>
                            )}
                            {item.method === "ASSESSMENT_OUTCOME" && (
                              <label>
                                Exact Assessment definition ID for PASS
                                <input
                                  type="text"
                                  value={String(item.parameters.assessmentDefinitionId || "")}
                                  disabled={saving || academic.curriculum.state !== "draft"}
                                  onChange={event =>
                                    updateCompletionRule(index, {
                                      parameters: {
                                        assessmentDefinitionId: event.target.value,
                                        requiredOutcome: "PASS",
                                      },
                                    })
                                  }
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </AcademyPanel>
          </div>
          <div className="builder-side">
            <AcademyPanel icon="settings" title="Publishing" meta={lesson.status}>
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Program</strong>
                    <p>{lesson.program}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Format</strong>
                    <p>{lesson.format}</p>
                  </div>
                  <TonePill tone="blue">{lesson.duration}</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Accessibility readiness</strong>
                    <p>
                      {academic
                        ? "Accessibility evidence is not available"
                        : readiness === 100
                          ? "All required checks complete"
                          : "Resolve remaining blockers"}
                    </p>
                  </div>
                  <TonePill tone={academic ? "orange" : readiness === 100 ? "green" : "orange"}>
                    {academic ? "Not assessed" : `${readiness}%`}
                  </TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Version</strong>
                    <p>Changes create an immutable version record</p>
                  </div>
                  <TonePill>{lesson.version}</TonePill>
                </div>
              </div>
            </AcademyPanel>
            <AcademyPanel icon="shield" title="Human boundary" meta="Required">
              <p style={{ margin: 0, color: "var(--muted)", fontSize: 14, lineHeight: 1.55 }}>
                AI may suggest wording, examples and checks, but an assigned instructor approves learning
                outcomes, accessibility, assessment alignment and publishing.
              </p>
            </AcademyPanel>
          </div>
        </div>
      ) : null}
      {academic && ["media", "interactions", "accessibility", "teaching-notes"].includes(tab) && (
        <AcademyPanel icon="info" title={tab.replace("-", " ")}>
          <p>This lesson has no connected {tab.replace("-", " ")} records.</p>
        </AcademyPanel>
      )}
      {tab === "media" && !academic ? (
        <div className="content-grid">
          <AcademyPanel icon="video" title="Video and audio" meta="1 primary media item">
            <div className="evidence-stack">
              <div className="evidence-card">
                <span>
                  <Icon name="video" />
                </span>
                <div>
                  <strong>api-boundaries-v1.4.mp4</strong>
                  <p>18:42 · transcript linked · captions draft</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Media replacement opened")}
                >
                  Replace
                </button>
              </div>
              <div className="evidence-card">
                <span>
                  <Icon name="file" />
                </span>
                <div>
                  <strong>api-boundaries-transcript.vtt</strong>
                  <p>English original · 2 language drafts</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Transcript editor opened")}
                >
                  Edit
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="globe" title="Captions and languages" meta="Original retained">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>English</strong>
                  <p>Instructor-approved original</p>
                </div>
                <TonePill tone="green">Ready</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Luganda</strong>
                  <p>Machine draft awaiting language review</p>
                </div>
                <TonePill tone="orange">Review</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>French</strong>
                  <p>No translation requested for current cohort</p>
                </div>
                <TonePill>Optional</TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "interactions" && !academic ? (
        <div className="content-grid">
          <AcademyPanel icon="test" title="Knowledge checks" meta="3 items">
            <div className="question-sequence">
              {[
                "Who owns the API key?",
                "Which data belongs in the browser?",
                "Where must human approval remain visible?",
              ].map((question, index) => (
                <div className="sequence-item" key={question}>
                  <span className="sequence-number">{index + 1}</span>
                  <div>
                    <strong>{question}</strong>
                    <p>{index === 2 ? "Scenario response" : "Multiple choice"} · aligned to APB-M6</p>
                  </div>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => notify("Question editor opened", question)}
                  >
                    <Icon name="edit" size="sm" />
                  </button>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="primary-btn button-sm"
                onClick={() => notify("Knowledge check added")}
              >
                <Icon name="plus" size="sm" />
                Add check
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Interactive blocks" meta="2 blocks">
            <div className="evidence-stack">
              <div className="evidence-card">
                <span>
                  <Icon name="workflow" />
                </span>
                <div>
                  <strong>Boundary decision canvas</strong>
                  <p>Learners classify data, permissions and owners</p>
                </div>
                <TonePill tone="green">Ready</TonePill>
              </div>
              <div className="evidence-card">
                <span>
                  <Icon name="code" />
                </span>
                <div>
                  <strong>Server action comparison</strong>
                  <p>Editable example · isolated execution</p>
                </div>
                <TonePill tone="blue">Lab-linked</TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "accessibility" && !academic ? (
        <div className="content-grid">
          <AcademyPanel icon="shield" title="Accessibility checks" meta={`${readiness}% complete`}>
            <div className="check-list">
              {(
                [
                  ["transcript", "Complete transcript", "Every spoken idea is available in text."],
                  ["captions", "Captions reviewed", "Timing, speaker labels and terminology checked."],
                  [
                    "descriptions",
                    "Visual descriptions",
                    "Diagrams and code screenshots carry useful descriptions.",
                  ],
                  ["keyboard", "Keyboard interaction", "All interactive blocks work without a pointer."],
                  [
                    "contrast",
                    "Contrast and focus",
                    "Text, status and focus indicators meet Academy requirements.",
                  ],
                ] as Array<[keyof typeof checks, string, string]>
              ).map(item => (
                <button
                  type="button"
                  key={item[0]}
                  className={`check-row ${checks[item[0]] ? "done" : ""}`}
                  onClick={() => toggleCheck(item[0])}
                >
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>{item[2]}</span>
                  </span>
                </button>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Readiness" meta="Publish gate">
            <div className="donut-wrap">
              <div style={{ position: "relative" }}>
                <div
                  className="donut"
                  style={{
                    background: `conic-gradient(var(--green) 0 ${readiness}%,rgba(255,255,255,.06) ${readiness}% 100%)`,
                  }}
                />
                <div className="donut-center">
                  <strong>{readiness}%</strong>
                  <span>ready</span>
                </div>
              </div>
              <div className="legend-list">
                <div className="legend-row">
                  <i style={{ background: "var(--green)" }} />
                  <strong>Complete checks</strong>
                  <span>{Object.values(checks).filter(Boolean).length}</span>
                </div>
                <div className="legend-row">
                  <i style={{ background: "var(--orange)" }} />
                  <strong>Remaining</strong>
                  <span>{Object.values(checks).filter(value => !value).length}</span>
                </div>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "teaching-notes" && !academic ? (
        <AcademyPanel icon="book" title="Private teaching notes" meta="Not visible to learners">
          <div className="form-field">
            <label>Facilitation notes</label>
            <textarea
              value={notes}
              onChange={event => setNotes(event.target.value)}
              style={{ minHeight: 260 }}
            />
          </div>
          <div className="modal-form-note" style={{ marginTop: 10 }}>
            <Icon name="lock" size="sm" />
            Private notes are visible only to assigned teaching staff and remain outside learner exports.
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "versions" && academic ? (
        <AcademyPanel icon="activity" title="Version history" meta="Parent academic history">
          {history.loading && <p role="status">Loading academic history…</p>}
          {history.error && <p role="alert">{history.error}</p>}
          <div className="timeline">
            {history.data?.items.map(item => (
              <div className="timeline-item" key={item.id}>
                <span className="timeline-dot">
                  <Icon name="book" />
                </span>
                <span>
                  <strong>{item.action}</strong>
                  <p>
                    {item.reason} · {item.actorId} · {new Date(item.createdAt).toLocaleString()}
                  </p>
                </span>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : tab === "versions" ? (
        <AcademyPanel icon="activity" title="Version history" meta="Attributable">
          <div className="timeline">
            {[
              ["v1.4", "Accessibility blockers identified", "Amina Mutesi · Today"],
              ["v1.3", "Added Kijani server action example", "Amina Mutesi · 2 days ago"],
              ["v1.2", "Academic review approved", "Academic Operations · 1 week ago"],
              ["v1.0", "First published", "Amina Mutesi · 3 weeks ago"],
            ].map(item => (
              <div className="timeline-item" key={item[0]}>
                <span className="timeline-dot">
                  <Icon name="book" size="sm" />
                </span>
                <span>
                  <strong>
                    {item[0]} · {item[1]}
                  </strong>
                  <p>{item[2]}</p>
                </span>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Version preview opened", item[0])}
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
    </div>
  );
}

function AcademicLessonDetail({
  page,
  navigate,
  notify,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const ids = academicLessonIdentity(page),
    programs = useTeachingPrograms();
  const versions = useTeachingCurricula(ids?.programId || ""),
    content = useTeachingContent(ids?.programId || "", ids?.curriculumId || "");
  const program = programs.data?.find(item => item.id === ids?.programId),
    version = versions.data?.find(item => item.id === ids?.curriculumId);
  const module = content.data?.modules.find(item => item.lessons.some(lesson => lesson.id === ids?.lessonId));
  const lesson = module?.lessons.find(item => item.id === ids?.lessonId);
  if (programs.loading || versions.loading || content.loading)
    return (
      <div className="instructor-page">
        <p role="status">Loading the exact lesson…</p>
      </div>
    );
  if (!program || !version || !content.data || !module || !lesson)
    return (
      <div className="instructor-page">
        <button className="ghost-btn button-sm" onClick={() => navigate("teaching", "lessons")}>
          Back to lessons
        </button>
        <p role="alert">
          {programs.error ||
            versions.error ||
            content.error ||
            "This lesson is not available in your assigned academic scope."}
        </p>
      </div>
    );
  return (
    <LessonDetailView
      key={`${programs.context}:${lesson.id}:${content.data.version}`}
      lesson={projectTeachingLesson(program, content.data, module, lesson, !!version.publicationId)}
      navigate={navigate}
      notify={notify}
    />
  );
}

function AcademicCurriculumDraftForm({
  program,
  versions,
  preferredSourceId,
  navigate,
  notify,
  onCancel,
}: {
  program: AcademicProgramSummary;
  versions: CurriculumSummary[];
  preferredSourceId: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  onCancel: () => void;
}) {
  const identity = useIdentity();
  const sources = useMemo(
    () => versions.filter(item => ["approved", "changes_requested", "rejected"].includes(item.state)),
    [versions],
  );
  const initialSource = sources.find(item => item.id === preferredSourceId)?.id || sources[0]?.id || "";
  const [sourceId, setSourceId] = useState(initialSource);
  const [versionLabel, setVersionLabel] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const sender = useRef(createCommandSender({ headerOnly: true }));
  const submitting = useRef(false);
  const source = sources.find(item => item.id === sourceId);
  const canCreate = !!identity.session?.permissions.includes("curriculum.manage");
  const context = identity.session
    ? `${identity.session.tenantId}:${identity.session.user.id}:${identity.session.workspace.id}`
    : "";
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !canCreate || (versions.length > 0 && !source)) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const session = await checkIdentitySession();
      if (`${session.tenantId}:${session.user.id}:${session.workspace.id}` !== context)
        throw new Error("Your workspace changed. Reopen this Program in its current teaching scope.");
      let receipt: AcademicReceipt;
      if (source) {
        const input: CloneAcademicCurriculum = {
          expectedVersion: source.version,
          expectedParentVersion: program.version,
          versionLabel: versionLabel.trim(),
          reason: reason.trim(),
        };
        receipt = await sender.current<AcademicReceipt>(
          `academic-catalogue/program/${program.id}/curricula/${source.id}/commands/clone`,
          input,
        );
      } else {
        const input: CreateCurriculum = {
          expectedVersion: program.version,
          versionLabel: versionLabel.trim(),
          reason: reason.trim(),
          definition: { title: program.name, visibility: "private" },
        };
        receipt = await sender.current<AcademicReceipt>(
          `academic-catalogue/program/${program.id}/curricula/commands/create`,
          input,
        );
      }
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      setReason("");
      setVersionLabel("");
      onCancel();
      notify(
        "Curriculum draft created",
        source
          ? `${versionLabel.trim()} was created from the selected exact version.`
          : `${versionLabel.trim()} is the first curriculum version for this Program.`,
      );
      navigate("teaching", `program-${program.id}_${receipt.id}`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The curriculum draft could not be created.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return (
    <form
      id="academic-curriculum-draft"
      onSubmit={submit}
      aria-label="Create curriculum draft"
      style={{ marginBottom: 16 }}
    >
      {error && <p role="alert">{error}</p>}
      {!canCreate && <p role="alert">Current curriculum management permission is required.</p>}
      {!!versions.length && !sources.length && (
        <p role="alert">
          A decided exact curriculum version is required before another draft can be created.
        </p>
      )}
      <div className="form-row">
        <label className="field-label" htmlFor="academic-curriculum-draft-source">
          <span>Exact source version</span>
          <select
            id="academic-curriculum-draft-source"
            className="select-field"
            required={versions.length > 0}
            value={sourceId}
            onChange={event => setSourceId(event.target.value)}
            disabled={busy || !sources.length}
          >
            {!versions.length && <option value="">No prior version · create first draft</option>}
            {!!versions.length && !sources.length && <option value="">No decided version available</option>}
            {sources.map(item => (
              <option key={item.id} value={item.id}>
                {item.versionLabel} · {academicCurriculumStatus(item)} · version {item.version}
              </option>
            ))}
          </select>
        </label>
        <label className="field-label" htmlFor="academic-curriculum-draft-label">
          <span>New version label</span>
          <input
            id="academic-curriculum-draft-label"
            className="text-field"
            required
            minLength={1}
            maxLength={80}
            value={versionLabel}
            onChange={event => setVersionLabel(event.target.value)}
            disabled={busy}
            placeholder="Name the new draft version"
          />
        </label>
      </div>
      <label className="field-label" htmlFor="academic-curriculum-draft-reason" style={{ marginTop: 10 }}>
        <span>Reason for change</span>
        <textarea
          id="academic-curriculum-draft-reason"
          className="text-area"
          required
          minLength={10}
          maxLength={2000}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={busy}
        />
      </label>
      <div className="panel-footer" style={{ marginTop: 10 }}>
        <button type="button" className="secondary-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="primary-btn"
          disabled={busy || !canCreate || (versions.length > 0 && !source)}
        >
          {busy ? "Creating…" : "Create draft"}
        </button>
      </div>
    </form>
  );
}

function AcademicProgramDetail({
  page,
  navigate,
  notify,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [programId, requestedVersion] = page.slice("program-".length).split("_");
  const programs = useTeachingPrograms(),
    versions = useTeachingCurricula(programId),
    definition = useTeachingProgramVersions(programId);
  const identity = useIdentity();
  const [draftOpen, setDraftOpen] = useState(false);
  const program = programs.data?.find(item => item.id === programId);
  const current = definition.data?.items.find(
    item => item.id === definition.data?.currentVersionId,
  )?.curriculumVersionId;
  const drafts = versions.data?.filter(item => item.state === "draft") || [];
  const selectedId = requestedVersion || current || (drafts.length === 1 ? drafts[0].id : "");
  const selected = versions.data?.find(item => item.id === selectedId);
  const content = useTeachingContent(programId, selected?.id || "");
  const error = programs.error || versions.error || definition.error || content.error;
  return (
    <div className="instructor-page">
      <button
        type="button"
        className="ghost-btn button-sm"
        onClick={() => navigate("teaching", "curriculum")}
      >
        <Icon name="arrow-left" size="sm" />
        Back to curriculum
      </button>
      {(programs.loading || versions.loading || definition.loading || content.loading) && (
        <p role="status">Loading the exact academic version…</p>
      )}
      {error && <p role="alert">{error}</p>}
      <InstructorHero
        eyebrow={
          selected
            ? `${selected.versionLabel} · ${academicCurriculumStatus(selected)}`
            : "Academic curriculum"
        }
        title={program?.name || "Program unavailable"}
        text={content.data?.description || "Select an existing version from Curriculum version history."}
        actions={
          <>
            <button
              type="button"
              className="secondary-btn"
              disabled={!content.data}
              onClick={() =>
                document.getElementById("academic-curriculum-map")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Icon name="eye" size="sm" />
              Preview
            </button>
            <button
              type="button"
              className="primary-btn"
              aria-expanded={draftOpen}
              disabled={
                !program ||
                versions.loading ||
                !!versions.error ||
                !identity.session?.permissions.includes("curriculum.manage")
              }
              onClick={() => setDraftOpen(value => !value)}
            >
              <Icon name="edit" size="sm" />
              Create draft
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard
              icon="book"
              title="Modules"
              detail={`${content.data?.modules.reduce((sum, module) => sum + module.lessons.length, 0) ?? 0} lessons`}
              value={String(content.data?.modules.length ?? 0)}
            />
            <HeroSideCard icon="shield" title="Accessibility" detail="No assessment recorded" value="—" />
            <HeroSideCard
              icon="users"
              title="Active cohorts"
              detail="Delivery information is separate"
              value="—"
            />
          </>
        }
      />
      <div id="academic-curriculum-map">
        <AcademyPanel
          icon="workflow"
          title="Curriculum map"
          meta={selected?.versionLabel || "No version selected"}
        >
          {draftOpen && program && (
            <AcademicCurriculumDraftForm
              program={program}
              versions={versions.data || []}
              preferredSourceId={selectedId}
              navigate={navigate}
              notify={notify}
              onCancel={() => setDraftOpen(false)}
            />
          )}
          <div className="simple-card-grid">
            {content.data?.modules.map(module => (
              <div className="simple-card" key={module.id}>
                <div className="simple-card-head">
                  <span className="simple-card-icon">
                    <strong>{module.position}</strong>
                  </span>
                  <div className="simple-card-copy">
                    <h3>{module.title}</h3>
                    <p>
                      {module.lessons.length} lessons · {selected && academicCurriculumStatus(selected)}
                    </p>
                  </div>
                </div>
                <div className="simple-card-body">
                  <p>{module.description}</p>
                  {module.lessons.map(lesson => (
                    <button
                      type="button"
                      className="ghost-btn button-sm"
                      key={lesson.id}
                      onClick={() =>
                        navigate("teaching", academicLessonRoute(programId, selectedId, lesson.id))
                      }
                    >
                      {lesson.title}
                      <Icon name="arrow-right" size="sm" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {!content.loading && !content.data?.modules.length && (
            <p>No modules are recorded for this version.</p>
          )}
        </AcademyPanel>
      </div>
    </div>
  );
}

function TeachingView({
  page,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All statuses");
  const library = useTeachingLessons(page === "lessons"),
    identity = useIdentity();
  const assignedPrograms = useTeachingPrograms();
  const qualityRecords = useTeachingVersionRecords("quality", page === "quality");
  const versionRecords = useTeachingVersionRecords("evidence", page === "versions");

  if (page.startsWith("lesson-")) {
    return <AcademicLessonDetail page={page} navigate={navigate} notify={notify} />;
  }
  if (page.startsWith("program-"))
    return <AcademicProgramDetail page={page} navigate={navigate} notify={notify} />;
  if (page === "lessons") {
    const filtered = (library.data || []).filter(
      lesson =>
        `${lesson.title} ${lesson.module} ${lesson.program}`.toLowerCase().includes(query.toLowerCase()) &&
        (status === "All statuses" || lesson.status === status),
    );
    return (
      <div className="instructor-page">
        <PageTitle
          title="Lessons"
          text="Author, review, publish and version lesson content with accessibility and learning alignment visible."
          actions={
            <button
              type="button"
              className="primary-btn"
              disabled={library.loading || !identity.session?.permissions.includes("curriculum.manage")}
              onClick={() => openModal({ type: "create-lesson" })}
            >
              <Icon name="plus" size="sm" />
              Create lesson
            </button>
          }
        />
        {library.loading && <p role="status">Loading assigned lessons…</p>}
        {library.error && <p role="alert">{library.error}</p>}
        <AcademyPanel icon="book" title="Lesson library" meta={`${filtered.length} lessons`}>
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search lessons…"
              />
            </div>
            <select className="select-field" value={status} onChange={event => setStatus(event.target.value)}>
              <option>All statuses</option>
              <option>Published</option>
              <option>Draft</option>
              <option>Review</option>
              <option>Approved</option>
              <option>Changes requested</option>
              <option>Rejected</option>
            </select>
            <button
              type="button"
              className="secondary-btn"
              disabled
              title="Governed lesson exports are not connected yet."
              onClick={() => openModal({ type: "export", payload: "lessons" })}
            >
              <Icon name="download" size="sm" />
              Export
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lesson</th>
                  <th>Format</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Accessibility</th>
                  <th>Engagement</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lesson => (
                  <tr key={lesson.id} onClick={() => navigate("teaching", lesson.id)}>
                    <td>
                      <div className="person-cell">
                        <span className="simple-card-icon" style={{ width: 34, height: 34 }}>
                          <Icon name="book" size="sm" />
                        </span>
                        <span>
                          <strong>{lesson.title}</strong>
                          <span>
                            {lesson.module} · {lesson.program}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td>{lesson.format}</td>
                    <td>
                      <TonePill
                        tone={
                          lesson.status === "Published"
                            ? "green"
                            : lesson.status === "Review"
                              ? "orange"
                              : "purple"
                        }
                      >
                        {lesson.status}
                      </TonePill>
                    </td>
                    <td>{lesson.duration}</td>
                    <td>{lesson.accessibility === null ? "Not assessed" : `${lesson.accessibility}%`}</td>
                    <td>{lesson.engagement === null ? "Not available" : `${lesson.engagement}%`}</td>
                    <td>{lesson.version}</td>
                  </tr>
                ))}
                {!library.loading && !filtered.length && (
                  <tr>
                    <td colSpan={7}>No lessons match your current assignments and search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
  }
  if (page === "resources") {
    const resources = [
      ["slides", "Accessible Product Decisions · slides", "Presentation · 38 slides", "APB Module 6"],
      ["file", "Kijani acceptance criteria", "Markdown · v2.1", "Project simulation"],
      ["video", "Evaluation clinic recording", "68 minutes · captions", "APB Module 7"],
      ["code", "Server action examples", "Repository pack · 12 files", "APB Module 5"],
      ["image", "AI system boundary canvas", "Figma template", "All programs"],
      ["book", "Instructor facilitation guide", "PDF · 24 pages", "Teaching staff"],
    ];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Teaching resources"
          text="Reusable, versioned resources connected to lessons, Classes, Labs and project evidence."
          actions={
            <>
              <button type="button" className="secondary-btn" onClick={() => notify("Folder created")}>
                New folder
              </button>
              <button type="button" className="primary-btn" onClick={() => notify("Resource upload opened")}>
                <Icon name="upload" size="sm" />
                Upload resource
              </button>
            </>
          }
        />
        <div className="simple-card-grid">
          {resources.map(resource => (
            <button
              type="button"
              className="simple-card"
              key={resource[1]}
              onClick={() => notify("Resource opened", resource[1])}
            >
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name={resource[0]} />
                </span>
                <div className="simple-card-copy">
                  <h3>{resource[1]}</h3>
                  <p>{resource[2]}</p>
                </div>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Connected to</span>
                  <strong>{resource[3]}</strong>
                </div>
                <div className="meta-pair">
                  <span>Visibility</span>
                  <strong>Assigned instructors</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <span>Open resource</span>
                <Icon name="arrow-right" size="sm" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (page === "quality") {
    const records = qualityRecords.data || [],
      issues = records.filter(item => !item.quality?.structuralReadiness);
    return (
      <div className="instructor-page">
        <PageTitle
          title="Content quality"
          text="Recorded structural checks and independent academic review for each exact curriculum version."
          actions={
            <button
              type="button"
              className="secondary-btn"
              disabled={qualityRecords.loading}
              onClick={qualityRecords.refresh}
            >
              <Icon name="refresh" size="sm" />
              Run scan
            </button>
          }
        />
        {qualityRecords.loading && <p role="status">Checking assigned curriculum versions…</p>}
        {qualityRecords.error && <p role="alert">{qualityRecords.error}</p>}
        <div className="stat-grid">
          <StatTile
            icon="book"
            label="Published lessons"
            value={records
              .filter(item => item.curriculum.publicationId)
              .reduce((sum, item) => sum + (item.quality?.lessons || 0), 0)}
            detail="Across recorded published versions"
          />
          <StatTile
            icon="shield"
            label="Accessibility ready"
            value="—"
            detail="No accessibility assessment recorded"
            tone="cyan"
          />
          <StatTile
            icon="alert"
            label="Blocked items"
            value={issues.length}
            detail="Versions requiring structural corrections"
            tone="orange"
          />
          <StatTile
            icon="activity"
            label="Outcome alignment"
            value="—"
            detail="Independent review remains required"
            tone="blue"
          />
        </div>
        <AcademyPanel
          icon="alert"
          title="Quality actions"
          meta={`${issues.length} versions need corrections`}
        >
          <div className="priority-list">
            {issues.map(item => (
              <button
                type="button"
                className="priority-item"
                key={item.curriculum.id}
                onClick={() => navigate("teaching", `program-${item.program.id}_${item.curriculum.id}`)}
              >
                <span className="priority-icon orange">
                  <Icon name="shield" />
                </span>
                <span>
                  <strong>
                    {item.program.name} · {item.curriculum.versionLabel}
                  </strong>
                  <p>{item.quality?.issues.join(" · ")}</p>
                </span>
                <span className="priority-meta">
                  <TonePill tone="orange">Resolve</TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
            {!qualityRecords.loading && !qualityRecords.error && !issues.length && (
              <p>No structural blockers were returned for the available versions.</p>
            )}
          </div>
        </AcademyPanel>
      </div>
    );
  }
  if (page === "versions") {
    const records = versionRecords.data || [];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Curriculum version history"
          text="Review recorded revisions, independent decisions and immutable publication evidence."
          actions={
            <button
              type="button"
              className="secondary-btn"
              disabled={versionRecords.loading || !records.length || !!versionRecords.error}
              onClick={() =>
                downloadTextFile("academy-curriculum-history.json", JSON.stringify(records, null, 2))
              }
            >
              <Icon name="download" size="sm" />
              Export log
            </button>
          }
        />
        {versionRecords.loading && <p role="status">Loading recorded version history…</p>}
        {versionRecords.error && <p role="alert">{versionRecords.error}</p>}
        <AcademyPanel icon="activity" title="Version activity" meta="Assigned programs">
          <div className="timeline">
            {records.map(item => (
              <div className="timeline-item" key={item.curriculum.id}>
                <span className="timeline-dot">
                  <Icon name="book" size="sm" />
                </span>
                <span>
                  <button
                    type="button"
                    className="ghost-btn button-sm"
                    onClick={() => navigate("teaching", `program-${item.program.id}_${item.curriculum.id}`)}
                  >
                    {item.program.name} · {item.curriculum.versionLabel} ·{" "}
                    {academicCurriculumStatus(item.curriculum)}
                  </button>
                  <p>
                    Created by {item.evidence?.createdBy}
                    {item.evidence?.previousVersionId ? ` · Revises ${item.evidence.previousVersionId}` : ""}
                  </p>
                  {item.evidence?.decisions.map(decision => (
                    <p key={decision.id}>
                      {decision.decision} · {decision.reason} · {decision.actorId} ·{" "}
                      {new Date(decision.decidedAt).toLocaleString()}
                    </p>
                  ))}
                  {item.evidence?.publication && (
                    <p>
                      Published {new Date(item.evidence.publication.publishedAt).toLocaleString()} ·{" "}
                      {item.evidence.publication.contentSha256}
                    </p>
                  )}
                </span>
                <time>{item.evidence && new Date(item.evidence.createdAt).toLocaleString()}</time>
              </div>
            ))}
            {!versionRecords.loading && !versionRecords.error && !records.length && (
              <p>No academic versions are available in your assigned scope.</p>
            )}
          </div>
        </AcademyPanel>
      </div>
    );
  }
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Curriculum & Teaching"
        title="Design learning that becomes a working system and credible proof."
        text="Connect outcomes, lessons, Class sessions, AI Labs, assignments, projects and evidence without losing accessibility or human academic authority."
        actions={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => openModal({ type: "create-lesson" })}
            >
              <Icon name="plus" size="sm" />
              Create lesson
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("teaching", "quality")}>
              <Icon name="shield" size="sm" />
              Content quality
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard
              icon="book"
              title="Assigned programs"
              detail="Current authorized catalogue"
              value={String(assignedPrograms.data?.length || 0)}
            />
            <HeroSideCard icon="edit" title="Draft lessons" detail="Open the lesson library" value="—" />
            <HeroSideCard icon="shield" title="Accessibility" detail="No assessment recorded" value="—" />
          </>
        }
      />
      <div className="simple-card-grid">
        {(assignedPrograms.data || []).map(program => (
          <button
            type="button"
            className="simple-card"
            key={program.id}
            onClick={() => navigate("teaching", `program-${program.id}`)}
          >
            <div className="simple-card-head">
              <span className="simple-card-icon">
                <Icon name="book" />
              </span>
              <div className="simple-card-copy">
                <h3>{program.name}</h3>
                <p>
                  {program.code} · {program.ownerName}
                </p>
              </div>
              <TonePill tone={program.currentVersionId ? "green" : "orange"}>{program.status}</TonePill>
            </div>
            <div className="simple-card-body">
              <div className="meta-pair">
                <span>Accessibility</span>
                <strong>Not assessed</strong>
              </div>
            </div>
            <div className="simple-card-foot">
              <span>{program.visibility}</span>
              <Icon name="arrow-right" size="sm" />
            </div>
          </button>
        ))}
        {assignedPrograms.loading && <p role="status">Loading assigned Programs…</p>}
        {assignedPrograms.error && <p role="alert">{assignedPrograms.error}</p>}
        {!assignedPrograms.loading && !assignedPrograms.error && !assignedPrograms.data?.length && (
          <p>No academic Programs are assigned to this workspace.</p>
        )}
      </div>
      <div className="content-grid" style={{ marginTop: 12 }}>
        <AcademyPanel icon="workflow" title="Delivery chain" meta="Connected">
          <div className="integration-flow">
            <div className="integration-step">
              <span>1 · Outcome</span>
              <strong>Curriculum</strong>
              <p>Named capability and evidence</p>
            </div>
            <div className="integration-step">
              <span>2 · Practice</span>
              <strong>Class + Lab</strong>
              <p>Live guidance and safe experiment</p>
            </div>
            <div className="integration-step">
              <span>3 · Build</span>
              <strong>Build Room</strong>
              <p>Visible team and AI work</p>
            </div>
            <div className="integration-step">
              <span>4 · Proof</span>
              <strong>Assessment</strong>
              <p>Human-reviewed evidence</p>
            </div>
          </div>
        </AcademyPanel>
        <AcademyPanel icon="alert" title="Next content decisions" meta="Current academic records">
          <div className="priority-list">
            <button type="button" className="priority-item" onClick={() => navigate("teaching", "quality")}>
              <span className="priority-icon orange">
                <Icon name="shield" />
              </span>
              <span>
                <strong>Review curriculum readiness</strong>
                <p>Check the exact versions available in your scope.</p>
              </span>
              <Icon name="arrow-right" size="sm" />
            </button>
            <button type="button" className="priority-item" onClick={() => navigate("teaching", "versions")}>
              <span className="priority-icon cyan">
                <Icon name="activity" />
              </span>
              <span>
                <strong>Review version history</strong>
                <p>Read recorded academic decisions and publications.</p>
              </span>
              <Icon name="arrow-right" size="sm" />
            </button>
          </div>
        </AcademyPanel>
      </div>
    </div>
  );
}

function SessionDetailView({
  session,
  language,
  navigate,
  notify,
  openModal,
}: {
  session: ClassSession;
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [checked, setChecked] = useState<Record<number, boolean>>({ 0: true });
  const [tab, setTab] = useState("command");
  const [handoffBusy, setHandoffBusy] = useState(false);
  const host = async (mode: "host" | "rehearsal" = "host") => {
    setHandoffBusy(true);
    try {
      const handoff = await requestClassHandoff("learning_session", session.id, mode);
      window.location.assign(handoff.destinationUrl);
    } catch (cause) {
      notify("Class handoff unavailable", cause instanceof Error ? cause.message : "Refresh and try again.");
    } finally {
      setHandoffBusy(false);
    }
  };
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("class", "schedule")}>
        <Icon name="arrow-left" size="sm" />
        Back to schedule
      </button>
      <section className="host-card" style={{ marginTop: 12 }}>
        <div className="class-connection-top">
          <div>
            <div className="hero-eyebrow">
              {session.status} · {session.mode}
            </div>
            <h2>{session.title}</h2>
            <p>
              {session.dateLabel} · {session.timeLabel} · {session.duration} · {session.cohort}
            </p>
            <span className="external-domain">
              <Icon name="external" size="sm" />
              Hosted securely on class.chatoner.com
            </span>
          </div>
          <TonePill
            tone={session.status === "live" ? "green" : session.status === "completed" ? "default" : "blue"}
          >
            {session.status}
          </TonePill>
        </div>
        <div className="class-code-panel">
          <div className="class-code-value">{session.code}</div>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => {
              copyToClipboard(session.code);
              notify("Class code copied", session.code);
            }}
          >
            <Icon name="copy" size="sm" />
            Copy code
          </button>
        </div>
        <div className="host-actions">
          <button
            type="button"
            className="primary-btn"
            disabled={handoffBusy}
            onClick={() => void host("host")}
          >
            <Icon name="video" size="sm" />
            {session.status === "live" ? "Enter live studio" : "Open host studio"}
            <Icon name="external" size="sm" />
          </button>
          <button
            type="button"
            className="secondary-btn"
            disabled={handoffBusy}
            onClick={() => void host("rehearsal")}
          >
            <Icon name="play" size="sm" />
            Rehearse
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => openModal({ type: "class-invite", payload: session.id })}
          >
            <Icon name="share" size="sm" />
            Learner invitation
          </button>
          <button type="button" className="ghost-btn" onClick={() => downloadClassCalendar(session)}>
            <Icon name="calendar" size="sm" />
            Calendar file
          </button>
        </div>
        <div className="class-trust-line">
          <Icon name="shield" size="sm" />
          <p>
            Academy verifies the current teaching assignment and issues a short-lived, one-time Class
            destination for this exact learning session.
          </p>
        </div>
      </section>
      <nav className="detail-tabs" aria-label="Class session sections">
        {["command", "agenda", "participants", "questions", "attendance", "follow-up"].map(item => (
          <button
            type="button"
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item[0].toUpperCase() + item.slice(1)}
          </button>
        ))}
      </nav>
      {tab === "command" ? (
        <div className="class-command-grid">
          <AcademyPanel
            icon="check-circle"
            title="Readiness checklist"
            meta={`${Object.values(checked).filter(Boolean).length}/${session.preparation.length} complete`}
          >
            <div className="check-list">
              {session.preparation.map((item, index) => (
                <button
                  type="button"
                  className={`check-row ${checked[index] ? "done" : ""}`}
                  key={item}
                  onClick={() => setChecked(current => ({ ...current, [index]: !current[index] }))}
                >
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>
                      {index === 0
                        ? "Connected lesson and resources are available."
                        : index === 1
                          ? "Class question queue and examples are ready."
                          : "Camera, microphone, captions and interpretation can be checked in rehearsal."}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="link" title="Connected context" meta="Automatic">
            <div className="identity-carry">
              <div className="identity-row">
                <span>
                  <Icon name="users" size="sm" />
                </span>
                <div>
                  <strong>Instructor identity</strong>
                  <small>Amina Mutesi · Lead Instructor</small>
                </div>
                <em>Verified</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="book" size="sm" />
                </span>
                <div>
                  <strong>Program and cohort</strong>
                  <small>
                    {session.program} · {session.cohort}
                  </small>
                </div>
                <em>Connected</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="globe" size="sm" />
                </span>
                <div>
                  <strong>Language context</strong>
                  <small>
                    {language.primary}
                    {language.secondary !== "None" ? ` + ${language.secondary}` : ""} · original retained
                  </small>
                </div>
                <em>Applied</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="shield" size="sm" />
                </span>
                <div>
                  <strong>Class authority</strong>
                  <small>Host controls, recording and attendance sync</small>
                </div>
                <em>Instructor</em>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "agenda" ? (
        <div className="content-grid">
          <AcademyPanel icon="clipboard" title="Session agenda" meta={`${session.agenda.length} items`}>
            <div className="plan-list">
              {session.agenda.map((item, index) => (
                <div className="plan-step" key={item}>
                  <span className="step-number">{index + 1}</span>
                  <div>
                    <strong>{item}</strong>
                    <p>
                      {[
                        "10 minutes · connect to prior learning",
                        "15 minutes · instructor demonstration",
                        "25 minutes · learner practice and discussion",
                        "15 minutes · reflection, next action and evidence",
                      ][index] || "10 minutes"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => notify("Agenda item options opened", item)}
                  >
                    <Icon name="more" size="sm" />
                  </button>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="primary-btn button-sm"
                onClick={() => notify("Agenda item added")}
              >
                <Icon name="plus" size="sm" />
                Add item
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="book" title="Learning objectives" meta="Aligned">
            <div className="check-list">
              {[
                "Explain accessibility as a product and risk decision",
                "Evaluate an AI-assisted interface against named acceptance criteria",
                "Record a human decision and residual risk as evidence",
              ].map(item => (
                <div className="check-row done" key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>Connected to APB-M6 and Kijani assignment evidence</span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "participants" ? (
        <AcademyPanel icon="users" title="Expected participants" meta={`${session.learnerCount} learners`}>
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input placeholder="Search expected learners…" />
            </div>
            <button type="button" className="secondary-btn" onClick={() => openModal({ type: "members" })}>
              <Icon name="users" size="sm" />
              Open full directory
            </button>
          </div>
          <div className="roster-grid">
            {LEARNERS.slice(0, 12).map(learner => (
              <button
                type="button"
                className="roster-person"
                key={learner.id}
                onClick={() => navigate("cohorts", `learner-${learner.id}`)}
                style={{ color: "inherit", cursor: "pointer", textAlign: "left" }}
              >
                <Avatar initials={learner.initials} small />
                <span>
                  <strong>{learner.name}</strong>
                  <span>{learner.languages}</span>
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "questions" ? (
        <div className="content-grid">
          <AcademyPanel icon="message" title="Learner questions" meta={`${session.questions} waiting`}>
            {[
              "How should we prove that a screen-reader announcement is useful rather than merely present?",
              "Can an AI agent approve an accessibility exception?",
              "What evidence should remain after a third-party widget changes?",
            ].map((question, index) => (
              <div className="question-card" key={question}>
                <header>
                  <span>
                    <Icon name="message" size="sm" />
                  </span>
                  <div>
                    <h4>{question}</h4>
                    <p>
                      {["Maya Chen", "Ronald Isabirye", "Nakato Kezia"][index]} · {index + 2} votes
                    </p>
                  </div>
                  <TonePill tone={index === 0 ? "orange" : "purple"}>
                    {index === 0 ? "Pinned" : "Queued"}
                  </TonePill>
                </header>
                <footer>
                  <span className="tag-chip">APB-M6</span>
                  <div className="row-actions">
                    <button
                      type="button"
                      className="secondary-btn button-sm"
                      onClick={() => notify("Question pinned", question)}
                    >
                      Pin
                    </button>
                    <button
                      type="button"
                      className="primary-btn button-sm"
                      onClick={() => notify("Question marked answered", question)}
                    >
                      Answer
                    </button>
                  </div>
                </footer>
              </div>
            ))}
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Polls" meta="2 prepared">
            <div className="question-card">
              <header>
                <span>
                  <Icon name="chart" size="sm" />
                </span>
                <div>
                  <h4>Where should coupon feedback be announced?</h4>
                  <p>Single choice · 4 options · hidden until launched</p>
                </div>
              </header>
              <footer>
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => notify("Poll launched", "Learners will see it inside Chatoner Class.")}
                >
                  <Icon name="play" size="sm" />
                  Launch
                </button>
              </footer>
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => navigate("class", "questions")}
              >
                <Icon name="plus" size="sm" />
                Create poll
              </button>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "attendance" ? (
        <AcademyPanel icon="users" title="Session attendance" meta="Synced after Class">
          <div className="stat-grid" style={{ marginTop: 0 }}>
            <StatTile
              icon="check"
              label="Expected"
              value={session.learnerCount}
              detail="Enrolled in this cohort"
            />
            <StatTile
              icon="clock"
              label="Corrections"
              value="2"
              detail="Require instructor decision"
              tone="orange"
            />
            <StatTile
              icon="activity"
              label="Participation"
              value="87%"
              detail="Questions, polls and activities"
              tone="cyan"
            />
            <StatTile
              icon="shield"
              label="Sync status"
              value="Ready"
              detail="Signed Class session record"
              tone="blue"
            />
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="primary-btn"
              onClick={() => openModal({ type: "sync-attendance", payload: session.id })}
            >
              <Icon name="refresh" size="sm" />
              Sync from Chatoner Class
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("class", "attendance")}>
              <Icon name="users" size="sm" />
              Open register
            </button>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "follow-up" ? (
        <div className="content-grid">
          <AcademyPanel icon="play" title="Recording and resources" meta={session.recording}>
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Recording</strong>
                  <p>
                    {session.recording === "ready"
                      ? "Published with captions and transcript"
                      : "Scheduled to process after the session"}
                  </p>
                </div>
                <TonePill tone={session.recording === "ready" ? "green" : "blue"}>
                  {session.recording}
                </TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Class summary</strong>
                  <p>AI may draft; instructor must review before learners receive it</p>
                </div>
                <TonePill>Draft</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Connected assignment</strong>
                  <p>Kijani checkout accessibility sprint</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => navigate("assessment", "assignment-checkout-accessibility")}
                >
                  Open
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="send" title="Follow-up message" meta="Cohort">
            <div className="form-field">
              <label>Message</label>
              <textarea defaultValue="Thank you for today’s class. Before Friday, attach one piece of evidence that shows how a human decision remained visible in your product." />
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="primary-btn button-sm"
                onClick={() =>
                  notify("Follow-up sent", "The message was delivered in the Academy and Class context.")
                }
              >
                <Icon name="send" size="sm" />
                Send follow-up
              </button>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
    </div>
  );
}

function CreateClassPage({
  language,
  navigate,
  notify,
  createSession,
}: {
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  createSession: (session: ClassSession) => void;
}) {
  const [title, setTitle] = useState("New AI Product Builder class");
  const [sessionType, setSessionType] = useState("Live lesson");
  const [cohortId, setCohortId] = useState(
    COHORTS.find(item => item.status === "active")?.id || COHORTS[0].id,
  );
  const [date, setDate] = useState("2026-11-12");
  const [time, setTime] = useState("15:00");
  const [duration, setDuration] = useState("60 minutes");
  const [recordingPreference, setRecordingPreference] = useState("Ask before session");
  const [sessionLanguage, setSessionLanguage] = useState(language.primary);
  const [objective, setObjective] = useState(
    "Learners can explain a decision, test it with evidence and identify the remaining risk.",
  );
  const selected = COHORTS.find(item => item.id === cohortId) || COHORTS[0];

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!title.trim() || !date || !time) {
      notify(
        "Complete the required Class details",
        "A title, date and start time are required before a Class code can be generated.",
      );
      return;
    }
    const start = new Date(`${date}T${time}:00`);
    createSession({
      id: `custom-${Date.now()}`,
      title: title.trim(),
      code: createClassCode(),
      dateLabel: start.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      timeLabel: `${time} EAT`,
      localTime: `${time} EAT`,
      duration,
      instructor: "Amina Mutesi",
      program: selected.program,
      cohort: selected.title,
      mode: sessionType,
      status: "upcoming",
      preparation: [
        "Connected lesson or objective reviewed",
        "Question queue prepared",
        "Device, caption and interpretation rehearsal completed",
      ],
      agenda: [
        objective.trim() || "Connect to prior learning",
        "Instructor demonstration",
        "Learner practice and discussion",
        "Reflection, next action and evidence",
      ],
      learnerCount: selected.learners,
      questions: 0,
      recording: recordingPreference === "Do not record" ? "off" : "scheduled",
      language: sessionLanguage,
    });
  };

  return (
    <div className="instructor-page">
      <PageTitle
        title="Create Chatoner Class session"
        text="Schedule the Academy context here, generate a unique Class code and prepare separate instructor and learner handoffs."
      />
      <AcademyPanel icon="video" title="Class details" meta="Every session receives a unique link and code">
        <form className="form-stack" onSubmit={submit}>
          <div className="form-row">
            <label className="field-label">
              <span>Session title</span>
              <input
                className="text-field"
                value={title}
                onChange={event => setTitle(event.target.value)}
                required
              />
            </label>
            <label className="field-label">
              <span>Session type</span>
              <select value={sessionType} onChange={event => setSessionType(event.target.value)}>
                <option>Live lesson</option>
                <option>Clinic</option>
                <option>Project review</option>
                <option>Office hour</option>
                <option>Oral defense</option>
              </select>
            </label>
          </div>
          <div className="form-row">
            <label className="field-label">
              <span>Programme and cohort</span>
              <select value={cohortId} onChange={event => setCohortId(event.target.value)}>
                {COHORTS.filter(item => item.status !== "completed").map(item => (
                  <option key={item.id} value={item.id}>
                    {item.title} · {item.program}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label">
              <span>Language and captions</span>
              <select value={sessionLanguage} onChange={event => setSessionLanguage(event.target.value)}>
                {[language.primary, language.secondary, ...LANGUAGE_NAMES]
                  .filter((item, index, list) => item !== "None" && list.indexOf(item) === index)
                  .map(item => (
                    <option key={item}>{item}</option>
                  ))}
              </select>
            </label>
          </div>
          <div className="form-row three">
            <label className="field-label">
              <span>Date</span>
              <input
                className="text-field"
                type="date"
                value={date}
                onChange={event => setDate(event.target.value)}
                required
              />
            </label>
            <label className="field-label">
              <span>Start</span>
              <input
                className="text-field"
                type="time"
                value={time}
                onChange={event => setTime(event.target.value)}
                required
              />
            </label>
            <label className="field-label">
              <span>Duration</span>
              <select value={duration} onChange={event => setDuration(event.target.value)}>
                <option>45 minutes</option>
                <option>60 minutes</option>
                <option>75 minutes</option>
                <option>90 minutes</option>
              </select>
            </label>
          </div>
          <div className="form-row">
            <label className="field-label">
              <span>Recording</span>
              <select
                value={recordingPreference}
                onChange={event => setRecordingPreference(event.target.value)}
              >
                <option>Ask before session</option>
                <option>Record with consent</option>
                <option>Do not record</option>
              </select>
            </label>
            <label className="field-label">
              <span>Connected learning objective</span>
              <textarea
                className="text-area"
                value={objective}
                onChange={event => setObjective(event.target.value)}
              />
            </label>
          </div>
          <div className="modal-form-note">
            <Icon name="link" size="sm" />
            After creation, Academy stores the Class code and separate host and learner invitations. A signed
            production handoff will automatically fill the correct code for logged-in learners.
          </div>
          <div className="hero-actions">
            <button type="submit" className="primary-btn">
              <Icon name="calendar" size="sm" />
              Create session
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("class", "schedule")}>
              Cancel
            </button>
          </div>
        </form>
      </AcademyPanel>
    </div>
  );
}

function ClassView({
  page,
  sessions,
  sessionsLoading,
  sessionsError,
  language,
  navigate,
  notify,
  openModal,
  createSession,
}: {
  page: string;
  sessions: ClassSession[];
  sessionsLoading: boolean;
  sessionsError: string;
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
  createSession: (session: ClassSession) => void;
}) {
  const [statuses] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      LEARNERS.slice(0, 20).map((learner, index) => [
        learner.id,
        index % 11 === 0 ? "Absent" : index % 7 === 0 ? "Late" : "Present",
      ]),
    ),
  );
  const [sessionId, setSessionId] = useState("");
  useEffect(() => {
    if (sessions.length && !sessions.some(session => session.id === sessionId)) setSessionId(sessions[0].id);
  }, [sessionId, sessions]);

  if (sessionsLoading)
    return <EmptyState icon="video" title="Loading Class sessions" text="Checking current teaching scope." />;
  if (sessionsError)
    return <EmptyState icon="video" title="Class sessions unavailable" text={sessionsError} />;
  if (!sessions.length)
    return (
      <EmptyState
        icon="video"
        title="No Class sessions"
        text="Create an Academy session for an assigned Cohort to begin."
      />
    );

  if (page.startsWith("session-")) {
    const id = page.replace("session-", "");
    const session = sessions.find(item => item.id === id) || sessions[0];
    return (
      <SessionDetailView
        session={session}
        language={language}
        navigate={navigate}
        notify={notify}
        openModal={openModal}
      />
    );
  }
  if (page === "create")
    return (
      <CreateClassPage
        language={language}
        navigate={navigate}
        notify={notify}
        createSession={createSession}
      />
    );
  if (page === "recordings")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Class recordings"
          text="Publish only after captions, transcript, language and learner-consent checks are complete."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => notify("Recording library refreshed")}
            >
              <Icon name="refresh" size="sm" />
              Refresh
            </button>
          }
        />
        <div className="simple-card-grid">
          {sessions
            .filter(session => session.status === "completed" || session.recording !== "off")
            .map(session => (
              <div className="simple-card" key={session.id}>
                <div className="simple-card-head">
                  <span className="simple-card-icon">
                    <Icon name="play" />
                  </span>
                  <div className="simple-card-copy">
                    <h3>{session.title}</h3>
                    <p>
                      {session.dateLabel} · {session.duration}
                    </p>
                  </div>
                  <TonePill
                    tone={
                      session.recording === "ready"
                        ? "green"
                        : session.recording === "processing"
                          ? "orange"
                          : "blue"
                    }
                  >
                    {session.recording}
                  </TonePill>
                </div>
                <div className="simple-card-body">
                  <div className="meta-pair">
                    <span>Captions</span>
                    <strong>{session.recording === "ready" ? "Reviewed" : "Scheduled"}</strong>
                  </div>
                  <div className="meta-pair">
                    <span>Transcript</span>
                    <strong>{session.recording === "ready" ? "Published" : "Pending"}</strong>
                  </div>
                  <div className="meta-pair">
                    <span>Views</span>
                    <strong>{session.status === "completed" ? "28" : "—"}</strong>
                  </div>
                </div>
                <div className="simple-card-foot">
                  <div className="row-actions">
                    <button
                      type="button"
                      className="secondary-btn button-sm"
                      onClick={() => notify("Recording preview opened", session.title)}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="primary-btn button-sm"
                      onClick={() => notify("Recording published", session.title)}
                      disabled={session.recording !== "ready"}
                    >
                      Publish
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  if (page === "attendance") {
    const current = sessions.find(item => item.id === sessionId) || sessions[0];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Attendance"
          text="Reconcile Academy workshops and clinics. Class attendance requires the D17 verified connection."
          actions={
            <>
              <select
                className="select-field"
                value={sessionId}
                onChange={event => setSessionId(event.target.value)}
              >
                {sessions.map(session => (
                  <option key={session.id} value={session.id}>
                    {session.title}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="primary-btn"
                disabled
                title="Verified D17 Class attendance is not connected."
              >
                <Icon name="refresh" size="sm" />
                Sync from Class
              </button>
            </>
          }
        />
        <AcademyAttendancePanel notify={notify} />
        <AcademyPanel icon="activity" title="Class attendance preview" meta="D17 connection unavailable">
          <p>Class attendance shown below is preview data. It is not an Academy attendance record.</p>
        </AcademyPanel>
        <div className="stat-grid">
          <StatTile
            icon="check"
            label="Present"
            value={Object.values(statuses).filter(value => value === "Present").length}
            detail="Synced and manually confirmed"
          />
          <StatTile
            icon="clock"
            label="Late"
            value={Object.values(statuses).filter(value => value === "Late").length}
            detail="Threshold: 10 minutes"
            tone="orange"
          />
          <StatTile
            icon="x"
            label="Absent"
            value={Object.values(statuses).filter(value => value === "Absent").length}
            detail="Support signal after 2 events"
            tone="orange"
          />
          <StatTile
            icon="activity"
            label="Participation"
            value="87%"
            detail="Questions, polls and activities"
            tone="cyan"
          />
        </div>
        <AcademyPanel icon="users" title={current.title} meta={`${current.dateLabel} · ${current.code}`}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Participation</th>
                  <th>Correction</th>
                </tr>
              </thead>
              <tbody>
                {LEARNERS.slice(0, 20).map((learner, index) => (
                  <tr key={learner.id}>
                    <td>
                      <div className="person-cell">
                        <Avatar initials={learner.initials} small />
                        <span>
                          <strong>{learner.name}</strong>
                          <span>{learner.cohort}</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="attendance-status">
                        {["Present", "Late", "Absent"].map(status => (
                          <button
                            type="button"
                            key={status}
                            className={statuses[learner.id] === status ? "active" : ""}
                            disabled
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td>
                      {statuses[learner.id] === "Absent"
                        ? "—"
                        : `14:${String(49 + (index % 9)).padStart(2, "0")}`}
                    </td>
                    <td>{statuses[learner.id] === "Absent" ? "—" : `${62 + ((index * 7) % 37)}%`}</td>
                    <td>
                      {index === 4 || index === 11 ? (
                        <button type="button" className="secondary-btn button-sm" disabled>
                          Review
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel-footer">
            <span>Class preview changes are unavailable until D17 supplies verified evidence.</span>
            <button type="button" className="primary-btn button-sm" disabled>
              <Icon name="save" size="sm" />
              Save register
            </button>
          </div>
        </AcademyPanel>
      </div>
    );
  }
  if (page === "questions")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Questions & polls"
          text="Prepare prompts before class, moderate learner questions and preserve useful discussion as evidence."
          actions={
            <button type="button" className="primary-btn" onClick={() => notify("New poll editor opened")}>
              <Icon name="plus" size="sm" />
              Create poll
            </button>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="message" title="Question queue" meta="9 for today">
            {[
              "How should we prove that a screen-reader announcement is useful?",
              "Can an AI agent approve an exception?",
              "What does sufficient staging evidence look like?",
              "How do we retain original-language evidence?",
            ].map((question, index) => (
              <div className="question-card" key={question}>
                <header>
                  <span>
                    <Icon name="message" size="sm" />
                  </span>
                  <div>
                    <h4>{question}</h4>
                    <p>
                      {["Maya Chen", "Ronald Isabirye", "Nakato Kezia", "Sofia Martinez"][index]} ·{" "}
                      {index + 1} votes
                    </p>
                  </div>
                  <TonePill tone={index === 0 ? "orange" : "purple"}>
                    {index === 0 ? "Pinned" : "Queued"}
                  </TonePill>
                </header>
                <footer>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => notify("Question moved", question)}
                  >
                    Move
                  </button>
                  <button
                    type="button"
                    className="primary-btn button-sm"
                    onClick={() => notify("Question marked answered", question)}
                  >
                    Answer
                  </button>
                </footer>
              </div>
            ))}
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Prepared polls" meta="2 items">
            <div className="question-card">
              <header>
                <span>
                  <Icon name="chart" size="sm" />
                </span>
                <div>
                  <h4>Who owns the final merge decision?</h4>
                  <p>Single choice · 4 answers · APB-M6</p>
                </div>
              </header>
              <footer>
                <TonePill tone="green">Ready</TonePill>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Poll preview opened")}
                >
                  Preview
                </button>
              </footer>
            </div>
            <div className="question-card">
              <header>
                <span>
                  <Icon name="chart" size="sm" />
                </span>
                <div>
                  <h4>Which evidence changes your accessibility decision?</h4>
                  <p>Multiple choice · 5 answers · APB-M6</p>
                </div>
              </header>
              <footer>
                <TonePill>Draft</TonePill>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Poll editor opened")}
                >
                  Edit
                </button>
              </footer>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "languages")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Languages & captions"
          text="Configure multilingual teaching while preserving the instructor’s original meaning and learner evidence."
          actions={
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "language" })}>
              <Icon name="globe" size="sm" />
              Change language context
            </button>
          }
        />
        <div className="language-grid">
          <AcademyPanel icon="globe" title="Instructor language" meta="Current default">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Primary</strong>
                  <p>Writing, Class controls and lesson delivery</p>
                </div>
                <TonePill tone="purple">{language.primary}</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Secondary</strong>
                  <p>Optional translation and interpretation</p>
                </div>
                <TonePill tone="blue">{language.secondary}</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Original retained</strong>
                  <p>Source messages, captions and evidence remain available</p>
                </div>
                <TonePill tone="green">{language.retainOriginal ? "On" : "Off"}</TonePill>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="video" title="Class accessibility" meta="Defaults">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Live captions</strong>
                  <p>Enabled before learners enter</p>
                </div>
                <Switch
                  value={true}
                  label="Live captions"
                  onChange={() => notify("Caption default changed")}
                />
              </div>
              <div className="setting-row">
                <div>
                  <strong>Interpreter channel</strong>
                  <p>Invite approved interpreter when required</p>
                </div>
                <Switch
                  value={false}
                  label="Interpreter channel"
                  onChange={() => notify("Interpreter setting changed")}
                />
              </div>
              <div className="setting-row">
                <div>
                  <strong>Translated transcript</strong>
                  <p>Publish only after human language review</p>
                </div>
                <Switch
                  value={true}
                  label="Translated transcript"
                  onChange={() => notify("Transcript setting changed")}
                />
              </div>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "preferences")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Studio preferences"
          text="Set safe defaults for host devices, recording, waiting room and learner participation."
        />
        <div className="content-grid">
          <AcademyPanel icon="video" title="Devices" meta="Chatoner Class">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Camera</strong>
                  <p>Integrated camera · 1080p</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Camera test started")}
                >
                  Test
                </button>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Microphone</strong>
                  <p>Default communications device</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Microphone test started")}
                >
                  Test
                </button>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Speaker</strong>
                  <p>System output</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Speaker test started")}
                >
                  Test
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Session controls" meta="Safe defaults">
            <div className="action-list">
              {[
                ["Waiting room", "Review identity before entry", true],
                ["Recording consent", "Ask learners before recording", true],
                ["Learner screen share", "Allow after instructor approval", true],
                ["Open microphone on entry", "Keep muted by default", false],
              ].map(item => (
                <div className="setting-row" key={String(item[0])}>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                  <Switch
                    value={Boolean(item[2])}
                    label={String(item[0])}
                    onChange={() => notify(`${item[0]} setting changed`)}
                  />
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  const visible = page === "live" ? sessions.filter(s => s.status === "live") : sessions;
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Academy → Chatoner Class"
        title="Prepare here. Host the actual live class on class.chatoner.com."
        text="Every session receives its own link and code. Signed-in learners entering from Academy receive the correct session code and learning context automatically."
        actions={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("class", sessions[0] ? `session-${sessions[0].id}` : "schedule")}
            >
              <Icon name="video" size="sm" />
              Host live class
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "create-class" })}
            >
              <Icon name="plus" size="sm" />
              Create class
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard icon="video" title="Live now" detail="Accessible Product Decisions" value="1" />
            <HeroSideCard icon="message" title="Questions waiting" detail="Across next session" value="9" />
            <HeroSideCard icon="users" title="Expected learners" detail="Today’s class" value="34" />
          </>
        }
      />
      <div className="session-grid">
        {visible.map(session => (
          <button
            type="button"
            className="session-card"
            key={session.id}
            onClick={() => navigate("class", `session-${session.id}`)}
          >
            <div className="session-card-top">
              <span className="simple-card-icon">
                <Icon name={session.status === "completed" ? "play" : "video"} />
              </span>
              <div className="simple-card-copy">
                <h3>{session.title}</h3>
                <p>
                  {session.dateLabel} · {session.timeLabel} · {session.duration}
                </p>
                <p>{session.cohort}</p>
              </div>
              <TonePill
                tone={
                  session.status === "live" ? "green" : session.status === "completed" ? "default" : "blue"
                }
              >
                {session.status}
              </TonePill>
            </div>
            <div className="session-card-metrics">
              <div>
                <span>Class code</span>
                <strong>{session.code}</strong>
              </div>
              <div>
                <span>Learners</span>
                <strong>{session.learnerCount}</strong>
              </div>
              <div>
                <span>Questions</span>
                <strong>{session.questions}</strong>
              </div>
            </div>
            <div className="simple-card-foot">
              <span>Open session command</span>
              <Icon name="arrow-right" size="sm" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function InstructorBuildChannelView({
  page,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string, tone?: InstructorNotice["tone"]) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [announcement, setAnnouncement] = useState("Week 6: move from interface claims to evidence");
  const [audience, setAudience] = useState("AI Product Builder · October 2026");
  const [schedule, setSchedule] = useState("Send now");
  const [decisionNote, setDecisionNote] = useState("");
  const [decisions, setDecisions] = useState([
    {
      id: "PD-18",
      user: "Keyboard learner",
      question: "How should coupon feedback appear?",
      decision: "Visible inline status plus polite announcement",
      owner: "Ronald",
      status: "Approved",
    },
    {
      id: "PD-19",
      user: "Low-bandwidth learner",
      question: "Should the drawer load payment content immediately?",
      decision: "Defer third-party widget until explicit continue",
      owner: "Nakato",
      status: "Needs evidence",
    },
    {
      id: "PD-20",
      user: "Screen-reader learner",
      question: "Where should focus return after closing?",
      decision: "Return to the delivery trigger",
      owner: "Joel",
      status: "Approved",
    },
  ]);
  const [clinicQuery, setClinicQuery] = useState("");
  const [clinicCases, setClinicCases] = useState([
    {
      id: "CL-104",
      title: "Focus returns behind delivery drawer",
      environment: "Chrome 151 · Windows · NVDA",
      owner: "Daniel",
      severity: "High",
      status: "Reproduced",
    },
    {
      id: "CL-107",
      title: "Coupon status not announced",
      environment: "Safari · macOS · VoiceOver",
      owner: "Amina",
      severity: "Medium",
      status: "Pairing",
    },
    {
      id: "CL-111",
      title: "Payment fixture timeout in staging",
      environment: "Staging · synthetic card",
      owner: "Joel",
      severity: "Medium",
      status: "Evidence needed",
    },
  ]);
  const [showcases, setShowcases] = useState([
    {
      id: "SH-01",
      title: "Accessible checkout walkthrough",
      team: "Kijani Team",
      slot: "Today · 4:10 PM",
      readiness: 92,
      feedback: 7,
      visibility: "Cohort",
    },
    {
      id: "SH-02",
      title: "Consent-aware support agent",
      team: "Team Mirembe",
      slot: "Friday · 2:20 PM",
      readiness: 74,
      feedback: 3,
      visibility: "Private review",
    },
    {
      id: "SH-03",
      title: "Climate data story",
      team: "Team Nuru",
      slot: "Friday · 2:40 PM",
      readiness: 100,
      feedback: 12,
      visibility: "Approved showcase",
    },
  ]);
  const [requirements, setRequirements] = useState([
    {
      id: "UX-04",
      requirement: "A learner completes checkout without guessing the next action.",
      owner: "Ronald",
      milestone: "Checkout flow",
      status: "Verified",
      evidence: 4,
    },
    {
      id: "ACC-02",
      requirement: "Keyboard and screen-reader users receive visible, announced validation.",
      owner: "Nakato",
      milestone: "Accessibility",
      status: "Conditional",
      evidence: 5,
    },
    {
      id: "SEC-06",
      requirement: "Payment secrets and live personal data never enter AI or test context.",
      owner: "Asha",
      milestone: "Data boundary",
      status: "Verified",
      evidence: 3,
    },
    {
      id: "EVAL-03",
      requirement: "Automated tests, human verification and residual risk are retained.",
      owner: "Joel",
      milestone: "Evaluation",
      status: "Action required",
      evidence: 6,
    },
  ]);
  const [evaluationRuns, setEvaluationRuns] = useState([
    {
      id: "EV-184",
      scope: "Checkout accessibility",
      requirements: "UX-04 · ACC-02",
      automated: "17/17",
      human: "1/1",
      risk: "1 residual",
      status: "Passed with condition",
    },
    {
      id: "EV-179",
      scope: "Payment fixture boundary",
      requirements: "SEC-06",
      automated: "9/9",
      human: "1/1",
      risk: "0",
      status: "Passed",
    },
    {
      id: "EV-171",
      scope: "Drawer keyboard flow",
      requirements: "ACC-02",
      automated: "6/6",
      human: "0/1",
      risk: "Manual check",
      status: "Incomplete",
    },
  ]);
  const [reviewNote, setReviewNote] = useState(
    "Complete the third-party payment widget screen-reader verification and attach the outcome before learner merge.",
  );
  const [reviewState, setReviewState] = useState("Conditional approval");

  if (page === "announcements") {
    const send = () => {
      if (!announcement.trim()) return;
      notify(
        schedule === "Send now" ? "Announcement sent" : "Announcement scheduled",
        `${audience} · ${schedule}. Original wording and translations remain versioned.`,
      );
    };
    return (
      <div className="instructor-page" data-operational-layout="broadcast-studio">
        <InstructorHero
          eyebrow="Cohort communication operations"
          title="Create one clear announcement, target the right audience and retain delivery evidence."
          text="This is a broadcast and delivery workspace—not an ordinary chat channel. Audience, language, schedule, acknowledgements and connected actions remain explicit."
          actions={
            <>
              <button type="button" className="primary-btn" onClick={send}>
                <Icon name="send" size="sm" />
                {schedule === "Send now" ? "Send announcement" : "Schedule announcement"}
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "announcement" })}
              >
                <Icon name="plus" size="sm" />
                Advanced composer
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard icon="users" title="Audience" value="34 learners" detail="One exact cohort" />
              <HeroSideCard icon="globe" title="Languages" value="EN + LG" detail="Original retained" />
              <HeroSideCard
                icon="activity"
                title="Last open rate"
                value="91%"
                detail="Action completion 74%"
              />
            </>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="message" title="Announcement composer" meta="Versioned draft">
            <div className="form-stack">
              <div className="form-field">
                <label>Announcement</label>
                <textarea
                  value={announcement}
                  onChange={event => setAnnouncement(event.target.value)}
                  style={{ minHeight: 150 }}
                />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Audience</label>
                  <select value={audience} onChange={event => setAudience(event.target.value)}>
                    <option>AI Product Builder · October 2026</option>
                    <option>All assigned cohorts</option>
                    <option>Kijani project team</option>
                    <option>Instructors and mentors</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Delivery</label>
                  <select value={schedule} onChange={event => setSchedule(event.target.value)}>
                    <option>Send now</option>
                    <option>Today · 6:00 PM EAT</option>
                    <option>Tomorrow · 8:00 AM EAT</option>
                  </select>
                </div>
              </div>
              <div className="check-list">
                <div className="check-row done">
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>Connect to Module 6</strong>
                    <span>Learners open the exact lesson and Build Room action.</span>
                  </span>
                </div>
                <div className="check-row done">
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>Translation review</strong>
                    <span>English original retained; Luganda translation approved.</span>
                  </span>
                </div>
                <div className="check-row">
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>Require acknowledgement</strong>
                    <span>Optional for this announcement.</span>
                  </span>
                </div>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Delivery and action evidence" meta="Last 30 days">
            <div className="metric-bars">
              {[
                ["Delivered", 100],
                ["Opened", 91],
                ["Translation viewed", 38],
                ["Connected action complete", 74],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
            <div className="panel-footer">
              <span>No engagement score is created from read receipts.</span>
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => openModal({ type: "export", payload: "build-room-announcements" })}
              >
                <Icon name="download" size="sm" />
                Export delivery
              </button>
            </div>
          </AcademyPanel>
        </div>
        <AcademyPanel icon="activity" title="Announcement ledger" meta="Attributable">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Announcement</th>
                  <th>Audience</th>
                  <th>Author</th>
                  <th>Delivery</th>
                  <th>Read</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [
                    "Week 6 · From claims to evidence",
                    "34 learners",
                    "Amina Mutesi",
                    "Today 7:12 AM",
                    "29 / 34",
                    "Module 6",
                  ],
                  [
                    "Class recording and reflection",
                    "34 learners",
                    "Amina Mutesi",
                    "29 Oct",
                    "32 / 34",
                    "Recording",
                  ],
                  [
                    "Capstone panel preparation",
                    "28 learners",
                    "Daniel Mwesigwa",
                    "28 Oct",
                    "27 / 28",
                    "Evidence checklist",
                  ],
                ].map(item => (
                  <tr key={item[0]}>
                    <td>
                      <strong>{item[0]}</strong>
                    </td>
                    <td>{item[1]}</td>
                    <td>{item[2]}</td>
                    <td>{item[3]}</td>
                    <td>{item[4]}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary-btn button-sm"
                        onClick={() => notify("Connected action opened", String(item[5]))}
                      >
                        {item[5]}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
  }

  if (page === "product-thinking") {
    const addDecision = () => {
      if (!decisionNote.trim()) return;
      setDecisions(current => [
        {
          id: `PD-${21 + current.length}`,
          user: "Defined user",
          question: "Instructor-added product question",
          decision: decisionNote.trim(),
          owner: "Amina",
          status: "Draft",
        },
        ...current,
      ]);
      setDecisionNote("");
      notify(
        "Product decision added",
        "The decision is linked to its user, evidence, owner and review state.",
      );
    };
    return (
      <div className="instructor-page" data-operational-layout="product-decision-canvas">
        <PageTitle
          title="Product Decision Studio"
          text="Facilitate user evidence, hypotheses, trade-offs and accountable decisions before implementation begins."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("teaching", "program-ai-product-builder")}
              >
                <Icon name="book" size="sm" />
                Learning outcomes
              </button>
              <button type="button" className="primary-btn" onClick={() => openModal({ type: "huddle" })}>
                <Icon name="video" size="sm" />
                Decision clinic
              </button>
            </>
          }
        />
        <div className="stat-grid">
          <StatTile icon="users" label="User evidence" value="12" detail="Interviews and observations" />
          <StatTile icon="workflow" label="Open hypotheses" value="4" detail="Need validation" tone="cyan" />
          <StatTile
            icon="shield"
            label="Decisions"
            value={decisions.length}
            detail="2 approved · 1 conditional"
            tone="blue"
          />
          <StatTile icon="alert" label="Risks" value="3" detail="Named owners" tone="orange" />
        </div>
        <div className="content-grid">
          <AcademyPanel icon="workflow" title="Decision canvas" meta={`${decisions.length} decisions`}>
            <div className="evidence-stack">
              {decisions.map(item => (
                <article className="evidence-card" key={item.id}>
                  <span>
                    <Icon name="workflow" />
                  </span>
                  <div>
                    <strong>
                      {item.id} · {item.question}
                    </strong>
                    <p>
                      User: {item.user} · Decision: {item.decision} · Owner: {item.owner}
                    </p>
                  </div>
                  <TonePill
                    tone={
                      item.status === "Approved"
                        ? "green"
                        : item.status === "Needs evidence"
                          ? "orange"
                          : "purple"
                    }
                  >
                    {item.status}
                  </TonePill>
                </article>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="edit" title="Record a product decision" meta="Evidence required">
            <div className="form-field">
              <label>Decision and rationale</label>
              <textarea
                value={decisionNote}
                onChange={event => setDecisionNote(event.target.value)}
                placeholder="State the user, evidence, chosen direction, rejected alternative and remaining risk…"
                style={{ minHeight: 160 }}
              />
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={addDecision}
                disabled={!decisionNote.trim()}
              >
                <Icon name="plus" size="sm" />
                Add decision
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => notify("Evidence picker opened")}
              >
                Attach evidence
              </button>
            </div>
            <div className="decision-callout">
              <strong>Decision ownership stays human.</strong>
              <p>
                AI may organise options or identify missing evidence, but the team and instructor own the
                product decision.
              </p>
            </div>
          </AcademyPanel>
        </div>
        <AcademyPanel icon="clipboard" title="Product review questions" meta="Instructor facilitation">
          <div className="question-sequence">
            {[
              "Whose outcome changes if this succeeds?",
              "What evidence contradicts the current assumption?",
              "Which trade-off was rejected and why?",
              "What is the smallest safe experiment?",
              "How will a learner or user challenge the system?",
            ].map((question, index) => (
              <div className="sequence-item" key={question}>
                <span className="sequence-number">{index + 1}</span>
                <div>
                  <strong>{question}</strong>
                  <p>
                    {
                      [
                        "User and outcome",
                        "Evidence quality",
                        "Trade-off reasoning",
                        "Controlled pilot",
                        "Human authority",
                      ][index]
                    }
                  </p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Question shared", question)}
                >
                  Ask group
                </button>
              </div>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }

  if (page === "code-clinic") {
    const visible = clinicCases.filter(item =>
      `${item.id} ${item.title} ${item.environment} ${item.owner}`
        .toLowerCase()
        .includes(clinicQuery.toLowerCase()),
    );
    const move = (id: string, status: string) => {
      setClinicCases(current => current.map(item => (item.id === id ? { ...item, status } : item)));
      notify("Clinic case updated", `${id} · ${status}`);
    };
    return (
      <div className="instructor-page" data-operational-layout="debugging-clinic">
        <PageTitle
          title="Code Clinic & Debugging Queue"
          text="Triage reproducible problems, inspect environment evidence, assign support and retain the path from symptom to verified fix."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => notify("Repro template downloaded")}
              >
                Repro template
              </button>
              <button type="button" className="primary-btn" onClick={() => openModal({ type: "huddle" })}>
                <Icon name="video" size="sm" />
                Start live clinic
              </button>
            </>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="code" title="Clinic queue" meta={`${visible.length} cases`}>
            <div className="panel-toolbar">
              <div className="search-page-box">
                <Icon name="search" size="sm" />
                <input
                  value={clinicQuery}
                  onChange={event => setClinicQuery(event.target.value)}
                  placeholder="Search issue, environment or owner…"
                />
              </div>
              <button
                type="button"
                className="primary-btn button-sm"
                onClick={() => {
                  setClinicCases(current => [
                    {
                      id: `CL-${120 + current.length}`,
                      title: "New reproducible case",
                      environment: "Environment not yet captured",
                      owner: "Unassigned",
                      severity: "Medium",
                      status: "Needs reproduction",
                    },
                    ...current,
                  ]);
                  notify("Clinic case added");
                }}
              >
                <Icon name="plus" size="sm" />
                New case
              </button>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Environment</th>
                    <th>Owner</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Next action</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(item => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.id}</strong>
                        <span style={{ display: "block", color: "var(--muted)", fontSize: 14, marginTop: 3 }}>
                          {item.title}
                        </span>
                      </td>
                      <td>{item.environment}</td>
                      <td>{item.owner}</td>
                      <td>
                        <TonePill tone={item.severity === "High" ? "red" : "orange"}>
                          {item.severity}
                        </TonePill>
                      </td>
                      <td>{item.status}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary-btn button-sm"
                          onClick={() =>
                            move(item.id, item.status === "Reproduced" ? "Fix under review" : "Reproduced")
                          }
                        >
                          {item.status === "Reproduced" ? "Review fix" : "Reproduce"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="terminal" title="Minimum reproducible case" meta="Required before agent use">
            <div className="check-list">
              {[
                [true, "Exact steps", "Start state, actions and expected outcome"],
                [true, "Environment", "Browser, operating system, assistive technology and build"],
                [true, "Evidence", "Screenshot, recording, logs or test output"],
                [false, "Data boundary", "Confirm synthetic or approved fixture data"],
                [false, "Agent scope", "Files, branch, tools, network and stop authority"],
              ].map((item, index) => (
                <div className={`check-row ${item[0] ? "done" : ""}`} key={index}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>{item[2]}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={() =>
                  notify("Sandbox opened", "A clean isolated reproduction environment is ready.")
                }
              >
                <Icon name="terminal" size="sm" />
                Open sandbox
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("buildRooms", "qa-and-evaluation")}
              >
                <Icon name="test" size="sm" />
                Evaluation queue
              </button>
            </div>
          </AcademyPanel>
        </div>
        <AcademyPanel icon="activity" title="Clinic outcome ledger" meta="No invisible debugging">
          <div className="timeline">
            {[
              ["CL-099", "Focus trap resolved", "Nakato · learner fix · Daniel verified"],
              ["CL-101", "Fixture leak blocked", "Asha · data boundary decision"],
              ["CL-103", "Translation rendering corrected", "Joel · test added · Amina approved"],
            ].map(item => (
              <div className="timeline-item" key={item[0]}>
                <span className="timeline-dot">
                  <Icon name="check" size="sm" />
                </span>
                <span>
                  <strong>
                    {item[0]} · {item[1]}
                  </strong>
                  <p>{item[2]}</p>
                </span>
                <time>Recorded</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }

  if (page === "show-and-learn") {
    const updateVisibility = (id: string, visibility: string) => {
      setShowcases(current => current.map(item => (item.id === id ? { ...item, visibility } : item)));
      notify("Showcase visibility updated", `${id} · ${visibility}`);
    };
    return (
      <div className="instructor-page" data-operational-layout="showcase-gallery">
        <PageTitle
          title="Showcase & Peer Review"
          text="Schedule learner demonstrations, collect structured peer feedback and decide what becomes cohort-visible or credential evidence."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "announcement" })}
              >
                Announce showcase
              </button>
              <button type="button" className="primary-btn" onClick={() => openModal({ type: "huddle" })}>
                <Icon name="video" size="sm" />
                Open showcase studio
              </button>
            </>
          }
        />
        <div className="simple-card-grid">
          {showcases.map(item => (
            <article className="simple-card" key={item.id}>
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name="eye" />
                </span>
                <div className="simple-card-copy">
                  <h3>{item.title}</h3>
                  <p>
                    {item.team} · {item.slot}
                  </p>
                </div>
                <TonePill
                  tone={
                    item.visibility === "Approved showcase"
                      ? "green"
                      : item.visibility === "Private review"
                        ? "orange"
                        : "blue"
                  }
                >
                  {item.visibility}
                </TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Readiness</span>
                  <strong>{item.readiness}%</strong>
                </div>
                <ProgressMeter value={item.readiness} />
                <div className="meta-pair">
                  <span>Peer feedback</span>
                  <strong>{item.feedback} responses</strong>
                </div>
                <div className="meta-pair">
                  <span>Evidence decision</span>
                  <strong>{item.readiness === 100 ? "Eligible" : "Not yet"}</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Showcase review opened", item.title)}
                >
                  Review
                </button>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() =>
                    updateVisibility(
                      item.id,
                      item.visibility === "Approved showcase" ? "Cohort" : "Approved showcase",
                    )
                  }
                >
                  {item.visibility === "Approved showcase" ? "Return to cohort" : "Approve showcase"}
                </button>
              </div>
            </article>
          ))}
        </div>
        <div className="content-grid" style={{ marginTop: 14 }}>
          <AcademyPanel icon="evidence" title="Peer feedback rubric" meta="4 prompts">
            <div className="question-sequence">
              {[
                ["Outcome", "What changed for the intended user?"],
                ["Evidence", "Which claim is supported, and by what?"],
                ["Responsible use", "Where is human authority visible?"],
                ["Next iteration", "What one improvement would increase trust or usefulness?"],
              ].map((item, index) => (
                <div className="sequence-item" key={item[0]}>
                  <span className="sequence-number">{index + 1}</span>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Publication controls" meta="Instructor decision">
            <div className="policy-list">
              <div className="policy-item">
                <Icon name="users" size="sm" />
                <span>
                  <strong>Learner consent</strong>
                  <span>Required before public or cross-cohort visibility.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="lock" size="sm" />
                <span>
                  <strong>Private drafts stay private</strong>
                  <span>Peer review does not make source files or messages public.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="award" size="sm" />
                <span>
                  <strong>Showcase is not a credential</strong>
                  <span>Evidence still requires formal human assessment.</span>
                </span>
              </div>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  }

  if (page === "project-brief") {
    const updateRequirement = (id: string, status: string) => {
      setRequirements(current => current.map(item => (item.id === id ? { ...item, status } : item)));
      notify("Requirement status updated", `${id} · ${status}`);
    };
    return (
      <div className="instructor-page" data-operational-layout="requirements-control">
        <InstructorHero
          eyebrow="Canonical project control"
          title="Keep requirements, owners, milestones, changes and acceptance evidence in one approved brief."
          text="This workspace governs the project—not merely the conversation about it. Every change is versioned, attributed and linked to affected teaching, tests and credential evidence."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "assignment-brief" })}
              >
                <Icon name="eye" size="sm" />
                Learner brief
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() =>
                  notify(
                    "Change request opened",
                    "A versioned requirement-change request is ready for rationale and impact analysis.",
                  )
                }
              >
                <Icon name="plus" size="sm" />
                Request change
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard
                icon="clipboard"
                title="Requirements"
                value={requirements.length}
                detail="Two verified · one conditional"
              />
              <HeroSideCard
                icon="calendar"
                title="Milestones"
                value="6"
                detail="Next: staging verification"
              />
              <HeroSideCard icon="users" title="Named owners" value="5" detail="No unowned requirement" />
            </>
          }
        />
        <AcademyPanel icon="clipboard" title="Requirement control matrix" meta="Version 3.2">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Requirement</th>
                  <th>Owner</th>
                  <th>Milestone</th>
                  <th>Evidence</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requirements.map(item => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.id}</strong>
                    </td>
                    <td>{item.requirement}</td>
                    <td>{item.owner}</td>
                    <td>{item.milestone}</td>
                    <td>{item.evidence} items</td>
                    <td>
                      <TonePill
                        tone={
                          item.status === "Verified"
                            ? "green"
                            : item.status === "Conditional"
                              ? "orange"
                              : "red"
                        }
                      >
                        {item.status}
                      </TonePill>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="secondary-btn button-sm"
                        onClick={() =>
                          updateRequirement(item.id, item.status === "Verified" ? "Reopened" : "Verified")
                        }
                      >
                        {item.status === "Verified" ? "Reopen" : "Verify"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
        <div className="content-grid" style={{ marginTop: 14 }}>
          <AcademyPanel icon="workflow" title="Milestone path" meta="Current: evaluation">
            <div className="integration-flow">
              {[
                ["1", "Research", "Complete"],
                ["2", "Design", "Complete"],
                ["3", "Build", "Complete"],
                ["4", "Evaluate", "Current"],
                ["5", "Pilot", "Blocked"],
                ["6", "Evidence", "Pending"],
              ].map(item => (
                <div className="integration-step" key={item[0]}>
                  <span>{item[0]}</span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Change history" meta="Immutable">
            <div className="timeline">
              {[
                ["v3.2", "Separated third-party widget verification from branch preview", "Amina · 2 Nov"],
                ["v3.1", "Added original-language evidence retention", "Daniel · 29 Oct"],
                ["v3.0", "Approved cohort brief and rubric", "Amina · 21 Oct"],
              ].map(item => (
                <div className="timeline-item" key={item[0]}>
                  <span className="timeline-dot">
                    <Icon name="activity" size="sm" />
                  </span>
                  <span>
                    <strong>
                      {item[0]} · {item[1]}
                    </strong>
                    <p>{item[2]}</p>
                  </span>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => notify("Brief version opened", item[0])}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  }

  if (page === "qa-and-evaluation") {
    const runEvaluation = () => {
      setEvaluationRuns(current => [
        {
          id: `EV-${190 + current.length}`,
          scope: "Instructor-triggered complete evaluation",
          requirements: "UX-04 · ACC-02 · SEC-06 · EVAL-03",
          automated: "Running",
          human: "Pending",
          risk: "Pending",
          status: "In progress",
        },
        ...current,
      ]);
      notify(
        "Evaluation started",
        "Automated checks are running; human verification remains a separate named action.",
      );
    };
    return (
      <div className="instructor-page" data-operational-layout="evaluation-control">
        <PageTitle
          title="QA & Evaluation Control Centre"
          text="Coordinate requirement coverage, automated checks, human verification, defects and residual risk without treating a passing test as the final academic decision."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("assessment", "grading")}
              >
                <Icon name="clipboard" size="sm" />
                Submission queue
              </button>
              <button type="button" className="primary-btn" onClick={runEvaluation}>
                <Icon name="play" size="sm" />
                Run evaluation
              </button>
            </>
          }
        />
        <div className="stat-grid">
          <StatTile icon="test" label="Automated checks" value="32 / 32" detail="Latest complete run" />
          <StatTile icon="eye" label="Human checks" value="3 / 4" detail="One staging check" tone="orange" />
          <StatTile
            icon="clipboard"
            label="Requirements"
            value="4 / 4"
            detail="Traceability linked"
            tone="cyan"
          />
          <StatTile icon="alert" label="Residual risks" value="2" detail="Named owners" tone="orange" />
        </div>
        <AcademyPanel icon="test" title="Evaluation run matrix" meta={`${evaluationRuns.length} runs`}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Scope</th>
                  <th>Requirements</th>
                  <th>Automated</th>
                  <th>Human</th>
                  <th>Residual risk</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {evaluationRuns.map(item => (
                  <tr key={item.id} onClick={() => notify("Evaluation record opened", item.id)}>
                    <td>
                      <strong>{item.id}</strong>
                    </td>
                    <td>{item.scope}</td>
                    <td>{item.requirements}</td>
                    <td>{item.automated}</td>
                    <td>{item.human}</td>
                    <td>{item.risk}</td>
                    <td>
                      <TonePill
                        tone={
                          item.status === "Passed"
                            ? "green"
                            : item.status.includes("condition")
                              ? "orange"
                              : item.status === "Incomplete"
                                ? "red"
                                : "blue"
                        }
                      >
                        {item.status}
                      </TonePill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
        <div className="content-grid" style={{ marginTop: 14 }}>
          <AcademyPanel icon="alert" title="Defect and risk queue" meta="4 open">
            <div className="priority-list">
              {[
                [
                  "High",
                  "Third-party payment widget screen-reader behavior",
                  "Joel · staging verification",
                  "EVAL-03",
                ],
                [
                  "Medium",
                  "Coupon translation may exceed mobile status region",
                  "Nakato · language review",
                  "ACC-02",
                ],
                ["Medium", "Focus restore test missing Safari 18", "Daniel · test matrix", "ACC-02"],
                [
                  "Low",
                  "Evaluation report needs learner-readable summary",
                  "Ronald · reflection",
                  "PROOF-02",
                ],
              ].map(item => (
                <button
                  type="button"
                  className="priority-item"
                  key={item[1]}
                  onClick={() => notify("Risk record opened", String(item[1]))}
                >
                  <span className={`priority-icon ${item[0] === "High" ? "orange" : ""}`}>
                    <Icon name="alert" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <p>
                      {item[2]} · {item[3]}
                    </p>
                  </span>
                  <TonePill tone={item[0] === "High" ? "red" : "orange"}>{item[0]}</TonePill>
                </button>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Requirement coverage" meta="Evidence-backed">
            <div className="metric-bars">
              {[
                ["UX-04", 100],
                ["ACC-02", 86],
                ["SEC-06", 100],
                ["EVAL-03", 78],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
            <div className="decision-callout" style={{ marginTop: 12 }}>
              <strong>Coverage is not approval.</strong>
              <p>
                An authorised instructor still reviews learner understanding, process, evidence and unresolved
                risk.
              </p>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  }

  if (page === "instructor-review") {
    const record = (next: string) => {
      setReviewState(next);
      notify(
        "Instructor decision recorded",
        `${next}. The named reviewer, reasons, conditions and next owner are retained.`,
      );
    };
    return (
      <div className="instructor-page" data-operational-layout="restricted-review">
        <InstructorHero
          eyebrow="Restricted human decision workspace"
          title="Review evidence, record conditions and protect private deliberation."
          text="Only assigned instructors, assessors and authorised reviewers can enter. Learners receive the decision, reasons and next action—not unrestricted private review notes."
          actions={
            <>
              <TonePill tone="orange">Restricted</TonePill>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("buildRooms", "group-build-checkout")}
              >
                <Icon name="message" size="sm" />
                Learner-visible room
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard
                icon="clipboard"
                title="Pending reviews"
                value="5"
                detail="One older than 24 hours"
              />
              <HeroSideCard
                icon="shield"
                title="Current state"
                value={reviewState}
                detail="Kijani checkpoint"
              />
              <HeroSideCard icon="users" title="Authorised" value="5" detail="No cohort-wide access" />
            </>
          }
        />
        <div className="review-layout">
          <AcademyPanel icon="evidence" title="Evidence and decision record" meta="Kijani · PR #184">
            <div className="check-list">
              {[
                [true, "Requirements linked", "UX-04, ACC-02, SEC-06 and EVAL-03"],
                [
                  true,
                  "Learner contribution visible",
                  "Decisions and accepted AI assistance are attributable",
                ],
                [true, "Automated evaluation complete", "17 automated checks passed"],
                [true, "Human keyboard check complete", "Recording and notes retained"],
                [false, "Third-party widget verification", "Required before learner merge"],
              ].map((item, index) => (
                <div className={`check-row ${item[0] ? "done" : ""}`} key={index}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>{item[2]}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="form-field" style={{ marginTop: 14 }}>
              <label>Private review note</label>
              <textarea
                value={reviewNote}
                onChange={event => setReviewNote(event.target.value)}
                style={{ minHeight: 180 }}
              />
            </div>
            <div className="modal-form-note">
              <Icon name="lock" size="sm" />
              This note is excluded from the learner-visible feedback and normal cohort exports.
            </div>
          </AcademyPanel>
          <aside className="decision-panel">
            <AcademyPanel icon="shield" title="Human checkpoint" meta={reviewState}>
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Reviewer</strong>
                    <p>Amina Mutesi · Instructor Lead</p>
                  </div>
                  <TonePill tone="green">Verified</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Evidence</strong>
                    <p>4 requirements · 18 checks</p>
                  </div>
                  <TonePill tone="orange">1 condition</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Merge authority</strong>
                    <p>Named learners only after condition</p>
                  </div>
                  <TonePill>Human</TonePill>
                </div>
              </div>
              <div className="hero-actions" style={{ display: "grid" }}>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => record("Approved")}
                  disabled={!reviewNote.trim()}
                >
                  <Icon name="check" size="sm" />
                  Approve
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => record("Conditional approval")}
                  disabled={!reviewNote.trim()}
                >
                  Approve with condition
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => record("Revision requested")}
                  disabled={!reviewNote.trim()}
                >
                  Request revision
                </button>
                <button type="button" className="ghost-btn" onClick={() => record("Escalated to moderation")}>
                  Escalate
                </button>
              </div>
            </AcademyPanel>
          </aside>
        </div>
        <AcademyPanel icon="activity" title="Decision timeline" meta="Immutable">
          <div className="timeline">
            {[
              ["upload", "Evidence set submitted", "Ronald, Nakato and Joel · 9:39 AM"],
              ["test", "Automated and human checks reconciled", "Evaluation Agent + Joel · 9:42 AM"],
              ["eye", "Instructor review opened", "Amina Mutesi · 9:44 AM"],
              ["shield", reviewState, "Amina Mutesi · current decision"],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>Recorded</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }

  return (
    <div className="instructor-page">
      <EmptyState
        icon="message"
        title="Registered Build Room channel"
        text="This instructor-created channel has its own members, purpose, retention, huddles and evidence. Add the first operational update or connect it to a project requirement."
        action={
          <button
            type="button"
            className="primary-btn"
            onClick={() => notify("Channel purpose editor opened")}
          >
            Configure channel
          </button>
        }
      />
    </div>
  );
}

function BuildRoomsOverviewView({
  page,
  language,
  subgroups,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  language: LanguageSettings;
  subgroups: BuildRoomSubgroup[];
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const rooms = [
    {
      id: "group-build-checkout",
      title: "Kijani checkout Build Room",
      cohort: "AI Product Builder · October 2026",
      members: 124,
      active: 18,
      agents: 1,
      reviews: 1,
      evidence: 82,
      status: "Live",
      last: "Now",
      icon: "message",
    },
    {
      id: "product-thinking",
      title: "Product thinking",
      cohort: "AI Product Builder · October 2026",
      members: 38,
      active: 7,
      agents: 0,
      reviews: 2,
      evidence: 68,
      status: "Active",
      last: "6 minutes ago",
      icon: "hash",
    },
    {
      id: "qa-and-evaluation",
      title: "QA and evaluation",
      cohort: "Kijani project",
      members: 14,
      active: 5,
      agents: 1,
      reviews: 1,
      evidence: 74,
      status: "Active",
      last: "18 minutes ago",
      icon: "test",
    },
    {
      id: "show-and-learn",
      title: "Show and learn",
      cohort: "AI Product Builder · October 2026",
      members: 36,
      active: 4,
      agents: 0,
      reviews: 0,
      evidence: 61,
      status: "Active",
      last: "1 hour ago",
      icon: "eye",
    },
    {
      id: "instructor-review",
      title: "Instructor review",
      cohort: "Kijani project · restricted",
      members: 5,
      active: 2,
      agents: 0,
      reviews: 1,
      evidence: 92,
      status: "Restricted",
      last: "2 hours ago",
      icon: "lock",
    },
    {
      id: "context-clinic-archive",
      title: "Context architecture clinic",
      cohort: "Prompt & Context · September 2026",
      members: 31,
      active: 0,
      agents: 0,
      reviews: 0,
      evidence: 100,
      status: "Archived",
      last: "28 October",
      icon: "folder",
    },
  ];
  const assignedLearners = new Set(subgroups.flatMap(group => group.memberIds)).size;
  const subgroupReviews = subgroups.reduce((sum, group) => sum + group.reviewCount, 0);
  const averageProgress = Math.round(
    subgroups.reduce((sum, group) => sum + group.progress, 0) / Math.max(1, subgroups.length),
  );
  const activeSubgroup = subgroups.find(group => group.channel === page || `subgroup-${group.id}` === page);
  const subgroupForMessages = activeSubgroup || subgroups[0];
  const [composer, setComposer] = useState("");
  const [messages, setMessages] = useState<Array<{ id: number; author: string; text: string; time: string }>>(
    () => [
      {
        id: 1,
        author: "Amina Mutesi",
        text: `${subgroupForMessages?.name || "Small group"}: keep the group decision, evidence and named owner visible. The parent brief still applies.`,
        time: "9:10 AM",
      },
      {
        id: 2,
        author: buildRoomMember(subgroupForMessages?.leadId || "")?.name || "Group lead",
        text: "We have linked the current branch, selected the next requirement and identified the decision that needs instructor review.",
        time: "9:24 AM",
      },
      {
        id: 3,
        author: "AI Builder",
        text: `I am idle. Any task must be approved for ${subgroupForMessages?.branch || "this group branch"}; I cannot access another small group.`,
        time: "9:31 AM",
      },
    ],
  );

  if (page === "small-groups") {
    return (
      <div className="instructor-page">
        <InstructorHero
          eyebrow="One class · persistent smaller teams"
          title="Form, rebalance and facilitate smaller groups inside the same Build Room."
          text="Every learner keeps access to the shared class room and receives one focused group channel with its own plan, branch, huddle, Lab context, tests and evidence. Instructors can enter every group without exposing one group’s private conversation to another."
          actions={
            <>
              <button
                type="button"
                className="primary-btn"
                onClick={() => openModal({ type: "manage-groups" })}
              >
                <Icon name="grid" size="sm" />
                Manage groups
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("buildRooms", "group-build-checkout")}
              >
                <Icon name="message" size="sm" />
                Open parent room
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard
                icon="users"
                title="Small groups"
                detail="Inside one class and parent room"
                value={subgroups.length}
              />
              <HeroSideCard
                icon="users"
                title="Learners assigned"
                detail="No duplicate placement"
                value={assignedLearners}
              />
              <HeroSideCard
                icon="shield"
                title="Reviews needed"
                detail="Group checkpoints"
                value={subgroupReviews}
              />
            </>
          }
        />
        <div className="small-group-grid">
          {subgroups.map(group => {
            const lead = buildRoomMember(group.leadId);
            const lab = LABS.find(item => item.id === group.labId);
            return (
              <article className="small-group-card" key={group.id}>
                <header>
                  <span className="small-group-icon">
                    <Icon name="users" />
                  </span>
                  <div>
                    <span className="overline">
                      {group.formation} · {group.visibility}
                    </span>
                    <h3>{group.name}</h3>
                    <p>#{group.channel.replace(/^subgroup-/, "")}</p>
                  </div>
                  <TonePill
                    tone={
                      group.status === "Needs review"
                        ? "orange"
                        : group.status === "Paused"
                          ? "default"
                          : group.status === "Complete"
                            ? "green"
                            : "blue"
                    }
                  >
                    {group.status}
                  </TonePill>
                </header>
                <p className="small-group-purpose">{group.purpose}</p>
                <div className="group-avatar-row">
                  <span className="group-avatar-stack">
                    {group.memberIds.slice(0, 5).map(memberId => {
                      const member = buildRoomMember(memberId);
                      return <i key={memberId}>{member?.initials || "LR"}</i>;
                    })}
                    <b>+{Math.max(0, group.memberIds.length - 5)}</b>
                  </span>
                  <span>
                    <strong>{group.memberIds.length} learners</strong>
                    <small>Lead · {lead?.name || "Not assigned"}</small>
                  </span>
                </div>
                <div className="small-group-metrics">
                  <div>
                    <span>Progress</span>
                    <strong>{group.progress}%</strong>
                  </div>
                  <div>
                    <span>Evidence</span>
                    <strong>{group.evidence}%</strong>
                  </div>
                  <div>
                    <span>Active</span>
                    <strong>{group.activeNow}</strong>
                  </div>
                  <div>
                    <span>Reviews</span>
                    <strong>{group.reviewCount}</strong>
                  </div>
                </div>
                <ProgressMeter value={group.progress} />
                <div className="group-context-line">
                  <span>
                    <Icon name="branch" size="sm" />
                    {group.branch}
                  </span>
                  <span>
                    <Icon name="flask" size="sm" />
                    {lab?.title || "Connected Lab"}
                  </span>
                </div>
                <footer>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => navigate("buildRooms", group.channel)}
                  >
                    <Icon name="message" size="sm" />
                    Open group
                  </button>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() =>
                      notify(
                        "Canonical huddle session required",
                        "Create and assign this subgroup huddle through Academy before opening Class.",
                      )
                    }
                  >
                    <Icon name="video" size="sm" />
                    Huddle
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => openModal({ type: "manage-groups", payload: group.id })}
                    aria-label={`Manage ${group.name}`}
                  >
                    <Icon name="settings" size="sm" />
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
        <div className="content-grid" style={{ marginTop: 14 }}>
          <AcademyPanel icon="shield" title="Smaller-group safeguards" meta="Applied to every group">
            <div className="action-list">
              {[
                [
                  "Shared parent context",
                  "Announcements, brief, requirements and instructor decisions remain available to the whole class.",
                ],
                [
                  "Private work by default",
                  "Group messages, drafts and branches are visible only to assigned members and authorised staff.",
                ],
                [
                  "Separate AI scope",
                  "An agent approved for one group cannot read or write another group’s branch or private channel.",
                ],
                [
                  "Attributable movement",
                  "Member moves, group merges, huddles and evidence transfers record who made the change and why.",
                ],
              ].map(item => (
                <div className="setting-row" key={item[0]}>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                  <TonePill tone="green">On</TonePill>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Cross-group view" meta="No private message content">
            <div className="metric-bars">
              {subgroups.map(group => (
                <div className="metric-bar" key={group.id}>
                  <span>{group.name}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${group.progress}%` }} />
                  </div>
                  <strong>{group.progress}%</strong>
                </div>
              ))}
            </div>
            <div className="panel-footer">
              <span>Average progress · {averageProgress}%</span>
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => openModal({ type: "export", payload: "small-group-progress" })}
              >
                <Icon name="download" size="sm" />
                Export
              </button>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  }

  if (activeSubgroup) {
    const send = () => {
      if (!composer.trim()) return;
      setMessages(current => [
        ...current,
        { id: Date.now(), author: "Amina Mutesi", text: composer.trim(), time: nowTime() },
      ]);
      setComposer("");
    };
    const lab = LABS.find(item => item.id === activeSubgroup.labId) || LABS[0];
    return (
      <div className="instructor-page subgroup-channel-page" style={{ padding: 0, maxWidth: "none" }}>
        <section className="academy-panel subgroup-channel-shell">
          <div className="panel-head subgroup-channel-head">
            <Icon name="users" />
            <strong>{activeSubgroup.name}</strong>
            <span>
              #{activeSubgroup.channel.replace(/^subgroup-/, "")} · {activeSubgroup.memberIds.length} learners
            </span>
            <div className="panel-head-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => openModal({ type: "manage-groups", payload: activeSubgroup.id })}
              >
                <Icon name="users" size="sm" />
                Members & settings
              </button>
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() =>
                  openExternalHandoff(
                    instructorLabHandoffUrl(
                      lab,
                      activeSubgroup.cohortId,
                      "checkout-accessibility",
                      "facilitator",
                      "dashboard",
                      language,
                    ),
                  )
                }
              >
                <Icon name="flask" size="sm" />
                Open Lab
                <Icon name="external" size="sm" />
              </button>
              <button
                type="button"
                className="primary-btn button-sm"
                onClick={() =>
                  notify(
                    "Canonical huddle session required",
                    "Create and assign this subgroup huddle through Academy before opening Class.",
                  )
                }
              >
                <Icon name="video" size="sm" />
                Start huddle
              </button>
            </div>
          </div>
          <div className="group-privacy-banner">
            <Icon name="lock" />
            <div>
              <strong>Private small-group workspace inside group-build-checkout</strong>
              <p>
                Assigned learners, instructors and authorised reviewers can read this channel. Shared
                announcements and the parent assignment remain visible to the whole class.
              </p>
            </div>
            <TonePill tone="green">Instructor access</TonePill>
          </div>
          <div className="group-summary-bar">
            <span>
              <small>Purpose</small>
              <strong>{activeSubgroup.purpose}</strong>
            </span>
            <span>
              <small>Branch</small>
              <strong>{activeSubgroup.branch}</strong>
            </span>
            <span>
              <small>Progress</small>
              <strong>{activeSubgroup.progress}%</strong>
            </span>
            <span>
              <small>Evidence</small>
              <strong>{activeSubgroup.evidence}%</strong>
            </span>
            <span>
              <small>Due</small>
              <strong>{activeSubgroup.due}</strong>
            </span>
          </div>
          <div className="subgroup-conversation">
            {messages.map(message => (
              <div className="conversation-message" key={message.id}>
                <Avatar
                  initials={message.author
                    .split(" ")
                    .map(word => word[0])
                    .join("")
                    .slice(0, 2)}
                  small
                />
                <div className="conversation-bubble">
                  <strong>{message.author}</strong>
                  <p>{message.text}</p>
                  <time>{message.time}</time>
                </div>
              </div>
            ))}
          </div>
          <div className="conversation-composer">
            <textarea
              value={composer}
              onChange={event => setComposer(event.target.value)}
              placeholder={`Message #${activeSubgroup.channel.replace(/^subgroup-/, "")}…`}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            <button type="button" className="primary-btn" onClick={send}>
              <Icon name="send" size="sm" />
              Send
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (page === "review-queue") {
    const reviews = [
      {
        room: "Kijani checkout Build Room",
        item: "Conditional accessibility checkpoint",
        detail: "18 checks pass; third-party widget staging evidence remains",
        due: "Today",
        target: "group-build-checkout",
        tone: "orange",
      },
      {
        room: "Trust, Safety & Consent",
        item: "Consent evidence boundary",
        detail: "Group requests instructor decision before linking the evidence pack",
        due: "2h",
        target: "subgroup-trust-safety",
        tone: "red",
      },
      {
        room: "Product thinking",
        item: "Problem statement decision",
        detail: "Team selected target user; instructor rationale required",
        due: "Today",
        target: "product-thinking",
        tone: "purple",
      },
      {
        room: "QA and evaluation",
        item: "Failure dataset scope",
        detail: "Proposed samples include unapproved customer-like data",
        due: "4h",
        target: "qa-and-evaluation",
        tone: "red",
      },
      {
        room: "Show and learn",
        item: "Evidence candidate",
        detail: "Learner recording needs captions before credential use",
        due: "Tomorrow",
        target: "show-and-learn",
        tone: "cyan",
      },
    ];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Build Room instructor review queue"
          text="Human checkpoints, small-group evidence candidates, AI scopes and safety exceptions that require an accountable instructor decision."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "build-room-review-queue" })}
            >
              <Icon name="download" size="sm" />
              Export queue
            </button>
          }
        />
        <AcademyPanel icon="shield" title="Human decisions" meta={`${reviews.length} pending`}>
          <div className="priority-list">
            {reviews.map(review => (
              <button
                type="button"
                className="priority-item"
                key={review.item}
                onClick={() => navigate("buildRooms", review.target)}
              >
                <span className={`priority-icon ${review.tone}`}>
                  <Icon name="shield" />
                </span>
                <span>
                  <strong>{review.item}</strong>
                  <p>
                    {review.room} · {review.detail}
                  </p>
                </span>
                <span className="priority-meta">
                  <TonePill
                    tone={
                      review.tone === "red"
                        ? "red"
                        : review.tone === "orange"
                          ? "orange"
                          : review.tone === "cyan"
                            ? "blue"
                            : "purple"
                    }
                  >
                    {review.due}
                  </TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  }
  if (page === "rooms" || page === "archived") {
    const listed =
      page === "archived"
        ? rooms.filter(room => room.status === "Archived")
        : rooms.filter(room => room.status !== "Archived");
    return (
      <div className="instructor-page">
        <PageTitle
          title={page === "archived" ? "Archived Build Rooms" : "All Build Rooms"}
          text={
            page === "archived"
              ? "Completed collaboration spaces remain searchable with their messages, decisions, AI records, tests and evidence."
              : "Cohort and project rooms with visible participation, small groups, agent activity, review checkpoints and evidence progress."
          }
          actions={
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "new-channel" })}>
              <Icon name="plus" size="sm" />
              Create room
            </button>
          }
        />
        <div className="simple-card-grid">
          {listed.map(room => (
            <button
              type="button"
              className="simple-card"
              key={room.id}
              onClick={() => navigate("buildRooms", room.id)}
            >
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name={room.icon} />
                </span>
                <div className="simple-card-copy">
                  <h3>{room.title}</h3>
                  <p>{room.cohort}</p>
                </div>
                <TonePill
                  tone={
                    room.status === "Live"
                      ? "green"
                      : room.status === "Archived"
                        ? "default"
                        : room.status === "Restricted"
                          ? "orange"
                          : "blue"
                  }
                >
                  {room.status}
                </TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Members</span>
                  <strong>
                    {room.members} · {room.active} active
                  </strong>
                </div>
                <div className="meta-pair">
                  <span>Small groups</span>
                  <strong>{room.id === "group-build-checkout" ? subgroups.length : "—"}</strong>
                </div>
                <div className="meta-pair">
                  <span>Instructor reviews</span>
                  <strong>{room.reviews}</strong>
                </div>
                <div>
                  <div className="meta-pair">
                    <span>Evidence readiness</span>
                    <strong>{room.evidence}%</strong>
                  </div>
                  <ProgressMeter value={room.evidence} />
                </div>
              </div>
              <div className="simple-card-foot">
                <span>{room.last}</span>
                <Icon name="arrow-right" size="sm" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }
  const isChannel = !["overview", "rooms", "archived", "review-queue", "small-groups"].includes(page);
  if (isChannel && page !== "group-build-checkout")
    return (
      <InstructorBuildChannelView page={page} navigate={navigate} notify={notify} openModal={openModal} />
    );
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Facilitated multiplayer learning"
        title="Keep class collaboration focused with one parent room and smaller persistent groups."
        text="Instructors see room health, group progress, participation, agent scopes, review checkpoints, tests and evidence—without exposing private small-group conversations or turning collaboration into surveillance."
        actions={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("buildRooms", "group-build-checkout")}
            >
              <Icon name="message" size="sm" />
              Open live Build Room
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate("buildRooms", "small-groups")}
            >
              <Icon name="grid" size="sm" />
              Manage small groups
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard icon="message" title="Active rooms" detail="Across assigned cohorts" value="12" />
            <HeroSideCard
              icon="users"
              title="Small groups"
              detail="Inside the live class room"
              value={subgroups.length}
            />
            <HeroSideCard
              icon="shield"
              title="Reviews needed"
              detail="Parent + group checkpoints"
              value={5 + subgroupReviews}
            />
          </>
        }
      />
      <div className="stat-grid">
        <StatTile
          icon="users"
          label="Connected people"
          value="184"
          detail="Learners, instructors and reviewers"
        />
        <StatTile
          icon="activity"
          label="Weekly participation"
          value="89%"
          detail="Meaningful activity, not presence"
          tone="cyan"
        />
        <StatTile
          icon="grid"
          label="Group progress"
          value={`${averageProgress}%`}
          detail={`${assignedLearners} learners assigned`}
          tone="blue"
        />
        <StatTile
          icon="award"
          label="Evidence candidates"
          value="28"
          detail="11 await instructor review"
          tone="orange"
        />
      </div>
      <div className="content-grid">
        <AcademyPanel icon="message" title="Live and priority rooms" meta="Instructor assigned">
          <div className="priority-list">
            {rooms.slice(0, 5).map(room => (
              <button
                type="button"
                className="priority-item"
                key={room.id}
                onClick={() => navigate("buildRooms", room.id)}
              >
                <span
                  className={`priority-icon ${room.status === "Live" ? "cyan" : room.reviews ? "orange" : ""}`}
                >
                  <Icon name={room.icon} />
                </span>
                <span>
                  <strong>{room.title}</strong>
                  <p>
                    {room.cohort} · {room.members} members · {room.active} active
                    {room.id === "group-build-checkout" ? ` · ${subgroups.length} groups` : ""}
                  </p>
                </span>
                <span className="priority-meta">
                  <TonePill tone={room.status === "Live" ? "green" : room.reviews ? "orange" : "purple"}>
                    {room.reviews ? `${room.reviews} review` : room.status}
                  </TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="grid" title="Small-group pulse" meta="Parent-room overview">
          <div className="metric-bars">
            {subgroups.map(group => (
              <div className="metric-bar" key={group.id}>
                <span>{group.name}</span>
                <div className="metric-bar-track">
                  <i style={{ width: `${group.progress}%` }} />
                </div>
                <strong>{group.reviewCount ? `${group.reviewCount} review` : `${group.progress}%`}</strong>
              </div>
            ))}
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() => navigate("buildRooms", "small-groups")}
            >
              <Icon name="grid" size="sm" />
              Open groups
            </button>
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() => openModal({ type: "manage-groups" })}
            >
              <Icon name="settings" size="sm" />
              Configure
            </button>
          </div>
        </AcademyPanel>
      </div>
    </div>
  );
}

function LabDetailView({
  lab,
  language,
  navigate,
  notify,
  openModal: _openModal,
}: {
  lab: LabRecord;
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [tab, setTab] = useState("connection");
  const [cohort, setCohort] = useState("builder-oct-2026");
  const openLabs = (
    section: LabInstructorSectionId = "dashboard",
    mode: "instructor" | "learner-preview" = "instructor",
  ) =>
    openExternalHandoff(
      instructorLabsWorkspaceUrl(section, language, lab, cohort, "checkout-accessibility", mode),
    );
  const cohortName = COHORTS.find(item => item.id === cohort)?.title || cohort;
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("labs", "catalogue")}>
        <Icon name="arrow-left" size="sm" />
        Back to intended Lab catalogue
      </button>
      <section className="detail-hero connected-lab-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <span className="simple-card-icon">
            <Icon name={lab.icon} />
          </span>
          <div>
            <div className="hero-eyebrow">Independent connected platform · {lab.access}</div>
            <h2>{lab.title}</h2>
            <p>{lab.description}</p>
            <span className="external-domain">
              <Icon name="external" size="sm" />
              Actual workspace: labs.chatoner.com · instructor role
            </span>
          </div>
        </div>
        <div className="detail-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => openLabs("dashboard", "learner-preview")}
          >
            <Icon name="eye" size="sm" />
            Preview as learner
            <Icon name="external" size="sm" />
          </button>
          <button type="button" className="primary-btn" onClick={() => openLabs("dashboard")}>
            <Icon name="flask" size="sm" />
            Open this Lab in Labs
            <Icon name="external" size="sm" />
          </button>
        </div>
      </section>
      <div className="platform-boundary-banner">
        <span>
          <Icon name="link" />
        </span>
        <div>
          <strong>Academy coordinates; Chatoner AI Academy Labs executes.</strong>
          <p>
            This Instructor Platform connects the cohort, lesson, assignment, entitlement, language and
            evidence return path. Exercise authoring, practical sessions, runtime work, Lab-native grading and
            reports remain on the independent platform at labs.chatoner.com.
          </p>
        </div>
        <TonePill tone="green">Connected</TonePill>
      </div>
      <nav className="detail-tabs" aria-label="Connected Lab sections">
        {[
          ["connection", "Connection"],
          ["assignment", "Academy assignment"],
          ["runs", "Synced runs"],
          ["evidence", "Returned evidence"],
          ["access", "Access sync"],
        ].map(item => (
          <button
            type="button"
            className={tab === item[0] ? "active" : ""}
            key={item[0]}
            onClick={() => setTab(item[0])}
          >
            {item[1]}
          </button>
        ))}
      </nav>
      {tab === "connection" ? (
        <>
          <div className="boundary-grid">
            <article className="boundary-card academy">
              <span>
                <Icon name="logo" />
              </span>
              <div>
                <small>academy.chatoner.com</small>
                <h3>Instructor coordination</h3>
                <p>
                  Assign the intended Lab, select eligible cohorts, connect learning outcomes and receive
                  evidence status.
                </p>
                <ul>
                  <li>Program, cohort and lesson mapping</li>
                  <li>Entitlement request and return route</li>
                  <li>Cross-system notifications and summary analytics</li>
                  <li>Assessment and credential decisions remain human</li>
                </ul>
              </div>
            </article>
            <article className="boundary-card labs">
              <span>
                <Icon name="flask" />
              </span>
              <div>
                <small>labs.chatoner.com</small>
                <h3>Instructor Lab workspace</h3>
                <p>Create, guide and grade practical work in the independent Labs application.</p>
                <ul>
                  <li>Programs, Labs, notes and translations</li>
                  <li>Exercise authoring, limits and rubrics</li>
                  <li>1:1 practical sessions and live guidance</li>
                  <li>Lab grading, feedback and learning reports</li>
                </ul>
                <button type="button" className="primary-btn button-sm" onClick={() => openLabs("dashboard")}>
                  Open Labs workspace
                  <Icon name="external" size="sm" />
                </button>
              </div>
            </article>
          </div>
          <AcademyPanel icon="grid" title="Instructor role inside Labs" meta="Open the exact workspace">
            <div className="lab-workspace-grid compact">
              {LAB_INSTRUCTOR_SECTIONS.map(section => (
                <button
                  type="button"
                  className="lab-role-card"
                  key={section.id}
                  onClick={() => openLabs(section.id)}
                >
                  <span>
                    <Icon name={section.icon} />
                  </span>
                  <div>
                    <strong>{section.label}</strong>
                    <p>{section.detail}</p>
                  </div>
                  {section.badge ? (
                    <TonePill tone={section.badge === "Live" ? "green" : "orange"}>{section.badge}</TonePill>
                  ) : (
                    <Icon name="external" size="sm" />
                  )}
                </button>
              ))}
            </div>
          </AcademyPanel>
        </>
      ) : null}
      {tab === "assignment" ? (
        <div className="content-grid">
          <AcademyPanel icon="clipboard" title="Academy assignment mapping" meta="Saved in Academy">
            <div className="form-stack">
              <div className="form-field">
                <label>Assigned cohort</label>
                <select value={cohort} onChange={event => setCohort(event.target.value)}>
                  {COHORTS.filter(item => item.status !== "completed").map(item => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Connected lesson</label>
                <select defaultValue="Accessible Product Decisions">
                  <option>Accessible Product Decisions</option>
                  <option>APIs, Data Boundaries and Server Actions</option>
                  <option>Evaluation Clinic</option>
                </select>
              </div>
              <div className="form-field">
                <label>Expected practical outcome</label>
                <textarea defaultValue="Build and test a checkout change in an isolated Lab, explain the human decision boundary and return selected evidence to Academy." />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Available from</label>
                  <input type="date" defaultValue="2026-11-04" />
                </div>
                <div className="form-field">
                  <label>Due</label>
                  <input type="date" defaultValue="2026-11-13" />
                </div>
              </div>
              <div className="hero-actions">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => notify("Lab assignment mapping saved", `${lab.title} · ${cohortName}`)}
                >
                  <Icon name="save" size="sm" />
                  Save Academy mapping
                </button>
                <button type="button" className="secondary-btn" onClick={() => openLabs("content")}>
                  <Icon name="external" size="sm" />
                  Configure activity in Labs
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="link" title="Automatic handoff context" meta="Signed in production">
            <div className="identity-carry">
              <div className="identity-row">
                <span>
                  <Icon name="users" size="sm" />
                </span>
                <div>
                  <strong>Instructor and cohort</strong>
                  <small>Amina Mutesi · {cohortName}</small>
                </div>
                <em>Verified</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="shield" size="sm" />
                </span>
                <div>
                  <strong>Lab and entitlement</strong>
                  <small>
                    {lab.slug} · {lab.entitlement}
                  </small>
                </div>
                <em>Checked</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="book" size="sm" />
                </span>
                <div>
                  <strong>Learning context</strong>
                  <small>Lesson, assignment, requirements and return path</small>
                </div>
                <em>Connected</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="globe" size="sm" />
                </span>
                <div>
                  <strong>Language</strong>
                  <small>
                    {language.primary}
                    {language.secondary !== "None" ? ` + ${language.secondary}` : ""}
                  </small>
                </div>
                <em>Applied</em>
              </div>
            </div>
            <div className="class-trust-line">
              <Icon name="shield" size="sm" />
              <p>
                Production exchanges this context for a short-lived, audience-bound token. Labs revalidates
                instructor assignment, Lab entitlement, role and allowed return URL.
              </p>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "runs" ? (
        <AcademyPanel
          icon="activity"
          title="Synced run summary"
          meta={`${lab.activeRuns} active on labs.chatoner.com`}
        >
          <div className="sync-readonly-note">
            <Icon name="info" size="sm" />
            <p>
              This page receives permission-filtered status events. Inspecting prompts, outputs or detailed
              runtime records opens the independent Labs workspace.
            </p>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Status</th>
                  <th>Elapsed</th>
                  <th>Usage</th>
                  <th>Tests</th>
                  <th>Evidence</th>
                  <th>Open</th>
                </tr>
              </thead>
              <tbody>
                {LEARNERS.slice(0, 12).map((learner, index) => (
                  <tr key={learner.id}>
                    <td>
                      <div className="person-cell">
                        <Avatar initials={learner.initials} small />
                        <span>
                          <strong>{learner.name}</strong>
                          <span>{learner.project}</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <TonePill tone={index % 5 === 0 ? "orange" : index % 3 === 0 ? "green" : "blue"}>
                        {index % 5 === 0 ? "Paused" : index % 3 === 0 ? "Completed" : "Running"}
                      </TonePill>
                    </td>
                    <td>{12 + index * 3}m</td>
                    <td>{34 + index * 4}%</td>
                    <td>{index % 3 === 0 ? "18/18" : "12/18"}</td>
                    <td>{index % 3 === 0 ? "Returned" : "Not selected"}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary-btn button-sm"
                        onClick={() => openLabs("learners")}
                      >
                        <Icon name="external" size="sm" />
                        Inspect in Labs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      ) : null}
      {tab === "evidence" ? (
        <div className="content-grid">
          <AcademyPanel icon="award" title="Evidence returned from Labs" meta="Academy review">
            <div className="evidence-stack">
              {LEARNERS.slice(0, 6).map((learner, index) => (
                <div className="evidence-card" key={learner.id}>
                  <span>
                    <Icon name="flask" />
                  </span>
                  <div>
                    <strong>
                      {learner.name} · Lab run #{184 + index}
                    </strong>
                    <p>
                      Signed run reference, selected artifacts, tests, reflection and learner decision owner
                    </p>
                  </div>
                  <TonePill tone={index < 3 ? "green" : "orange"}>{index < 3 ? "Ready" : "Review"}</TonePill>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => openLabs("grading")}
                  >
                    <Icon name="external" size="sm" />
                    Open source
                  </button>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Evidence boundary" meta="No silent copying">
            <div className="check-list">
              {[
                "The learner selects the Lab run or artifact to return",
                "Labs signs the evidence reference and preserves the source version",
                "Academy stores assessment links, not unrestricted runtime access",
                "The instructor reviews the practical evidence and rationale",
                "Credential decisions remain separate and human",
              ].map(item => (
                <div className="check-row done" key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>Required for evidence use</span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "access" ? (
        <div className="content-grid">
          <AcademyPanel icon="shield" title="Synced access summary" meta="Read only in Academy">
            <div className="action-list">
              {[
                ["Instructor role", "Assigned to AI Product Builder · October 2026", "Verified"],
                ["Intended Lab", lab.title, "Locked"],
                ["Entitlement", `${lab.entitlement} · ${lab.access}`, "Active"],
                ["Data boundary", "Synthetic or approved data only", "Applied"],
                ["Internet", "Disabled unless Labs records an approved allow-list", "Restricted"],
                ["Evidence return", "Selected artifacts only", "Enabled"],
              ].map(item => (
                <div className="setting-row" key={item[0]}>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                  <TonePill tone={item[2] === "Restricted" ? "orange" : "green"}>{item[2]}</TonePill>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button type="button" className="primary-btn" onClick={() => openLabs("content")}>
                <Icon name="external" size="sm" />
                Manage activity rules in Labs
              </button>
              <button type="button" className="secondary-btn" onClick={() => openLabs("help")}>
                <Icon name="help" size="sm" />
                Instructor Lab help
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Connection health" meta="Last sync 3 minutes ago">
            <div className="sync-status-grid">
              {[
                ["SSO and role", "Operational"],
                ["Entitlement exchange", "Operational"],
                ["Run events", "Operational"],
                ["Evidence return", "Operational"],
                ["Policy events", "Operational"],
                ["Return URL", "Allow-listed"],
              ].map(item => (
                <div className="sync-status-card" key={item[0]}>
                  <span className="status-dot" />
                  <span>
                    <strong>{item[0]}</strong>
                    <small>{item[1]}</small>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
    </div>
  );
}

function LabsInstructorView({
  page,
  language,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const openWorkspace = (section: LabInstructorSectionId, lab?: LabRecord) =>
    openExternalHandoff(instructorLabsWorkspaceUrl(section, language, lab));
  if (page.startsWith("lab-")) {
    const id = page.replace("lab-", "");
    const lab = LABS.find(item => item.id === id) || LABS.find(item => item.slug === id) || LABS[0];
    return (
      <LabDetailView
        lab={lab}
        language={language}
        navigate={navigate}
        notify={notify}
        openModal={openModal}
      />
    );
  }
  if (page === "workspace")
    return (
      <div className="instructor-page">
        <InstructorHero
          eyebrow="Independent instructor workspace · labs.chatoner.com"
          title="Create, guide and grade where practical Lab work actually happens."
          text="Chatoner AI Academy Labs is a separate platform with its own Instructor & Assessor role. Academy carries verified identity and learning context into the exact Labs section, then receives permission-filtered status and selected evidence back."
          actions={
            <>
              <button type="button" className="primary-btn" onClick={() => openWorkspace("dashboard")}>
                <Icon name="external" size="sm" />
                Open Teaching Overview
              </button>
              <button type="button" className="secondary-btn" onClick={() => navigate("labs", "overview")}>
                <Icon name="link" size="sm" />
                Connection overview
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard
                icon="external"
                title="Platform"
                detail="Independent but interconnected"
                value="Labs"
              />
              <HeroSideCard icon="users" title="Instructor role" detail="Create · guide · grade" value="IA" />
              <HeroSideCard icon="activity" title="Last sync" detail="Run and evidence events" value="3m" />
            </>
          }
        />
        <div className="platform-boundary-banner">
          <span>
            <Icon name="shield" />
          </span>
          <div>
            <strong>No embedded imitation of the Lab platform.</strong>
            <p>
              Each action below opens the real instructor destination on labs.chatoner.com with Academy SSO,
              role, cohort, assignment, language and return context.
            </p>
          </div>
          <TonePill tone="green">Role verified</TonePill>
        </div>
        <div className="lab-workspace-grid">
          {LAB_INSTRUCTOR_SECTIONS.map(section => (
            <button
              type="button"
              className="lab-role-card"
              key={section.id}
              onClick={() => openWorkspace(section.id)}
            >
              <span>
                <Icon name={section.icon} />
              </span>
              <div>
                <small>labs.chatoner.com · Instructor</small>
                <strong>{section.label}</strong>
                <p>{section.detail}</p>
              </div>
              {section.badge ? (
                <TonePill tone={section.badge === "Live" ? "green" : "orange"}>{section.badge}</TonePill>
              ) : (
                <Icon name="external" size="sm" />
              )}
            </button>
          ))}
        </div>
        <div className="content-grid" style={{ marginTop: 14 }}>
          <AcademyPanel
            icon="link"
            title="Context carried from Academy"
            meta="Short-lived token in production"
          >
            <div className="identity-carry">
              <div className="identity-row">
                <span>
                  <Icon name="users" size="sm" />
                </span>
                <div>
                  <strong>Instructor identity</strong>
                  <small>Amina Mutesi · assigned programs and cohorts only</small>
                </div>
                <em>Verified</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="book" size="sm" />
                </span>
                <div>
                  <strong>Learning context</strong>
                  <small>Program, lesson, assignment, subgroup and expected evidence</small>
                </div>
                <em>Connected</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="globe" size="sm" />
                </span>
                <div>
                  <strong>Language</strong>
                  <small>
                    {language.primary}
                    {language.secondary !== "None" ? ` + ${language.secondary}` : ""}
                  </small>
                </div>
                <em>Applied</em>
              </div>
              <div className="identity-row">
                <span>
                  <Icon name="arrow-left" size="sm" />
                </span>
                <div>
                  <strong>Return destination</strong>
                  <small>Back to the exact Academy cohort, assignment or review</small>
                </div>
                <em>Allow-listed</em>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Events returned to Academy" meta="Permission filtered">
            <div className="action-list">
              {[
                ["Lab assignment acknowledged", "Intended Lab and entitlement accepted"],
                ["Run status", "Started, progressed, paused, completed, failed or stopped"],
                ["Evidence selected", "Signed reference and allowed metadata returned"],
                ["Policy event", "Safety or access event requiring instructor attention"],
                ["Grading status", "Practical review progress without copying private Lab content"],
              ].map(item => (
                <div className="setting-row" key={item[0]}>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                  <TonePill tone="green">Synced</TonePill>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "runs")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Synced cohort Lab runs"
          text="Permission-filtered status from labs.chatoner.com. Detailed prompts, outputs, private notes and runtime controls remain in the independent Labs workspace."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => notify("Run status refreshed", "Latest Labs events reconciled.")}
              >
                <Icon name="refresh" size="sm" />
                Refresh sync
              </button>
              <button type="button" className="primary-btn" onClick={() => openWorkspace("learners")}>
                <Icon name="external" size="sm" />
                Open Learners & Cohorts in Labs
              </button>
            </>
          }
        />
        <div className="sync-readonly-note">
          <Icon name="info" />
          <p>
            Academy can show status, elapsed time, usage percentage, safety state and returned evidence
            reference. Inspecting a run opens Labs and revalidates the instructor’s relationship to that
            learner and cohort.
          </p>
        </div>
        <AcademyPanel icon="activity" title="Current run status" meta="52 synced">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Lab</th>
                  <th>Cohort</th>
                  <th>Status</th>
                  <th>Elapsed</th>
                  <th>Usage</th>
                  <th>Safety</th>
                  <th>Evidence</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {LEARNERS.slice(0, 24).map((learner, index) => {
                  const lab = LABS[index % LABS.length];
                  return (
                    <tr key={learner.id}>
                      <td>
                        <div className="person-cell">
                          <Avatar initials={learner.initials} small />
                          <span>
                            <strong>{learner.name}</strong>
                            <span>{learner.project}</span>
                          </span>
                        </div>
                      </td>
                      <td>{lab.title}</td>
                      <td>{learner.cohort}</td>
                      <td>
                        <TonePill tone={index % 5 === 0 ? "orange" : index % 4 === 0 ? "green" : "blue"}>
                          {index % 5 === 0 ? "Paused" : index % 4 === 0 ? "Completed" : "Running"}
                        </TonePill>
                      </td>
                      <td>{8 + ((index * 7) % 54)}m</td>
                      <td>{35 + ((index * 3) % 56)}%</td>
                      <td>
                        {index % 11 === 0 ? (
                          <TonePill tone="orange">Review</TonePill>
                        ) : (
                          <TonePill tone="green">Clear</TonePill>
                        )}
                      </td>
                      <td>{index % 4 === 0 ? "Returned" : "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary-btn button-sm"
                          onClick={() =>
                            openExternalHandoff(
                              instructorLabsWorkspaceUrl(
                                "learners",
                                language,
                                lab,
                                learner.cohortId,
                                "checkout-sprint",
                              ),
                            )
                          }
                        >
                          <Icon name="external" size="sm" />
                          Inspect
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
  if (page === "evidence")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Evidence returned from Labs"
          text="Learner-selected, signed Lab evidence references enter Academy review without moving unrestricted runtime content into the instructor portal."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "export", payload: "labs-returned-evidence" })}
              >
                <Icon name="download" size="sm" />
                Export index
              </button>
              <button type="button" className="primary-btn" onClick={() => openWorkspace("grading")}>
                <Icon name="external" size="sm" />
                Open Lab grading queue
              </button>
            </>
          }
        />
        <div className="stat-grid">
          <StatTile icon="award" label="Returned this week" value="28" detail="Across 19 learners" />
          <StatTile
            icon="clipboard"
            label="Awaiting Academy review"
            value="8"
            detail="Human assessment decision"
            tone="orange"
          />
          <StatTile
            icon="shield"
            label="Signed references"
            value="100%"
            detail="Source and version retained"
            tone="cyan"
          />
          <StatTile
            icon="link"
            label="Broken links"
            value="0"
            detail="Last integrity check 7 min"
            tone="blue"
          />
        </div>
        <div className="simple-card-grid">
          {LEARNERS.slice(0, 8).map((learner, index) => {
            const lab = LABS[index % 5];
            return (
              <article className="simple-card" key={learner.id}>
                <div className="simple-card-head">
                  <span className="simple-card-icon">
                    <Icon name="flask" />
                  </span>
                  <div className="simple-card-copy">
                    <h3>{learner.name}</h3>
                    <p>
                      {lab.title} · Run #{208 + index}
                    </p>
                  </div>
                  <TonePill tone={index < 5 ? "green" : "orange"}>{index < 5 ? "Ready" : "Review"}</TonePill>
                </div>
                <div className="simple-card-body">
                  <div className="meta-pair">
                    <span>Artifacts</span>
                    <strong>{4 + (index % 5)}</strong>
                  </div>
                  <div className="meta-pair">
                    <span>Tests</span>
                    <strong>{index % 3 === 0 ? "18/18" : "14/18"}</strong>
                  </div>
                  <div className="meta-pair">
                    <span>Reflection</span>
                    <strong>Present</strong>
                  </div>
                  <div className="meta-pair">
                    <span>Source</span>
                    <strong>Signed</strong>
                  </div>
                </div>
                <div className="simple-card-foot">
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() =>
                      openExternalHandoff(
                        instructorLabsWorkspaceUrl(
                          "grading",
                          language,
                          lab,
                          learner.cohortId,
                          "checkout-sprint",
                        ),
                      )
                    }
                  >
                    Open source in Labs
                    <Icon name="external" size="sm" />
                  </button>
                  <button
                    type="button"
                    className="primary-btn button-sm"
                    onClick={() => navigate("assessment", "grading")}
                  >
                    Review in Academy
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  if (page === "analytics")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Synced AI Lab analytics"
          text="Academy receives aggregate learning and operational indicators; the full Instructor Learning Reports workspace remains on labs.chatoner.com."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openModal({ type: "export", payload: "lab-analytics" })}
              >
                <Icon name="download" size="sm" />
                Export Academy summary
              </button>
              <button type="button" className="primary-btn" onClick={() => openWorkspace("reports")}>
                <Icon name="external" size="sm" />
                Open Learning Reports in Labs
              </button>
            </>
          }
        />
        <div className="stat-grid">
          <StatTile icon="flask" label="Assigned learners" value="62" detail="5 connected Lab pathways" />
          <StatTile
            icon="check"
            label="Completion"
            value="84%"
            detail="+6 points week over week"
            tone="cyan"
          />
          <StatTile
            icon="clock"
            label="Median run"
            value="42m"
            detail="Within published limits"
            tone="blue"
          />
          <StatTile
            icon="alert"
            label="Safety reviews"
            value="3"
            detail="No unresolved critical event"
            tone="orange"
          />
        </div>
        <div className="analytics-grid">
          <AcademyPanel icon="chart" title="Lab completion" meta="Synced by environment">
            <div className="metric-bars">
              {LABS.map(lab => (
                <div className="metric-bar" key={lab.id}>
                  <span>{lab.title}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${lab.completion}%` }} />
                  </div>
                  <strong>{lab.completion}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="award" title="Evidence conversion" meta="Selected run → accepted proof">
            <div className="chart-row">
              {[
                ["Selected", 92],
                ["Reviewed", 81],
                ["Accepted", 73],
                ["Credential", 61],
              ].map(item => (
                <div className="chart-column" key={String(item[0])}>
                  <strong>{item[1]}%</strong>
                  <i style={{ height: `${Number(item[1]) * 1.35}px` }} />
                  <span>{item[0]}</span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "safety")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Access and safety sync"
          text="Academy displays the policy version and events applied by Labs. Instructor activity rules, limits and assessment controls are changed inside the independent Labs workspace."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => notify("Policy sync refreshed", "Current Labs policy versions loaded.")}
              >
                <Icon name="refresh" size="sm" />
                Refresh
              </button>
              <button type="button" className="primary-btn" onClick={() => openWorkspace("content")}>
                <Icon name="external" size="sm" />
                Manage Lab rules in Labs
              </button>
            </>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="shield" title="Current published learner policy" meta="Synced · read only">
            <div className="action-list">
              {[
                [
                  "Approved data only",
                  "Synthetic, Academy sample or explicitly approved instructor dataset",
                  "Applied",
                ],
                ["Internet disabled", "A named domain allow-list is required in Labs", "Restricted"],
                ["Time and token limits", "Shown before entry and preserved by policy version", "Applied"],
                ["Human evidence selection", "AI cannot submit proof or award a credential", "Applied"],
                ["Instructor extension", "Time-bound, reasoned and audited inside Labs", "Allowed"],
              ].map(item => (
                <div className="setting-row" key={item[0]}>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>{item[1]}</p>
                  </div>
                  <TonePill tone={item[2] === "Restricted" ? "orange" : "green"}>{item[2]}</TonePill>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="alert" title="Recent policy events" meta="3 resolved">
            <div className="timeline">
              {[
                [
                  "globe",
                  "External domain blocked",
                  "Unapproved documentation domain · no data transmitted",
                  "Today",
                ],
                [
                  "database",
                  "Data pattern warning",
                  "Sample resembled personal data · learner replaced dataset",
                  "Yesterday",
                ],
                ["clock", "Resource limit reached", "Run stopped and state preserved", "3 days ago"],
              ].map(item => (
                <div className="timeline-item" key={item[1]}>
                  <span className="timeline-dot">
                    <Icon name={item[0]} size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <p>{item[2]}</p>
                  </span>
                  <TonePill tone="green">Resolved</TonePill>
                </div>
              ))}
            </div>
            <button type="button" className="secondary-btn button-sm" onClick={() => openWorkspace("help")}>
              <Icon name="help" size="sm" />
              Open Instructor Help in Labs
            </button>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "assignments")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Academy Lab assignments"
          text="Connect an Academy outcome and eligible cohort to the exact independent Lab. The activity itself is authored and delivered inside labs.chatoner.com."
          actions={
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "assign-lab" })}>
              <Icon name="plus" size="sm" />
              Assign intended Lab
            </button>
          }
        />
        <div className="platform-boundary-banner">
          <span>
            <Icon name="link" />
          </span>
          <div>
            <strong>Assignment mapping is not Lab authoring.</strong>
            <p>
              Academy decides who should reach which Lab and why. The Instructor & Assessor uses Programs,
              Labs & Notes or Exercise Authoring on labs.chatoner.com to prepare the practical activity.
            </p>
          </div>
          <button
            type="button"
            className="secondary-btn button-sm"
            onClick={() => openWorkspace("authoring")}
          >
            Open Exercise Authoring
            <Icon name="external" size="sm" />
          </button>
        </div>
        <div className="simple-card-grid">
          {LABS.filter(lab => lab.assignedCohorts > 0).map(lab => (
            <article className="simple-card" key={lab.id}>
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name={lab.icon} />
                </span>
                <div className="simple-card-copy">
                  <h3>{lab.title}</h3>
                  <p>
                    {lab.assignedCohorts} cohorts · entitlement {lab.entitlement}
                  </p>
                </div>
                <TonePill tone="green">Connected</TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Academy lesson</span>
                  <strong>Accessible Product Decisions</strong>
                </div>
                <div className="meta-pair">
                  <span>Synced runs</span>
                  <strong>{lab.activeRuns}</strong>
                </div>
                <div className="meta-pair">
                  <span>Completion</span>
                  <strong>{lab.completion}%</strong>
                </div>
                <div className="meta-pair">
                  <span>Evidence return</span>
                  <strong>Enabled</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => navigate("labs", `lab-${lab.id}`)}
                >
                  View connection
                </button>
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => openExternalHandoff(instructorLabsWorkspaceUrl("content", language, lab))}
                >
                  Open activity in Labs
                  <Icon name="external" size="sm" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  if (page === "catalogue")
    return (
      <div className="instructor-page">
        <InstructorHero
          eyebrow="Academy → exact Lab handoff"
          title="Choose the intended environment, then continue on labs.chatoner.com."
          text="The catalogue here is an Academy mapping view—not a duplicate Lab platform. Every card opens a connection record or the exact instructor Lab environment with verified role and entitlement context."
          actions={
            <>
              <button type="button" className="primary-btn" onClick={() => openModal({ type: "assign-lab" })}>
                <Icon name="plus" size="sm" />
                Assign Lab
              </button>
              <button type="button" className="secondary-btn" onClick={() => openWorkspace("content")}>
                <Icon name="external" size="sm" />
                Programs, Labs & Notes
              </button>
            </>
          }
          side={
            <>
              <HeroSideCard
                icon="flask"
                title="Available environments"
                detail="Included + advanced"
                value={LABS.length}
              />
              <HeroSideCard icon="link" title="Assigned pathways" detail="Across active cohorts" value="5" />
              <HeroSideCard icon="shield" title="Entitlement checks" detail="Server validated" value="On" />
            </>
          }
        />
        <div className="lab-instructor-grid">
          {LABS.map(lab => (
            <article className="lab-card" key={lab.id}>
              <button
                type="button"
                className="lab-card-open"
                onClick={() => navigate("labs", `lab-${lab.id}`)}
                aria-label={`Open ${lab.title} connection`}
              >
                <div className="lab-card-top">
                  <span className="simple-card-icon">
                    <Icon name={lab.icon} />
                  </span>
                  <div className="simple-card-copy">
                    <h3>{lab.title}</h3>
                    <p>{lab.category}</p>
                  </div>
                  <TonePill tone={lab.access === "Included" ? "green" : "orange"}>{lab.access}</TonePill>
                </div>
                <p>{lab.description}</p>
                <div className="cohort-metrics">
                  <div className="cohort-metric">
                    <span>Cohorts</span>
                    <strong>{lab.assignedCohorts}</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Active runs</span>
                    <strong>{lab.activeRuns}</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Completion</span>
                    <strong>{lab.completion}%</strong>
                  </div>
                  <div className="cohort-metric">
                    <span>Entitlement</span>
                    <strong>{lab.entitlement}</strong>
                  </div>
                </div>
                <ProgressMeter value={lab.completion} />
              </button>
              <div className="lab-card-actions">
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => navigate("labs", `lab-${lab.id}`)}
                >
                  Connection
                </button>
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => openExternalHandoff(instructorLabsWorkspaceUrl("dashboard", language, lab))}
                >
                  Open in Labs
                  <Icon name="external" size="sm" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Independent but interconnected"
        title="Coordinate Academy learning and open the real Instructor Lab workspace when practical work begins."
        text="Chatoner AI Academy Labs operates independently at labs.chatoner.com. This Academy surface handles assignment mapping, verified handoff, synchronized status and returned evidence; the Labs Instructor & Assessor role handles creation, guidance and Lab-native grading."
        actions={
          <>
            <button type="button" className="primary-btn" onClick={() => navigate("labs", "workspace")}>
              <Icon name="external" size="sm" />
              Open Instructor Lab workspace
            </button>
            <button type="button" className="secondary-btn" onClick={() => openModal({ type: "assign-lab" })}>
              <Icon name="plus" size="sm" />
              Assign intended Lab
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard icon="link" title="Connection" detail="Academy ↔ Labs" value="Live" />
            <HeroSideCard icon="activity" title="Synced runs" detail="Across 5 pathways" value="52" />
            <HeroSideCard icon="award" title="Evidence returned" detail="This week" value="28" />
          </>
        }
      />
      <div className="platform-boundary-banner">
        <span>
          <Icon name="shield" />
        </span>
        <div>
          <strong>Clear system boundary</strong>
          <p>
            Academy never pretends to be the Lab. Every creation, guidance, runtime, 1:1, Lab grading,
            feedback and report action opens labs.chatoner.com in the exact Instructor workspace.
          </p>
        </div>
        <TonePill tone="green">SSO connected</TonePill>
      </div>
      <div className="boundary-grid">
        <article className="boundary-card academy">
          <span>
            <Icon name="logo" />
          </span>
          <div>
            <small>Academy instructor</small>
            <h3>Coordinate learning</h3>
            <ul>
              <li>Map program, lesson and assignment</li>
              <li>Select eligible cohort and intended Lab</li>
              <li>Request or verify entitlement</li>
              <li>Review returned evidence in Academy assessment</li>
            </ul>
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() => navigate("labs", "assignments")}
            >
              Open assignments
            </button>
          </div>
        </article>
        <article className="boundary-card labs">
          <span>
            <Icon name="flask" />
          </span>
          <div>
            <small>Labs Instructor & Assessor</small>
            <h3>Create, guide and grade</h3>
            <ul>
              <li>Teaching Overview and 1:1 requests</li>
              <li>Programs, Labs, notes and Exercise Authoring</li>
              <li>Practical grading and feedback</li>
              <li>Learners, cohorts, reports and help</li>
            </ul>
            <button
              type="button"
              className="primary-btn button-sm"
              onClick={() => openWorkspace("dashboard")}
            >
              Open Labs
              <Icon name="external" size="sm" />
            </button>
          </div>
        </article>
      </div>
      <div className="content-grid" style={{ marginTop: 14 }}>
        <AcademyPanel icon="activity" title="Connection health" meta="Operational">
          <div className="sync-status-grid">
            {[
              ["Academy SSO", "Operational"],
              ["Instructor role exchange", "Operational"],
              ["Entitlement validation", "Operational"],
              ["Run-event sync", "Operational"],
              ["Evidence return", "Operational"],
              ["Return URLs", "Allow-listed"],
            ].map(item => (
              <div className="sync-status-card" key={item[0]}>
                <span className="status-dot" />
                <span>
                  <strong>{item[0]}</strong>
                  <small>{item[1]}</small>
                </span>
              </div>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="grid" title="Instructor Lab shortcuts" meta="Open exact destination">
          <div className="priority-list">
            {LAB_INSTRUCTOR_SECTIONS.slice(0, 5).map(section => (
              <button
                type="button"
                className="priority-item"
                key={section.id}
                onClick={() => openWorkspace(section.id)}
              >
                <span className="priority-icon cyan">
                  <Icon name={section.icon} />
                </span>
                <span>
                  <strong>{section.label}</strong>
                  <p>{section.detail}</p>
                </span>
                <span className="priority-meta">
                  <Icon name="external" size="sm" />
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="secondary-btn button-sm"
            onClick={() => navigate("labs", "workspace")}
          >
            See all 9 workspaces
          </button>
        </AcademyPanel>
      </div>
    </div>
  );
}

function ConnectedAssessmentAssignmentDetail({
  assignmentId,
  navigate,
  notify,
}: {
  assignmentId: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const assignments = useInstructorAssessmentAssignments();
  const assignment = assignments.items.find(item => item.id === assignmentId);
  const assessorManagement = useInstructorAssessmentAssessorManagement(
    assignmentId,
    assignment?.access === "lead",
  );
  const [assessorUserId, setAssessorUserId] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const availableAssessors = assessorManagement.items.filter(item => item.assignmentStatus === "available");
  useEffect(() => {
    if (!assignment) return;
    const starts = Math.max(Date.now(), new Date(assignment.opensAt).getTime());
    setValidFrom(new Date(starts).toISOString().slice(0, 16));
    setValidUntil(new Date(assignment.closesAt).toISOString().slice(0, 16));
  }, [assignment]);
  const assignAssessor = async () => {
    if (!assessorUserId || !validFrom || !validUntil) return;
    setAssigning(true);
    setAssignmentError("");
    try {
      const receipt = await assignInstructorAssessmentAssessor(assignmentId, {
        assessorUserId,
        validFrom: new Date(validFrom).toISOString(),
        validUntil: new Date(validUntil).toISOString(),
      });
      notify("Assessor assigned", `Exact assignment ${receipt.id} is active for the selected window.`);
      setAssessorUserId("");
      assessorManagement.refresh();
    } catch (cause) {
      setAssignmentError(cause instanceof Error ? cause.message : "The assessor could not be assigned");
    } finally {
      setAssigning(false);
    }
  };
  return (
    <div className="instructor-page">
      <button
        type="button"
        className="ghost-btn button-sm"
        onClick={() => navigate("assessment", "assignments")}
      >
        <Icon name="arrow-left" size="sm" />
        Back to assignments
      </button>
      {assignments.loading ? <p role="status">Loading assessment assignment…</p> : null}
      {assignments.error ? <p role="alert">{assignments.error}</p> : null}
      {!assignments.loading && !assignments.error && !assignment ? (
        <EmptyState
          icon="clipboard"
          title="Assessment assignment unavailable"
          text="This exact assignment is not in your current lead or assessor scope."
        />
      ) : null}
      {assignment ? (
        <>
          <section className="detail-hero" style={{ marginTop: 12 }}>
            <div className="detail-identity">
              <span className="simple-card-icon">
                <Icon name="clipboard" />
              </span>
              <div>
                <div className="hero-eyebrow">{assignment.cohortName} · Published</div>
                <h2>{assignment.title}</h2>
                <p>{assignment.programName}</p>
              </div>
            </div>
            <TonePill tone={assignment.access === "lead" ? "purple" : "green"}>
              {assignment.access === "lead" ? "Scoped lead" : "Assigned assessor"}
            </TonePill>
          </section>
          <div className="content-grid">
            <AcademyPanel icon="calendar" title="Assessment window" meta="Immutable">
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Opens</strong>
                    <p>{new Date(assignment.opensAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Closes</strong>
                    <p>{new Date(assignment.closesAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Attempt limit</strong>
                    <p>Server-enforced maximum</p>
                  </div>
                  <TonePill tone="blue">{assignment.attemptLimit}</TonePill>
                </div>
              </div>
            </AcademyPanel>
            <AcademyPanel icon="shield" title="Governed sources" meta="Exact versions">
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Scoring policy</strong>
                    <p>Version {assignment.scoringPolicyVersion}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Assessment publication</strong>
                    <p>{assignment.assessmentPublicationId}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Evidence mode</strong>
                    <p>
                      {assignment.sourceKind === "d12_sitting" ? "Verified D12 sitting" : "D10 submission"}
                    </p>
                  </div>
                </div>
                {assignment.learningAssignmentId ? (
                  <div className="setting-row">
                    <div>
                      <strong>Learning assignment</strong>
                      <p>{assignment.learningAssignmentId}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </AcademyPanel>
          </div>
          {assignment.access === "lead" ? (
            <AcademyPanel icon="users" title="Assessor management" meta="Exact current D07 authority">
              {assessorManagement.loading ? <p role="status">Loading eligible assessors…</p> : null}
              {assessorManagement.error ? <p role="alert">{assessorManagement.error}</p> : null}
              {assignmentError ? <p role="alert">{assignmentError}</p> : null}
              {assessorManagement.items
                .filter(item => item.assessorAssignmentId)
                .map(item => (
                  <div className="setting-row" key={item.assessorUserId}>
                    <div>
                      <strong>{item.assessorName}</strong>
                      <p>
                        {item.validFrom ? new Date(item.validFrom).toLocaleString() : "—"} –{" "}
                        {item.validUntil ? new Date(item.validUntil).toLocaleString() : "—"}
                      </p>
                    </div>
                    <TonePill tone={item.assignmentStatus === "current" ? "green" : "default"}>
                      {item.assignmentStatus}
                    </TonePill>
                  </div>
                ))}
              {availableAssessors.length ? (
                <div className="content-grid" style={{ marginTop: 12 }}>
                  <div className="form-field">
                    <label htmlFor="assessment-assessor-candidate">Eligible assessor</label>
                    <select
                      id="assessment-assessor-candidate"
                      value={assessorUserId}
                      onChange={event => setAssessorUserId(event.target.value)}
                    >
                      <option value="">Select an exact current assessor…</option>
                      {availableAssessors.map(item => (
                        <option key={item.assessorUserId} value={item.assessorUserId}>
                          {item.assessorName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="assessment-assessor-valid-from">Valid from</label>
                    <input
                      id="assessment-assessor-valid-from"
                      type="datetime-local"
                      value={validFrom}
                      onChange={event => setValidFrom(event.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="assessment-assessor-valid-until">Valid until</label>
                    <input
                      id="assessment-assessor-valid-until"
                      type="datetime-local"
                      value={validUntil}
                      onChange={event => setValidUntil(event.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={assigning || !assessorUserId || !validFrom || !validUntil}
                    onClick={() => void assignAssessor()}
                  >
                    {assigning ? "Assigning…" : "Assign assessor"}
                  </button>
                </div>
              ) : !assessorManagement.loading && !assessorManagement.error ? (
                <p>No unassigned current D07 assessors are available for this exact scope.</p>
              ) : null}
            </AcademyPanel>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
/* Historical submission-review prototype retained temporarily as design reference only; it is not executable.
function LegacySubmissionReviewPreview({
  submission,
  navigate,
  notify,
}: {
  submission: SubmissionRecord;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [tab, setTab] = useState("work");
  const [scores, setScores] = useState<Record<string, number>>({
    framing: 4,
    implementation: 4,
    evaluation: 3,
    responsible: 4,
    reflection: 3,
  });
  const [feedback, setFeedback] = useState(
    "You have connected the problem, implementation and evidence well. Before approval, verify the third-party widget in staging and name the remaining risk in the final reflection.",
  );
  const [privateNote, setPrivateNote] = useState(
    "Calibration note: strong human-decision trace. Keep staging condition explicit.",
  );
  const criteria = [
    ["framing", "Problem and decision framing", 20],
    ["implementation", "System implementation", 25],
    ["evaluation", "Evaluation and failure evidence", 25],
    ["responsible", "Responsible AI and human authority", 20],
    ["reflection", "Reflection and communication", 10],
  ] as Array<[string, string, number]>;
  const score = Math.round(criteria.reduce((sum, [key, , weight]) => sum + (scores[key] / 4) * weight, 0));
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("assessment", "grading")}>
        <Icon name="arrow-left" size="sm" />
        Back to grading queue
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar initials={submission.initials} />
          <div>
            <div className="hero-eyebrow">
              {submission.status} · submitted {submission.submitted}
            </div>
            <h2>{submission.learner}</h2>
            <p>
              {submission.assignment} · {submission.cohort} · {submission.evidence} evidence items
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <RiskPill
            risk={
              submission.risk === "high"
                ? "intervention"
                : submission.risk === "medium"
                  ? "watch"
                  : "on-track"
            }
          />
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("cohorts", `learner-${submission.learnerId}`)}
          >
            <Icon name="users" size="sm" />
            Learner record
          </button>
          <button
            type="button"
            className="secondary-btn"
            disabled
            title="Preview: D11 formal grading is not connected"
          >
            <Icon name="save" size="sm" />
            Save draft
          </button>
        </div>
      </section>
      <nav className="detail-tabs" aria-label="Submission review sections">
        {["work", "evidence", "rubric", "feedback", "integrity", "audit"].map(item => (
          <button
            type="button"
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item[0].toUpperCase() + item.slice(1)}
          </button>
        ))}
      </nav>
      {tab === "work" ? (
        <div className="review-layout">
          <div className="review-content">
            <AcademyPanel icon="file" title="Learner submission" meta="Original retained">
              <div className="rich-editor">
                <h3>Decision summary</h3>
                <p>
                  I changed the coupon feedback so it is announced politely without moving the learner away
                  from the field. I used AI Builder to propose a patch after I limited it to two files and
                  disabled internet access.
                </p>
                <h3>What I decided</h3>
                <p>
                  I accepted the live-region change but rejected an auto-focus proposal because it interrupted
                  keyboard order. I kept human merge authority and attached the AI activity record.
                </p>
                <h3>Remaining risk</h3>
                <p>
                  The embedded payment widget still needs staging verification because it is outside the
                  isolated preview.
                </p>
              </div>
            </AcademyPanel>
            <AcademyPanel icon="link" title="Connected work" meta="Traceable">
              <div className="evidence-stack">
                <div className="evidence-card">
                  <span>
                    <Icon name="message" />
                  </span>
                  <div>
                    <strong>Kijani checkout Build Room</strong>
                    <p>Discussion, plan, AI scope and instructor checkpoint</p>
                  </div>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => navigate("buildRooms", "group-build-checkout")}
                  >
                    Open
                  </button>
                </div>
                <div className="evidence-card">
                  <span>
                    <Icon name="git" />
                  </span>
                  <div>
                    <strong>Pull request #184</strong>
                    <p>2 files · +27 −6 · learner merge required</p>
                  </div>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => notify("Repository change opened")}
                  >
                    Inspect
                  </button>
                </div>
                <div className="evidence-card">
                  <span>
                    <Icon name="eye" />
                  </span>
                  <div>
                    <strong>Staging preview</strong>
                    <p>Desktop, tablet and mobile states</p>
                  </div>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => navigate("buildRooms", "group-build-checkout")}
                  >
                    Preview
                  </button>
                </div>
              </div>
            </AcademyPanel>
          </div>
          <aside className="decision-panel">
            <AcademyPanel icon="clipboard" title="Review summary" meta="Provisional">
              <div className="donut-wrap" style={{ gridTemplateColumns: "1fr" }}>
                <div style={{ position: "relative", margin: "0 auto" }}>
                  <div
                    className="donut"
                    style={{
                      background: `conic-gradient(var(--purple) 0 ${score}%,rgba(255,255,255,.06) ${score}% 100%)`,
                    }}
                  />
                  <div className="donut-center">
                    <strong>{score}%</strong>
                    <span>provisional</span>
                  </div>
                </div>
              </div>
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>AI disclosure</strong>
                    <p>Prompt, scope and human decisions</p>
                  </div>
                  <TonePill tone={submission.aiDisclosure ? "green" : "red"}>
                    {submission.aiDisclosure ? "Present" : "Missing"}
                  </TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Integrity signal</strong>
                    <p>{submission.integritySignal}</p>
                  </div>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Evidence</strong>
                    <p>Files, tests, preview and reflection</p>
                  </div>
                  <TonePill tone="blue">{submission.evidence}</TonePill>
                </div>
              </div>
              <div className="hero-actions" style={{ display: "grid" }}>
                <button
                  type="button"
                  className="primary-btn"
                  disabled
                  title="Preview: D11 formal grading is not connected"
                >
                  <Icon name="check" size="sm" />
                  Approve · {score}%
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled
                  title="Preview: D11 formal grading is not connected"
                >
                  <Icon name="refresh" size="sm" />
                  Request revision
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  disabled
                  title="Preview: D11 moderation is not connected"
                >
                  <Icon name="shield" size="sm" />
                  Send to moderation
                </button>
              </div>
              <p>Preview: formal grading and moderation require the D11 assessment integration.</p>
            </AcademyPanel>
          </aside>
        </div>
      ) : null}
      {tab === "evidence" ? (
        <div className="content-grid">
          <AcademyPanel icon="award" title="Evidence pack" meta={`${submission.evidence} items`}>
            <div className="evidence-stack">
              {[
                ["image", "coupon-error-mobile.png", "Annotated defect · UX-04"],
                ["video", "keyboard-and-announcement.webm", "Human test · captions included"],
                ["test", "checkout-a11y-184.json", "18/18 automated checks"],
                ["git", "Pull request #184", "Implementation and learner merge"],
                ["file", "AI activity record CB-184", "Prompt, scope, permissions and stop history"],
                ["file", "Reflection.md", "Decision, rejection and remaining risk"],
              ]
                .slice(0, Math.min(6, submission.evidence))
                .map(item => (
                  <div className="evidence-card" key={item[1]}>
                    <span>
                      <Icon name={item[0]} />
                    </span>
                    <div>
                      <strong>{item[1]}</strong>
                      <p>{item[2]}</p>
                    </div>
                    <button
                      type="button"
                      className="secondary-btn button-sm"
                      onClick={() => notify("Evidence opened", item[1])}
                    >
                      Open
                    </button>
                  </div>
                ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="workflow" title="Requirement traceability" meta="4 requirements">
            <div className="requirement-list">
              {[
                ["UX-04", "Accessible error feedback", "Verified"],
                ["ACC-02", "Keyboard and screen-reader validation", "Verified"],
                ["SEC-06", "Server-side data boundary", "Verified"],
                ["EVAL-03", "Automated + human evaluation", "Condition"],
              ].map(item => (
                <div className="requirement-row" key={item[0]}>
                  <code>{item[0]}</code>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>Evidence connected and attributable</span>
                  </span>
                  <TonePill tone={item[2] === "Verified" ? "green" : "orange"}>{item[2]}</TonePill>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "rubric" ? (
        <div className="review-layout">
          <AcademyPanel icon="evidence" title="Product Evidence Rubric v3" meta={`${score}% provisional`}>
            <div className="rubric-grid">
              {criteria.map(([key, label, weight]) => (
                <div className="rubric-criterion" key={key}>
                  <div className="rubric-criterion-head">
                    <strong>{label}</strong>
                    <TonePill tone="purple">{weight}%</TonePill>
                  </div>
                  <p style={{ margin: "6px 0 0", color: "var(--muted)", fontSize: 14 }}>
                    1 insufficient · 2 developing · 3 competent · 4 exceptional
                  </p>
                  <div className="score-buttons">
                    {[0, 1, 2, 3, 4].map(value => (
                      <button
                        type="button"
                        key={value}
                        className={scores[key] === value ? "active" : ""}
                        onClick={() => setScores(current => ({ ...current, [key]: value }))}
                      >
                        {value === 0 ? "N/A" : value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <aside className="decision-panel">
            <AcademyPanel icon="chart" title="Weighted score" meta="Updates live">
              <div style={{ textAlign: "center", padding: 14 }}>
                <strong style={{ fontSize: 44 }}>{score}%</strong>
                <p style={{ color: "var(--muted)", fontSize: 14 }}>Pass threshold 70% · distinction 90%</p>
              </div>
              <ProgressMeter value={score} />
              <div className="decision-callout" style={{ marginTop: 12 }}>
                <strong>Human academic decision</strong>
                <p>
                  The calculated score supports the decision; it does not make it. The instructor must review
                  evidence, risk, feedback and any moderation requirement.
                </p>
              </div>
            </AcademyPanel>
          </aside>
        </div>
      ) : null}
      {tab === "feedback" ? (
        <div className="content-grid">
          <AcademyPanel icon="message" title="Learner-visible feedback" meta="Required">
            <div className="form-field">
              <label>Feedback</label>
              <textarea
                value={feedback}
                onChange={event => setFeedback(event.target.value)}
                style={{ minHeight: 240 }}
              />
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => notify("Feedback template inserted")}
              >
                Insert template
              </button>
              <button
                type="button"
                className="primary-btn button-sm"
                disabled
                title="Preview: D11 assessment feedback is not connected"
              >
                Save feedback
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="lock" title="Private moderation note" meta="Restricted">
            <div className="form-field">
              <label>Private note</label>
              <textarea
                value={privateNote}
                onChange={event => setPrivateNote(event.target.value)}
                style={{ minHeight: 180 }}
              />
            </div>
            <div className="modal-form-note">
              <Icon name="lock" size="sm" />
              This note is excluded from learner feedback and standard exports. Access is restricted to
              assigned reviewers and Academic Operations.
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "integrity" ? (
        <div className="content-grid">
          <AcademyPanel icon="shield" title="Integrity and AI disclosure" meta={submission.risk}>
            <div className="check-list">
              {[
                [
                  submission.aiDisclosure,
                  "AI use disclosed",
                  "Invocations, prompts and relevant outputs are visible.",
                ],
                [true, "Human decisions identified", "Accepted and rejected suggestions are explained."],
                [submission.risk !== "high", "Source and data trail clear", submission.integritySignal],
                [
                  submission.evidence >= 4,
                  "Sufficient evidence",
                  "Artifacts support the claims made in the reflection.",
                ],
              ].map((item, index) => (
                <div className={`check-row ${item[0] ? "done" : ""}`} key={index}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>{item[2]}</span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="alert" title="Instructor interpretation" meta="Do not automate misconduct">
            <p style={{ margin: 0, color: "var(--muted)", fontSize: 14, lineHeight: 1.6 }}>
              Signals are prompts for human review, not findings of misconduct. Consider the learner’s
              process, disclosure, source trail, accommodations and evidence before deciding any action.
            </p>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                disabled
                title="Preview: D11 integrity review is not connected"
              >
                Record integrity review
              </button>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "audit" ? (
        <AcademyPanel icon="activity" title="Review audit trail" meta="Immutable decisions">
          <div className="timeline">
            {[
              ["upload", "Submission received", `${submission.learner} · ${submission.submitted}`],
              [
                "shield",
                "Automated evidence checks completed",
                "Structure, links and file safety · no academic decision",
              ],
              ["eye", "Instructor opened review", "Amina Mutesi · Today 11:24 AM"],
              ["save", "Draft rubric scores saved", `${score}% provisional · not visible to learner`],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>Recorded</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
    </div>
  );
}
*/

function assessmentEvaluationInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("");
}

function ConnectedAssessmentEvaluationQueue({
  page,
  navigate,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const { items, error, loading, refresh } = useInstructorAssessmentEvaluationQueue();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All statuses");
  const now = Date.now();
  const filtered = items.filter(item => {
    const matchesQuery = `${item.learnerName} ${item.title} ${item.cohortName}`
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesStatus =
      status === "All statuses" ||
      (status === "Pending" && !item.evaluationId) ||
      (status === "Finalized" && Boolean(item.evaluationId));
    const matchesAge =
      page !== "queue-older-24h" || now - new Date(item.submittedAt).getTime() >= 24 * 60 * 60 * 1000;
    const matchesSubset = page !== "queue-high-risk" || Boolean(item.integrityReferral);
    return matchesQuery && matchesStatus && matchesAge && matchesSubset;
  });
  const title =
    page === "grading"
      ? "Grading queue"
      : page === "queue-older-24h"
        ? "Reviews older than 24 hours"
        : page === "queue-high-risk"
          ? "High-risk evidence queue"
          : "Credential evidence queue";
  const unsupportedSubset = page === "queue-credential";
  return (
    <div className="instructor-page">
      <PageTitle
        title={title}
        text="Exact submitted attempts currently assigned to you. Evaluation does not publish an official result."
        actions={
          <button type="button" className="secondary-btn" onClick={refresh}>
            <Icon name="refresh" size="sm" />
            Refresh
          </button>
        }
      />
      <div className="stat-grid">
        <StatTile
          icon="clipboard"
          label="Pending"
          value={items.filter(item => !item.evaluationId).length}
          detail="Exact attempts assigned to you"
        />
        <StatTile
          icon="check-circle"
          label="Finalized"
          value={items.filter(item => item.evaluationId).length}
          detail="Immutable evaluations"
          tone="cyan"
        />
        {page === "queue-high-risk" ? (
          <StatTile
            icon="shield"
            label="Factual referrals"
            value={items.filter(item => item.integrityReferral).length}
            detail="Not misconduct verdicts"
            tone="orange"
          />
        ) : null}
      </div>
      {unsupportedSubset ? (
        <AcademyPanel icon="shield" title="Governed subset unavailable" meta="No inferred classification">
          <p>
            This exact queue does not yet expose governed integrity-risk or credential-readiness decisions. No
            browser-derived substitute is shown.
          </p>
        </AcademyPanel>
      ) : loading ? (
        <AcademyPanel icon="clipboard" title="Loading assigned attempts" meta="Current scope">
          <p>Checking your exact assessor assignments…</p>
        </AcademyPanel>
      ) : error ? (
        <AcademyPanel icon="alert" title="Evaluation queue unavailable" meta="Fail closed">
          <p>{error}</p>
          <button type="button" className="secondary-btn button-sm" onClick={refresh}>
            Retry
          </button>
        </AcademyPanel>
      ) : (
        <AcademyPanel icon="clipboard" title="Assigned attempts" meta={`${filtered.length} exact records`}>
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search learner, assessment or cohort…"
              />
            </div>
            <select className="select-field" value={status} onChange={event => setStatus(event.target.value)}>
              <option>All statuses</option>
              <option>Pending</option>
              <option>Finalized</option>
            </select>
          </div>
          {!filtered.length ? (
            <div className="empty-state">
              <Icon name="clipboard" />
              <h3>No assigned attempts match</h3>
              <p>Only exact current assessor assignments appear here.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Assessment</th>
                    <th>Attempt</th>
                    <th>Submitted</th>
                    <th>Evidence</th>
                    <th>Status</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(item => (
                    <tr
                      key={item.attemptId}
                      onClick={() => navigate("assessment", `submission-${item.attemptId}`)}
                    >
                      <td>
                        <div className="person-cell">
                          <Avatar initials={assessmentEvaluationInitials(item.learnerName)} small />
                          <span>
                            <strong>{item.learnerName}</strong>
                            <span>{item.cohortName}</span>
                          </span>
                        </div>
                      </td>
                      <td>{item.title}</td>
                      <td>
                        #{item.attemptNumber} · {item.sourceKind === "d12_sitting" ? "Exam" : "Submission"}
                      </td>
                      <td>{new Date(item.submittedAt).toLocaleString()}</td>
                      <td>{item.fileIds.length + (item.responseText ? 1 : 0)}</td>
                      <td>
                        <TonePill
                          tone={item.integrityReferral ? "orange" : item.evaluationId ? "green" : "purple"}
                        >
                          {item.integrityReferral ? "Referred" : item.evaluationId ? "Finalized" : "Pending"}
                        </TonePill>
                      </td>
                      <td>{item.outcomeLabel ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AcademyPanel>
      )}
    </div>
  );
}

function ConnectedAssessmentEvaluationDetail({
  attemptId,
  navigate,
  notify,
}: {
  attemptId: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const { items, error, loading, refresh } = useInstructorAssessmentEvaluationQueue();
  const item: InstructorAssessmentEvaluationQueueItem | undefined = items.find(
    candidate => candidate.attemptId === attemptId,
  );
  const [scores, setScores] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState("");
  const [restrictedNote, setRestrictedNote] = useState("");
  const [integrityCategory, setIntegrityCategory] =
    useState<InstructorAssessmentIntegrityReferralCategory>("authorship_concern");
  const [integritySummary, setIntegritySummary] = useState("");
  const [integrityEvidenceFileIds, setIntegrityEvidenceFileIds] = useState<string[]>([]);
  const [referringIntegrity, setReferringIntegrity] = useState(false);
  const [integrityError, setIntegrityError] = useState("");
  const [saving, setSaving] = useState(false);
  const [commandError, setCommandError] = useState("");
  useEffect(() => {
    if (!item || item.evaluationId) return;
    setScores(current =>
      Object.keys(current).length
        ? current
        : Object.fromEntries(item.criteria.map(criterion => [criterion.code, ""])),
    );
  }, [item]);
  if (loading)
    return (
      <AcademyPanel icon="clipboard" title="Loading evaluation" meta="Current scope">
        <p>Checking the exact assigned attempt…</p>
      </AcademyPanel>
    );
  if (error || !item)
    return (
      <div className="instructor-page">
        <button
          type="button"
          className="ghost-btn button-sm"
          onClick={() => navigate("assessment", "grading")}
        >
          <Icon name="arrow-left" size="sm" /> Back to grading queue
        </button>
        <AcademyPanel icon="alert" title="Evaluation unavailable" meta="Fail closed">
          <p>{error || "This attempt is not currently assigned to you."}</p>
        </AcademyPanel>
      </div>
    );
  const complete = item.criteria.every(criterion => scores[criterion.code] !== "");
  const decisions = item.criteria.map(criterion => ({
    criterionCode: criterion.code,
    points: Number(scores[criterion.code]),
  }));
  const valid =
    complete &&
    decisions.every(
      (decision, index) =>
        Number.isInteger(decision.points) &&
        decision.points >= 0 &&
        decision.points <= item.criteria[index].maxPoints,
    ) &&
    feedback.trim().length >= 5 &&
    (!restrictedNote.trim() || restrictedNote.trim().length >= 5);
  const previewBasisPoints = complete
    ? Math.round(
        decisions.reduce(
          (sum, decision, index) =>
            sum + (decision.points * item.criteria[index].weightBasisPoints) / item.criteria[index].maxPoints,
          0,
        ),
      )
    : null;
  const finalize = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setCommandError("");
    try {
      const receipt = await finalizeInstructorAssessmentEvaluation(item.attemptId, {
        criterionDecisions: decisions,
        releasableFeedback: feedback.trim(),
        ...(restrictedNote.trim() ? { restrictedNote: restrictedNote.trim() } : {}),
      });
      notify(
        "Evaluation finalized",
        `${receipt.outcomeLabel} · ${(receipt.scoreBasisPoints / 100).toFixed(2)}%`,
      );
      refresh();
    } catch (cause) {
      setCommandError(cause instanceof Error ? cause.message : "Evaluation could not be finalized");
    } finally {
      setSaving(false);
    }
  };
  const referIntegrity = async () => {
    if (item.integrityReferral || integritySummary.trim().length < 20 || referringIntegrity) return;
    setReferringIntegrity(true);
    setIntegrityError("");
    try {
      await referInstructorAssessmentIntegrity(item.attemptId, {
        category: integrityCategory,
        factualSummary: integritySummary.trim(),
        evidenceFileIds: integrityEvidenceFileIds,
      });
      notify(
        "Factual integrity concern referred",
        "Human review is required; no verdict or score change was made.",
      );
      refresh();
    } catch (cause) {
      setIntegrityError(cause instanceof Error ? cause.message : "Integrity concern could not be referred");
    } finally {
      setReferringIntegrity(false);
    }
  };
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("assessment", "grading")}>
        <Icon name="arrow-left" size="sm" /> Back to grading queue
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar initials={assessmentEvaluationInitials(item.learnerName)} />
          <div>
            <div className="hero-eyebrow">
              {item.evaluationId ? "Finalized" : "Pending evaluation"} · submitted{" "}
              {new Date(item.submittedAt).toLocaleString()}
            </div>
            <h2>{item.learnerName}</h2>
            <p>
              {item.title} · {item.cohortName} · attempt {item.attemptNumber}
            </p>
          </div>
        </div>
      </section>
      <div className="review-layout">
        <div className="review-content">
          <AcademyPanel
            icon="file"
            title={
              item.sourceKind === "d12_sitting" ? "Exact secure-room response" : "Exact learner submission"
            }
            meta={
              item.sourceKind === "d12_sitting"
                ? `Verified sitting ${item.sittingEvidenceId?.slice(0, 12) ?? "unavailable"}…`
                : `Version ${item.submissionVersion}`
            }
          >
            {item.responseText ? (
              <div className="rich-editor">
                <p>{item.responseText}</p>
              </div>
            ) : (
              <p>No text response was submitted.</p>
            )}
            <div className="action-list">
              {item.fileIds.map(fileId => (
                <div className="setting-row" key={fileId}>
                  <div>
                    <strong>File evidence reference</strong>
                    <p>{fileId}</p>
                  </div>
                  <TonePill tone="blue">Pinned</TonePill>
                </div>
              ))}
            </div>
            <p>
              Evidence snapshot: <code>{item.evidenceSnapshotHash}</code>
            </p>
            {item.responseCommitment ? (
              <p>
                Response commitment: <code>{item.responseCommitment}</code>
              </p>
            ) : null}
          </AcademyPanel>
          <AcademyPanel
            icon="shield"
            title="Assessment integrity"
            meta={item.integrityReferral ? "Immutable factual referral" : "Human referral only"}
          >
            {item.integrityReferral ? (
              <>
                <TonePill tone="orange">{item.integrityReferral.category.replace(/_/g, " ")}</TonePill>
                <p>{item.integrityReferral.factualSummary}</p>
                <div className="action-list">
                  {item.integrityReferral.evidenceFileIds.map(fileId => (
                    <div className="setting-row" key={fileId}>
                      <div>
                        <strong>Pinned evidence reference</strong>
                        <p>{fileId}</p>
                      </div>
                      <TonePill tone="blue">Referenced</TonePill>
                    </div>
                  ))}
                </div>
                <p>
                  Referral snapshot: <code>{item.integrityReferral.referralSnapshotHash}</code>
                </p>
              </>
            ) : (
              <div className="form-stack">
                <div className="form-field">
                  <label htmlFor="integrity-referral-category">Concern category</label>
                  <select
                    id="integrity-referral-category"
                    value={integrityCategory}
                    onChange={event =>
                      setIntegrityCategory(
                        event.target.value as InstructorAssessmentIntegrityReferralCategory,
                      )
                    }
                  >
                    <option value="authorship_concern">Authorship concern</option>
                    <option value="prohibited_assistance_concern">Prohibited assistance concern</option>
                    <option value="evidence_provenance_concern">Evidence provenance concern</option>
                    <option value="process_irregularity">Process irregularity</option>
                    <option value="other">Other factual concern</option>
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="integrity-referral-summary">Factual observations</label>
                  <textarea
                    id="integrity-referral-summary"
                    value={integritySummary}
                    onChange={event => setIntegritySummary(event.target.value)}
                    placeholder="Record observed facts only. Do not enter a misconduct verdict or safeguarding, health or support-case narrative."
                    style={{ minHeight: 140 }}
                  />
                </div>
                {item.fileIds.length ? (
                  <div className="form-field">
                    <label>Relevant pinned submission files (optional)</label>
                    {item.fileIds.map(fileId => (
                      <label className="setting-row" key={fileId}>
                        <span>
                          <strong>File evidence reference</strong>
                          <span>{fileId}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={integrityEvidenceFileIds.includes(fileId)}
                          onChange={event =>
                            setIntegrityEvidenceFileIds(current =>
                              event.target.checked
                                ? [...current, fileId]
                                : current.filter(candidate => candidate !== fileId),
                            )
                          }
                        />
                      </label>
                    ))}
                  </div>
                ) : null}
                {integrityError ? <p className="form-error">{integrityError}</p> : null}
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={integritySummary.trim().length < 20 || referringIntegrity}
                  onClick={() => void referIntegrity()}
                >
                  <Icon name="shield" size="sm" />
                  {referringIntegrity ? "Referring…" : "Refer factual concern"}
                </button>
              </div>
            )}
            <div className="modal-form-note">
              <Icon name="shield" size="sm" />A referral blocks result publication pending governed human
              review. It is not a verdict, sanction, score or support case.
            </div>
          </AcademyPanel>
          <AcademyPanel
            icon="evidence"
            title="Published scoring policy"
            meta={`Version ${item.scoringPolicyVersion}`}
          >
            <div className="rubric-grid">
              {item.criteria.map(criterion => (
                <div className="rubric-criterion" key={criterion.code}>
                  <div className="rubric-criterion-head">
                    <strong>{criterion.code}</strong>
                    <TonePill tone="purple">{(criterion.weightBasisPoints / 100).toFixed(2)}%</TonePill>
                  </div>
                  <div className="form-field">
                    <label htmlFor={`score-${criterion.code}`}>Points (0–{criterion.maxPoints})</label>
                    <input
                      id={`score-${criterion.code}`}
                      type="number"
                      min={0}
                      max={criterion.maxPoints}
                      step={1}
                      disabled={Boolean(item.evaluationId)}
                      value={item.evaluationId ? "" : (scores[criterion.code] ?? "")}
                      onChange={event =>
                        setScores(current => ({ ...current, [criterion.code]: event.target.value }))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            <p>
              Policy hash: <code>{item.scoringPolicyDefinitionHash}</code>
            </p>
          </AcademyPanel>
          <AcademyPanel icon="message" title="Learner-visible feedback" meta="Required">
            {item.evaluationId ? (
              <p>{item.releasableFeedback}</p>
            ) : (
              <div className="form-field">
                <label htmlFor="releasable-evaluation-feedback">Feedback</label>
                <textarea
                  id="releasable-evaluation-feedback"
                  value={feedback}
                  onChange={event => setFeedback(event.target.value)}
                  style={{ minHeight: 180 }}
                />
              </div>
            )}
          </AcademyPanel>
          <AcademyPanel icon="lock" title="Private moderation note" meta="Restricted">
            {item.evaluationId ? (
              <p>{item.restrictedNote || "No restricted note was recorded."}</p>
            ) : (
              <div className="form-field">
                <label htmlFor="restricted-evaluation-note">Private note (optional)</label>
                <textarea
                  id="restricted-evaluation-note"
                  value={restrictedNote}
                  onChange={event => setRestrictedNote(event.target.value)}
                  placeholder="Visible only to the exact current assessor. Do not enter safeguarding, health or support-case narratives."
                  style={{ minHeight: 140 }}
                />
              </div>
            )}
            <div className="modal-form-note">
              <Icon name="lock" size="sm" />
              Encrypted separately and excluded from learner feedback, evaluation snapshots, audit events,
              outbox events and released results.
            </div>
          </AcademyPanel>
        </div>
        <aside className="decision-panel">
          <AcademyPanel
            icon="clipboard"
            title="Human evaluation"
            meta={item.evaluationId ? "Immutable" : "Not yet finalized"}
          >
            {item.evaluationId ? (
              <>
                <strong style={{ fontSize: 36 }}>{((item.scoreBasisPoints ?? 0) / 100).toFixed(2)}%</strong>
                <p>{item.outcomeLabel}</p>
                <p>
                  Evaluation snapshot: <code>{item.evaluationSnapshotHash}</code>
                </p>
              </>
            ) : (
              <>
                <strong style={{ fontSize: 36 }}>
                  {previewBasisPoints === null ? "—" : `${(previewBasisPoints / 100).toFixed(2)}%`}
                </strong>
                <p>Preview only. The server calculates the authoritative score and outcome.</p>
                {commandError ? <p className="form-error">{commandError}</p> : null}
                <button
                  type="button"
                  className="primary-btn"
                  disabled={!valid || saving}
                  onClick={() => void finalize()}
                >
                  <Icon name="check" size="sm" /> {saving ? "Finalizing…" : "Finalize evaluation"}
                </button>
              </>
            )}
            <p>Moderation and official result publication remain separate governed decisions.</p>
          </AcademyPanel>
        </aside>
      </div>
    </div>
  );
}

function AssessmentBuilderView({
  notify,
  navigate,
}: {
  notify: (title: string, detail?: string) => void;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const [title, setTitle] = useState("Responsible AI Product Decisions");
  const [questions, setQuestions] = useState([
    {
      id: "q1",
      type: "Scenario",
      title: "A learner’s agent proposes removing a human approval step. What should happen?",
      points: 10,
    },
    {
      id: "q2",
      type: "Multiple choice",
      title: "Which artifact best demonstrates a model evaluation failure?",
      points: 5,
    },
    {
      id: "q3",
      type: "Short response",
      title: "Explain why a signed handoff token is safer than editable query parameters.",
      points: 10,
    },
    {
      id: "q4",
      type: "Practical",
      title: "Inspect a Build Room AI activity record and identify two permission risks.",
      points: 25,
    },
  ]);
  const add = () =>
    setQuestions(current => [
      ...current,
      { id: `q${current.length + 1}`, type: "Multiple choice", title: "New assessment question", points: 5 },
    ]);
  return (
    <div className="instructor-page">
      <button
        type="button"
        className="ghost-btn button-sm"
        onClick={() => navigate("assessment", "assessments")}
      >
        <Icon name="arrow-left" size="sm" />
        Back to assessments
      </button>
      <PageTitle
        title="Assessment builder"
        text="Preview · Create a valid, accessible assessment with explicit learning alignment and a human publishing decision."
        actions={
          <>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => notify("Assessment preview opened")}
            >
              <Icon name="eye" size="sm" />
              Preview
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled
              title="Preview: D11 assessment publication is not connected"
            >
              <Icon name="upload" size="sm" />
              Publish
            </button>
          </>
        }
      />
      <div className="builder-layout">
        <div className="builder-stage">
          <AcademyPanel
            icon="test"
            title="Question sequence"
            meta={`${questions.length} items · ${questions.reduce((sum, item) => sum + item.points, 0)} points`}
          >
            <div className="form-field" style={{ marginBottom: 12 }}>
              <label>Assessment title</label>
              <input value={title} onChange={event => setTitle(event.target.value)} />
            </div>
            <div className="question-sequence">
              {questions.map((question, index) => (
                <div className="sequence-item" key={question.id}>
                  <span className="sequence-number">{index + 1}</span>
                  <div>
                    <strong>{question.title}</strong>
                    <p>
                      {question.type} · {question.points} points · APB-M6/M8
                    </p>
                  </div>
                  <div className="row-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => notify("Question moved up", question.title)}
                    >
                      <Icon name="arrow-up" size="sm" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => notify("Question editor opened", question.title)}
                    >
                      <Icon name="edit" size="sm" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setQuestions(current => current.filter(item => item.id !== question.id))}
                    >
                      <Icon name="trash" size="sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button type="button" className="primary-btn button-sm" onClick={add}>
                <Icon name="plus" size="sm" />
                Add question
              </button>
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => navigate("assessment", "questions")}
              >
                <Icon name="book" size="sm" />
                From question bank
              </button>
            </div>
          </AcademyPanel>
        </div>
        <div className="builder-side">
          <AcademyPanel icon="settings" title="Assessment settings" meta="Draft">
            <div className="form-stack">
              <div className="form-field">
                <label>Type</label>
                <select>
                  <option>Scenario + practical</option>
                  <option>Quiz</option>
                  <option>Oral defense</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Time limit</label>
                  <select>
                    <option>60 minutes</option>
                    <option>90 minutes</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Attempts</label>
                  <select>
                    <option>1</option>
                    <option>2</option>
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Pass mark</label>
                <input type="number" defaultValue="70" />
              </div>
              <div className="setting-row">
                <div>
                  <strong>Randomise question order</strong>
                  <p>Keep scenario prerequisites grouped</p>
                </div>
                <Switch value={false} label="Randomise" onChange={() => notify("Randomisation changed")} />
              </div>
              <div className="setting-row">
                <div>
                  <strong>Accessible timer</strong>
                  <p>Announcements at 30, 10 and 5 minutes</p>
                </div>
                <Switch
                  value={true}
                  label="Accessible timer"
                  onChange={() => notify("Timer accessibility changed")}
                />
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Quality gate" meta="3/4 ready">
            <div className="check-list">
              {[
                "Outcomes aligned",
                "Answer key complete",
                "Accessibility review passed",
                "Independent calibration pending",
              ].map((item, index) => (
                <div className={`check-row ${index < 3 ? "done" : ""}`} key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>{index < 3 ? "Ready" : "Required before publishing to high-stakes use"}</span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    </div>
  );
}

function QuestionEditorView({
  notify,
  navigate,
}: {
  notify: (title: string, detail?: string) => void;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const [type, setType] = useState("Scenario");
  const [prompt, setPrompt] = useState(
    "A project team asks an AI coding agent to change a checkout flow. The agent requests repository write access, internet access and permission to merge. Which permissions should the instructor approve, and why?",
  );
  return (
    <div className="instructor-page">
      <button
        type="button"
        className="ghost-btn button-sm"
        onClick={() => navigate("assessment", "questions")}
      >
        <Icon name="arrow-left" size="sm" />
        Back to question bank
      </button>
      <PageTitle
        title="Question editor"
        text="Create an aligned item with an explainable answer, useful feedback and accessibility checks."
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={() => notify("Question preview opened")}>
              <Icon name="eye" size="sm" />
              Preview
            </button>
            <button type="button" className="primary-btn" onClick={() => notify("Question saved")}>
              Save question
            </button>
          </>
        }
      />
      <div className="builder-layout">
        <AcademyPanel icon="edit" title="Question content" meta="Draft">
          <div className="form-stack">
            <div className="form-row">
              <div className="form-field">
                <label>Type</label>
                <select value={type} onChange={event => setType(event.target.value)}>
                  <option>Scenario</option>
                  <option>Multiple choice</option>
                  <option>Short response</option>
                  <option>Practical</option>
                </select>
              </div>
              <div className="form-field">
                <label>Difficulty</label>
                <select defaultValue="Applied">
                  <option>Foundation</option>
                  <option>Applied</option>
                  <option>Advanced</option>
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>Prompt</label>
              <textarea
                value={prompt}
                onChange={event => setPrompt(event.target.value)}
                style={{ minHeight: 180 }}
              />
            </div>
            <div className="form-field">
              <label>Expected answer / marking guidance</label>
              <textarea
                defaultValue="Approve only the minimum repository scope in an isolated branch. Keep internet disabled unless a named domain and purpose are required. Do not grant merge permission; a learner must inspect tests and make the final merge decision. The answer should mention traceability and immediate stop controls."
                style={{ minHeight: 170 }}
              />
            </div>
            <div className="form-field">
              <label>Learner feedback</label>
              <textarea defaultValue="Look for minimum access, visible AI work, a human merge decision and a reasoned exception process." />
            </div>
          </div>
        </AcademyPanel>
        <div className="builder-side">
          <AcademyPanel icon="workflow" title="Alignment" meta="Required">
            <div className="form-field">
              <label>Learning outcome</label>
              <select>
                <option>APB-M8 · Preserve human authority in AI-assisted systems</option>
                <option>APB-M6 · Evaluate trustworthy interfaces</option>
              </select>
            </div>
            <div className="form-field" style={{ marginTop: 10 }}>
              <label>Tags</label>
              <div className="tag-row">
                <span className="tag-chip active">AI agents</span>
                <span className="tag-chip active">Permissions</span>
                <span className="tag-chip active">Human authority</span>
                <button type="button" className="tag-chip" onClick={() => notify("Tag editor opened")}>
                  <Icon name="plus" size="sm" />
                  Add
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Item quality" meta="After use">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Usage</strong>
                  <p>4 assessments</p>
                </div>
                <TonePill>4</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Difficulty index</strong>
                  <p>Expected correct rate</p>
                </div>
                <TonePill tone="blue">0.68</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Discrimination</strong>
                  <p>Separates stronger evidence</p>
                </div>
                <TonePill tone="green">0.42</TonePill>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Accessibility" meta="Ready">
            <div className="check-list">
              {[
                "Prompt is readable without visual context",
                "Time expectation is reasonable",
                "Feedback explains the decision",
                "No cultural assumption blocks the answer",
              ].map(item => (
                <div className="check-row done" key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    </div>
  );
}

function ConnectedRubricLibraryView({
  navigate,
}: {
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const policies = useInstructorAssessmentPolicies();
  return (
    <div className="instructor-page">
      <PageTitle
        title="Rubrics"
        text="Published assessment rubrics and governed D11 scoring-policy status in your current assessor scope."
        actions={
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("assessment", "policy-reviews")}
          >
            <Icon name="shield" size="sm" /> Independent reviews
          </button>
        }
      />
      {policies.loading ? <p role="status">Loading scoring-policy records…</p> : null}
      {policies.error ? <p role="alert">{policies.error}</p> : null}
      {!policies.loading && !policies.error && !policies.items.length ? (
        <EmptyState
          icon="evidence"
          title="No published assessment rubrics"
          text="A governed assessment publication in your current scope is required before a scoring policy can be authored."
        />
      ) : null}
      <div className="simple-card-grid">
        {policies.items.map(item => (
          <button
            type="button"
            className="simple-card"
            key={item.assessmentPublicationId}
            onClick={() => navigate("assessment", `rubric-${item.assessmentPublicationId}`)}
          >
            <div className="simple-card-head">
              <span className="simple-card-icon">
                <Icon name="evidence" />
              </span>
              <div className="simple-card-copy">
                <h3>{item.title}</h3>
                <p>Published assessment · {item.rubricCriteria.length} criteria</p>
              </div>
              <TonePill
                tone={item.state === "published" ? "green" : item.state === "in_review" ? "orange" : "purple"}
              >
                {item.state === "in_review"
                  ? "In review"
                  : item.state === "published"
                    ? "Published"
                    : item.state === "rejected"
                      ? "Changes required"
                      : "Policy required"}
              </TonePill>
            </div>
            <div className="simple-card-body">
              <div className="meta-pair">
                <span>Rubric version</span>
                <strong>{item.rubricVersionLabel}</strong>
              </div>
              <div className="meta-pair">
                <span>Policy version</span>
                <strong>{item.policyVersion ?? "Not submitted"}</strong>
              </div>
              <div className="meta-pair">
                <span>Decision</span>
                <strong>{item.decision ?? "Pending author action"}</strong>
              </div>
            </div>
            <div className="simple-card-foot">
              <span>Open exact rubric</span>
              <Icon name="arrow-right" size="sm" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ConnectedRubricDetailView({
  publicationId,
  navigate,
  notify,
}: {
  publicationId: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const policies = useInstructorAssessmentPolicies();
  const policy = policies.items.find(item => item.assessmentPublicationId === publicationId);
  if (policies.loading)
    return <EmptyState icon="evidence" title="Loading rubric" text="Checking the exact publication." />;
  if (policies.error) return <EmptyState icon="evidence" title="Rubric unavailable" text={policies.error} />;
  if (!policy)
    return (
      <EmptyState icon="evidence" title="Rubric not found" text="This publication is outside your scope." />
    );
  return (
    <ScoringPolicyProposalView
      policy={policy}
      navigate={navigate}
      notify={notify}
      refresh={policies.refresh}
    />
  );
}

function ScoringPolicyReviewQueueView({
  navigate,
}: {
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const reviews = useInstructorScoringPolicyReviews();
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("assessment", "rubrics")}>
        <Icon name="arrow-left" size="sm" /> Back to rubrics
      </button>
      <PageTitle
        title="Independent scoring-policy reviews"
        text="Exact submitted definitions requiring a different authorized human reviewer and recent MFA."
      />
      {reviews.loading ? <p role="status">Loading policy reviews…</p> : null}
      {reviews.error ? <p role="alert">{reviews.error}</p> : null}
      {!reviews.loading && !reviews.error && !reviews.items.length ? (
        <EmptyState
          icon="shield"
          title="No policy reviews"
          text="There are no independently reviewable proposals in your current approval scope."
        />
      ) : null}
      <div className="simple-card-grid">
        {reviews.items.map(item => (
          <button
            type="button"
            className="simple-card"
            key={item.proposalId}
            onClick={() => navigate("assessment", `policy-review-${item.proposalId}`)}
          >
            <div className="simple-card-head">
              <span className="simple-card-icon">
                <Icon name="shield" />
              </span>
              <div className="simple-card-copy">
                <h3>{item.rationale}</h3>
                <p>
                  {item.criteria.length} criteria · {item.outcomeBands.length} outcome bands
                </p>
              </div>
              <TonePill tone="orange">Review</TonePill>
            </div>
            <div className="simple-card-foot">
              <span>Definition {item.definitionHash.slice(0, 12)}…</span>
              <Icon name="arrow-right" size="sm" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScoringPolicyReviewView({
  proposalId,
  navigate,
  notify,
}: {
  proposalId: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const reviews = useInstructorScoringPolicyReviews();
  const proposal = reviews.items.find(item => item.proposalId === proposalId);
  if (reviews.loading)
    return <EmptyState icon="shield" title="Loading policy review" text="Checking current approval scope." />;
  if (reviews.error)
    return <EmptyState icon="shield" title="Policy review unavailable" text={reviews.error} />;
  if (!proposal)
    return (
      <EmptyState
        icon="shield"
        title="Policy review not found"
        text="This proposal is no longer reviewable in your scope."
      />
    );
  return (
    <ScoringPolicyDecisionForm
      proposal={proposal}
      navigate={navigate}
      notify={notify}
      refresh={reviews.refresh}
    />
  );
}

function ScoringPolicyDecisionForm({
  proposal,
  navigate,
  notify,
  refresh,
}: {
  proposal: InstructorScoringPolicyProposal;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  refresh: () => void;
}) {
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const decide = async (decision: "approved" | "rejected") => {
    setSubmitting(true);
    setError("");
    try {
      const receipt = await reviewInstructorScoringPolicy(proposal.proposalId, {
        expectedVersion: proposal.version,
        expectedDefinitionHash: proposal.definitionHash,
        decision,
        rationale: rationale.trim(),
      });
      notify(
        `Scoring policy ${decision}`,
        receipt.policyId
          ? "The exact approved definition is now published."
          : "The author must submit a new proposal.",
      );
      refresh();
      navigate("assessment", "policy-reviews");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to record policy decision");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="instructor-page">
      <button
        type="button"
        className="ghost-btn button-sm"
        onClick={() => navigate("assessment", "policy-reviews")}
      >
        <Icon name="arrow-left" size="sm" /> Back to reviews
      </button>
      <PageTitle
        title="Review scoring policy"
        text={`Hash-pinned definition ${proposal.definitionHash.slice(0, 16)}…`}
      />
      <AcademyPanel icon="evidence" title="Criterion scoring" meta="Read-only submitted definition">
        <div className="rubric-grid">
          {proposal.criteria.map(item => (
            <div className="rubric-criterion" key={item.code}>
              <strong>{item.code}</strong>
              <p>
                {item.weightBasisPoints} basis points · {item.maxPoints} maximum points
              </p>
            </div>
          ))}
        </div>
      </AcademyPanel>
      <AcademyPanel icon="award" title="Outcome bands" meta="Read-only submitted definition">
        <div className="rubric-grid">
          {proposal.outcomeBands.map(item => (
            <div className="rubric-criterion" key={item.code}>
              <strong>{item.label}</strong>
              <p>
                {item.code} · {item.minBasisPoints}–{item.maxBasisPoints} basis points
              </p>
            </div>
          ))}
        </div>
        <label className="form-field">
          <span>Independent decision rationale</span>
          <textarea value={rationale} onChange={event => setRationale(event.target.value)} />
        </label>
        {error ? <p role="alert">{error}</p> : null}
        <div className="hero-actions">
          <button
            type="button"
            className="secondary-btn"
            disabled={submitting || rationale.trim().length < 3}
            onClick={() => void decide("rejected")}
          >
            Reject
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={submitting || rationale.trim().length < 3}
            onClick={() => void decide("approved")}
          >
            Approve and publish
          </button>
        </div>
      </AcademyPanel>
    </div>
  );
}

function ScoringPolicyProposalView({
  policy,
  navigate,
  notify,
  refresh,
}: {
  policy: InstructorScoringPolicyAuthoringItem;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  refresh: () => void;
}) {
  const editable = policy.state === "available" || policy.state === "rejected";
  const [criteria, setCriteria] = useState(() =>
    Object.fromEntries(
      policy.rubricCriteria.map(item => [item.code, { weightBasisPoints: 0, maxPoints: 0 }]),
    ),
  );
  const [bands, setBands] = useState<InstructorScoringPolicyOutcomeBand[]>([
    { code: "", label: "", minBasisPoints: 0, maxBasisPoints: 10000 },
  ]);
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const total = Object.values(criteria).reduce((sum, item) => sum + item.weightBasisPoints, 0);
  const bandsValid =
    bands.every(
      (band, index) =>
        /^[a-z][a-z0-9._-]{1,63}$/.test(band.code) &&
        !!band.label.trim() &&
        band.minBasisPoints === (index ? bands[index - 1].maxBasisPoints + 1 : 0) &&
        band.maxBasisPoints >= band.minBasisPoints,
    ) && bands[bands.length - 1]?.maxBasisPoints === 10000;
  const valid =
    editable &&
    total === 10000 &&
    Object.values(criteria).every(item => item.maxPoints > 0) &&
    bandsValid &&
    rationale.trim().length >= 3 &&
    !submitting;
  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await submitInstructorScoringPolicy(policy.assessmentPublicationId, {
        schemaVersion: "d11-scoring-outcome-policy.v1",
        criteria: policy.rubricCriteria.map(item => ({ code: item.code, ...criteria[item.code] })),
        outcomeBands: bands.map(item => ({ ...item, label: item.label.trim() })),
        rationale: rationale.trim(),
      });
      notify("Scoring policy submitted", "An independent reviewer must approve this exact definition.");
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to submit scoring policy");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("assessment", "rubrics")}>
        <Icon name="arrow-left" size="sm" /> Back to rubrics
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <span className="simple-card-icon">
            <Icon name="evidence" />
          </span>
          <div>
            <div className="hero-eyebrow">Published assessment rubric · {policy.rubricVersionLabel}</div>
            <h2>{policy.title}</h2>
            <p>
              {policy.rubricCriteria.length} exact criteria · policy {policy.state.replace("_", " ")}
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <button type="button" className="primary-btn" disabled={!valid} onClick={() => void submit()}>
            {submitting ? "Submitting…" : editable ? "Submit for review" : "Policy recorded"}
          </button>
        </div>
      </section>
      <AcademyPanel icon="evidence" title="Canonical rubric criteria" meta={`${total}/10,000 basis points`}>
        <div className="rubric-grid">
          {policy.rubricCriteria.map(criterion => (
            <div className="rubric-criterion" key={criterion.code}>
              <div className="rubric-criterion-head">
                <strong>{criterion.title}</strong>
              </div>
              <p style={{ color: "var(--muted)", fontSize: 14 }}>{criterion.description}</p>
              <small className="code">{criterion.code}</small>
              {editable ? (
                <div className="form-grid" style={{ marginTop: 10 }}>
                  {(["weightBasisPoints", "maxPoints"] as const).map(field => (
                    <label className="form-field" key={field}>
                      <span>{field === "weightBasisPoints" ? "Weight basis points" : "Maximum points"}</span>
                      <input
                        type="number"
                        min="1"
                        max="10000"
                        value={criteria[criterion.code][field] || ""}
                        onChange={event =>
                          setCriteria(current => ({
                            ...current,
                            [criterion.code]: {
                              ...current[criterion.code],
                              [field]: Number(event.target.value),
                            },
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </AcademyPanel>
      {editable ? (
        <AcademyPanel icon="award" title="Outcome bands" meta="Ordered, contiguous 0–10,000 coverage">
          <div className="rubric-grid">
            {bands.map((band, index) => (
              <div className="rubric-criterion" key={index}>
                <div className="form-grid">
                  {(["code", "label", "minBasisPoints", "maxBasisPoints"] as const).map(field => (
                    <label className="form-field" key={field}>
                      <span>
                        {
                          {
                            code: "Code",
                            label: "Label",
                            minBasisPoints: "Minimum basis points",
                            maxBasisPoints: "Maximum basis points",
                          }[field]
                        }
                      </span>
                      <input
                        type={field.includes("BasisPoints") ? "number" : "text"}
                        min={field.includes("BasisPoints") ? 0 : undefined}
                        max={field.includes("BasisPoints") ? 10000 : undefined}
                        value={band[field]}
                        onChange={event =>
                          setBands(current =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    [field]: field.includes("BasisPoints")
                                      ? Number(event.target.value)
                                      : event.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
                {bands.length > 1 ? (
                  <button
                    type="button"
                    className="ghost-btn button-sm"
                    onClick={() => setBands(current => current.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    Remove band
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() =>
                setBands(current => [
                  ...current,
                  { code: "", label: "", minBasisPoints: 0, maxBasisPoints: 10000 },
                ])
              }
            >
              Add outcome band
            </button>
          </div>
          <label className="form-field">
            <span>Submission rationale</span>
            <textarea value={rationale} onChange={event => setRationale(event.target.value)} />
          </label>
          <p>
            Weights: {total}/10,000 · outcome coverage: {bandsValid ? "valid" : "incomplete"}
          </p>
          {error ? <p role="alert">{error}</p> : null}
        </AcademyPanel>
      ) : (
        <div className="decision-callout">
          <strong>Governed policy state: {policy.state.replace("_", " ")}</strong>
          <p>
            {policy.definitionHash
              ? `Definition ${policy.definitionHash.slice(0, 12)}… · version ${policy.policyVersion}`
              : "Independent review is pending."}
          </p>
        </div>
      )}
    </div>
  );
}

function useInstructorClassSessions() {
  const identity = useSignedInIdentity();
  const [state, setState] = useState<{ items: ClassSession[]; error: string }>();
  useEffect(() => {
    let active = true;
    void academyRequest<D10AssignedCohorts>("instructor/learning/cohorts", undefined, "GET")
      .then(async cohorts => {
        const grouped = await Promise.all(
          cohorts.items.map(async cohort => {
            const [sessions, cohortRoster] = await Promise.all([
              academyRequest<D10AttendanceSessions>(
                `instructor/learning/cohorts/${cohort.id}/attendance-sessions`,
                undefined,
                "GET",
              ),
              academyRequest<D10CohortRoster>(
                `instructor/learning/cohorts/${cohort.id}/roster`,
                undefined,
                "GET",
              ),
            ]);
            return sessions.items.map(session => {
              const startsAt = new Date(session.startsAt);
              const endsAt = new Date(session.endsAt);
              return {
                id: session.id,
                title: session.title,
                code: "One-time handoff",
                dateLabel: startsAt.toLocaleDateString(),
                timeLabel: startsAt.toLocaleTimeString(),
                localTime: startsAt.toLocaleString(),
                duration: `${Math.max(0, Math.round((endsAt.getTime() - startsAt.getTime()) / 60000))} minutes`,
                instructor: identity.name,
                program: "Assigned program",
                cohort: cohort.name,
                mode: session.sessionKind.replace("_", " "),
                status:
                  endsAt.getTime() < Date.now()
                    ? ("completed" as const)
                    : startsAt.getTime() <= Date.now()
                      ? ("live" as const)
                      : ("upcoming" as const),
                preparation: ["Confirm Class device readiness", "Review the linked learning context"],
                agenda: ["Live Academy learning session"],
                learnerCount: cohortRoster.items.length,
                questions: 0,
                recording: "off" as const,
                language: undefined,
              };
            });
          }),
        );
        if (active) setState({ items: grouped.flat(), error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Current Class sessions are unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [identity.name]);
  return { items: state?.items || [], loading: !state, error: state?.error || "" };
}

function AcademyAttendancePanel({ notify }: { notify: (title: string, detail?: string) => void }) {
  const [cohorts, setCohorts] = useState<D10AssignedCohorts["items"]>([]);
  const [cohortId, setCohortId] = useState("");
  const [sessions, setSessions] = useState<D10AttendanceSessions["items"]>([]);
  const [sessionId, setSessionId] = useState("");
  const [activities, setActivities] = useState<D10PublishedContent["attendanceActivities"]>([]);
  const [roster, setRoster] = useState<D10AttendanceRoster["items"]>([]);
  const [kind, setKind] = useState<"workshop" | "clinic" | "in_person">("workshop");
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [activityId, setActivityId] = useState("");
  const [learnerId, setLearnerId] = useState("");
  const [status, setStatus] = useState<"PRESENT" | "LATE" | "ABSENT" | "EXCUSED" | "UNKNOWN">("PRESENT");
  const [reason, setReason] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void academyRequest<D10AssignedCohorts>("instructor/learning/cohorts", undefined, "GET")
      .then(result => {
        if (active) {
          setCohorts(result.items);
          setCohortId(result.items[0]?.id || "");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Teaching scope unavailable");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!cohortId) return;
    let active = true;
    void Promise.all([
      academyRequest<D10AttendanceSessions>(
        `instructor/learning/cohorts/${cohortId}/attendance-sessions`,
        undefined,
        "GET",
      ),
      academyRequest<D10PublishedContent>(
        `instructor/learning/cohorts/${cohortId}/published-content`,
        undefined,
        "GET",
      ),
    ])
      .then(([result, content]) => {
        if (active) {
          setSessions(result.items);
          setSessionId(result.items[0]?.id || "");
          setActivities(content.attendanceActivities);
          setError("");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Academy attendance unavailable");
      });
    return () => {
      active = false;
    };
  }, [cohortId]);
  useEffect(() => {
    if (!sessionId) {
      setRoster([]);
      return;
    }
    let active = true;
    void academyRequest<D10AttendanceRoster>(
      `instructor/learning/attendance-sessions/${sessionId}/roster`,
      undefined,
      "GET",
    )
      .then(result => {
        if (active) {
          setRoster(result.items);
          setLearnerId(result.items[0]?.enrollmentId || "");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Attendance roster unavailable");
      });
    return () => {
      active = false;
    };
  }, [sessionId]);
  const createSession = async () => {
    setBusy(true);
    try {
      const activity = activities.find(item => item.activityId === activityId);
      await createCommandSender({ headerOnly: true, context: "d10-attendance-session" })(
        "instructor/learning/attendance-sessions/create",
        {
          cohortId,
          sessionKind: kind,
          title: title.trim(),
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          ...(activity
            ? { moduleId: activity.moduleId, lessonId: activity.lessonId, activityId: activity.activityId }
            : {}),
        },
      );
      const result = await academyRequest<D10AttendanceSessions>(
        `instructor/learning/cohorts/${cohortId}/attendance-sessions`,
        undefined,
        "GET",
      );
      setSessions(result.items);
      setSessionId(result.items[0]?.id || "");
      setTitle("");
      setStartsAt("");
      setEndsAt("");
      setActivityId("");
      notify("Academy learning session created");
    } catch (cause) {
      notify("Session unavailable", cause instanceof Error ? cause.message : "Refresh and try again.");
    } finally {
      setBusy(false);
    }
  };
  const reconcile = async () => {
    const learner = roster.find(item => item.enrollmentId === learnerId);
    if (!learner) return;
    setBusy(true);
    try {
      await createCommandSender({ headerOnly: true, context: "d10-attendance-reconcile" })(
        "instructor/learning/attendance/reconcile",
        {
          sessionId,
          enrollmentId: learner.enrollmentId,
          status,
          reason: reason.trim(),
          evidenceReference: evidenceReference.trim(),
          expectedVersion: learner.version,
        },
      );
      const result = await academyRequest<D10AttendanceRoster>(
        `instructor/learning/attendance-sessions/${sessionId}/roster`,
        undefined,
        "GET",
      );
      setRoster(result.items);
      setReason("");
      setEvidenceReference("");
      notify(learner.version ? "Attendance corrected" : "Attendance recorded");
    } catch (cause) {
      notify("Attendance unavailable", cause instanceof Error ? cause.message : "Refresh and try again.");
    } finally {
      setBusy(false);
    }
  };
  const openClass = async (mode: "host" | "rehearsal") => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const handoff = await requestClassHandoff("learning_session", sessionId, mode);
      window.location.assign(handoff.destinationUrl);
    } catch (cause) {
      notify("Class handoff unavailable", cause instanceof Error ? cause.message : "Refresh and try again.");
    } finally {
      setBusy(false);
    }
  };
  const selected = roster.find(item => item.enrollmentId === learnerId);
  return (
    <AcademyPanel icon="check" title="Academy attendance" meta="Current D07 teaching scope">
      {error && <p role="alert">{error}</p>}
      {!error && !cohorts.length && <p>No current assigned Cohorts are available.</p>}
      {!!cohorts.length && (
        <>
          <div className="form-field">
            <label htmlFor="d10-attendance-cohort">Assigned Cohort</label>
            <select
              id="d10-attendance-cohort"
              className="select-field"
              value={cohortId}
              onChange={event => setCohortId(event.target.value)}
            >
              {cohorts.map(cohort => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="d10-attendance-kind">Session kind</label>
            <select
              id="d10-attendance-kind"
              className="select-field"
              value={kind}
              onChange={event => setKind(event.target.value as typeof kind)}
            >
              <option value="workshop">Workshop</option>
              <option value="clinic">Clinic</option>
              <option value="in_person">In-person session</option>
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="d10-attendance-title">Session title</label>
            <input
              id="d10-attendance-title"
              value={title}
              maxLength={240}
              onChange={event => setTitle(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="d10-attendance-start">Starts at</label>
            <input
              id="d10-attendance-start"
              type="datetime-local"
              value={startsAt}
              onChange={event => setStartsAt(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="d10-attendance-end">Ends at</label>
            <input
              id="d10-attendance-end"
              type="datetime-local"
              value={endsAt}
              onChange={event => setEndsAt(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="d10-attendance-activity">Exact D04 attendance Activity</label>
            <select
              id="d10-attendance-activity"
              className="select-field"
              value={activityId}
              onChange={event => setActivityId(event.target.value)}
            >
              <option value="">Attendance record only</option>
              {activities.map(item => (
                <option key={item.activityId} value={item.activityId}>
                  {item.lessonTitle} · {item.activityText}
                </option>
              ))}
            </select>
            <small>Only a linked D04 requirement can change learning progress.</small>
          </div>
          <button
            type="button"
            className="primary-btn"
            disabled={busy || title.trim().length < 3 || !startsAt || !endsAt}
            onClick={() => void createSession()}
          >
            Create Academy session
          </button>
          <div className="form-field">
            <label htmlFor="d10-attendance-session">Academy session</label>
            <select
              id="d10-attendance-session"
              className="select-field"
              value={sessionId}
              onChange={event => setSessionId(event.target.value)}
            >
              {sessions.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title} · {new Date(item.startsAt).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
          {!sessions.length && <p>No Academy sessions have been created for this Cohort.</p>}
          {!!sessionId && (
            <div className="button-row">
              <button
                type="button"
                className="primary-btn"
                disabled={busy}
                onClick={() => void openClass("host")}
              >
                Host in Class
              </button>
              <button
                type="button"
                className="secondary-btn"
                disabled={busy}
                onClick={() => void openClass("rehearsal")}
              >
                Rehearse in Class
              </button>
            </div>
          )}
          {!!sessionId && (
            <>
              <div className="form-field">
                <label htmlFor="d10-attendance-learner">Learner</label>
                <select
                  id="d10-attendance-learner"
                  className="select-field"
                  value={learnerId}
                  onChange={event => setLearnerId(event.target.value)}
                >
                  {roster.map(item => (
                    <option key={item.enrollmentId} value={item.enrollmentId}>
                      {item.displayName} · {item.status}
                    </option>
                  ))}
                </select>
              </div>
              {!roster.length && <p>No current learners are assigned to this Cohort.</p>}
              {!!selected && (
                <>
                  <p>
                    Current attendance: {selected.status} · version {selected.version}
                  </p>
                  <div className="form-field">
                    <label htmlFor="d10-attendance-status">Reconciled status</label>
                    <select
                      id="d10-attendance-status"
                      className="select-field"
                      value={status}
                      onChange={event => setStatus(event.target.value as typeof status)}
                    >
                      {(["PRESENT", "LATE", "ABSENT", "EXCUSED", "UNKNOWN"] as const).map(item => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="d10-attendance-reason">Reason</label>
                    <textarea
                      id="d10-attendance-reason"
                      value={reason}
                      maxLength={4000}
                      onChange={event => setReason(event.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="d10-attendance-evidence">Evidence reference</label>
                    <input
                      id="d10-attendance-evidence"
                      value={evidenceReference}
                      maxLength={1000}
                      onChange={event => setEvidenceReference(event.target.value)}
                    />
                    <small>
                      Use the approved register or observation reference; do not enter private Class data.
                    </small>
                  </div>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={
                      busy ||
                      selected.status === status ||
                      reason.trim().length < 5 ||
                      evidenceReference.trim().length < 8
                    }
                    onClick={() => void reconcile()}
                  >
                    Record attendance
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}
    </AcademyPanel>
  );
}

function LearningAssignmentDeliveryPanel({ notify }: { notify: (title: string, detail?: string) => void }) {
  const [cohorts, setCohorts] = useState<D10AssignedCohorts["items"]>([]);
  const [cohortId, setCohortId] = useState("");
  const [modules, setModules] = useState<Array<{ id: string; title: string; version: number }>>([]);
  const [submissionActivities, setSubmissionActivities] = useState<
    Array<{
      moduleId: string;
      lessonId: string;
      lessonTitle: string;
      activityId: string;
      activityText: string;
    }>
  >([]);
  const [assignments, setAssignments] = useState<D10Assignments["items"]>([]);
  const [moduleId, setModuleId] = useState("");
  const [activityId, setActivityId] = useState("");
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [revision, setRevision] = useState<{
    assignmentId: string;
    version: number;
    kind: "deadline" | "close";
  } | null>(null);
  const [revisionDueAt, setRevisionDueAt] = useState("");
  const [revisionReason, setRevisionReason] = useState("");
  const [selectedSubmissionAssignmentId, setSelectedSubmissionAssignmentId] = useState("");
  const [submissions, setSubmissions] = useState<D10AssignmentSubmissions["items"]>([]);
  const [submissionReview, setSubmissionReview] = useState<{ submissionId: string; version: number } | null>(
    null,
  );
  const [formativeFeedback, setFormativeFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void academyRequest<D10AssignedCohorts>("instructor/learning/cohorts", undefined, "GET")
      .then(result => {
        if (active) {
          setCohorts(result.items);
          setCohortId(current => current || result.items[0]?.id || "");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Current teaching scope unavailable");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!cohortId) return;
    let active = true;
    void Promise.all([
      academyRequest<{
        modules: Array<{ id: string; title: string; version: number }>;
        submissionActivities: Array<{
          moduleId: string;
          lessonId: string;
          lessonTitle: string;
          activityId: string;
          activityText: string;
        }>;
      }>(`instructor/learning/cohorts/${cohortId}/published-content`, undefined, "GET"),
      academyRequest<D10Assignments>(`instructor/learning/cohorts/${cohortId}/assignments`, undefined, "GET"),
    ])
      .then(([content, delivery]) => {
        if (active) {
          setModules(content.modules);
          setSubmissionActivities(content.submissionActivities);
          setActivityId("");
          setModuleId(current =>
            content.modules.some(module => module.id === current) ? current : content.modules[0]?.id || "",
          );
          setAssignments(delivery.items);
          setError("");
        }
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Cohort delivery unavailable");
      });
    return () => {
      active = false;
    };
  }, [cohortId]);
  useEffect(() => {
    if (!selectedSubmissionAssignmentId) return;
    let active = true;
    void academyRequest<D10AssignmentSubmissions>(
      `instructor/learning/assignments/${selectedSubmissionAssignmentId}/submissions`,
      undefined,
      "GET",
    )
      .then(result => {
        if (active) setSubmissions(result.items);
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : "Submissions unavailable");
      });
    return () => {
      active = false;
    };
  }, [selectedSubmissionAssignmentId]);
  const publish = async () => {
    if (!cohortId || !moduleId || !dueAt) return;
    setBusy(true);
    try {
      const activity = submissionActivities.find(
        item => item.activityId === activityId && item.moduleId === moduleId,
      );
      await createCommandSender({ headerOnly: true, context: "d10-instructor-assignment" })(
        "instructor/learning/assignments/publish",
        {
          cohortId,
          moduleId,
          title: title.trim(),
          instructions: instructions.trim(),
          dueAt: new Date(dueAt).toISOString(),
          ...(activity ? { lessonId: activity.lessonId, activityId: activity.activityId } : {}),
        },
      );
      const delivery = await academyRequest<D10Assignments>(
        `instructor/learning/cohorts/${cohortId}/assignments`,
        undefined,
        "GET",
      );
      setAssignments(delivery.items);
      setTitle("");
      setInstructions("");
      setDueAt("");
      setActivityId("");
      notify("Learning assignment published");
    } catch (cause) {
      notify(
        "Learning assignment unavailable",
        cause instanceof Error ? cause.message : "Try again after refreshing.",
      );
    } finally {
      setBusy(false);
    }
  };
  const revise = async () => {
    if (!revision) return;
    setBusy(true);
    try {
      await createCommandSender({ headerOnly: true, context: "d10-instructor-assignment" })(
        revision.kind === "deadline"
          ? "instructor/learning/assignments/change-deadline"
          : "instructor/learning/assignments/close",
        {
          assignmentId: revision.assignmentId,
          expectedVersion: revision.version,
          reason: revisionReason.trim(),
          ...(revision.kind === "deadline" ? { dueAt: new Date(revisionDueAt).toISOString() } : {}),
        },
      );
      const delivery = await academyRequest<D10Assignments>(
        `instructor/learning/cohorts/${cohortId}/assignments`,
        undefined,
        "GET",
      );
      setAssignments(delivery.items);
      setRevision(null);
      setRevisionDueAt("");
      setRevisionReason("");
      notify(revision.kind === "deadline" ? "Official deadline changed" : "Learning assignment closed");
    } catch (cause) {
      notify(
        "Learning assignment unavailable",
        cause instanceof Error ? cause.message : "Try again after refreshing.",
      );
    } finally {
      setBusy(false);
    }
  };
  const requestSubmissionRevision = async () => {
    if (!submissionReview) return;
    setBusy(true);
    try {
      await createCommandSender({ headerOnly: true, context: "d10-instructor-submission" })(
        "instructor/learning/submissions/request-revision",
        {
          submissionId: submissionReview.submissionId,
          expectedVersion: submissionReview.version,
          formativeFeedback: formativeFeedback.trim(),
        },
      );
      const result = await academyRequest<D10AssignmentSubmissions>(
        `instructor/learning/assignments/${selectedSubmissionAssignmentId}/submissions`,
        undefined,
        "GET",
      );
      setSubmissions(result.items);
      setSubmissionReview(null);
      setFormativeFeedback("");
      notify("Formative revision requested");
    } catch (cause) {
      notify(
        "Revision request unavailable",
        cause instanceof Error ? cause.message : "Try again after refreshing.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <AcademyPanel icon="clipboard" title="Learning assignment delivery" meta="Current D07 teaching scope">
      {error && <p role="alert">{error}</p>}
      {!error && !cohorts.length && <p>No current assigned Cohorts are available.</p>}
      {!!cohorts.length && (
        <>
          <div className="form-field">
            <label htmlFor="d10-assignment-cohort">Assigned Cohort</label>
            <select
              id="d10-assignment-cohort"
              className="select-field"
              value={cohortId}
              onChange={event => setCohortId(event.target.value)}
            >
              {cohorts.map(cohort => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </div>
          {!modules.length && (
            <p>This Cohort has no published exact Curriculum Modules available for learning assignments.</p>
          )}
          {!!modules.length && (
            <>
              <div className="form-field">
                <label htmlFor="d10-assignment-module">Published Module</label>
                <select
                  id="d10-assignment-module"
                  className="select-field"
                  value={moduleId}
                  onChange={event => {
                    setModuleId(event.target.value);
                    setActivityId("");
                  }}
                >
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="d10-assignment-activity">Exact Lesson Activity</label>
                <select
                  id="d10-assignment-activity"
                  className="select-field"
                  value={activityId}
                  onChange={event => setActivityId(event.target.value)}
                >
                  <option value="">General learning work</option>
                  {submissionActivities
                    .filter(item => item.moduleId === moduleId)
                    .map(item => (
                      <option key={item.activityId} value={item.activityId}>
                        {item.lessonTitle} · {item.activityText}
                      </option>
                    ))}
                </select>
                <small>
                  Only a D04 Activity explicitly requiring a submission can change Lesson progress.
                </small>
              </div>
              <div className="form-field">
                <label htmlFor="d10-assignment-title">Assignment title</label>
                <input
                  id="d10-assignment-title"
                  value={title}
                  maxLength={240}
                  onChange={event => setTitle(event.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="d10-assignment-instructions">Learning instructions</label>
                <textarea
                  id="d10-assignment-instructions"
                  value={instructions}
                  maxLength={10000}
                  onChange={event => setInstructions(event.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="d10-assignment-deadline">Official deadline</label>
                <input
                  id="d10-assignment-deadline"
                  type="datetime-local"
                  value={dueAt}
                  onChange={event => setDueAt(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="primary-btn"
                disabled={busy || title.trim().length < 3 || instructions.trim().length < 10 || !dueAt}
                onClick={() => void publish()}
              >
                Publish learning assignment
              </button>
            </>
          )}
          <div className="module-list">
            {assignments.map(assignment => (
              <div className="policy-item" key={assignment.id}>
                <Icon name="clipboard" size="sm" />
                <span>
                  <strong>{assignment.title}</strong>
                  <small>
                    Official deadline: {new Date(assignment.dueAt).toLocaleString()} · {assignment.state}
                  </small>
                </span>
                {assignment.state === "published" && (
                  <span>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() =>
                        setRevision({
                          assignmentId: assignment.id,
                          version: assignment.version,
                          kind: "deadline",
                        })
                      }
                    >
                      Change deadline
                    </button>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() =>
                        setRevision({
                          assignmentId: assignment.id,
                          version: assignment.version,
                          kind: "close",
                        })
                      }
                    >
                      Close assignment
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => {
                    setSelectedSubmissionAssignmentId(assignment.id);
                    setSubmissions([]);
                  }}
                >
                  View submissions
                </button>
              </div>
            ))}
            {!assignments.length && <p>No learning assignments have been published for this Cohort.</p>}
          </div>
          {selectedSubmissionAssignmentId && (
            <div className="module-list">
              <strong>Learning submissions</strong>
              {!submissions.length && <p>No learner work has been submitted for this assignment.</p>}
              {submissions.map(submission => (
                <div className="policy-item" key={submission.submissionId}>
                  <Icon name="clipboard" size="sm" />
                  <span>
                    <strong>
                      Learner {submission.personId.slice(0, 8)} · {submission.state.replace(/_/g, " ")}
                    </strong>
                    <small>
                      Revision {submission.version} · {new Date(submission.updatedAt).toLocaleString()}
                    </small>
                    {submission.responseText && <p>{submission.responseText}</p>}
                    {!!submission.fileIds.length && (
                      <small>{submission.fileIds.length} verified file reference(s)</small>
                    )}
                    {submission.formativeFeedback && (
                      <p>Formative feedback: {submission.formativeFeedback}</p>
                    )}
                  </span>
                  {submission.state !== "returned_for_revision" && (
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() =>
                        setSubmissionReview({
                          submissionId: submission.submissionId,
                          version: submission.version,
                        })
                      }
                    >
                      Request revision
                    </button>
                  )}
                </div>
              ))}
              {submissionReview && (
                <div className="form-field">
                  <label htmlFor="d10-formative-feedback">Formative revision feedback</label>
                  <textarea
                    id="d10-formative-feedback"
                    value={formativeFeedback}
                    maxLength={4000}
                    onChange={event => setFormativeFeedback(event.target.value)}
                  />
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={busy || formativeFeedback.trim().length < 5}
                    onClick={() => void requestSubmissionRevision()}
                  >
                    Request revision
                  </button>
                  <button type="button" className="secondary-btn" onClick={() => setSubmissionReview(null)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
          {revision && (
            <div className="form-field">
              <strong>
                {revision.kind === "deadline" ? "Change official deadline" : "Close learning assignment"}
              </strong>
              {revision.kind === "deadline" && (
                <>
                  <label htmlFor="d10-revision-deadline">New deadline</label>
                  <input
                    id="d10-revision-deadline"
                    type="datetime-local"
                    value={revisionDueAt}
                    onChange={event => setRevisionDueAt(event.target.value)}
                  />
                </>
              )}
              <label htmlFor="d10-revision-reason">Reason</label>
              <textarea
                id="d10-revision-reason"
                value={revisionReason}
                maxLength={4000}
                onChange={event => setRevisionReason(event.target.value)}
              />
              <button
                type="button"
                className="primary-btn"
                disabled={
                  busy || revisionReason.trim().length < 5 || (revision.kind === "deadline" && !revisionDueAt)
                }
                onClick={() => void revise()}
              >
                Confirm {revision.kind === "deadline" ? "deadline change" : "closure"}
              </button>
              <button type="button" className="secondary-btn" onClick={() => setRevision(null)}>
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </AcademyPanel>
  );
}

function ConnectedAssessmentAssignmentLibrary({
  navigate,
  notify,
}: {
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const assignments = useInstructorAssessmentAssignments();
  const policies = useInstructorAssessmentPolicies();
  const [cohorts, setCohorts] = useState<D10AssignedCohorts["items"]>([]);
  const [showExamAssignment, setShowExamAssignment] = useState(false);
  const [publicationId, setPublicationId] = useState("");
  const [cohortId, setCohortId] = useState("");
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [attemptLimit, setAttemptLimit] = useState("1");
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All statuses");
  const publishedPolicies = policies.items.filter(item => item.state === "published");
  useEffect(() => {
    let active = true;
    void academyRequest<D10AssignedCohorts>("instructor/learning/cohorts", undefined, "GET")
      .then(result => {
        if (active) {
          setCohorts(result.items);
          setCohortId(current => current || result.items[0]?.id || "");
        }
      })
      .catch(cause => {
        if (active)
          setPublishError(cause instanceof Error ? cause.message : "Current Cohort scope is unavailable");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setPublicationId(current =>
      publishedPolicies.some(item => item.assessmentPublicationId === current)
        ? current
        : publishedPolicies[0]?.assessmentPublicationId || "",
    );
  }, [publishedPolicies]);
  const publishExamAssignment = async () => {
    if (!publicationId || !cohortId || !opensAt || !closesAt) return;
    setPublishing(true);
    setPublishError("");
    try {
      const receipt = await publishInstructorExamAssessmentAssignment(publicationId, {
        cohortId,
        opensAt: new Date(opensAt).toISOString(),
        closesAt: new Date(closesAt).toISOString(),
        attemptLimit: Number(attemptLimit),
      });
      notify(
        "Exam assessment assignment published",
        `Exact D11 assignment ${receipt.id} is available to D12 scheduling.`,
      );
      setShowExamAssignment(false);
      assignments.refresh();
    } catch (cause) {
      setPublishError(
        cause instanceof Error ? cause.message : "The exam assessment assignment could not be published",
      );
    } finally {
      setPublishing(false);
    }
  };
  const assignmentStatus = (opensAt: string, closesAt: string) => {
    const now = Date.now();
    if (new Date(opensAt).getTime() > now) return "Upcoming";
    if (new Date(closesAt).getTime() <= now) return "Closed";
    return "Current";
  };
  const filtered = assignments.items.filter(item => {
    const itemStatus = assignmentStatus(item.opensAt, item.closesAt);
    return (
      `${item.title} ${item.programName} ${item.cohortName}`.toLowerCase().includes(query.toLowerCase()) &&
      (status === "All statuses" || itemStatus === status)
    );
  });
  return (
    <div className="instructor-page">
      <PageTitle
        title="Assignments"
        text="Exact published assessment work, windows and scoring-policy versions in your current lead or assessor scope."
        actions={
          <button
            type="button"
            className="primary-btn"
            disabled={!publishedPolicies.length || !cohorts.length}
            title="Publish from an approved assessment policy and exact Cohort scope"
            onClick={() => setShowExamAssignment(value => !value)}
          >
            <Icon name="plus" size="sm" />
            Create exam assignment
          </button>
        }
      />
      {showExamAssignment ? (
        <AcademyPanel
          icon="calendar"
          title="Publish exam assessment assignment"
          meta="D11 academic authority"
        >
          <p>
            This creates the immutable academic assignment required by D12. It does not schedule a session or
            establish candidate eligibility.
          </p>
          {publishError ? <p role="alert">{publishError}</p> : null}
          <div className="content-grid">
            <div className="form-field">
              <label htmlFor="exam-assignment-publication">Published assessment policy</label>
              <select
                id="exam-assignment-publication"
                value={publicationId}
                onChange={event => setPublicationId(event.target.value)}
              >
                {publishedPolicies.map(item => (
                  <option key={item.assessmentPublicationId} value={item.assessmentPublicationId}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="exam-assignment-cohort">Cohort</label>
              <select
                id="exam-assignment-cohort"
                value={cohortId}
                onChange={event => setCohortId(event.target.value)}
              >
                {cohorts.map(cohort => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="exam-assignment-opens">Opens</label>
              <input
                id="exam-assignment-opens"
                type="datetime-local"
                value={opensAt}
                onChange={event => setOpensAt(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="exam-assignment-closes">Closes</label>
              <input
                id="exam-assignment-closes"
                type="datetime-local"
                value={closesAt}
                onChange={event => setClosesAt(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="exam-assignment-attempts">Attempt limit</label>
              <input
                id="exam-assignment-attempts"
                type="number"
                min="1"
                max="20"
                value={attemptLimit}
                onChange={event => setAttemptLimit(event.target.value)}
              />
            </div>
          </div>
          <div className="hero-actions">
            <button
              type="button"
              className="primary-btn"
              disabled={
                publishing ||
                !publicationId ||
                !cohortId ||
                !opensAt ||
                !closesAt ||
                Number(attemptLimit) < 1 ||
                Number(attemptLimit) > 20
              }
              onClick={() => void publishExamAssignment()}
            >
              {publishing ? "Publishing…" : "Publish exam assignment"}
            </button>
            <button
              type="button"
              className="secondary-btn"
              disabled={publishing}
              onClick={() => setShowExamAssignment(false)}
            >
              Cancel
            </button>
          </div>
        </AcademyPanel>
      ) : null}
      <LearningAssignmentDeliveryPanel notify={notify} />
      <AcademyPanel
        icon="clipboard"
        title="Assessment assignment library"
        meta={`${filtered.length} assignments`}
      >
        <div className="panel-toolbar">
          <div className="search-page-box">
            <Icon name="search" size="sm" />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search assignments…"
            />
          </div>
          <select className="select-field" value={status} onChange={event => setStatus(event.target.value)}>
            <option>All statuses</option>
            <option>Current</option>
            <option>Upcoming</option>
            <option>Closed</option>
          </select>
          <button type="button" className="secondary-btn" onClick={assignments.refresh}>
            <Icon name="refresh" size="sm" />
            Refresh
          </button>
        </div>
        {assignments.loading ? <p role="status">Loading assessment assignments…</p> : null}
        {assignments.error ? <p role="alert">{assignments.error}</p> : null}
        {!assignments.loading && !assignments.error && !filtered.length ? (
          <EmptyState
            icon="clipboard"
            title="No assessment assignments"
            text="No exact D11 assignment currently matches this scope and filter."
          />
        ) : null}
        {filtered.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Cohort</th>
                  <th>Opens</th>
                  <th>Closes</th>
                  <th>Policy</th>
                  <th>Attempts</th>
                  <th>Access</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const itemStatus = assignmentStatus(item.opensAt, item.closesAt);
                  return (
                    <tr key={item.id}>
                      <td>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => navigate("assessment", `assignment-${item.id}`)}
                        >
                          <strong>{item.title}</strong>
                        </button>
                        <span style={{ display: "block", color: "var(--muted)", fontSize: 14, marginTop: 3 }}>
                          {item.programName}
                        </span>
                      </td>
                      <td>
                        <TonePill
                          tone={
                            itemStatus === "Current"
                              ? "green"
                              : itemStatus === "Upcoming"
                                ? "blue"
                                : "default"
                          }
                        >
                          {itemStatus}
                        </TonePill>
                      </td>
                      <td>{item.sourceKind === "d12_sitting" ? "Exam sitting" : "Submission"}</td>
                      <td>{item.cohortName}</td>
                      <td>{new Date(item.opensAt).toLocaleString()}</td>
                      <td>{new Date(item.closesAt).toLocaleString()}</td>
                      <td>v{item.scoringPolicyVersion}</td>
                      <td>{item.attemptLimit}</td>
                      <td>{item.access === "lead" ? "Scoped lead" : "Assigned assessor"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </AcademyPanel>
    </div>
  );
}

function ConnectedAssessmentModerationCase({
  item,
  refresh,
  notify,
}: {
  item: InstructorAssessmentModerationQueueItem;
  refresh: () => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [outcome, setOutcome] = useState<"confirmed" | "adjusted">("confirmed");
  const [rationale, setRationale] = useState("");
  const [scores, setScores] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [commandError, setCommandError] = useState("");
  useEffect(() => {
    setScores(current =>
      Object.keys(current).length
        ? current
        : Object.fromEntries(
            item.criteria.map(criterion => [
              criterion.code,
              String(
                item.criterionDecisions.find(decision => decision.criterionCode === criterion.code)?.points ??
                  "",
              ),
            ]),
          ),
    );
  }, [item]);
  const claimed = Boolean(item.moderatorAssignmentId) && !item.moderationDecisionId;
  const finalized = Boolean(item.moderationDecisionId);
  const decisions = item.criteria.map(criterion => ({
    criterionCode: criterion.code,
    points: Number(scores[criterion.code]),
  }));
  const adjustedScoresValid =
    outcome === "confirmed" ||
    decisions.every(
      (decision, index) =>
        scores[decision.criterionCode] !== "" &&
        Number.isInteger(decision.points) &&
        decision.points >= 0 &&
        decision.points <= item.criteria[index].maxPoints,
    );
  const claim = async () => {
    if (saving) return;
    setSaving(true);
    setCommandError("");
    try {
      const receipt = await claimInstructorAssessmentModeration(item.evaluationId);
      notify("Moderation claimed", `Independent review due ${new Date(receipt.expiresAt).toLocaleString()}`);
      refresh();
    } catch (cause) {
      setCommandError(cause instanceof Error ? cause.message : "Moderation could not be claimed");
    } finally {
      setSaving(false);
    }
  };
  const finalize = async () => {
    if (saving || rationale.trim().length < 10 || !adjustedScoresValid) return;
    setSaving(true);
    setCommandError("");
    try {
      const receipt = await finalizeInstructorAssessmentModeration(item.evaluationId, {
        expectedEvaluationVersion: 1,
        outcome,
        rationale: rationale.trim(),
        ...(outcome === "adjusted" ? { criterionDecisions: decisions } : {}),
      });
      notify(
        "Moderation finalized",
        `${receipt.finalOutcomeLabel} · ${(receipt.finalScoreBasisPoints / 100).toFixed(2)}%`,
      );
      refresh();
    } catch (cause) {
      setCommandError(cause instanceof Error ? cause.message : "Moderation could not be finalized");
    } finally {
      setSaving(false);
    }
  };
  return (
    <AcademyPanel
      icon="shield"
      title={`${item.learnerName} · ${item.title}`}
      meta={finalized ? "Finalized" : claimed ? "Claimed by you" : "Available for independent review"}
    >
      <div className="setting-row">
        <div>
          <strong>
            {item.programName} · {item.cohortName}
          </strong>
          <p>
            Original result: {(item.scoreBasisPoints / 100).toFixed(2)}% · {item.outcomeLabel}
          </p>
        </div>
        <TonePill tone={finalized ? "green" : claimed ? "blue" : "orange"}>
          {finalized ? "Finalized" : claimed ? "In review" : "Available"}
        </TonePill>
      </div>
      {commandError ? <p className="form-error">{commandError}</p> : null}
      {!item.moderatorAssignmentId ? (
        <button type="button" className="primary-btn" disabled={saving} onClick={() => void claim()}>
          <Icon name="shield" size="sm" /> {saving ? "Claiming…" : "Claim independent review"}
        </button>
      ) : (
        <div className="review-layout">
          <div className="review-content">
            <AcademyPanel
              icon="file"
              title="Pinned learner evidence"
              meta={`Evaluation ${item.evaluationSnapshotHash.slice(0, 12)}…`}
            >
              <p>
                {item.sourceKind === "d12_sitting"
                  ? `Verified secure-room response · sitting ${item.sittingEvidenceId?.slice(0, 12) ?? "unavailable"}…`
                  : "Pinned D10 submission response"}
              </p>
              {item.responseText ? (
                <div className="rich-editor">
                  <p>{item.responseText}</p>
                </div>
              ) : (
                <p>No text response was submitted.</p>
              )}
              <div className="action-list">
                {item.fileIds.map(fileId => (
                  <div className="setting-row" key={fileId}>
                    <div>
                      <strong>File evidence reference</strong>
                      <p>{fileId}</p>
                    </div>
                    <TonePill tone="blue">Pinned</TonePill>
                  </div>
                ))}
              </div>
            </AcademyPanel>
            <AcademyPanel
              icon="clipboard"
              title="Original human evaluation"
              meta={`Policy v${item.scoringPolicyVersion}`}
            >
              <div className="rubric-grid">
                {item.criteria.map(criterion => {
                  const original = item.criterionDecisions.find(
                    decision => decision.criterionCode === criterion.code,
                  );
                  return (
                    <div className="rubric-criterion" key={criterion.code}>
                      <div className="rubric-criterion-head">
                        <strong>{criterion.code}</strong>
                        <TonePill tone="purple">
                          {original?.points ?? 0}/{criterion.maxPoints}
                        </TonePill>
                      </div>
                      {claimed && outcome === "adjusted" ? (
                        <div className="form-field">
                          <label htmlFor={`moderation-score-${item.evaluationId}-${criterion.code}`}>
                            Adjusted points (0–{criterion.maxPoints})
                          </label>
                          <input
                            id={`moderation-score-${item.evaluationId}-${criterion.code}`}
                            type="number"
                            min={0}
                            max={criterion.maxPoints}
                            step={1}
                            value={scores[criterion.code] ?? ""}
                            onChange={event =>
                              setScores(current => ({ ...current, [criterion.code]: event.target.value }))
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <p>{item.releasableFeedback}</p>
              <div className="modal-form-note">
                <Icon name="lock" size="sm" /> Restricted evaluator notes are intentionally not exposed to
                moderation.
              </div>
            </AcademyPanel>
          </div>
          <aside className="decision-panel">
            <AcademyPanel
              icon="check-circle"
              title="Independent decision"
              meta={
                finalized
                  ? "Immutable"
                  : `Claim expires ${new Date(item.claimExpiresAt ?? "").toLocaleString()}`
              }
            >
              {finalized ? (
                <>
                  <strong style={{ fontSize: 36 }}>
                    {((item.finalScoreBasisPoints ?? 0) / 100).toFixed(2)}%
                  </strong>
                  <p>
                    {item.finalOutcomeLabel} · {item.moderationOutcome}
                  </p>
                  <p>{item.rationale}</p>
                  <p>
                    Moderation snapshot: <code>{item.moderationSnapshotHash}</code>
                  </p>
                </>
              ) : (
                <>
                  <div className="form-field">
                    <label htmlFor={`moderation-outcome-${item.evaluationId}`}>Decision</label>
                    <select
                      id={`moderation-outcome-${item.evaluationId}`}
                      className="select-field"
                      value={outcome}
                      onChange={event => setOutcome(event.target.value as "confirmed" | "adjusted")}
                    >
                      <option value="confirmed">Confirm original evaluation</option>
                      <option value="adjusted">Adjust criterion decisions</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor={`moderation-rationale-${item.evaluationId}`}>Independent rationale</label>
                    <textarea
                      id={`moderation-rationale-${item.evaluationId}`}
                      value={rationale}
                      onChange={event => setRationale(event.target.value)}
                      style={{ minHeight: 140 }}
                    />
                  </div>
                  <p>The server derives any adjusted score and outcome from the pinned policy.</p>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={saving || rationale.trim().length < 10 || !adjustedScoresValid}
                    onClick={() => void finalize()}
                  >
                    <Icon name="check" size="sm" /> {saving ? "Finalizing…" : "Finalize moderation"}
                  </button>
                </>
              )}
            </AcademyPanel>
          </aside>
        </div>
      )}
    </AcademyPanel>
  );
}

function ConnectedAssessmentOfficialResultCase({
  item,
  refresh,
  notify,
}: {
  item: InstructorAssessmentOfficialResultQueueItem;
  refresh: () => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [publicationReason, setPublicationReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [commandError, setCommandError] = useState("");
  const published = Boolean(item.officialResultVersionId);
  const publish = async () => {
    if (saving || publicationReason.trim().length < 10) return;
    setSaving(true);
    setCommandError("");
    try {
      const receipt = await publishInstructorAssessmentOfficialResult(item.moderationDecisionId, {
        publicationReason: publicationReason.trim(),
      });
      notify(
        "Official result published",
        `${receipt.outcomeLabel} · ${(receipt.scoreBasisPoints / 100).toFixed(2)}%`,
      );
      refresh();
    } catch (cause) {
      setCommandError(cause instanceof Error ? cause.message : "Official result could not be published");
    } finally {
      setSaving(false);
    }
  };
  return (
    <AcademyPanel
      icon="award"
      title={`${item.learnerName} · ${item.title}`}
      meta={published ? `Published version ${item.resultVersion}` : "Ready for independent publication"}
    >
      <div className="setting-row">
        <div>
          <strong>
            {item.programName} · {item.cohortName}
          </strong>
          <p>
            Moderated result: {(item.scoreBasisPoints / 100).toFixed(2)}% · {item.outcomeLabel}
          </p>
        </div>
        <TonePill tone={published ? "green" : "purple"}>{published ? "Published" : "Ready"}</TonePill>
      </div>
      <p>{item.releasableFeedback}</p>
      <p>
        Moderation snapshot: <code>{item.moderationSnapshotHash}</code>
      </p>
      {published ? (
        <>
          <p>{item.publicationReason}</p>
          <p>Published {new Date(item.publishedAt ?? "").toLocaleString()}</p>
          <p>
            Official-result snapshot: <code>{item.resultSnapshotHash}</code>
          </p>
        </>
      ) : (
        <>
          <div className="form-field">
            <label htmlFor={`official-result-reason-${item.moderationDecisionId}`}>
              Official publication rationale
            </label>
            <textarea
              id={`official-result-reason-${item.moderationDecisionId}`}
              value={publicationReason}
              onChange={event => setPublicationReason(event.target.value)}
              style={{ minHeight: 120 }}
            />
          </div>
          <p>
            The database derives the released score, outcome and feedback from the exact moderated lineage.
          </p>
          <div className="modal-form-note">
            <Icon name="lock" size="sm" /> Learner responses, files and restricted evaluator notes are not
            exposed here.
          </div>
          {commandError ? <p className="form-error">{commandError}</p> : null}
          <button
            type="button"
            className="primary-btn"
            disabled={saving || publicationReason.trim().length < 10}
            onClick={() => void publish()}
          >
            <Icon name="award" size="sm" /> {saving ? "Publishing…" : "Publish official result"}
          </button>
        </>
      )}
    </AcademyPanel>
  );
}

function ConnectedAssessmentModerationView({ notify }: { notify: (title: string, detail?: string) => void }) {
  const { items, error, loading, refresh } = useInstructorAssessmentModerationQueue();
  const publication = useInstructorAssessmentResultPublicationQueue();
  const refreshAll = () => {
    refresh();
    publication.refresh();
  };
  return (
    <div className="instructor-page">
      <PageTitle
        title="Moderation"
        text="Independent second review and separate official publication of exact finalized evaluations. Scores and outcomes remain server governed."
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={refreshAll}>
              <Icon name="refresh" size="sm" /> Refresh
            </button>
            <button
              type="button"
              className="secondary-btn"
              disabled
              title="Moderation export is not connected"
            >
              <Icon name="download" size="sm" /> Export log unavailable
            </button>
          </>
        }
      />
      <div className="stat-grid">
        <StatTile
          icon="shield"
          label="Available"
          value={items.filter(item => !item.moderatorAssignmentId).length}
          detail="Eligible independent reviews"
        />
        <StatTile
          icon="clock"
          label="Claimed"
          value={items.filter(item => item.moderatorAssignmentId && !item.moderationDecisionId).length}
          detail="Current exact claims"
          tone="blue"
        />
        <StatTile
          icon="check-circle"
          label="Finalized"
          value={items.filter(item => item.moderationDecisionId).length}
          detail="Immutable decisions"
          tone="cyan"
        />
      </div>
      {loading ? (
        <AcademyPanel icon="shield" title="Loading moderation work" meta="Current scope">
          <p>Checking exact independent-review eligibility…</p>
        </AcademyPanel>
      ) : error ? (
        <AcademyPanel icon="alert" title="Moderation unavailable" meta="Fail closed">
          <p>{error}</p>
          <button type="button" className="secondary-btn button-sm" onClick={refresh}>
            Retry
          </button>
        </AcademyPanel>
      ) : !items.length ? (
        <EmptyState
          icon="shield"
          title="No moderation work available"
          text="Only eligible finalized evaluations and your exact claims appear here."
        />
      ) : (
        <div className="content-stack">
          {items.map(item => (
            <ConnectedAssessmentModerationCase
              key={item.evaluationId}
              item={item}
              refresh={refreshAll}
              notify={notify}
            />
          ))}
        </div>
      )}
      <AcademyPanel
        icon="award"
        title="Official result publication"
        meta={`${publication.items.length} exact records`}
      >
        {publication.loading ? <p>Checking independent publication authority…</p> : null}
        {publication.error ? (
          <>
            <p className="form-error">{publication.error}</p>
            <button type="button" className="secondary-btn button-sm" onClick={publication.refresh}>
              Retry
            </button>
          </>
        ) : null}
        {!publication.loading && !publication.error && !publication.items.length ? (
          <p>No exact moderated results are currently available in your independent publication scope.</p>
        ) : null}
        <div className="content-stack">
          {publication.items.map(item => (
            <ConnectedAssessmentOfficialResultCase
              key={item.moderationDecisionId}
              item={item}
              refresh={publication.refresh}
              notify={notify}
            />
          ))}
        </div>
      </AcademyPanel>
    </div>
  );
}

function AssessmentView({
  page,
  navigate,
  notify,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  if (page.startsWith("assignment-")) {
    const id = page.replace("assignment-", "");
    return <ConnectedAssessmentAssignmentDetail assignmentId={id} navigate={navigate} notify={notify} />;
  }
  if (page.startsWith("submission-")) {
    return (
      <ConnectedAssessmentEvaluationDetail
        attemptId={page.replace("submission-", "")}
        navigate={navigate}
        notify={notify}
      />
    );
  }
  if (page.startsWith("assessment-")) return <AssessmentBuilderView notify={notify} navigate={navigate} />;
  if (page.startsWith("question-")) return <QuestionEditorView notify={notify} navigate={navigate} />;
  if (page === "policy-reviews") return <ScoringPolicyReviewQueueView navigate={navigate} />;
  if (page.startsWith("policy-review-"))
    return (
      <ScoringPolicyReviewView
        proposalId={page.replace("policy-review-", "")}
        navigate={navigate}
        notify={notify}
      />
    );
  if (page.startsWith("rubric-"))
    return (
      <ConnectedRubricDetailView
        publicationId={page.replace("rubric-", "")}
        navigate={navigate}
        notify={notify}
      />
    );
  if (page === "assignments") {
    return <ConnectedAssessmentAssignmentLibrary navigate={navigate} notify={notify} />;
  }
  if (["grading", "queue-older-24h", "queue-high-risk", "queue-credential"].includes(page)) {
    return <ConnectedAssessmentEvaluationQueue page={page} navigate={navigate} />;
    /* Legacy demonstration queue is retained only as unreachable design reference while adjacent D11 previews remain.
    let filtered = SUBMISSIONS.filter(
      item =>
        `${item.learner} ${item.assignment} ${item.cohort}`.toLowerCase().includes(query.toLowerCase()) &&
        (status === "All statuses" || item.status === status),
    );
    if (page === "queue-older-24h")
      filtered = filtered.filter(item => item.age.includes("d") || Number(item.age.replace("h", "")) >= 24);
    if (page === "queue-high-risk")
      filtered = filtered.filter(item => item.risk === "high" || item.risk === "medium");
    if (page === "queue-credential")
      filtered = filtered.filter(
        item => item.assignment.toLowerCase().includes("capstone") || item.evidence >= 10,
      );
    return (
      <div className="instructor-page">
        <PageTitle
          title={
            page === "grading"
              ? "Grading queue"
              : page === "queue-older-24h"
                ? "Reviews older than 24 hours"
                : page === "queue-high-risk"
                  ? "High-risk evidence queue"
                  : "Credential evidence queue"
          }
          text="Preview · Review learner work, evidence, integrity signals and rubric decisions within a visible service level. Formal grading is not connected."
          actions={
            <button
              type="button"
              className="secondary-btn"
              disabled
              title="Preview: D11 grading queue export is not connected"
            >
              <Icon name="download" size="sm" />
              Export queue
            </button>
          }
        />
        <div className="stat-grid">
          <StatTile
            icon="clipboard"
            label="Pending"
            value={SUBMISSIONS.filter(item => item.status === "Pending").length}
            detail="Assigned to you"
          />
          <StatTile
            icon="clock"
            label="Median age"
            value="18.4h"
            detail="Target under 24 hours"
            tone="cyan"
          />
          <StatTile
            icon="refresh"
            label="Revision"
            value={SUBMISSIONS.filter(item => item.status === "Revision").length}
            detail="Learner action needed"
            tone="orange"
          />
          <StatTile
            icon="shield"
            label="Moderation"
            value={SUBMISSIONS.filter(item => item.status === "Moderation").length}
            detail="Second reviewer"
            tone="blue"
          />
        </div>
        <AcademyPanel
          icon="clipboard"
          title="Review work preview"
          meta={`${filtered.length} demonstration submissions`}
        >
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search learner, assignment or cohort…"
              />
            </div>
            <select className="select-field" value={status} onChange={event => setStatus(event.target.value)}>
              <option>All statuses</option>
              <option>Pending</option>
              <option>In review</option>
              <option>Revision</option>
              <option>Moderation</option>
              <option>Graded</option>
            </select>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                setQuery("");
                setStatus("All statuses");
              }}
            >
              <Icon name="refresh" size="sm" />
              Reset
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Assignment</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Risk</th>
                  <th>Evidence</th>
                  <th>AI disclosure</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} onClick={() => navigate("assessment", item.id)}>
                    <td>
                      <div className="person-cell">
                        <Avatar
                          initials={item.initials}
                          small
                          tone={item.risk === "high" ? "red" : item.risk === "medium" ? "orange" : "purple"}
                        />
                        <span>
                          <strong>{item.learner}</strong>
                          <span>{item.cohort}</span>
                        </span>
                      </div>
                    </td>
                    <td>{item.assignment}</td>
                    <td>
                      {item.submitted}
                      <span style={{ display: "block", color: "var(--muted)", fontSize: 14 }}>
                        {item.age} old
                      </span>
                    </td>
                    <td>
                      <TonePill
                        tone={
                          item.status === "Revision"
                            ? "orange"
                            : item.status === "Moderation"
                              ? "blue"
                              : item.status === "Graded"
                                ? "green"
                                : "purple"
                        }
                      >
                        {item.status}
                      </TonePill>
                    </td>
                    <td>
                      <TonePill
                        tone={item.risk === "high" ? "red" : item.risk === "medium" ? "orange" : "green"}
                      >
                        {item.risk}
                      </TonePill>
                    </td>
                    <td>{item.evidence}</td>
                    <td>
                      {item.aiDisclosure ? (
                        <TonePill tone="green">Present</TonePill>
                      ) : (
                        <TonePill tone="red">Missing</TonePill>
                      )}
                    </td>
                    <td>{item.score ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
    */
  }
  if (page === "assessments") {
    const assessments = [
      [
        "responsible-ai-decisions",
        "Responsible AI Product Decisions",
        "Scenario + practical",
        "Draft",
        1,
        "60 min",
        70,
        0,
      ],
      [
        "context-systems-check",
        "Context Systems Check",
        "Quiz + short response",
        "Published",
        2,
        "45 min",
        70,
        84,
      ],
      ["product-defense", "Product Evidence Oral Defense", "Oral defense", "Published", 1, "30 min", 75, 91],
      ["leadership-risk-scenario", "AI Leadership Risk Scenario", "Scenario", "Review", 1, "50 min", 70, 0],
    ];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Assessments"
          text="Preview · Create quizzes, practicals, scenarios and oral defenses with valid alignment, accessibility and human publishing."
          actions={
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("assessment", "assessment-responsible-ai-decisions")}
            >
              <Icon name="plus" size="sm" />
              Create assessment
            </button>
          }
        />
        <div className="simple-card-grid">
          {assessments.map(item => (
            <button
              type="button"
              className="simple-card"
              key={String(item[0])}
              onClick={() => navigate("assessment", `assessment-${item[0]}`)}
            >
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name="test" />
                </span>
                <div className="simple-card-copy">
                  <h3>{item[1]}</h3>
                  <p>{item[2]}</p>
                </div>
                <TonePill
                  tone={item[3] === "Published" ? "green" : item[3] === "Review" ? "orange" : "purple"}
                >
                  {item[3]}
                </TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Attempts</span>
                  <strong>{item[4]}</strong>
                </div>
                <div className="meta-pair">
                  <span>Time</span>
                  <strong>{item[5]}</strong>
                </div>
                <div className="meta-pair">
                  <span>Pass mark</span>
                  <strong>{item[6]}%</strong>
                </div>
                <div className="meta-pair">
                  <span>Average score</span>
                  <strong>{item[7] ? `${item[7]}%` : "Not used"}</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <span>Open builder</span>
                <Icon name="arrow-right" size="sm" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (page === "questions") {
    const questions = [
      [
        "agent-permissions",
        "Which permissions should an AI coding agent receive?",
        "Scenario",
        "Applied",
        "APB-M8",
        "Approved",
        "v2.1",
        12,
        0.42,
      ],
      [
        "retrieval-failure",
        "Which artifact demonstrates a retrieval failure?",
        "Multiple choice",
        "Foundation",
        "APB-M4",
        "Approved",
        "v1.7",
        28,
        0.36,
      ],
      [
        "human-authority",
        "Explain where human authority must remain visible.",
        "Short response",
        "Applied",
        "APB-M8",
        "Review",
        "v1.2",
        8,
        0.48,
      ],
      [
        "signed-handoff",
        "Why use a signed Class handoff token?",
        "Short response",
        "Advanced",
        "APB-M5",
        "Draft",
        "v0.4",
        0,
        0,
      ],
      [
        "accessibility-evidence",
        "Inspect this checkout evidence pack.",
        "Practical",
        "Advanced",
        "APB-M6",
        "Approved",
        "v2.0",
        6,
        0.51,
      ],
    ];
    return (
      <div className="instructor-page">
        <PageTitle
          title="Question bank"
          text="Preview · Reusable, versioned assessment items with alignment, quality and accessibility visible."
          actions={
            <>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => notify("Question import opened")}
              >
                <Icon name="upload" size="sm" />
                Import
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => navigate("assessment", "question-new")}
              >
                <Icon name="plus" size="sm" />
                Create question
              </button>
            </>
          }
        />
        <AcademyPanel icon="book" title="Question library" meta={`${questions.length} demonstration items`}>
          <div className="panel-toolbar">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input placeholder="Search prompts, outcomes or tags…" />
            </div>
            <select className="select-field">
              <option>All types</option>
              <option>Multiple choice</option>
              <option>Scenario</option>
              <option>Practical</option>
            </select>
            <select className="select-field">
              <option>All statuses</option>
              <option>Approved</option>
              <option>Review</option>
              <option>Draft</option>
            </select>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Type</th>
                  <th>Difficulty</th>
                  <th>Alignment</th>
                  <th>Status</th>
                  <th>Version</th>
                  <th>Uses</th>
                  <th>Discrimination</th>
                </tr>
              </thead>
              <tbody>
                {questions.map(item => (
                  <tr key={String(item[0])} onClick={() => navigate("assessment", `question-${item[0]}`)}>
                    <td>
                      <strong>{item[1]}</strong>
                    </td>
                    <td>{item[2]}</td>
                    <td>{item[3]}</td>
                    <td>{item[4]}</td>
                    <td>
                      <TonePill
                        tone={item[5] === "Approved" ? "green" : item[5] === "Review" ? "orange" : "purple"}
                      >
                        {item[5]}
                      </TonePill>
                    </td>
                    <td>{item[6]}</td>
                    <td>{item[7]}</td>
                    <td>{item[8] || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
  }
  if (page === "rubrics") {
    return <ConnectedRubricLibraryView navigate={navigate} />;
  }
  if (page === "moderation") return <ConnectedAssessmentModerationView notify={notify} />;
  return (
    <div className="instructor-page">
      <EmptyState
        icon="clipboard"
        title="Assessment workspace"
        text="Choose Assignments, Grading, Assessments, Question Bank, Rubrics or Moderation from the sidebar."
        action={
          <button type="button" className="primary-btn" onClick={() => navigate("assessment", "grading")}>
            Open grading queue
          </button>
        }
      />
    </div>
  );
}

function ProjectReviewDetailView({
  project,
  navigate,
  notify,
  openModal,
}: {
  project: ProjectReview;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [tab, setTab] = useState("overview");
  const [scores, setScores] = useState({
    architecture: project.architecture,
    evidence: project.evidence,
    verification: project.verification,
    responsible: project.risk === "high" ? 58 : 88,
  });
  const overall = Math.round(
    scores.architecture * 0.25 +
      scores.evidence * 0.3 +
      scores.verification * 0.25 +
      scores.responsible * 0.2,
  );
  const [feedback, setFeedback] = useState(
    project.risk === "high"
      ? "Before verification, add explicit consent and retention evidence and show how users can challenge an AI-generated insight."
      : "The architecture and evidence are strong. Complete the named staging condition and attach the final human merge note before verification.",
  );
  const decide = (decision: string) =>
    openModal({ type: "review-decision", payload: `${project.id}:${decision}:${overall}` });
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("projects", "reviews")}>
        <Icon name="arrow-left" size="sm" />
        Back to project reviews
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar initials={project.initials} />
          <div>
            <div className="hero-eyebrow">
              {project.type} · {project.status}
            </div>
            <h2>{project.title}</h2>
            <p>
              {project.learner} · {project.milestone}
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <RiskPill
            risk={project.risk === "high" ? "intervention" : project.risk === "medium" ? "watch" : "on-track"}
          />
          <button
            type="button"
            className="secondary-btn"
            onClick={() => notify("Live project opened", project.title)}
          >
            <Icon name="external" size="sm" />
            Live system
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => notify("Repository opened", project.title)}
          >
            <Icon name="git" size="sm" />
            Repository
          </button>
        </div>
      </section>
      <nav className="detail-tabs">
        {["overview", "demo", "architecture", "evidence", "responsible-use", "decision", "audit"].map(
          item => (
            <button
              type="button"
              key={item}
              className={tab === item ? "active" : ""}
              onClick={() => setTab(item)}
            >
              {item
                .split("-")
                .map(word => word[0].toUpperCase() + word.slice(1))
                .join(" ")}
            </button>
          ),
        )}
      </nav>
      {tab === "overview" ? (
        <div className="review-layout">
          <div className="review-content">
            <InstructorHero
              eyebrow="Project milestone review"
              title={project.milestone}
              text="Review the working system, repository, evidence, responsible-use controls and learner explanation before making a human verification decision."
              actions={
                <>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => notify("Project demo opened", project.title)}
                  >
                    <Icon name="play" size="sm" />
                    Open demo
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => navigate("buildRooms", "group-build-checkout")}
                  >
                    <Icon name="message" size="sm" />
                    Build Room
                  </button>
                </>
              }
              side={
                <>
                  <HeroSideCard
                    icon="workflow"
                    title="Architecture"
                    detail="System boundaries and reliability"
                    value={`${scores.architecture}%`}
                  />
                  <HeroSideCard
                    icon="award"
                    title="Evidence"
                    detail="Claims linked to artifacts"
                    value={`${scores.evidence}%`}
                  />
                  <HeroSideCard
                    icon="shield"
                    title="Verification"
                    detail="Tests and human review"
                    value={`${scores.verification}%`}
                  />
                </>
              }
            />
            <div className="content-grid">
              <AcademyPanel icon="link" title="Connected artifacts" meta="Traceable">
                <div className="evidence-stack">
                  {[
                    ["eye", "Live staging preview", "Version 0.9.4 · updated today"],
                    ["git", "Repository and PR history", "18 checks · 2 human merge notes"],
                    ["message", "Kijani Build Room", "124 members · instructor checkpoint"],
                    ["test", "Evaluation pack", "Automated and human evidence"],
                    ["file", "Decision and risk record", "4 decisions · 1 remaining condition"],
                  ].map(item => (
                    <div className="evidence-card" key={item[1]}>
                      <span>
                        <Icon name={item[0]} />
                      </span>
                      <div>
                        <strong>{item[1]}</strong>
                        <p>{item[2]}</p>
                      </div>
                      <button
                        type="button"
                        className="secondary-btn button-sm"
                        onClick={() => notify("Artifact opened", item[1])}
                      >
                        Open
                      </button>
                    </div>
                  ))}
                </div>
              </AcademyPanel>
              <AcademyPanel icon="alert" title="Current review focus" meta={project.risk}>
                <div className="decision-callout">
                  <strong>
                    {project.risk === "high"
                      ? "Consent boundary blocks verification"
                      : "One staging condition remains"}
                  </strong>
                  <p>
                    {project.risk === "high"
                      ? "The project processes sensitive conversation content without sufficient proof of consent, retention and learner challenge controls."
                      : "The third-party payment widget must be verified in staging before the human merge and final evidence selection."}
                  </p>
                </div>
                <div className="check-list" style={{ marginTop: 10 }}>
                  {[
                    "Working demo accessible",
                    "Repository ownership clear",
                    "Evaluation claims supported",
                    project.risk !== "high" ? "Responsible-use gate clear" : "Consent evidence incomplete",
                  ].map((item, index) => (
                    <div
                      className={`check-row ${index < 3 || project.risk !== "high" ? "done" : ""}`}
                      key={item}
                    >
                      <span className="check-marker">
                        <Icon name="check" size="sm" />
                      </span>
                      <span>
                        <strong>{item}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              </AcademyPanel>
            </div>
          </div>
          <aside className="decision-panel">
            <AcademyPanel icon="shield" title="Verification decision" meta={`${overall}% weighted`}>
              <div style={{ textAlign: "center", padding: 12 }}>
                <strong style={{ fontSize: 42 }}>{overall}%</strong>
                <p style={{ color: "var(--muted)", fontSize: 14 }}>
                  Human recommendation, not an automatic verdict
                </p>
              </div>
              <ProgressMeter value={overall} />
              <div className="hero-actions" style={{ display: "grid" }}>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={project.risk === "high"}
                  onClick={() => decide("verify")}
                >
                  <Icon name="check" size="sm" />
                  Verify milestone
                </button>
                <button type="button" className="secondary-btn" onClick={() => decide("revision")}>
                  <Icon name="refresh" size="sm" />
                  Request revision
                </button>
                <button type="button" className="ghost-btn" onClick={() => decide("panel")}>
                  <Icon name="users" size="sm" />
                  Send to panel
                </button>
              </div>
            </AcademyPanel>
          </aside>
        </div>
      ) : null}
      {tab === "demo" ? (
        <div className="content-grid">
          <AcademyPanel icon="eye" title="Live system preview" meta="Isolated staging">
            <div className="preview-frame desktop" style={{ margin: 0 }}>
              <div className="checkout-preview">
                <div className="checkout-preview-head">
                  <span>Kijani Market</span>
                  <span>Staging · v0.9.4</span>
                </div>
                <div className="checkout-product">
                  <span className="checkout-product-image">
                    <Icon name="image" />
                  </span>
                  <span>
                    <strong>Fresh produce basket</strong>
                    <span>Inclusive checkout demonstration</span>
                  </span>
                </div>
                <div className="checkout-field">
                  <label>Coupon code</label>
                  <div className="checkout-inline">
                    <input defaultValue="KJANI10" />
                    <button type="button" onClick={() => notify("Coupon applied in preview")}>
                      Apply
                    </button>
                  </div>
                  <small role="status">Coupon applied. UGX 4,000 saved.</small>
                </div>
                <button
                  type="button"
                  className="checkout-button"
                  onClick={() => notify("Preview order placed", "No production payment was made.")}
                >
                  Place demonstration order
                </button>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="test" title="Demo checklist" meta="Instructor">
            <div className="check-list">
              {[
                "Keyboard flow is complete",
                "Screen-reader announcement is polite and timely",
                "Mobile drawer restores focus",
                "Sensitive values remain server-side",
                "Third-party payment widget requires staging proof",
              ].map((item, index) => (
                <div className={`check-row ${index < 4 ? "done" : ""}`} key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>
                      {index < 4 ? "Verified in current preview" : "Named condition before final merge"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "architecture" ? (
        <div className="content-grid">
          <AcademyPanel icon="workflow" title="System architecture" meta="Learner explanation">
            <div className="integration-flow">
              <div className="integration-step">
                <span>1 · Client</span>
                <strong>Accessible checkout</strong>
                <p>Form state and feedback</p>
              </div>
              <div className="integration-step">
                <span>2 · Server</span>
                <strong>Order action</strong>
                <p>Validation and permissions</p>
              </div>
              <div className="integration-step">
                <span>3 · Services</span>
                <strong>Inventory + payment</strong>
                <p>Scoped connectors</p>
              </div>
              <div className="integration-step">
                <span>4 · Evidence</span>
                <strong>Audit + evaluation</strong>
                <p>Decision and test trail</p>
              </div>
            </div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>Architecture score</label>
              <input
                type="range"
                min="0"
                max="100"
                value={scores.architecture}
                onChange={event =>
                  setScores(current => ({ ...current, architecture: Number(event.target.value) }))
                }
              />
              <small>{scores.architecture}% · weighted at 25%</small>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Architecture controls" meta="Required">
            <div className="check-list">
              {[
                "Tenant and user boundaries named",
                "Secrets remain outside client code",
                "Retry and failure path documented",
                "Human override available",
                "Observability and audit events connected",
              ].map((item, index) => (
                <div
                  className={`check-row ${index !== 2 || project.risk !== "high" ? "done" : ""}`}
                  key={item}
                >
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "evidence" ? (
        <div className="content-grid">
          <AcademyPanel icon="award" title="Evidence pack" meta={`${project.evidence}% readiness`}>
            <div className="evidence-stack">
              {[
                ["image", "Problem and user evidence", "Interview synthesis · consent record"],
                ["git", "Implementation evidence", "PRs, tests and human merge notes"],
                ["test", "Evaluation evidence", "Golden cases, failures and human checks"],
                ["shield", "Responsible-use evidence", "Permissions, consent, override and retention"],
                ["file", "Reflection", "Decisions, rejected suggestions and remaining risks"],
              ].map((item, index) => (
                <div className="evidence-card" key={item[1]}>
                  <span>
                    <Icon name={item[0]} />
                  </span>
                  <div>
                    <strong>{item[1]}</strong>
                    <p>{item[2]}</p>
                  </div>
                  <TonePill
                    tone={
                      index === 3 && project.risk === "high"
                        ? "red"
                        : index === 4 && project.status === "In review"
                          ? "orange"
                          : "green"
                    }
                  >
                    {index === 3 && project.risk === "high"
                      ? "Missing"
                      : index === 4 && project.status === "In review"
                        ? "Condition"
                        : "Verified"}
                  </TonePill>
                </div>
              ))}
            </div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>Evidence score</label>
              <input
                type="range"
                min="0"
                max="100"
                value={scores.evidence}
                onChange={event =>
                  setScores(current => ({ ...current, evidence: Number(event.target.value) }))
                }
              />
              <small>{scores.evidence}% · weighted at 30%</small>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="workflow" title="Skill · System · Proof" meta="Credential-ready structure">
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Skill</strong>
                  <p>Learner demonstrates the named capability</p>
                </div>
                <TonePill tone="green">5 items</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>System</strong>
                  <p>Working implementation with boundaries</p>
                </div>
                <TonePill tone="green">7 items</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Proof</strong>
                  <p>Evaluation, reflection and human review</p>
                </div>
                <TonePill tone={project.risk === "high" ? "red" : "orange"}>
                  {project.risk === "high" ? "Gap" : "Condition"}
                </TonePill>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "responsible-use" ? (
        <div className="content-grid">
          <AcademyPanel icon="shield" title="Responsible-use gate" meta={project.risk}>
            <div className="check-list">
              {[
                [true, "Purpose and user impact are explicit"],
                [project.risk !== "high", "Consent and data rights are evidenced"],
                [true, "Human override and escalation exist"],
                [true, "AI limitations are communicated"],
                [project.risk !== "high", "Retention and deletion are defined"],
                [true, "AI activity and decision owner are visible"],
              ].map((item, index) => (
                <div className={`check-row ${item[0] ? "done" : ""}`} key={index}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item[1]}</strong>
                    <span>{item[0] ? "Evidence attached" : "Blocks verification until resolved"}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>Responsible-use score</label>
              <input
                type="range"
                min="0"
                max="100"
                value={scores.responsible}
                onChange={event =>
                  setScores(current => ({ ...current, responsible: Number(event.target.value) }))
                }
              />
              <small>{scores.responsible}% · gate and 20% of weighted review</small>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="alert" title="Decision boundary" meta="Human only">
            <div className="decision-callout">
              <strong>AI cannot verify a project or recommend a credential.</strong>
              <p>
                Automated checks may identify missing artifacts or policy conflicts. An assigned instructor or
                review panel interprets the evidence, records reasons and owns the outcome.
              </p>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "decision" ? (
        <div className="review-layout">
          <AcademyPanel icon="message" title="Learner feedback" meta="Visible after decision">
            <div className="form-field">
              <label>Feedback</label>
              <textarea
                value={feedback}
                onChange={event => setFeedback(event.target.value)}
                style={{ minHeight: 260 }}
              />
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => notify("Feedback draft saved")}
              >
                Save draft
              </button>
            </div>
          </AcademyPanel>
          <aside className="decision-panel">
            <AcademyPanel icon="shield" title="Human decision" meta={`${overall}% weighted`}>
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Architecture</strong>
                    <p>25% weight</p>
                  </div>
                  <TonePill>{scores.architecture}%</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Evidence</strong>
                    <p>30% weight</p>
                  </div>
                  <TonePill>{scores.evidence}%</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Verification</strong>
                    <p>25% weight</p>
                  </div>
                  <TonePill>{scores.verification}%</TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Responsible use</strong>
                    <p>20% + gate</p>
                  </div>
                  <TonePill tone={scores.responsible < 70 ? "red" : "green"}>{scores.responsible}%</TonePill>
                </div>
              </div>
              <div className="hero-actions" style={{ display: "grid" }}>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={scores.responsible < 70}
                  onClick={() => decide("verify")}
                >
                  <Icon name="check" size="sm" />
                  Verify project
                </button>
                <button type="button" className="secondary-btn" onClick={() => decide("revision")}>
                  <Icon name="refresh" size="sm" />
                  Request revision
                </button>
                <button type="button" className="ghost-btn" onClick={() => decide("panel")}>
                  <Icon name="users" size="sm" />
                  Panel review
                </button>
              </div>
            </AcademyPanel>
          </aside>
        </div>
      ) : null}
      {tab === "audit" ? (
        <AcademyPanel icon="activity" title="Project decision trail" meta="Attributable">
          <div className="timeline">
            {[
              ["upload", "Milestone submitted", `${project.learner} · evidence pack v2`],
              ["test", "Automated checks completed", "Structure and policy checks · no academic decision"],
              ["eye", "Instructor review opened", "Amina Mutesi · current review"],
              [
                "shield",
                "Build Room checkpoint recorded",
                "Conditional approval · staging evidence required",
              ],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>Recorded</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
    </div>
  );
}

function CapstoneDetailView({
  capstone,
  navigate,
  notify,
  openModal,
}: {
  capstone: CapstoneReview;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [tab, setTab] = useState("evidence");
  const [scores, setScores] = useState({
    impact: 88,
    technical: 91,
    responsible: capstone.responsibleUse === "Pass" ? 94 : 65,
    defense: 86,
  });
  const overall = Math.round((scores.impact + scores.technical + scores.responsible + scores.defense) / 4);
  const [note, setNote] = useState(
    "Strong system and evidence. Ask the learner to explain how the source-retention policy changes when a client engagement ends.",
  );
  return (
    <div className="instructor-page">
      <button type="button" className="ghost-btn button-sm" onClick={() => navigate("projects", "capstones")}>
        <Icon name="arrow-left" size="sm" />
        Back to capstones
      </button>
      <section className="detail-hero" style={{ marginTop: 12 }}>
        <div className="detail-identity">
          <Avatar initials={capstone.initials} />
          <div>
            <div className="hero-eyebrow">
              {capstone.status} · {capstone.program}
            </div>
            <h2>{capstone.title}</h2>
            <p>
              {capstone.learner} · defense {capstone.defenseDate}
            </p>
          </div>
        </div>
        <div className="detail-actions">
          <TonePill tone={capstone.responsibleUse === "Pass" ? "green" : "orange"}>
            Responsible use: {capstone.responsibleUse}
          </TonePill>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => notify("Capstone live system opened")}
          >
            Live system
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => {
              notify(
                "Canonical defense session required",
                "Schedule the governed defense session before sharing learner Class access.",
              );
              navigate("class", "schedule");
            }}
          >
            <Icon name="video" size="sm" />
            Defense Class
          </button>
        </div>
      </section>
      <nav className="detail-tabs">
        {["evidence", "panel", "scoring", "defense", "recommendation", "timeline"].map(item => (
          <button
            type="button"
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item[0].toUpperCase() + item.slice(1)}
          </button>
        ))}
      </nav>
      {tab === "evidence" ? (
        <div className="content-grid">
          <AcademyPanel
            icon="award"
            title="Capstone evidence checklist"
            meta={`${capstone.evidenceComplete}% complete`}
          >
            <div className="check-list">
              {[
                "Working system and live demonstration",
                "Repository and version history",
                "Problem and user evidence",
                "Evaluation dataset and failure analysis",
                "Responsible-use controls and decision record",
                "Adoption or impact evidence",
                "Learner reflection and AI disclosure",
              ].map((item, index) => (
                <div
                  className={`check-row ${index < Math.round(capstone.evidenceComplete / 15) ? "done" : ""}`}
                  key={item}
                >
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                    <span>
                      {index < Math.round(capstone.evidenceComplete / 15)
                        ? "Verified or ready for panel"
                        : "Evidence gap remains"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="workflow" title="Credential pathway" meta="Human reviewed">
            <div className="integration-flow">
              <div className="integration-step">
                <span>1</span>
                <strong>Evidence complete</strong>
                <p>{capstone.evidenceComplete}% ready</p>
              </div>
              <div className="integration-step">
                <span>2</span>
                <strong>Panel calibration</strong>
                <p>Reviewers align rubric</p>
              </div>
              <div className="integration-step">
                <span>3</span>
                <strong>Oral defense</strong>
                <p>{capstone.defenseDate}</p>
              </div>
              <div className="integration-step">
                <span>4</span>
                <strong>Recommendation</strong>
                <p>Panel records reasons</p>
              </div>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "panel" ? (
        <div className="content-grid">
          <AcademyPanel icon="users" title="Review panel" meta="3 members">
            <div className="evidence-stack">
              {[
                ["AM", "Amina Mutesi", "Lead Instructor · panel chair"],
                ["TM", "Theo Morgan", "External technical reviewer"],
                ["SN", "Sarah Namatovu", "Responsible AI reviewer"],
              ].map(item => (
                <div className="evidence-card" key={item[1]}>
                  <Avatar initials={item[0]} small />
                  <div>
                    <strong>{item[1]}</strong>
                    <p>{item[2]}</p>
                  </div>
                  <TonePill tone="green">Confirmed</TonePill>
                </div>
              ))}
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => notify("Panel member invitation opened")}
              >
                Manage panel
              </button>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Independence and conflicts" meta="Clear">
            <div className="check-list">
              {[
                "No reviewer assessed all prior milestones",
                "No declared financial or personal conflict",
                "Responsible-use reviewer has decision authority",
                "Panel rubric calibration is current",
              ].map(item => (
                <div className="check-row done" key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "scoring" ? (
        <div className="content-grid">
          <AcademyPanel icon="evidence" title="Capstone scoring" meta={`${overall}% provisional`}>
            <div className="rubric-grid">
              {(
                [
                  ["impact", "Problem, user and impact", 25],
                  ["technical", "System quality and reliability", 25],
                  ["responsible", "Responsible AI and governance", 25],
                  ["defense", "Defense and reflection", 25],
                ] as Array<[keyof typeof scores, string, number]>
              ).map(([key, label, weight]) => (
                <div className="rubric-criterion" key={key}>
                  <div className="rubric-criterion-head">
                    <strong>{label}</strong>
                    <TonePill tone="purple">{weight}%</TonePill>
                  </div>
                  <div className="form-field" style={{ marginTop: 8 }}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={scores[key]}
                      onChange={event =>
                        setScores(current => ({ ...current, [key]: Number(event.target.value) }))
                      }
                    />
                    <small>{scores[key]}%</small>
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Provisional outcome" meta="Panel decides">
            <div style={{ textAlign: "center", padding: 18 }}>
              <strong style={{ fontSize: 48 }}>{overall}%</strong>
              <p style={{ color: "var(--muted)", fontSize: 14 }}>
                Responsible-use gate must pass regardless of weighted total.
              </p>
            </div>
            <ProgressMeter value={overall} />
            <div className="decision-callout" style={{ marginTop: 12 }}>
              <strong>
                {scores.responsible >= 70
                  ? "Gate currently passes"
                  : "Responsible-use gate blocks recommendation"}
              </strong>
              <p>Panel members must cite evidence and record any dissent.</p>
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "defense" ? (
        <div className="content-grid">
          <AcademyPanel icon="video" title="Oral defense" meta={capstone.defenseDate}>
            <div className="action-list">
              <div className="setting-row">
                <div>
                  <strong>Chatoner Class session</strong>
                  <p>Private panel room · unique link and code</p>
                </div>
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => {
                    notify(
                      "Canonical defense session required",
                      "Schedule the governed defense session before opening its Class studio.",
                    );
                    navigate("class", "schedule");
                  }}
                >
                  Open studio
                </button>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Duration</strong>
                  <p>15-minute demo · 20-minute questions · 10-minute panel</p>
                </div>
                <TonePill tone="blue">45 min</TonePill>
              </div>
              <div className="setting-row">
                <div>
                  <strong>Recording</strong>
                  <p>Off unless learner and panel consent</p>
                </div>
                <TonePill>Off</TonePill>
              </div>
            </div>
          </AcademyPanel>
          <AcademyPanel icon="message" title="Panel questions" meta="Prepared">
            <div className="question-sequence">
              {[
                "Which decision would you reverse after seeing the evaluation failures?",
                "How does the source-retention policy change after an engagement ends?",
                "Where could a user challenge or override the system?",
                "Which AI-generated suggestion did you reject, and why?",
              ].map((question, index) => (
                <div className="sequence-item" key={question}>
                  <span className="sequence-number">{index + 1}</span>
                  <div>
                    <strong>{question}</strong>
                    <p>{["Evaluation", "Governance", "Human authority", "Reflection"][index]}</p>
                  </div>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      ) : null}
      {tab === "recommendation" ? (
        <div className="review-layout">
          <AcademyPanel icon="lock" title="Private panel note" meta="Not learner-visible">
            <div className="form-field">
              <label>Panel discussion note</label>
              <textarea
                value={note}
                onChange={event => setNote(event.target.value)}
                style={{ minHeight: 260 }}
              />
            </div>
            <div className="modal-form-note">
              <Icon name="lock" size="sm" />
              Panel notes are restricted. The learner receives the final decision, reasons and actionable
              feedback—not private deliberation.
            </div>
          </AcademyPanel>
          <aside className="decision-panel">
            <AcademyPanel icon="award" title="Credential recommendation" meta={`${overall}% provisional`}>
              <div className="action-list">
                <div className="setting-row">
                  <div>
                    <strong>Evidence</strong>
                    <p>Required complete</p>
                  </div>
                  <TonePill tone={capstone.evidenceComplete === 100 ? "green" : "orange"}>
                    {capstone.evidenceComplete}%
                  </TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Responsible use</strong>
                    <p>Mandatory gate</p>
                  </div>
                  <TonePill tone={scores.responsible >= 70 ? "green" : "red"}>
                    {scores.responsible >= 70 ? "Pass" : "Hold"}
                  </TonePill>
                </div>
                <div className="setting-row">
                  <div>
                    <strong>Panel score</strong>
                    <p>Four equal criteria</p>
                  </div>
                  <TonePill tone="blue">{overall}%</TonePill>
                </div>
              </div>
              <div className="hero-actions" style={{ display: "grid" }}>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={capstone.evidenceComplete < 100 || scores.responsible < 70}
                  onClick={() =>
                    openModal({ type: "review-decision", payload: `${capstone.id}:recommend:${overall}` })
                  }
                >
                  <Icon name="award" size="sm" />
                  Recommend credential
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() =>
                    openModal({ type: "review-decision", payload: `${capstone.id}:revision:${overall}` })
                  }
                >
                  Request revision
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() =>
                    openModal({ type: "review-decision", payload: `${capstone.id}:decline:${overall}` })
                  }
                >
                  Decline
                </button>
              </div>
            </AcademyPanel>
          </aside>
        </div>
      ) : null}
      {tab === "timeline" ? (
        <AcademyPanel icon="activity" title="Capstone decision timeline" meta="Immutable">
          <div className="timeline">
            {[
              [
                "upload",
                "Evidence pack submitted",
                `${capstone.learner} · ${capstone.evidenceComplete}% complete`,
              ],
              ["shield", "Responsible-use review completed", capstone.responsibleUse],
              ["users", "Panel confirmed", "Three reviewers · conflicts cleared"],
              ["video", "Defense scheduled", capstone.defenseDate],
            ].map(item => (
              <div className="timeline-item" key={item[1]}>
                <span className="timeline-dot">
                  <Icon name={item[0]} size="sm" />
                </span>
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <time>Recorded</time>
              </div>
            ))}
          </div>
        </AcademyPanel>
      ) : null}
    </div>
  );
}

function ProjectsInstructorView({
  page,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  if (page.startsWith("project-")) {
    const project = PROJECT_REVIEWS.find(item => item.id === page) || PROJECT_REVIEWS[0];
    return (
      <ProjectReviewDetailView project={project} navigate={navigate} notify={notify} openModal={openModal} />
    );
  }
  if (page.startsWith("capstone-")) {
    const capstone = CAPSTONES.find(item => item.id === page) || CAPSTONES[0];
    return (
      <CapstoneDetailView capstone={capstone} navigate={navigate} notify={notify} openModal={openModal} />
    );
  }
  if (page === "capstones")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Capstone reviews"
          text="Evidence completeness, panel independence, oral defense, responsible-use gate and credential recommendation."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "capstones" })}
            >
              <Icon name="download" size="sm" />
              Export panel pack
            </button>
          }
        />
        <div className="capstone-grid">
          {CAPSTONES.map(item => (
            <button
              type="button"
              className="simple-card"
              key={item.id}
              onClick={() => navigate("projects", item.id)}
            >
              <div className="simple-card-head">
                <Avatar initials={item.initials} />
                <div className="simple-card-copy">
                  <h3>{item.learner}</h3>
                  <p>{item.title}</p>
                </div>
                <TonePill
                  tone={
                    item.status === "Recommended" ? "green" : item.status === "Revision" ? "orange" : "blue"
                  }
                >
                  {item.status}
                </TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Program</span>
                  <strong>{item.program}</strong>
                </div>
                <div className="meta-pair">
                  <span>Evidence complete</span>
                  <strong>{item.evidenceComplete}%</strong>
                </div>
                <ProgressMeter value={item.evidenceComplete} />
                <div className="meta-pair">
                  <span>Defense</span>
                  <strong>{item.defenseDate}</strong>
                </div>
                <div className="meta-pair">
                  <span>Responsible use</span>
                  <strong>{item.responsibleUse}</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <span>{item.recommendation}</span>
                <Icon name="arrow-right" size="sm" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  if (page === "evidence")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Evidence verification"
          text="Verify that Skill, System and Proof claims are attributable, sufficient, safe and human reviewed."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "evidence-verification" })}
            >
              <Icon name="download" size="sm" />
              Export view
            </button>
          }
        />
        <div className="stat-grid">
          <StatTile icon="award" label="Evidence packs" value="28" detail="Across projects and capstones" />
          <StatTile icon="check" label="Verified" value="17" detail="Ready for decision" tone="cyan" />
          <StatTile icon="alert" label="Gaps" value="8" detail="Learner action required" tone="orange" />
          <StatTile icon="shield" label="Moderation" value="3" detail="Second review" tone="blue" />
        </div>
        <AcademyPanel icon="workflow" title="Evidence readiness by project" meta="Skill · System · Proof">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Learner</th>
                  <th>Skill</th>
                  <th>System</th>
                  <th>Proof</th>
                  <th>Responsible use</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {PROJECT_REVIEWS.map(project => (
                  <tr key={project.id} onClick={() => navigate("projects", project.id)}>
                    <td>
                      <strong>{project.title}</strong>
                      <span style={{ display: "block", color: "var(--muted)", fontSize: 14 }}>
                        {project.type}
                      </span>
                    </td>
                    <td>{project.learner}</td>
                    <td>{Math.min(100, project.architecture + 5)}%</td>
                    <td>{project.architecture}%</td>
                    <td>{project.evidence}%</td>
                    <td>
                      <TonePill
                        tone={
                          project.risk === "high" ? "red" : project.risk === "medium" ? "orange" : "green"
                        }
                      >
                        {project.risk === "high" ? "Hold" : project.risk === "medium" ? "Review" : "Pass"}
                      </TonePill>
                    </td>
                    <td>{project.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );
  if (page === "credentials")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Credential decisions"
          text="Academic recommendations remain separated from certificate issuance and are recorded with evidence, reasons and reviewers."
          actions={
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "credential-decisions" })}
            >
              <Icon name="download" size="sm" />
              Export audit
            </button>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="award" title="Ready for recommendation" meta="3 learners">
            <div className="priority-list">
              {[
                CAPSTONES[0],
                CAPSTONES[2],
                {
                  ...CAPSTONES[1],
                  learner: "Sofia Martinez",
                  initials: "SM",
                  title: "Campaign Performance Copilot",
                  evidenceComplete: 100,
                  status: "Panel review" as const,
                  responsibleUse: "Pass" as const,
                  recommendation: "Pending" as const,
                  id: "capstone-sofia",
                },
              ].map(item => (
                <button
                  type="button"
                  className="priority-item"
                  key={item.id}
                  onClick={() =>
                    navigate("projects", item.id === "capstone-sofia" ? "project-sofia" : item.id)
                  }
                >
                  <Avatar initials={item.initials} small />
                  <span>
                    <strong>{item.learner}</strong>
                    <p>
                      {item.title} · evidence {item.evidenceComplete}% · responsible use {item.responsibleUse}
                    </p>
                  </span>
                  <span className="priority-meta">
                    <TonePill tone={item.recommendation === "Recommend" ? "green" : "orange"}>
                      {item.recommendation}
                    </TonePill>
                    <Icon name="arrow-right" size="sm" />
                  </span>
                </button>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="shield" title="Decision safeguards" meta="Required">
            <div className="check-list">
              {[
                "Evidence pack is complete and attributable",
                "Responsible-use gate passes",
                "Independent review or panel requirement is met",
                "Learner-visible reasons are prepared",
                "Recommendation and issuance permissions are separated",
              ].map(item => (
                <div className="check-row done" key={item}>
                  <span className="check-marker">
                    <Icon name="check" size="sm" />
                  </span>
                  <span>
                    <strong>{item}</strong>
                  </span>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "panels")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Review panels"
          text="Manage membership, conflicts, calibration and Class defense sessions for high-stakes decisions."
          actions={
            <button type="button" className="primary-btn" onClick={() => notify("New panel setup opened")}>
              <Icon name="plus" size="sm" />
              Create panel
            </button>
          }
        />
        <div className="simple-card-grid">
          {[
            ["Capstone Panel · 15 Nov", "3 reviewers · 2 candidates", "Calibrated", "15 Nov · 2:00–5:00 PM"],
            [
              "Responsible AI Exception Panel",
              "4 reviewers · 1 case",
              "Calibration due",
              "12 Nov · 11:00 AM",
            ],
            ["Prompt Engineering Final Panel", "3 reviewers · 3 candidates", "Ready", "18 Nov · 10:00 AM"],
          ].map((item, index) => (
            <div className="simple-card" key={item[0]}>
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name="users" />
                </span>
                <div className="simple-card-copy">
                  <h3>{item[0]}</h3>
                  <p>{item[1]}</p>
                </div>
                <TonePill tone={index === 1 ? "orange" : "green"}>{item[2]}</TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Schedule</span>
                  <strong>{item[3]}</strong>
                </div>
                <div className="meta-pair">
                  <span>Conflicts</span>
                  <strong>None declared</strong>
                </div>
                <div className="meta-pair">
                  <span>Class room</span>
                  <strong>Private code assigned</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Panel opened", item[0])}
                >
                  Manage
                </button>
                <button
                  type="button"
                  className="primary-btn button-sm"
                  onClick={() => navigate("class", "schedule")}
                >
                  Open Class
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Projects & Credentials"
        title="Verify working systems. Protect the credibility of the proof."
        text="Review architecture, evidence, evaluation, responsible-use controls and learner decisions before a milestone or credential recommendation is recorded."
        actions={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("projects", "project-ronald")}
            >
              <Icon name="folder" size="sm" />
              Open priority review
            </button>
            <button type="button" className="secondary-btn" onClick={() => navigate("projects", "capstones")}>
              <Icon name="award" size="sm" />
              Capstone panel
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard icon="folder" title="Project reviews" detail="Assigned to you" value="5" />
            <HeroSideCard icon="award" title="Capstones" detail="2 need panel action" value="3" />
            <HeroSideCard
              icon="shield"
              title="Responsible-use holds"
              detail="Blocks recommendation"
              value="1"
            />
          </>
        }
      />
      <div className="project-review-grid">
        {PROJECT_REVIEWS.map(project => (
          <button
            type="button"
            className="simple-card"
            key={project.id}
            onClick={() => navigate("projects", project.id)}
          >
            <div className="simple-card-head">
              <Avatar initials={project.initials} />
              <div className="simple-card-copy">
                <h3>{project.title}</h3>
                <p>
                  {project.learner} · {project.type}
                </p>
              </div>
              <TonePill
                tone={
                  project.status === "Verified" ? "green" : project.status === "Revision" ? "orange" : "blue"
                }
              >
                {project.status}
              </TonePill>
            </div>
            <div className="simple-card-body">
              <div className="metric-bars">
                <div className="metric-bar">
                  <span>Architecture</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${project.architecture}%` }} />
                  </div>
                  <strong>{project.architecture}</strong>
                </div>
                <div className="metric-bar">
                  <span>Evidence</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${project.evidence}%` }} />
                  </div>
                  <strong>{project.evidence}</strong>
                </div>
                <div className="metric-bar">
                  <span>Verification</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${project.verification}%` }} />
                  </div>
                  <strong>{project.verification}</strong>
                </div>
              </div>
            </div>
            <div className="simple-card-foot">
              <RiskPill
                risk={
                  project.risk === "high" ? "intervention" : project.risk === "medium" ? "watch" : "on-track"
                }
              />
              <span>
                {project.milestone}
                <Icon name="arrow-right" size="sm" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function OfficeHoursView({
  notify,
  openModal,
}: {
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const [slots, setSlots] = useState(OFFICE_HOURS);
  const toggle = (id: string, on: boolean) =>
    setSlots(current => current.map(slot => (slot.id === id ? { ...slot, enabled: on } : slot)));
  return (
    <div className="instructor-page">
      <InstructorHero
        eyebrow="Learner support"
        title="Offer structured help without turning support into private, untracked work."
        text="Availability, bookings, Class links, learner context and follow-up remain connected to Academy while private notes stay permission-scoped."
        actions={
          <>
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "office-hours" })}>
              <Icon name="plus" size="sm" />
              Add availability
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                copyToClipboard(
                  "https://instructors.academy.chatoner.com/#/instructor/engagement/office-hours",
                );
                notify("Booking link copied");
              }}
            >
              <Icon name="link" size="sm" />
              Copy booking link
            </button>
          </>
        }
        side={
          <>
            <HeroSideCard
              icon="calendar"
              title="Bookings this week"
              detail="Across 3 appointment types"
              value="6"
            />
            <HeroSideCard icon="clock" title="Available time" detail="Enabled recurring windows" value="5h" />
            <HeroSideCard icon="users" title="Waitlist" detail="No urgent learner waiting" value="2" />
          </>
        }
      />
      <div className="office-grid">
        <AcademyPanel icon="calendar" title="Recurring availability" meta="Africa/Kampala">
          <div className="action-list">
            {slots.map(slot => (
              <div className="setting-row" key={slot.id}>
                <div>
                  <strong>
                    {slot.day} · {slot.start}–{slot.end}
                  </strong>
                  <p>
                    {slot.type} · {slot.bookings}/{slot.capacity} booked
                  </p>
                  <ProgressMeter value={slot.capacity ? (slot.bookings / slot.capacity) * 100 : 0} />
                </div>
                <div className="row-actions">
                  <Switch
                    value={slot.enabled}
                    label={`${slot.day} office hours`}
                    onChange={on => toggle(slot.id, on)}
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => openModal({ type: "office-hours" })}
                  >
                    <Icon name="edit" size="sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="users" title="Upcoming bookings" meta="6 this week">
          <div className="evidence-stack">
            {[
              ["JT", "Jamal Thompson", "Today · 11:30 · Consent and retention", "25 min"],
              ["MC", "Maya Chen", "Wednesday · 12:00 · Context source policy", "25 min"],
              ["RI", "Ronald Isabirye", "Wednesday · 12:30 · Staging evidence", "30 min"],
              ["SM", "Sofia Martinez", "Friday · 15:00 · Recommendation boundary", "20 min"],
            ].map(item => (
              <div className="evidence-card" key={item[1]}>
                <Avatar initials={item[0]} small />
                <div>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </div>
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => notify("Booking opened", item[1])}
                >
                  {item[3]}
                </button>
              </div>
            ))}
          </div>
        </AcademyPanel>
      </div>
      <AcademyPanel icon="chart" title="Booking demand" meta="Last 6 weeks">
        <div className="chart-row">
          {[
            ["W1", 4],
            ["W2", 7],
            ["W3", 5],
            ["W4", 8],
            ["W5", 6],
            ["W6", 9],
          ].map(item => (
            <div className="chart-column" key={String(item[0])}>
              <strong>{item[1]}</strong>
              <i style={{ height: `${Number(item[1]) * 14}px` }} />
              <span>{item[0]}</span>
            </div>
          ))}
        </div>
      </AcademyPanel>
    </div>
  );
}

function MessagesInstructorView({
  page,
  navigate,
  notify,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const initialId = page.startsWith("thread-") ? page.replace("thread-", "") : MESSAGE_THREADS[0].id;
  const [activeId, setActiveId] = useState(initialId);
  const [threads, setThreads] = useState(MESSAGE_THREADS);
  const [composer, setComposer] = useState("");
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const channelFor = (label: string) =>
    label === "Learners" ? "learner" : label === "Cohorts" ? "cohort" : label === "Staff" ? "staff" : "all";
  const filteredThreads = threads.filter(
    thread =>
      (filter === "All" || thread.channel === channelFor(filter)) &&
      `${thread.participant} ${thread.subject} ${thread.preview}`.toLowerCase().includes(query.toLowerCase()),
  );
  const active = threads.find(thread => thread.id === activeId) || threads[0];
  const applyFilter = (next: string) => {
    setFilter(next);
    const nextThreads = threads.filter(thread => next === "All" || thread.channel === channelFor(next));
    if (nextThreads.length && !nextThreads.some(thread => thread.id === activeId))
      setActiveId(nextThreads[0].id);
  };
  const send = () => {
    if (!composer.trim()) return;
    setThreads(current =>
      current.map(thread =>
        thread.id === active.id
          ? {
              ...thread,
              messages: [
                ...thread.messages,
                {
                  id: `m-${Date.now()}`,
                  author: "You",
                  initials: "AM",
                  text: composer.trim(),
                  time: nowTime(),
                  mine: true,
                },
              ],
              preview: composer.trim(),
              unread: 0,
            }
          : thread,
      ),
    );
    setComposer("");
    notify("Message sent", active.participant);
  };
  const select = (id: string) => {
    setActiveId(id);
    setThreads(current => current.map(thread => (thread.id === id ? { ...thread, unread: 0 } : thread)));
    navigate("engagement", `thread-${id}`);
  };
  return (
    <div className="instructor-page">
      <PageTitle
        title="Messages"
        text="Communicate through approved Academy channels with learner, cohort and staff context preserved."
        actions={
          <button type="button" className="primary-btn" onClick={() => notify("New message composer opened")}>
            <Icon name="plus" size="sm" />
            New message
          </button>
        }
      />
      <div className={`conversation-layout ${page.startsWith("thread-") ? "mobile-thread-open" : ""}`}>
        <aside className="thread-list">
          <div className="thread-list-head">
            <div className="search-page-box">
              <Icon name="search" size="sm" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search conversations…"
              />
            </div>
            <div className="tag-row" style={{ marginTop: 8 }}>
              {["All", "Learners", "Cohorts", "Staff"].map(item => (
                <button
                  type="button"
                  key={item}
                  className={`tag-chip ${filter === item ? "active" : ""}`}
                  onClick={() => applyFilter(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          {filteredThreads.map(thread => (
            <button
              type="button"
              className={`thread-button ${activeId === thread.id ? "active" : ""}`}
              key={thread.id}
              onClick={() => select(thread.id)}
            >
              <Avatar initials={thread.initials} small />
              <span>
                <strong>{thread.participant}</strong>
                <p>{thread.subject}</p>
                <p>{thread.preview}</p>
              </span>
              {thread.unread ? <span className="count">{thread.unread}</span> : null}
            </button>
          ))}
        </aside>
        <section className="conversation-pane">
          <div className="conversation-head">
            <button
              type="button"
              className="icon-btn mobile-menu"
              onClick={() => navigate("engagement", "messages")}
            >
              <Icon name="arrow-left" />
            </button>
            <Avatar initials={active.initials} small />
            <div>
              <strong>{active.participant}</strong>
              <span>
                {active.role} · {active.subject}
              </span>
            </div>
            <button type="button" className="icon-btn" onClick={() => notify("Conversation options opened")}>
              <Icon name="more" />
            </button>
          </div>
          <div className="conversation-scroll">
            {active.messages.map(message => (
              <div className={`conversation-message ${message.mine ? "mine" : ""}`} key={message.id}>
                <Avatar initials={message.initials} small />
                <div className="conversation-bubble">
                  <strong>{message.author}</strong>
                  <p>{message.text}</p>
                  <time>{message.time}</time>
                </div>
              </div>
            ))}
          </div>
          <div className="conversation-composer">
            <textarea
              value={composer}
              onChange={event => setComposer(event.target.value)}
              placeholder={`Message ${active.participant}…`}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            <button type="button" className="primary-btn" onClick={send}>
              <Icon name="send" size="sm" />
              Send
            </button>
          </div>
        </section>
      </div>
      <div className="modal-form-note" style={{ marginTop: 10 }}>
        <Icon name="shield" size="sm" />
        Use Academy messages for learning and support. Do not request passwords, private financial data or
        unnecessary sensitive information.
      </div>
    </div>
  );
}

function EngagementView({
  page,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  if (page === "office-hours") return <OfficeHoursView notify={notify} openModal={openModal} />;
  if (page === "messages" || page.startsWith("thread-"))
    return <MessagesInstructorView page={page} navigate={navigate} notify={notify} />;
  if (page === "announcements")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Announcements"
          text="Send clear, multilingual cohort updates connected to the exact learning, Class, Lab, project or deadline."
          actions={
            <button type="button" className="primary-btn" onClick={() => openModal({ type: "announcement" })}>
              <Icon name="plus" size="sm" />
              Create announcement
            </button>
          }
        />
        <div className="content-grid">
          <AcademyPanel icon="message" title="Recent announcements" meta="2 active">
            <div className="evidence-stack">
              {[
                [
                  "Week 6 · From claims to evidence",
                  "Builder October 2026 · sent today 7:12 AM",
                  "34 delivered · 29 read",
                ],
                [
                  "Capstone panel preparation",
                  "Prompt September 2026 · sent yesterday",
                  "28 delivered · 27 read",
                ],
                [
                  "Class recording and reflection",
                  "Builder October 2026 · sent 29 Oct",
                  "34 delivered · 32 read",
                ],
              ].map(item => (
                <div className="evidence-card" key={item[0]}>
                  <span>
                    <Icon name="message" />
                  </span>
                  <div>
                    <strong>{item[0]}</strong>
                    <p>
                      {item[1]} · {item[2]}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={() => notify("Announcement opened", item[0])}
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="chart" title="Communication reach" meta="Last 30 days">
            <div className="metric-bars">
              {[
                ["Delivered", 100],
                ["Opened", 91],
                ["Action completed", 74],
                ["Translation viewed", 38],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );
  if (page === "notes")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Mentoring notes"
          text="Private, purpose-limited support records visible only to assigned staff."
          actions={
            <button
              type="button"
              className="primary-btn"
              onClick={() => notify("Private note editor opened")}
            >
              <Icon name="plus" size="sm" />
              Add note
            </button>
          }
        />
        <AcademyPanel icon="lock" title="Restricted notes" meta="4 active records">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Purpose</th>
                  <th>Functional action</th>
                  <th>Owner</th>
                  <th>Review date</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [
                    "Jamal Thompson",
                    "Attendance and assignment support",
                    "Wednesday office hour + adjusted plan",
                    "Amina Mutesi",
                    "11 Nov",
                  ],
                  [
                    "Sofia Martinez",
                    "Extended reading time",
                    "Written instructions before live class",
                    "Amina Mutesi",
                    "18 Nov",
                  ],
                  [
                    "Hugo Mendes",
                    "Low-bandwidth access",
                    "Downloadable recording and transcript",
                    "Daniel Mwesigwa",
                    "12 Nov",
                  ],
                  [
                    "Maya Chen",
                    "Caption preference",
                    "Captions and written instructions",
                    "Amina Mutesi",
                    "End of cohort",
                  ],
                ].map(item => (
                  <tr
                    key={item[0]}
                    onClick={() => navigate("cohorts", `learner-${item[0].toLowerCase().replace(" ", "-")}`)}
                  >
                    <td>
                      <strong>{item[0]}</strong>
                    </td>
                    <td>{item[1]}</td>
                    <td>{item[2]}</td>
                    <td>{item[3]}</td>
                    <td>{item[4]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="modal-form-note">
            <Icon name="lock" size="sm" />
            Do not record diagnoses or unnecessary personal detail. Record only the support action, owner,
            expiry and minimum context needed to deliver it.
          </div>
        </AcademyPanel>
      </div>
    );
  if (page === "help-requests")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Learner help requests"
          text="Triage questions into messages, office hours, Build Rooms, Classes, Labs or formal support without losing context."
        />
        <AcademyPanel icon="alert" title="Requests waiting" meta="4 open">
          <div className="priority-list">
            {[
              [
                "JT",
                "Jamal Thompson",
                "Consent boundary in call-insight project",
                "High",
                "cohorts",
                "learner-jamal-thompson",
              ],
              [
                "MC",
                "Maya Chen",
                "Source retention policy clarification",
                "Today",
                "engagement",
                "thread-thread-maya",
              ],
              [
                "RI",
                "Ronald Isabirye",
                "Staging payment-widget evidence",
                "Today",
                "buildRooms",
                "group-build-checkout",
              ],
              ["HM", "Hugo Mendes", "Downloadable recording not visible", "Normal", "class", "recordings"],
            ].map(item => (
              <button
                type="button"
                className="priority-item"
                key={item[1]}
                onClick={() => navigate(item[4] as InstructorArea, item[5])}
              >
                <Avatar initials={item[0]} small />
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <span className="priority-meta">
                  <TonePill tone={item[3] === "High" ? "red" : item[3] === "Today" ? "orange" : "purple"}>
                    {item[3]}
                  </TonePill>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );
  return (
    <div className="instructor-page">
      <EmptyState
        icon="mail"
        title="Engagement workspace"
        text="Choose Office Hours, Messages, Announcements, Mentoring Notes or Help Requests from the sidebar."
        action={
          <button type="button" className="primary-btn" onClick={() => navigate("engagement", "messages")}>
            Open messages
          </button>
        }
      />
    </div>
  );
}

const D16_CERTIFIED_INSTRUCTOR_ANALYTICS_PAGES: ReadonlySet<string> = new Set([
  "overview",
  "attendance",
  "reviews",
]);

function AnalyticsView({
  page,
  navigate,
  notify,
  openModal,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const assignmentAnalytics = useInstructorAssignmentAnalytics(page === "overview");
  const attendanceAnalytics = useInstructorAttendanceAnalytics(page === "attendance");
  const reviewAnalytics = useInstructorReviewAnalytics(page === "reviews");
  if (!D16_CERTIFIED_INSTRUCTOR_ANALYTICS_PAGES.has(page))
    return (
      <div className="instructor-page" data-d16-analytics-state="unavailable">
        <PageTitle
          title={pageTitle("analytics", page)}
          text="This view is waiting for approved metric definitions, purpose-limited owner facts and freshness evidence."
        />
        <EmptyState
          icon="chart"
          title="Governed analytics unavailable"
          text="No sample metrics or exports are shown. Academy Analytics will enable this route after its source, scope, suppression and freshness contract is certified."
        />
      </div>
    );

  if (page === "overview") {
    const current = assignmentAnalytics.data?.values.current;
    const value =
      current !== undefined
        ? String(current)
        : assignmentAnalytics.data?.suppressedCells
          ? "Suppressed"
          : "Not projected";
    return (
      <div className="instructor-page" data-d16-analytics-state="connected">
        <PageTitle
          title="Instructor analytics"
          text="A governed view of your current teaching assignments, scoped to your Instructor identity."
        />
        {assignmentAnalytics.loading ? (
          <EmptyState
            icon="chart"
            title="Loading governed analytics"
            text="Checking the current Instructor-scoped projection."
          />
        ) : assignmentAnalytics.error ? (
          <EmptyState
            icon="alert"
            title="Instructor analytics unavailable"
            text={assignmentAnalytics.error}
            action={
              <button
                type="button"
                className="secondary-btn"
                onClick={() => void assignmentAnalytics.refresh()}
              >
                Try again
              </button>
            }
          />
        ) : (
          <>
            <div className="stat-grid">
              <StatTile
                icon="clipboard"
                label="Current teaching assignments"
                value={value}
                detail={
                  assignmentAnalytics.data?.availability === "stale"
                    ? "Projection is stale"
                    : `Minimum reportable cell: ${assignmentAnalytics.data?.minimumCellCount ?? 5}`
                }
              />
            </div>
            <AcademyPanel icon="shield" title="Purpose and limits" meta="D07 source · Instructor scope">
              <p>
                This count includes only active, started and unexpired teaching assignments. Capacity, effort,
                performance, learner outcomes, transactional actions and exports are unavailable.
              </p>
            </AcademyPanel>
          </>
        )}
      </div>
    );
  }

  if (page === "reviews")
    return (
      <div className="instructor-page" data-d16-analytics-state="connected">
        <PageTitle
          title="Review analytics"
          text="A governed count of canonical D11 evaluations finalized under your exact assessor identity."
        />
        {reviewAnalytics.loading ? (
          <EmptyState
            icon="chart"
            title="Loading governed analytics"
            text="Checking the current Instructor assessor-scoped projection."
          />
        ) : reviewAnalytics.error ? (
          <EmptyState
            icon="alert"
            title="Review analytics unavailable"
            text={reviewAnalytics.error}
            action={
              <button type="button" className="secondary-btn" onClick={() => void reviewAnalytics.refresh()}>
                Try again
              </button>
            }
          />
        ) : (
          <>
            <div className="stat-grid">
              <StatTile
                icon="clipboard"
                label="Finalized evaluations"
                value={
                  reviewAnalytics.data?.values.finalized === undefined
                    ? "Suppressed / none"
                    : String(reviewAnalytics.data.values.finalized)
                }
                detail={`Minimum reportable cell: ${reviewAnalytics.data?.minimumCellCount ?? 5}`}
              />
            </div>
            <AcademyPanel icon="shield" title="Purpose and limits" meta="D11 source · exact assessor scope">
              <p>
                This count contains no learner, assessment, score, outcome, feedback or rationale detail.
                Turnaround, feedback quality, performance inference, transactions and export remain
                unavailable.
              </p>
            </AcademyPanel>
          </>
        )}
      </div>
    );

  if (page === "attendance")
    return (
      <div className="instructor-page" data-d16-analytics-state="connected">
        <PageTitle
          title="Attendance analytics"
          text="Current D10 attendance statuses across Cohorts in your exact active teaching scope."
        />
        {attendanceAnalytics.loading ? (
          <EmptyState
            icon="chart"
            title="Loading governed analytics"
            text="Checking the current Instructor-scoped attendance projection."
          />
        ) : attendanceAnalytics.error ? (
          <EmptyState
            icon="alert"
            title="Attendance analytics unavailable"
            text={attendanceAnalytics.error}
            action={
              <button
                type="button"
                className="secondary-btn"
                onClick={() => void attendanceAnalytics.refresh()}
              >
                Try again
              </button>
            }
          />
        ) : (
          <>
            <div className="stat-grid">
              {(["PRESENT", "LATE", "ABSENT", "EXCUSED", "UNKNOWN"] as const).map(status => (
                <StatTile
                  key={status}
                  icon={status === "PRESENT" ? "check-circle" : status === "ABSENT" ? "alert" : "calendar"}
                  label={status.charAt(0) + status.slice(1).toLowerCase()}
                  value={
                    attendanceAnalytics.data?.values[status] === undefined
                      ? "Suppressed / none"
                      : String(attendanceAnalytics.data.values[status])
                  }
                  detail={`Minimum reportable cell: ${attendanceAnalytics.data?.minimumCellCount ?? 5}`}
                />
              ))}
            </div>
            <AcademyPanel icon="shield" title="Purpose and limits" meta="D10 source · exact teaching scope">
              <p>
                These aggregate statuses do not expose individual learners, reasons, evidence or sessions.
                Outcome inference, attendance mutation and export are unavailable from analytics.
              </p>
            </AcademyPanel>
          </>
        )}
      </div>
    );

  const weekly = [
    ["W1", 64],
    ["W2", 71],
    ["W3", 69],
    ["W4", 78],
    ["W5", 82],
    ["W6", 86],
  ] as const;
  const outcomes = [
    ["Builder Oct", 78, 88, 74],
    ["Prompt Sep", 91, 94, 86],
    ["Leadership Nov", 42, 96, 71],
    ["Context Aug", 100, 93, 92],
  ] as const;
  const exportButton = (
    <button
      type="button"
      className="secondary-btn"
      onClick={() => openModal({ type: "export", payload: page })}
    >
      <Icon name="download" size="sm" />
      Export report
    </button>
  );

  if (page === "outcomes" || page === "report-readiness")
    return (
      <div className="instructor-page">
        <PageTitle
          title={page === "report-readiness" ? "Credential readiness report" : "Learner outcomes"}
          text="Track learning progress, completion, assessment quality and evidence readiness without reducing learners to a single score."
          actions={exportButton}
        />
        <div className="stat-grid">
          <StatTile icon="users" label="Active learners" value="97" detail="Across three programmes" />
          <StatTile
            icon="award"
            label="Credential ready"
            value="71%"
            detail="Evidence thresholds met"
            tone="cyan"
          />
          <StatTile
            icon="check-circle"
            label="Completion forecast"
            value="86%"
            detail="Current active cohorts"
            tone="blue"
          />
          <StatTile
            icon="alert"
            label="Needs support"
            value="9"
            detail="Purpose-limited signals"
            tone="orange"
          />
        </div>
        <AcademyPanel icon="award" title="Readiness by cohort" meta="Updated 6 minutes ago">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th>Progress</th>
                  <th>Attendance</th>
                  <th>Evidence ready</th>
                  <th>Primary gap</th>
                </tr>
              </thead>
              <tbody>
                {outcomes.map(item => (
                  <tr key={item[0]} onClick={() => navigate("cohorts", "cohorts")}>
                    <td>
                      <strong>{item[0]}</strong>
                    </td>
                    <td>{item[1]}%</td>
                    <td>{item[2]}%</td>
                    <td>
                      <ProgressMeter value={item[3]} />
                    </td>
                    <td>
                      {item[3] < 75
                        ? "Proof and reflections"
                        : item[3] < 90
                          ? "Final verification"
                          : "No material gap"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
        <div className="content-grid">
          <AcademyPanel icon="chart" title="Outcome distribution" meta="Skill · System · Proof">
            <div className="metric-bars">
              {[
                ["Skill evidence", 86],
                ["System evidence", 79],
                ["Proof evidence", 71],
                ["Responsible-use evidence", 83],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="info" title="Interpretation" meta="Human review required">
            <div className="evidence-stack">
              <div className="evidence-card">
                <span>
                  <Icon name="shield" />
                </span>
                <div>
                  <strong>Readiness is not automatic certification</strong>
                  <p>
                    Automated checks can organise evidence, but the instructor and review panel retain the
                    final credential decision.
                  </p>
                </div>
              </div>
              <div className="evidence-card">
                <span>
                  <Icon name="users" />
                </span>
                <div>
                  <strong>Support before sanction</strong>
                  <p>Use trend changes as prompts for conversation, not as hidden behavioural scoring.</p>
                </div>
              </div>
            </div>
          </AcademyPanel>
        </div>
      </div>
    );

  if (page === "engagement")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Engagement analytics"
          text="See participation across Classes, Build Rooms, Labs, assignments and support channels with accessible, explainable indicators."
          actions={exportButton}
        />
        <div className="stat-grid">
          <StatTile icon="activity" label="Weekly active" value="91%" detail="+4 points since last week" />
          <StatTile
            icon="message"
            label="Build Room contributors"
            value="84%"
            detail="At least one attributable contribution"
            tone="cyan"
          />
          <StatTile
            icon="flask"
            label="Lab participation"
            value="76%"
            detail="Eligible assigned learners"
            tone="blue"
          />
          <StatTile
            icon="mail"
            label="Help resolved"
            value="88%"
            detail="Within two working days"
            tone="orange"
          />
        </div>
        <div className="content-grid">
          <AcademyPanel icon="chart" title="Weekly participation" meta="Six-week view">
            <div className="chart-row">
              {weekly.map(item => (
                <div className="chart-column" key={item[0]}>
                  <strong>{item[1]}%</strong>
                  <i style={{ height: `${Math.max(40, item[1] * 1.45)}px` }} />
                  <span>{item[0]}</span>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="activity" title="Participation mix" meta="Current week">
            <div className="metric-bars">
              {[
                ["Chatoner Class", 89],
                ["Build Rooms", 84],
                ["AI Labs", 76],
                ["Assignments", 81],
                ["Office hours", 32],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
        <AcademyPanel
          icon="users"
          title="Learners with meaningful change"
          meta="Conversation prompts, not hidden scores"
        >
          <div className="priority-list">
            {[
              [
                "JT",
                "Jamal Thompson",
                "Participation fell across Class and assignments for two weeks",
                "Open support plan",
              ],
              [
                "SM",
                "Sofia Martinez",
                "Build Room contribution increased after written instructions",
                "Review accommodation",
              ],
              [
                "HM",
                "Hugo Mendes",
                "Lab completion improved after low-bandwidth materials",
                "Preserve support",
              ],
            ].map(item => (
              <button
                type="button"
                className="priority-item"
                key={item[1]}
                onClick={() => navigate("cohorts", `learner-${item[1].toLowerCase().replace(/ /g, "-")}`)}
              >
                <Avatar initials={item[0]} small />
                <span>
                  <strong>{item[1]}</strong>
                  <p>{item[2]}</p>
                </span>
                <span className="priority-meta">
                  <span>{item[3]}</span>
                  <Icon name="arrow-right" size="sm" />
                </span>
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    );

  if (page === "attendance")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Attendance analytics"
          text="Combine verified Class attendance with correction requests and equivalent participation pathways."
          actions={exportButton}
        />
        <div className="stat-grid">
          <StatTile icon="video" label="Live attendance" value="88%" detail="Last four sessions" />
          <StatTile
            icon="play"
            label="Recording completion"
            value="67%"
            detail="For approved alternatives"
            tone="cyan"
          />
          <StatTile
            icon="alert"
            label="Corrections open"
            value="2"
            detail="Awaiting instructor action"
            tone="orange"
          />
          <StatTile
            icon="check-circle"
            label="Verified equivalent"
            value="6"
            detail="Transcript + reflection route"
            tone="blue"
          />
        </div>
        <AcademyPanel icon="calendar" title="Attendance by session" meta="AI Product Builder · October 2026">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Date</th>
                  <th>Present</th>
                  <th>Equivalent route</th>
                  <th>Absent</th>
                  <th>Correction requests</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Accessible Product Decisions", "4 Nov", "31 / 34", 1, 2, 0],
                  ["Context Systems Clinic", "6 Nov", "29 / 34", 2, 3, 1],
                  ["Responsible AI Boundaries", "28 Oct", "30 / 34", 2, 2, 1],
                  ["Evidence and Verification", "21 Oct", "32 / 34", 1, 1, 0],
                ].map(item => (
                  <tr key={String(item[0])} onClick={() => navigate("class", "attendance")}>
                    <td>
                      <strong>{item[0]}</strong>
                    </td>
                    <td>{item[1]}</td>
                    <td>{item[2]}</td>
                    <td>{item[3]}</td>
                    <td>{item[4]}</td>
                    <td>{item[5]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );

  if (page === "reviews" || page === "report-sla")
    return (
      <div className="instructor-page">
        <PageTitle
          title={page === "report-sla" ? "Review SLA report" : "Review performance"}
          text="Monitor queue age, feedback usefulness, moderation and decision consistency—not only review speed."
          actions={exportButton}
        />
        <div className="stat-grid">
          <StatTile icon="clock" label="Median turnaround" value="18.4h" detail="Target under 24 hours" />
          <StatTile
            icon="clipboard"
            label="Open reviews"
            value="11"
            detail="4 older than 24 hours"
            tone="orange"
          />
          <StatTile
            icon="refresh"
            label="Revision rate"
            value="18%"
            detail="Most common: missing proof"
            tone="cyan"
          />
          <StatTile
            icon="shield"
            label="Calibration"
            value="92%"
            detail="Across common rubric samples"
            tone="blue"
          />
        </div>
        <div className="content-grid">
          <AcademyPanel icon="chart" title="Queue age" meta="Current workload">
            <div className="metric-bars">
              {[
                ["0–8 hours", 36],
                ["8–24 hours", 45],
                ["24–48 hours", 14],
                ["Over 48 hours", 5],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
          <AcademyPanel icon="evidence" title="Feedback quality sample" meta="Human-reviewed sample">
            <div className="metric-bars">
              {[
                ["Specific and actionable", 94],
                ["Linked to rubric", 91],
                ["Names residual risk", 82],
                ["Invites learner reflection", 88],
              ].map(item => (
                <div className="metric-bar" key={String(item[0])}>
                  <span>{item[0]}</span>
                  <div className="metric-bar-track">
                    <i style={{ width: `${item[1]}%` }} />
                  </div>
                  <strong>{item[1]}%</strong>
                </div>
              ))}
            </div>
          </AcademyPanel>
        </div>
      </div>
    );

  if (page === "content")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Content analytics"
          text="Track lesson usefulness, accessibility readiness, completion and learner questions by version."
          actions={exportButton}
        />
        <div className="stat-grid">
          <StatTile icon="book" label="Published lessons" value="24" detail="Across assigned programmes" />
          <StatTile
            icon="shield"
            label="Accessibility ready"
            value="92%"
            detail="Two blockers remain"
            tone="orange"
          />
          <StatTile
            icon="activity"
            label="Median completion"
            value="84%"
            detail="Self-paced resources"
            tone="cyan"
          />
          <StatTile
            icon="message"
            label="Unresolved questions"
            value="14"
            detail="Across current lessons"
            tone="blue"
          />
        </div>
        <AcademyPanel icon="book" title="Lesson quality" meta="Current published versions">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lesson</th>
                  <th>Version</th>
                  <th>Completion</th>
                  <th>Engagement</th>
                  <th>Accessibility</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {LESSONS.map(item => (
                  <tr key={item.id} onClick={() => navigate("teaching", `lesson-${item.id}`)}>
                    <td>
                      <strong>{item.title}</strong>
                    </td>
                    <td>{item.version}</td>
                    <td>{Math.max(62, item.engagement - 5)}%</td>
                    <td>{item.engagement}%</td>
                    <td>
                      <TonePill
                        tone={
                          item.accessibility >= 95 ? "green" : item.accessibility >= 85 ? "orange" : "red"
                        }
                      >
                        {item.accessibility}%
                      </TonePill>
                    </td>
                    <td>{item.status === "Published" ? "Monitor" : "Resolve blockers"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcademyPanel>
      </div>
    );

  if (page === "exports")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Data exports"
          text="Create purpose-limited, privacy-aware exports with visible scope, fields, retention and audit."
          actions={
            <button
              type="button"
              className="primary-btn"
              onClick={() => openModal({ type: "export", payload: "custom" })}
            >
              <Icon name="plus" size="sm" />
              Create export
            </button>
          }
        />
        <div className="simple-card-grid">
          {[
            [
              "Weekly delivery brief",
              "Cohort health, classes, reviews and support",
              "PDF + CSV",
              "Today · Amina Mutesi",
            ],
            [
              "Credential readiness",
              "Evidence gaps and human decision status",
              "CSV",
              "Yesterday · Academic Operations",
            ],
            [
              "Attendance corrections",
              "Verified Class attendance and correction decisions",
              "CSV",
              "30 Oct · Amina Mutesi",
            ],
            [
              "Content accessibility",
              "Lesson checks and unresolved blockers",
              "CSV",
              "28 Oct · Daniel Mwesigwa",
            ],
          ].map(item => (
            <article className="simple-card" key={item[0]}>
              <div className="simple-card-head">
                <span className="simple-card-icon">
                  <Icon name="download" />
                </span>
                <div className="simple-card-copy">
                  <h3>{item[0]}</h3>
                  <p>{item[1]}</p>
                </div>
                <TonePill tone="blue">{item[2]}</TonePill>
              </div>
              <div className="simple-card-body">
                <div className="meta-pair">
                  <span>Last generated</span>
                  <strong>{item[3]}</strong>
                </div>
                <div className="meta-pair">
                  <span>Retention</span>
                  <strong>30 days</strong>
                </div>
              </div>
              <div className="simple-card-foot">
                <button
                  type="button"
                  className="secondary-btn button-sm"
                  onClick={() => openModal({ type: "export", payload: String(item[0]) })}
                >
                  <Icon name="download" size="sm" />
                  Generate again
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    );

  if (page === "report-weekly")
    return (
      <div className="instructor-page">
        <PageTitle
          title="Weekly delivery brief"
          text="A reusable instructor and academic-operations view covering delivery, learner support, review work and next decisions."
          actions={exportButton}
        />
        <InstructorHero
          eyebrow="Saved report · Week 6"
          title="Delivery is on track, with two actions requiring human follow-through."
          text="Class participation improved, review turnaround remains within target, and the main risk is concentrated in consent evidence and two accessibility blockers."
          actions={
            <>
              <button
                type="button"
                className="primary-btn"
                onClick={() =>
                  notify("Report shared", "Academic Operations received the current saved view.")
                }
              >
                <Icon name="share" size="sm" />
                Share report
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("command", "priority-queue")}
              >
                <Icon name="clipboard" size="sm" />
                Open actions
              </button>
            </>
          }
          side={
            <HeroSideCard
              icon="calendar"
              title="Reporting period"
              value="3–9 Nov 2026"
              detail="Builder, Prompt and Leadership programmes"
            />
          }
        />
        <div className="stat-grid">
          <StatTile icon="video" label="Classes delivered" value="3 / 3" detail="No cancellations" />
          <StatTile
            icon="clipboard"
            label="Reviews completed"
            value="27"
            detail="Median 18.4 hours"
            tone="cyan"
          />
          <StatTile
            icon="users"
            label="Support actions"
            value="7"
            detail="Four already resolved"
            tone="orange"
          />
          <StatTile
            icon="award"
            label="Evidence verified"
            value="42"
            detail="Across 19 learners"
            tone="blue"
          />
        </div>
      </div>
    );

  return (
    <div className="instructor-page">
      <PageTitle
        title="Instructor analytics"
        text="A transparent, action-oriented view of delivery, learning, support, review quality and content readiness."
        actions={exportButton}
      />
      <InstructorHero
        eyebrow="Live reporting · Instructor scope"
        title="Measure what helps you teach, support and make defensible human decisions."
        text="This workspace combines Classes, Build Rooms, Labs, assessments and evidence while keeping the limits of each indicator visible."
        actions={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("analytics", "report-weekly")}
            >
              <Icon name="bookmark" size="sm" />
              Open weekly brief
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => openModal({ type: "export", payload: "analytics-overview" })}
            >
              <Icon name="download" size="sm" />
              Export
            </button>
          </>
        }
        side={
          <HeroSideCard
            icon="activity"
            title="Data freshness"
            value="6 min"
            detail="Class, Lab and assessment events reconciled"
          />
        }
      />
      <div className="stat-grid">
        <StatTile icon="users" label="Learners on track" value="82%" detail="Across active cohorts" />
        <StatTile
          icon="video"
          label="Attendance"
          value="88%"
          detail="Live + approved alternatives"
          tone="cyan"
        />
        <StatTile
          icon="clipboard"
          label="Review SLA"
          value="81%"
          detail="Completed under 24 hours"
          tone="orange"
        />
        <StatTile
          icon="award"
          label="Credential readiness"
          value="71%"
          detail="Evidence thresholds met"
          tone="blue"
        />
      </div>
      <div className="content-grid">
        <AcademyPanel icon="chart" title="Six-week delivery health" meta="Composite, explainable view">
          <div className="chart-row">
            {weekly.map(item => (
              <div className="chart-column" key={item[0]}>
                <strong>{item[1]}%</strong>
                <i style={{ height: `${Math.max(40, item[1] * 1.45)}px` }} />
                <span>{item[0]}</span>
              </div>
            ))}
          </div>
        </AcademyPanel>
        <AcademyPanel icon="alert" title="Where attention is needed" meta="4 decisions">
          <div className="priority-list">
            {[
              ["Review SLA", "4 reviews older than 24 hours", "assessment", "queue-older-24h"],
              ["Learner support", "Jamal needs a support conversation", "cohorts", "learner-jamal-thompson"],
              ["Content quality", "API Boundaries has two blockers", "teaching", "lesson-api-boundaries"],
              ["Capstone panel", "Calibration required before 15 Nov", "projects", "panels"],
            ].map(item => (
              <button
                type="button"
                className="priority-item"
                key={item[0]}
                onClick={() => navigate(item[2] as InstructorArea, item[3])}
              >
                <span className="priority-icon">
                  <Icon name="alert" />
                </span>
                <span>
                  <strong>{item[0]}</strong>
                  <p>{item[1]}</p>
                </span>
                <Icon name="arrow-right" size="sm" />
              </button>
            ))}
          </div>
        </AcademyPanel>
      </div>
    </div>
  );
}

function SearchInstructorView({
  page,
  navigate,
}: {
  page: string;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const scopeMap: Record<string, InstructorArea[]> = {
    all: RAIL_AREAS,
    people: ["cohorts"],
    classes: ["class"],
    reviews: ["assessment"],
    projects: ["projects", "buildRooms"],
    content: ["teaching"],
  };
  const allowed = scopeMap[page] || RAIL_AREAS;
  const academicSearch = useTeachingSearch(allowed.includes("teaching"));
  const results = [
    ...(academicSearch.data || []),
    ...SEARCH_RECORDS.filter(item => item.area !== "teaching"),
  ].filter(
    item =>
      allowed.includes(item.area) &&
      `${item.title} ${item.detail} ${item.label}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <div className="instructor-page">
      <PageTitle
        title={pageTitle("search", page)}
        text="Find learners, cohorts, Classes, Build Rooms, Labs, lessons, submissions, projects and evidence from one permission-aware search."
      />
      <div className="search-page-box">
        <Icon name="search" />
        <input
          autoFocus
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search Instructor Platform…"
        />
        <kbd className="kbd">⌘ K</kbd>
      </div>
      <div className="search-filters">
        {[
          ["all", "Everything"],
          ["people", "People"],
          ["classes", "Classes"],
          ["reviews", "Reviews"],
          ["projects", "Projects"],
          ["content", "Content"],
        ].map(item => (
          <button
            type="button"
            className={`filter-chip ${page === item[0] ? "active" : ""}`}
            key={item[0]}
            onClick={() => navigate("search", item[0])}
          >
            {item[1]}
          </button>
        ))}
      </div>
      <div className="search-result-grid">
        {academicSearch.loading ? <p role="status">Loading assigned academic records…</p> : null}
        {academicSearch.error ? <p role="alert">{academicSearch.error}</p> : null}
        {results.length ? (
          results.map(item => (
            <button
              type="button"
              className="global-result"
              key={item.id}
              onClick={() => navigate(item.area, item.page)}
            >
              <span className="result-icon">
                <Icon name={item.icon} />
              </span>
              <span>
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
              </span>
              <TonePill tone="blue">{item.label}</TonePill>
            </button>
          ))
        ) : (
          <EmptyState
            icon="search"
            title="No matching Instructor Platform records"
            text="Try another name, class code, requirement, project, lesson or evidence term."
          />
        )}
      </div>
    </div>
  );
}

function NotificationsInstructorView({
  page,
  notifications,
  setNotifications,
  openNotification,
  notify,
}: {
  page: string;
  notifications: NotificationRecord[];
  setNotifications: React.Dispatch<React.SetStateAction<NotificationRecord[]>>;
  openNotification: (item: NotificationRecord) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const filter: Record<string, NotificationRecord["category"][]> = {
    all: ["review", "class", "learner", "cohort", "system"],
    reviews: ["review"],
    classes: ["class"],
    learners: ["learner", "cohort"],
    system: ["system"],
  };
  const visible = notifications.filter(item => (filter[page] || filter.all).includes(item.category));
  const open = (item: NotificationRecord) => {
    setNotifications(current =>
      current.map(note => (note.id === item.id ? { ...note, unread: false } : note)),
    );
    openNotification(item);
  };
  const markAll = () => {
    setNotifications(current => current.map(item => ({ ...item, unread: false })));
    notify("Notifications marked as read");
  };
  return (
    <div className="instructor-page">
      <PageTitle
        title={pageTitle("notifications", page)}
        text="Review class, assessment, learner-support, cohort and system updates with direct links to the affected work."
        actions={
          <button type="button" className="secondary-btn" onClick={markAll}>
            <Icon name="check-circle" size="sm" />
            Mark all read
          </button>
        }
      />
      <div className="notification-list">
        {visible.map(item => (
          <article className={`notification-row ${item.unread ? "unread" : ""}`} key={item.id}>
            <span className="notification-icon">
              <Icon name={item.icon} />
            </span>
            <button type="button" className="notification-content-button" onClick={() => open(item)}>
              <strong>{item.title}</strong>
              <p>{item.detail}</p>
            </button>
            <span>
              <time>{item.time}</time>
              {item.unread ? (
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Mark notification read"
                  onClick={() =>
                    setNotifications(current =>
                      current.map(note => (note.id === item.id ? { ...note, unread: false } : note)),
                    )
                  }
                >
                  <Icon name="check" size="sm" />
                </button>
              ) : null}
            </span>
          </article>
        ))}
      </div>
    </div>
  );
}

function InstructorNotificationDetailDrawer({
  item,
  close,
  navigate,
}: {
  item: NotificationRecord | null;
  close: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  if (!item) return null;
  return (
    <>
      <button
        type="button"
        className="notification-detail-backdrop"
        aria-label="Close notification details"
        onClick={close}
      />
      <aside className="notification-detail-drawer" aria-label="Notification details">
        <div className="notification-detail-head">
          <div>
            <span className="eyebrow">Notification details</span>
            <p>{item.time}</p>
          </div>
          <button type="button" className="icon-btn" onClick={close} aria-label="Close notification details">
            <Icon name="x" />
          </button>
        </div>
        <div className="notification-detail-body">
          <span className="notification-detail-icon">
            <Icon name={item.icon} />
          </span>
          <div className="card-meta">
            <TonePill tone="blue">{item.category}</TonePill>
            <TonePill tone="green">Read</TonePill>
          </div>
          <h2>{item.title}</h2>
          <p>{item.detail}</p>
        </div>
        <div className="notification-detail-foot">
          <button type="button" className="ghost-btn" onClick={close}>
            Close
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => {
              close();
              if (item.deepLink?.startsWith("/")) {
                const access = new URL(platformEnvironment.appOrigins.auth);
                const prefix = access.pathname.replace(/\/auth\/?$/, "");
                window.location.assign(access.origin + prefix + item.deepLink);
              } else navigate(item.area, item.page);
            }}
          >
            <Icon name="arrow" size="sm" />
            Open related page
          </button>
        </div>
      </aside>
    </>
  );
}

function SettingsInstructorView({
  page,
  language,
  setLanguage,
  notify,
  openModal,
}: {
  page: string;
  language: LanguageSettings;
  setLanguage: React.Dispatch<React.SetStateAction<LanguageSettings>>;
  notify: (title: string, detail?: string) => void;
  openModal: (modal: InstructorModal) => void;
}) {
  const profileForm = useIdentityProfileForm();
  const profile = profileForm.profile;
  const [prefs, setPrefs] = useState({
    compact: false,
    motion: true,
    highContrast: false,
    captions: true,
    email: true,
    push: true,
    classReminder: true,
    reviewReminder: true,
    analytics: false,
  });
  const toggle = (key: keyof typeof prefs) => setPrefs(current => ({ ...current, [key]: !current[key] }));
  const save = () =>
    notify(
      "Settings saved",
      "Your Instructor Platform preferences were updated in this demonstration workspace.",
    );
  let body: React.ReactNode;
  if (page === "profile")
    body = (
      <div className="settings-card">
        <div className="profile-row">
          <div className="profile-photo-large">
            {profile.name
              .split(/\s+/)
              .slice(0, 2)
              .map(part => part[0])
              .join("")}
          </div>
          <div>
            <h3>{profile.name}</h3>
            <p>{profile.headline}</p>
            <button
              type="button"
              className="secondary-btn button-sm"
              disabled
              title="A profile photo upload contract is not available yet."
            >
              Change photo
            </button>
          </div>
        </div>
        <div className="form-row">
          <label className="field-label">
            <span>Display name</span>
            <input
              className="text-field"
              value={profile.name}
              onChange={event => profileForm.set("name", event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Professional title</span>
            <input
              className="text-field"
              value={profile.headline}
              onChange={event => profileForm.set("headline", event.target.value)}
            />
          </label>
        </div>
        <label className="field-label">
          <span>Instructor biography</span>
          <textarea
            className="text-area"
            value={profile.bio}
            onChange={event => profileForm.set("bio", event.target.value)}
          />
        </label>
        <div className="form-row">
          <label className="field-label">
            <span>Timezone</span>
            <select
              className="select-field"
              value={profile.timezone}
              onChange={event => profileForm.set("timezone", event.target.value)}
            >
              <option value="">Choose time zone</option>
              {profile.timezone &&
                !["Africa/Kampala", "Africa/Nairobi", "Europe/London", "America/New_York"].includes(
                  profile.timezone,
                ) && <option>{profile.timezone}</option>}
              <option>Africa/Kampala</option>
              <option>Africa/Nairobi</option>
              <option>Europe/London</option>
              <option>America/New_York</option>
            </select>
          </label>
          <label className="field-label">
            <span>Public profile visibility</span>
            <select
              className="select-field"
              value={profile.visibility}
              onChange={event => profileForm.set("visibility", event.target.value)}
            >
              <option value="assigned_learners">Learners in assigned programmes</option>
              <option value="academy">Academy members</option>
              <option value="private">Private</option>
            </select>
          </label>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="primary-btn"
            disabled={!profileForm.ready || profileForm.busy}
            onClick={async () => {
              try {
                await profileForm.save();
                notify("Profile saved", "Your Academy identity profile has been updated.");
              } catch (error) {
                notify(
                  "Profile not saved",
                  error instanceof Error ? error.message : "Unable to save profile.",
                );
              }
            }}
          >
            <Icon name="check" size="sm" />
            Save profile
          </button>
        </div>
        <IdentitySecurityControls />
      </div>
    );
  else if (page === "access")
    body = (
      <div className="settings-card">
        <h3>Role and decision authority</h3>
        <p className="settings-intro">
          Access is inherited from assigned programmes and cohorts. High-stakes decisions remain explicitly
          human and auditable.
        </p>
        <div className="evidence-stack">
          {(
            profileForm.account.access?.grants
              .filter(grant => grant.workspaceId === profileForm.account.session?.workspace.id)
              .map(grant => [
                grant.name,
                `${grant.description || grant.roleKey} · ${grant.scopeType || "Workspace"}${grant.scopeId ? `: ${grant.scopeId}` : ""}${grant.expiresAt ? ` · Expires ${new Date(grant.expiresAt).toLocaleString()}` : ""}`,
                "Active",
              ]) || []
          ).map(item => (
            <div className="evidence-card" key={item[0]}>
              <span>
                <Icon name="shield" />
              </span>
              <div>
                <strong>{item[0]}</strong>
                <p>{item[1]}</p>
              </div>
              <TonePill tone={item[2] === "Active" ? "green" : "purple"}>{item[2]}</TonePill>
            </div>
          ))}
        </div>
        <div className="modal-form-note">
          <Icon name="lock" size="sm" />
          Changes to programme assignment, credential authority or learner-data scope require Academic
          Operations approval.
        </div>
      </div>
    );
  else if (page === "preferences")
    body = (
      <div className="settings-card">
        <h3>Workspace preferences</h3>
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>Compact information density</strong>
              <small>Show more rows while preserving readable text.</small>
            </span>
            <Switch
              value={prefs.compact}
              onChange={() => toggle("compact")}
              label="Compact information density"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Interface motion</strong>
              <small>Use subtle transitions and progress motion.</small>
            </span>
            <Switch value={prefs.motion} onChange={() => toggle("motion")} label="Interface motion" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Class reminders</strong>
              <small>Show preparation prompts before assigned live sessions.</small>
            </span>
            <Switch
              value={prefs.classReminder}
              onChange={() => toggle("classReminder")}
              label="Class reminders"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Review SLA reminders</strong>
              <small>Notify when a submission approaches the 24-hour target.</small>
            </span>
            <Switch
              value={prefs.reviewReminder}
              onChange={() => toggle("reviewReminder")}
              label="Review SLA reminders"
            />
          </div>
        </div>
        <div className="form-row">
          <label className="field-label">
            <span>Default landing area</span>
            <select className="select-field" defaultValue="Instructor Command">
              <option>Instructor Command</option>
              <option>Class schedule</option>
              <option>Grading queue</option>
              <option>Build Rooms</option>
            </select>
          </label>
          <label className="field-label">
            <span>Week starts on</span>
            <select className="select-field" defaultValue="Monday">
              <option>Monday</option>
              <option>Sunday</option>
              <option>Saturday</option>
            </select>
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="primary-btn" onClick={save}>
            Save preferences
          </button>
        </div>
      </div>
    );
  else if (page === "accessibility")
    body = (
      <div className="settings-card">
        <h3>Accessibility preferences</h3>
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>High-contrast controls</strong>
              <small>Increase control and focus distinction.</small>
            </span>
            <Switch
              value={prefs.highContrast}
              onChange={() => toggle("highContrast")}
              label="High-contrast controls"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Reduce non-essential motion</strong>
              <small>Disable decorative animation while preserving state changes.</small>
            </span>
            <Switch
              value={!prefs.motion}
              onChange={() => toggle("motion")}
              label="Reduce non-essential motion"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Captions on by default</strong>
              <small>Request captions in Chatoner Class handoffs.</small>
            </span>
            <Switch
              value={prefs.captions}
              onChange={() => toggle("captions")}
              label="Captions on by default"
            />
          </div>
        </div>
        <div className="form-row">
          <label className="field-label">
            <span>Text size</span>
            <select className="select-field" defaultValue="Comfortable">
              <option>Compact</option>
              <option>Comfortable</option>
              <option>Large</option>
            </select>
          </label>
          <label className="field-label">
            <span>Focus indicator</span>
            <select className="select-field" defaultValue="Strong">
              <option>Standard</option>
              <option>Strong</option>
            </select>
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="primary-btn" onClick={save}>
            Save accessibility
          </button>
        </div>
      </div>
    );
  else if (page === "languages")
    body = (
      <div className="settings-card">
        <h3>Languages, translation and captions</h3>
        <div className="form-row">
          <label className="field-label">
            <span>Primary writing language</span>
            <select
              className="select-field"
              value={language.primary}
              onChange={event => setLanguage(current => ({ ...current, primary: event.target.value }))}
            >
              {LANGUAGE_NAMES.map(item => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="field-label">
            <span>Secondary translation</span>
            <select
              className="select-field"
              value={language.secondary}
              onChange={event => setLanguage(current => ({ ...current, secondary: event.target.value }))}
            >
              {[...LANGUAGE_NAMES, "None"].map(item => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>Show translations</strong>
              <small>Display an approved secondary-language view where available.</small>
            </span>
            <Switch
              value={language.showTranslation}
              onChange={on => setLanguage(current => ({ ...current, showTranslation: on }))}
              label="Show translations"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Retain original language</strong>
              <small>Always preserve and expose the original message or evidence.</small>
            </span>
            <Switch
              value={language.retainOriginal}
              onChange={on => setLanguage(current => ({ ...current, retainOriginal: on }))}
              label="Retain original language"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary-btn" onClick={() => openModal({ type: "language" })}>
            Open full language settings
          </button>
          <button type="button" className="primary-btn" onClick={save}>
            Save languages
          </button>
        </div>
      </div>
    );
  else if (page === "integrations")
    body = (
      <div className="settings-card">
        <h3>Connected Academy platforms</h3>
        <div className="integration-list">
          {[
            [
              "video",
              "Chatoner Class",
              "class.chatoner.com",
              "Connected",
              "Host live teaching with session-specific links and codes.",
            ],
            [
              "flask",
              "Chatoner AI Labs",
              "labs.chatoner.com",
              "Connected",
              "Open intended Labs with cohort and entitlement context.",
            ],
            [
              "calendar",
              "Calendar",
              "Google / Outlook / iCalendar",
              "Connected",
              "Publish teaching and office-hour schedules.",
            ],
            [
              "code",
              "Repository provider",
              "GitHub · Academy projects",
              "Connected",
              "Review branches, pull requests and learner evidence.",
            ],
            [
              "mail",
              "Email delivery",
              "Academy notifications",
              "Connected",
              "Transactional learning communications only.",
            ],
          ].map(item => (
            <div className="integration-row" key={item[1]}>
              <span>
                <Icon name={item[0]} />
              </span>
              <div>
                <strong>{item[1]}</strong>
                <p>
                  {item[2]} · {item[4]}
                </p>
              </div>
              <TonePill tone="green">{item[3]}</TonePill>
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => notify(`${item[1]} settings opened`)}
              >
                Manage
              </button>
            </div>
          ))}
        </div>
        <div className="modal-form-note">
          <Icon name="shield" size="sm" />
          Production handoffs should use short-lived signed tokens, tenant checks and least-privilege scopes
          rather than trusting browser query parameters.
        </div>
      </div>
    );
  else if (page === "notification-rules")
    body = (
      <div className="settings-card">
        <h3>Notification rules</h3>
        <ProgramAnnouncementPreference />
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>Email summaries</strong>
              <small>Daily teaching and review digest.</small>
            </span>
            <Switch value={prefs.email} onChange={() => toggle("email")} label="Email summaries" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Browser and mobile push</strong>
              <small>Time-sensitive class, learner and review updates.</small>
            </span>
            <Switch value={prefs.push} onChange={() => toggle("push")} label="Browser and mobile push" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Analytics summaries</strong>
              <small>Weekly trend report with interpretation notes.</small>
            </span>
            <Switch
              value={prefs.analytics}
              onChange={() => toggle("analytics")}
              label="Analytics summaries"
            />
          </div>
        </div>
        <div className="form-row">
          <label className="field-label">
            <span>Quiet hours begin</span>
            <input className="text-field" type="time" defaultValue="20:00" />
          </label>
          <label className="field-label">
            <span>Quiet hours end</span>
            <input className="text-field" type="time" defaultValue="06:30" />
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="primary-btn" onClick={save}>
            Save rules
          </button>
        </div>
      </div>
    );
  else
    body = (
      <div className="settings-card">
        <h3>Privacy and data controls</h3>
        <div className="evidence-stack">
          {[
            [
              "Learner support notes",
              "Purpose-limited and restricted to assigned staff",
              "Review every 30 days",
            ],
            [
              "Class attendance",
              "Verified attendance, correction decisions and equivalent routes",
              "Retain under programme policy",
            ],
            [
              "AI activity",
              "Requester, permission scope, input, output, stop and human decision",
              "Retain with affected evidence",
            ],
            [
              "Instructor exports",
              "Named purpose, field scope, recipient and expiry",
              "Default expiry 30 days",
            ],
          ].map(item => (
            <div className="evidence-card" key={item[0]}>
              <span>
                <Icon name="lock" />
              </span>
              <div>
                <strong>{item[0]}</strong>
                <p>{item[1]}</p>
              </div>
              <TonePill tone="blue">{item[2]}</TonePill>
            </div>
          ))}
        </div>
        <div className="hero-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => openModal({ type: "export", payload: "my-instructor-data" })}
          >
            <Icon name="download" size="sm" />
            Export my data
          </button>
          <button
            type="button"
            className="secondary-btn danger"
            onClick={() => openModal({ type: "confirmation", payload: "Request account data deletion" })}
          >
            <Icon name="trash" size="sm" />
            Request deletion
          </button>
        </div>
      </div>
    );
  return (
    <div className="instructor-page">
      <PageTitle
        title={pageTitle("settings", page)}
        text="Manage your instructor identity, access, accessibility, languages, integrations, notifications and privacy."
      />
      {body}
    </div>
  );
}

function InstructorToastStack({ notices }: { notices: InstructorNotice[] }) {
  return (
    <div className="toast-stack" aria-live="polite">
      {notices.map(notice => (
        <div className={`toast ${notice.tone || ""}`} key={notice.id}>
          <span className="instructor-toast-icon">
            <Icon name={notice.tone === "red" ? "alert" : "check"} size="sm" />
          </span>
          <div>
            <strong>{notice.title}</strong>
            <p>{notice.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function GlobalInstructorSearch({
  open,
  close,
  navigate,
}: {
  open: boolean;
  close: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setQuery("");
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);
  const academicSearch = useTeachingSearch(open);
  const results = [...(academicSearch.data || []), ...SEARCH_RECORDS.filter(item => item.area !== "teaching")]
    .filter(item => `${item.title} ${item.detail} ${item.label}`.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 10);
  if (!open) return null;
  return (
    <div
      className="global-search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search Instructor Platform"
      onMouseDown={event => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="global-search-card">
        <div className="global-search-input">
          <Icon name="search" />
          <input
            ref={inputRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search learners, classes, Labs, submissions, projects and lessons…"
          />
          <kbd className="kbd">Esc</kbd>
          <button type="button" className="icon-btn" onClick={close} aria-label="Close search">
            <Icon name="x" />
          </button>
        </div>
        <div className="global-search-results">
          {academicSearch.loading ? <p role="status">Loading assigned academic records…</p> : null}
          {academicSearch.error ? <p role="alert">{academicSearch.error}</p> : null}
          {results.map(item => (
            <button
              type="button"
              className="search-result"
              key={item.id}
              onClick={() => {
                navigate(item.area, item.page);
                close();
              }}
            >
              <span>
                <Icon name={item.icon} />
              </span>
              <span>
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
              </span>
              <small>{item.label}</small>
            </button>
          ))}
          {!results.length ? (
            <EmptyState
              icon="search"
              title="No matching records"
              text="Try a learner name, class code, requirement, lesson, project or evidence term."
            />
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              navigate("search", "all");
              close();
            }}
          >
            <Icon name="search" size="sm" />
            Open full search
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalShell({
  icon,
  title,
  description,
  close,
  wide = false,
  children,
  actions,
}: {
  icon: string;
  title: string;
  description: string;
  close: () => void;
  wide?: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div
      className="generic-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={event => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className={`generic-modal ${wide ? "wide" : ""}`}>
        <div className="modal-head">
          <span className="modal-head-icon">
            <Icon name={icon} />
          </span>
          <div className="modal-head-copy">
            <strong>{title}</strong>
            <span>{description}</span>
          </div>
          <button type="button" className="icon-btn" onClick={close} aria-label={`Close ${title}`}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {actions ? <div className="modal-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

function MemberDirectoryModal({
  close,
  navigate,
  notify,
  subgroups,
}: {
  close: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
  subgroups: BuildRoomSubgroup[];
}) {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("All groups");
  const [smallGroup, setSmallGroup] = useState("All small groups");
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const groupForMember = (memberId: string) => subgroups.find(item => item.memberIds.includes(memberId));
  const filtered = BUILD_ROOM_MEMBERS.filter(
    item =>
      (group === "All groups" || item.group === group) &&
      (smallGroup === "All small groups" || groupForMember(item.id)?.id === smallGroup) &&
      `${item.name} ${item.role} ${item.detail} ${item.group} ${groupForMember(item.id)?.name || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  useEffect(() => setPage(1), [query, group, smallGroup]);
  return (
    <ModalShell
      icon="users"
      title="Build Room members"
      description={`${BUILD_ROOM_MEMBERS.length} connected people and approved agents · ${subgroups.length} persistent small groups · searchable and paginated`}
      close={close}
      wide
      actions={
        <>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => {
              navigate("buildRooms", "small-groups");
              close();
            }}
          >
            <Icon name="grid" size="sm" />
            Manage small groups
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={() =>
              notify("Group message composer opened", "The selected audience remains visible before sending.")
            }
          >
            <Icon name="message" size="sm" />
            Message group
          </button>
          <button type="button" className="primary-btn" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <div className="member-directory-toolbar expanded">
        <div className="search-page-box">
          <Icon name="search" size="sm" />
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search name, role, team, subgroup or description…"
          />
        </div>
        <select className="select-field" value={group} onChange={event => setGroup(event.target.value)}>
          <option>All groups</option>
          <option>Core team</option>
          <option>Cohort</option>
          <option>Academy staff</option>
          <option>Approved AI</option>
        </select>
        <select
          className="select-field"
          value={smallGroup}
          onChange={event => setSmallGroup(event.target.value)}
        >
          <option value="All small groups">All small groups</option>
          {subgroups.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="member-directory-list">
        {visible.map(item => {
          const subgroup = groupForMember(item.id);
          return (
            <div className="directory-member" key={item.id}>
              <Avatar
                initials={item.initials}
                small
                tone={item.role === "Instructor" ? "green" : item.role === "AI agent" ? "orange" : "purple"}
              />
              <div>
                <strong>{item.name}</strong>
                <p>
                  {item.role} · {item.detail} · {item.group}
                </p>
                {subgroup ? (
                  <small className="member-subgroup-label">
                    <Icon name="users" size="sm" />
                    {subgroup.name}
                  </small>
                ) : null}
              </div>
              <i
                className={`member-presence ${item.online ? "online" : ""}`}
                aria-label={item.online ? "Online" : "Offline"}
              />
              <button
                type="button"
                className="secondary-btn button-sm"
                onClick={() => {
                  notify("Direct conversation opened", item.name);
                  navigate("engagement", "messages");
                  close();
                }}
              >
                <Icon name="mail" size="sm" />
                Message
              </button>
            </div>
          );
        })}
      </div>
      <div className="directory-pagination">
        <button
          type="button"
          className="secondary-btn button-sm"
          disabled={safePage <= 1}
          onClick={() => setPage(current => Math.max(1, current - 1))}
        >
          <Icon name="arrow-left" size="sm" />
          Previous
        </button>
        <span>
          {filtered.length} results · page {safePage} of {pages}
        </span>
        <button
          type="button"
          className="secondary-btn button-sm"
          disabled={safePage >= pages}
          onClick={() => setPage(current => Math.min(pages, current + 1))}
        >
          Next
          <Icon name="arrow-right" size="sm" />
        </button>
      </div>
    </ModalShell>
  );
}

function SubgroupManagerModal({
  close,
  subgroups,
  setSubgroups,
  language,
  navigate,
  notify,
  initialGroupId,
}: {
  close: () => void;
  subgroups: BuildRoomSubgroup[];
  setSubgroups: React.Dispatch<React.SetStateAction<BuildRoomSubgroup[]>>;
  language: LanguageSettings;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string, tone?: InstructorNotice["tone"]) => void;
  initialGroupId?: string;
}) {
  const [tab, setTab] = useState<"groups" | "form" | "move" | "broadcast" | "settings">(
    initialGroupId ? "groups" : "groups",
  );
  const [selectedId, setSelectedId] = useState(initialGroupId || subgroups[0]?.id || "");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [strategy, setStrategy] = useState<BuildRoomSubgroup["formation"]>("Balanced");
  const [groupSize, setGroupSize] = useState(6);
  const [visibility, setVisibility] = useState<BuildRoomSubgroup["visibility"]>("Members + instructors");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [moveMember, setMoveMember] = useState("");
  const [moveTarget, setMoveTarget] = useState(subgroups[0]?.id || "");
  const [broadcast, setBroadcast] = useState("");
  const [privateGroups, setPrivateGroups] = useState(true);
  const [sharedBrief, setSharedBrief] = useState(true);
  const [separateBranches, setSeparateBranches] = useState(true);
  const [separateAgentScopes, setSeparateAgentScopes] = useState(true);
  const [instructorAccess, setInstructorAccess] = useState(true);
  const learners = BUILD_ROOM_MEMBERS.filter(item => item.role === "Learner").slice(0, 34);
  const assigned = new Set(subgroups.flatMap(group => group.memberIds));
  const selected = subgroups.find(group => group.id === selectedId) || subgroups[0];
  const toggleMember = (id: string) =>
    setSelectedMembers(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id],
    );
  const slugify = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || `group-${Date.now()}`;
  const createGroup = () => {
    const fallback = learners
      .filter(member => !assigned.has(member.id))
      .slice(0, groupSize)
      .map(member => member.id);
    const members = (selectedMembers.length ? selectedMembers : fallback).slice(0, 12);
    if (!name.trim() || !members.length) {
      notify(
        "Group needs a name and learners",
        "Choose at least one learner. Learners already in a group will be moved atomically, never duplicated.",
        "orange",
      );
      return;
    }
    const baseId = slugify(name);
    const id = subgroups.some(item => item.id === baseId) ? `${baseId}-${subgroups.length + 1}` : baseId;
    const group: BuildRoomSubgroup = {
      id,
      name: name.trim(),
      channel: `subgroup-${id}`,
      purpose: purpose.trim() || "Complete the shared assignment through a focused group contribution.",
      parentRoomId: "group-build-checkout",
      cohortId: "builder-oct-2026",
      classSessionId: "accessible-product-decisions-2026-11-04",
      memberIds: members,
      leadId: members[0],
      status: "Active",
      visibility,
      formation: strategy,
      language: `${language.primary}${language.secondary !== "None" ? ` + ${language.secondary}` : ""}`,
      activeNow: members.filter(memberId => buildRoomMember(memberId)?.online).length,
      progress: 0,
      evidence: 0,
      reviewCount: 0,
      branch: `team/${id}`,
      labId: "ai-app-builder",
      huddleCode: createClassCode(),
      due: "13 Nov 2026",
    };
    setSubgroups(current => {
      const moved = new Set(members);
      const remaining = current
        .map(existing => {
          const nextMembers = existing.memberIds.filter(memberId => !moved.has(memberId));
          const nextLead = nextMembers.includes(existing.leadId) ? existing.leadId : nextMembers[0] || "";
          return {
            ...existing,
            memberIds: nextMembers,
            leadId: nextLead,
            activeNow: nextMembers.filter(memberId => buildRoomMember(memberId)?.online).length,
          };
        })
        .filter(existing => existing.memberIds.length > 0);
      return [...remaining, group];
    });
    setSelectedId(id);
    setName("");
    setPurpose("");
    setSelectedMembers([]);
    setTab("groups");
    notify(
      "Small group created",
      `${group.name} · ${members.length} learners moved without duplicate placement · #${group.channel.replace(/^subgroup-/, "")}`,
    );
  };
  const autoForm = () => {
    const names = [
      "Team Canopy",
      "Team River",
      "Team Horizon",
      "Team Roots",
      "Team Spark",
      "Team Bridge",
      "Team Orbit",
      "Team Mosaic",
    ];
    let ordered = [...learners];
    if (strategy === "Random") ordered = ordered.sort((a, b) => (a.name.localeCompare(b.name) % 3) - 1);
    if (strategy === "Availability") ordered = ordered.sort((a, b) => Number(b.online) - Number(a.online));
    const count = Math.max(2, Math.ceil(ordered.length / groupSize));
    const buckets = Array.from({ length: count }, () => [] as MemberDirectoryItem[]);
    ordered.forEach((member, index) => buckets[index % count].push(member));
    const generated = buckets
      .filter(bucket => bucket.length)
      .map((bucket, index) => {
        const id = slugify(names[index] || `Team ${index + 1}`);
        return {
          id,
          name: names[index] || `Team ${index + 1}`,
          channel: `subgroup-${id}`,
          purpose:
            "Complete the shared checkout assignment through a balanced, evidence-backed contribution.",
          parentRoomId: "group-build-checkout",
          cohortId: "builder-oct-2026",
          classSessionId: "accessible-product-decisions-2026-11-04",
          memberIds: bucket.map(member => member.id),
          leadId: bucket[0].id,
          status: "Active" as const,
          visibility,
          formation: strategy,
          language: `${language.primary}${language.secondary !== "None" ? ` + ${language.secondary}` : ""}`,
          activeNow: bucket.filter(member => member.online).length,
          progress: 0,
          evidence: 0,
          reviewCount: 0,
          branch: `team/${id}`,
          labId: "ai-app-builder",
          huddleCode: createClassCode(),
          due: "13 Nov 2026",
        };
      });
    setSubgroups(generated);
    setSelectedId(generated[0]?.id || "");
    setTab("groups");
    notify(
      "Class divided into smaller groups",
      `${generated.length} ${strategy.toLowerCase()} groups · ${ordered.length} learners · target size ${groupSize}`,
    );
  };
  const move = () => {
    if (!moveMember || !moveTarget) {
      notify("Choose a learner and destination", "Both fields are required.", "orange");
      return;
    }
    setSubgroups(current =>
      current.map(group => ({
        ...group,
        memberIds:
          group.id === moveTarget
            ? [...group.memberIds.filter(id => id !== moveMember), moveMember]
            : group.memberIds.filter(id => id !== moveMember),
        leadId:
          group.leadId === moveMember && group.id !== moveTarget
            ? group.memberIds.find(id => id !== moveMember) || ""
            : group.leadId,
      })),
    );
    const member = buildRoomMember(moveMember);
    const target = subgroups.find(group => group.id === moveTarget);
    notify(
      "Learner moved",
      `${member?.name || moveMember} → ${target?.name || moveTarget}. The change is recorded in the room audit.`,
    );
    setMoveMember("");
  };
  const dissolve = (groupId: string) => {
    const group = subgroups.find(item => item.id === groupId);
    setSubgroups(current => current.filter(item => item.id !== groupId));
    setSelectedId(subgroups.find(item => item.id !== groupId)?.id || "");
    notify(
      "Small group dissolved",
      `${group?.name || groupId} was archived. Learner work remains attributable and can be reassigned.`,
      "orange",
    );
  };
  const startHuddle = (group: BuildRoomSubgroup) =>
    notify(
      "Canonical huddle session required",
      `Create and assign a governed Class session for ${group.name} before opening its huddle.`,
      "orange",
    );
  return (
    <ModalShell
      icon="grid"
      title="Manage Build Room small groups"
      description="Create persistent smaller teams within the same class and parent Build Room. Learners keep shared context while each group receives focused collaboration, branch, huddle and evidence scope."
      close={close}
      wide
      actions={
        <>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => {
              navigate("buildRooms", "small-groups");
              close();
            }}
          >
            <Icon name="grid" size="sm" />
            Open full groups view
          </button>
          <button type="button" className="primary-btn" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <nav className="subgroup-manager-tabs">
        {[
          ["groups", "Groups"],
          ["form", "Create / auto-form"],
          ["move", "Move learners"],
          ["broadcast", "Broadcast"],
          ["settings", "Safeguards"],
        ].map(item => (
          <button
            type="button"
            className={tab === item[0] ? "active" : ""}
            key={item[0]}
            onClick={() => setTab(item[0] as typeof tab)}
          >
            {item[1]}
          </button>
        ))}
      </nav>
      {tab === "groups" ? (
        <div className="subgroup-manager-list">
          {subgroups.map(group => {
            const lead = buildRoomMember(group.leadId);
            return (
              <article
                className={`subgroup-manager-card ${selectedId === group.id ? "selected" : ""}`}
                key={group.id}
                onClick={() => setSelectedId(group.id)}
              >
                <header>
                  <span>
                    <Icon name="users" />
                  </span>
                  <div>
                    <strong>{group.name}</strong>
                    <p>
                      #{group.channel.replace(/^subgroup-/, "")} · {group.memberIds.length} learners · lead{" "}
                      {lead?.name || "Unassigned"}
                    </p>
                  </div>
                  <TonePill
                    tone={
                      group.status === "Needs review"
                        ? "orange"
                        : group.status === "Paused"
                          ? "default"
                          : "green"
                    }
                  >
                    {group.status}
                  </TonePill>
                </header>
                <div className="subgroup-manager-meta">
                  <span>
                    <small>Progress</small>
                    <strong>{group.progress}%</strong>
                  </span>
                  <span>
                    <small>Evidence</small>
                    <strong>{group.evidence}%</strong>
                  </span>
                  <span>
                    <small>Branch</small>
                    <strong>{group.branch}</strong>
                  </span>
                  <span>
                    <small>Huddle code</small>
                    <strong>Not issued</strong>
                  </span>
                </div>
                <footer>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={event => {
                      event.stopPropagation();
                      navigate("buildRooms", group.channel);
                      close();
                    }}
                  >
                    <Icon name="message" size="sm" />
                    Open
                  </button>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={event => {
                      event.stopPropagation();
                      startHuddle(group);
                    }}
                  >
                    <Icon name="video" size="sm" />
                    Huddle
                  </button>
                  <button
                    type="button"
                    className="secondary-btn button-sm"
                    onClick={event => {
                      event.stopPropagation();
                      setTab("move");
                      setMoveTarget(group.id);
                    }}
                  >
                    <Icon name="users" size="sm" />
                    Members
                  </button>
                  <button
                    type="button"
                    className="icon-btn danger-icon"
                    onClick={event => {
                      event.stopPropagation();
                      dissolve(group.id);
                    }}
                    aria-label={`Dissolve ${group.name}`}
                  >
                    <Icon name="trash" size="sm" />
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : null}
      {tab === "form" ? (
        <div className="subgroup-form">
          <div className="auto-group-panel">
            <div>
              <span className="overline">Whole-class formation</span>
              <h3>Automatically form smaller groups</h3>
              <p>
                Distribute the 34 enrolled learners into persistent teams. The demonstration uses transparent
                rules; production should explain any balancing logic and allow instructor review before
                publishing.
              </p>
            </div>
            <div className="subgroup-form-grid">
              <label className="field-label">
                <span>Formation method</span>
                <select
                  value={strategy}
                  onChange={event => setStrategy(event.target.value as BuildRoomSubgroup["formation"])}
                >
                  <option>Balanced</option>
                  <option>Random</option>
                  <option>Language</option>
                  <option>Project role</option>
                  <option>Availability</option>
                  <option>Manual</option>
                </select>
              </label>
              <label className="field-label">
                <span>Target group size</span>
                <select value={groupSize} onChange={event => setGroupSize(Number(event.target.value))}>
                  <option value="4">4 learners</option>
                  <option value="5">5 learners</option>
                  <option value="6">6 learners</option>
                  <option value="7">7 learners</option>
                  <option value="8">8 learners</option>
                </select>
              </label>
              <label className="field-label">
                <span>Default visibility</span>
                <select
                  value={visibility}
                  onChange={event => setVisibility(event.target.value as BuildRoomSubgroup["visibility"])}
                >
                  <option>Members + instructors</option>
                  <option>Whole class</option>
                  <option>Instructor only</option>
                </select>
              </label>
            </div>
            <button type="button" className="primary-btn" onClick={autoForm}>
              <Icon name="sparkles" size="sm" />
              Preview and publish groups
            </button>
          </div>
          <div className="manual-group-panel">
            <span className="overline">Manual group</span>
            <div className="subgroup-form-grid">
              <label className="field-label">
                <span>Group name</span>
                <input
                  className="text-field"
                  value={name}
                  onChange={event => setName(event.target.value)}
                  placeholder="e.g. Accessibility & Experience"
                />
              </label>
              <label className="field-label span-2">
                <span>Purpose</span>
                <textarea
                  className="text-area"
                  value={purpose}
                  onChange={event => setPurpose(event.target.value)}
                  placeholder="Describe the focused contribution this group owns."
                />
              </label>
            </div>
            <div className="member-pick-head">
              <strong>Select learners</strong>
              <span>{selectedMembers.length} selected · up to 12</span>
            </div>
            <div className="member-pick-grid">
              {learners.map(member => (
                <button
                  type="button"
                  className={`member-pick ${selectedMembers.includes(member.id) ? "selected" : ""} ${assigned.has(member.id) ? "assigned" : ""}`}
                  key={member.id}
                  onClick={() => toggleMember(member.id)}
                >
                  <Avatar initials={member.initials} small />
                  <span>
                    <strong>{member.name}</strong>
                    <small>
                      {assigned.has(member.id)
                        ? "Already assigned · selecting moves this learner into the new group"
                        : "Available and unassigned"}
                    </small>
                  </span>
                  <i>{selectedMembers.includes(member.id) ? "✓" : "+"}</i>
                </button>
              ))}
            </div>
            <button type="button" className="primary-btn" onClick={createGroup}>
              <Icon name="plus" size="sm" />
              Create group
            </button>
          </div>
        </div>
      ) : null}
      {tab === "move" ? (
        <div className="move-member-panel">
          <div className="platform-boundary-banner compact">
            <span>
              <Icon name="users" />
            </span>
            <div>
              <strong>Move one learner at a time with an attributable reason.</strong>
              <p>
                The learner’s previous group history and contribution record remain available. Private
                messages are not copied into the destination group.
              </p>
            </div>
          </div>
          <div className="subgroup-form-grid">
            <label className="field-label">
              <span>Learner</span>
              <select value={moveMember} onChange={event => setMoveMember(event.target.value)}>
                <option value="">Choose learner</option>
                {learners.map(member => {
                  const current = subgroups.find(group => group.memberIds.includes(member.id));
                  return (
                    <option key={member.id} value={member.id}>
                      {member.name} · {current?.name || "Unassigned"}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="field-label">
              <span>Destination group</span>
              <select value={moveTarget} onChange={event => setMoveTarget(event.target.value)}>
                {subgroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} · {group.memberIds.length} learners
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label span-2">
              <span>Reason</span>
              <textarea
                className="text-area"
                defaultValue="Rebalance workload and place the learner with the group that owns the relevant requirement."
              />
            </label>
          </div>
          <button type="button" className="primary-btn" onClick={move}>
            <Icon name="arrow-right" size="sm" />
            Move learner
          </button>
          {selected ? (
            <div className="selected-group-members">
              <h3>{selected.name}</h3>
              <div className="member-chip-grid">
                {selected.memberIds.map(id => {
                  const member = buildRoomMember(id);
                  return (
                    <span className="group-member-chip" key={id}>
                      <Avatar initials={member?.initials || "LR"} small />
                      <strong>{member?.name || id}</strong>
                      {id === selected.leadId ? <small>Lead</small> : null}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {tab === "broadcast" ? (
        <div className="broadcast-panel">
          <div className="platform-boundary-banner compact">
            <span>
              <Icon name="message" />
            </span>
            <div>
              <strong>Broadcast from the parent room to every small group.</strong>
              <p>
                The message is copied as a shared instructor announcement with the same ID, language and
                timestamp. Replies remain in each group.
              </p>
            </div>
            <TonePill tone="blue">{subgroups.length} groups</TonePill>
          </div>
          <label className="field-label">
            <span>Instructor announcement</span>
            <textarea
              className="text-area"
              value={broadcast}
              onChange={event => setBroadcast(event.target.value)}
              placeholder="Share a requirement clarification, deadline, evidence rule or huddle instruction…"
            />
          </label>
          <div className="broadcast-audience">
            {subgroups.map(group => (
              <span key={group.id}>
                <Icon name="users" size="sm" />
                {group.name}
                <small>{group.memberIds.length}</small>
              </span>
            ))}
          </div>
          <button
            type="button"
            className="primary-btn"
            disabled={!broadcast.trim()}
            onClick={() => {
              notify(
                "Announcement sent to all small groups",
                `${subgroups.length} groups · original language retained · shared parent message ID recorded`,
              );
              setBroadcast("");
            }}
          >
            <Icon name="send" size="sm" />
            Broadcast to {subgroups.length} groups
          </button>
        </div>
      ) : null}
      {tab === "settings" ? (
        <div className="toggle-list subgroup-safeguards">
          <div className="toggle-line">
            <span>
              <strong>Private group channels by default</strong>
              <small>Learners see the parent room and only their assigned smaller group.</small>
            </span>
            <Switch value={privateGroups} onChange={setPrivateGroups} label="Private group channels" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Shared parent brief and announcements</strong>
              <small>
                Requirements, deadlines and instructor decisions stay consistent across all groups.
              </small>
            </span>
            <Switch value={sharedBrief} onChange={setSharedBrief} label="Shared parent context" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Separate repository branches</strong>
              <small>Each group writes only to its named branch until human integration.</small>
            </span>
            <Switch value={separateBranches} onChange={setSeparateBranches} label="Separate branches" />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Separate AI agent scopes</strong>
              <small>An approved agent cannot cross group channels, files or branches.</small>
            </span>
            <Switch
              value={separateAgentScopes}
              onChange={setSeparateAgentScopes}
              label="Separate AI scopes"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Instructor access to every group</strong>
              <small>
                Assigned instructors can enter all groups; mentors and reviewers need explicit access.
              </small>
            </span>
            <Switch value={instructorAccess} onChange={setInstructorAccess} label="Instructor access" />
          </div>
          <button
            type="button"
            className="primary-btn"
            onClick={() =>
              notify(
                "Small-group safeguards saved",
                "The parent Build Room now applies the selected visibility, branch and AI-scope rules.",
              )
            }
          >
            <Icon name="save" size="sm" />
            Save safeguards
          </button>
        </div>
      ) : null}
    </ModalShell>
  );
}

function AcademicCreateLessonModal({
  close,
  navigate,
  notify,
}: {
  close: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const cancel = () => {
    if (!busy) close();
  };
  return (
    <ModalShell
      icon="book"
      title="Create lesson"
      description="Start a versioned lesson with outcomes, content format, accessibility requirements and publishing workflow."
      close={cancel}
      wide
      actions={
        <>
          <button type="button" className="secondary-btn" disabled={busy} onClick={cancel}>
            Cancel
          </button>
          <button type="submit" form="academic-lesson-create" className="primary-btn" disabled={busy}>
            <Icon name="edit" size="sm" />
            {busy ? "Creating…" : "Create draft"}
          </button>
        </>
      }
    >
      <AcademicLessonFields
        formId="academic-lesson-create"
        onBusy={setBusy}
        onSaved={route => {
          notify("Lesson draft created");
          close();
          navigate("teaching", route);
        }}
      />
      <div className="modal-form-note">
        <Icon name="info" size="sm" />
        Complete the lesson activities before submitting its curriculum for review.
      </div>
    </ModalShell>
  );
}

function InstructorModalLayer({
  modal,
  close,
  navigate,
  notify,
  language,
  setLanguage,
  sessions,
  createSession,
  addChannel,
  subgroups,
  setSubgroups,
  roleId,
  setRoleId,
}: {
  modal: InstructorModal;
  close: () => void;
  navigate: (area: InstructorArea, page?: string) => void;
  notify: (title: string, detail?: string, tone?: InstructorNotice["tone"]) => void;
  language: LanguageSettings;
  setLanguage: React.Dispatch<React.SetStateAction<LanguageSettings>>;
  sessions: ClassSession[];
  createSession: (session: ClassSession) => void;
  addChannel: (channel: string) => void;
  subgroups: BuildRoomSubgroup[];
  setSubgroups: React.Dispatch<React.SetStateAction<BuildRoomSubgroup[]>>;
  roleId: TeachingRoleId;
  setRoleId: React.Dispatch<React.SetStateAction<TeachingRoleId>>;
}) {
  const identity = useSignedInIdentity();
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("2026-11-12");
  const [time, setTime] = useState("15:00");
  const [cohort, setCohort] = useState(COHORTS[0].id);
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState("Approve with conditions");
  const modalPayload = modal && "payload" in modal ? modal.payload : "";
  useEffect(() => {
    setName("");
    setTitle("");
    setNotes("");
    setDecision("Approve with conditions");
  }, [modal?.type, modalPayload]);
  if (!modal) return null;

  if (modal.type === "role-switch")
    return (
      <ModalShell
        icon="users"
        title="Preview a teaching role"
        description="Role preview changes route access and authority boundaries. Production roles are assigned by the Academy backend and cannot be self-elevated."
        close={close}
        wide
        actions={
          <button type="button" className="secondary-btn" onClick={close}>
            Close
          </button>
        }
      >
        <div className="role-choice-grid">
          {Object.values(INSTRUCTOR_ROLES).map(role => (
            <button
              type="button"
              className={`role-choice-card ${roleId === role.id ? "active" : ""}`}
              key={role.id}
              onClick={() => {
                setRoleId(role.id);
                safeLocalStorageSet("chatoner-instructor-role", role.id);
                notify("Teaching role preview changed", `${role.name} · ${role.scope}`);
                close();
              }}
            >
              <span>
                <Icon
                  name={
                    role.id === "mentor"
                      ? "message"
                      : role.id === "instructor"
                        ? "book"
                        : role.id === "instructor-assessor"
                          ? "clipboard"
                          : "shield"
                  }
                />
              </span>
              <div>
                <strong>{role.name}</strong>
                <p>{role.description}</p>
                <small>{role.scope}</small>
              </div>
              {roleId === role.id ? <Icon name="check" /> : null}
            </button>
          ))}
        </div>
      </ModalShell>
    );
  if (modal.type === "members")
    return <MemberDirectoryModal close={close} navigate={navigate} notify={notify} subgroups={subgroups} />;
  if (modal.type === "manage-groups")
    return (
      <SubgroupManagerModal
        close={close}
        subgroups={subgroups}
        setSubgroups={setSubgroups}
        language={language}
        navigate={navigate}
        notify={notify}
        initialGroupId={modal.payload}
      />
    );
  if (modal.type === "language")
    return (
      <ModalShell
        icon="globe"
        title="Language and translation settings"
        description="Set the instructor writing language, secondary translation and original-language retention across Academy, Chatoner Class and connected Labs."
        close={close}
        actions={
          <>
            <button
              type="button"
              className="secondary-btn"
              onClick={() =>
                setLanguage({
                  primary: "English",
                  secondary: "Luganda",
                  showTranslation: true,
                  retainOriginal: true,
                })
              }
            >
              Reset default
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                safeLocalStorageSet("chatoner-instructor-language", JSON.stringify(language));
                notify(
                  "Language settings saved",
                  `${languageCode(language.primary)}${language.secondary !== "None" ? ` + ${languageCode(language.secondary)}` : ""}`,
                );
                close();
              }}
            >
              <Icon name="check" size="sm" />
              Save language
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Primary language</span>
            <select
              value={language.primary}
              onChange={event => setLanguage(current => ({ ...current, primary: event.target.value }))}
            >
              {LANGUAGE_NAMES.map(item => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="field-label">
            <span>Secondary translation</span>
            <select
              value={language.secondary}
              onChange={event => setLanguage(current => ({ ...current, secondary: event.target.value }))}
            >
              {[...LANGUAGE_NAMES, "None"].map(item => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>Show translated view</strong>
              <small>Show a reviewed secondary language where available.</small>
            </span>
            <Switch
              value={language.showTranslation}
              onChange={on => setLanguage(current => ({ ...current, showTranslation: on }))}
              label="Show translations"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Retain original</strong>
              <small>Keep the source message, caption or evidence accessible.</small>
            </span>
            <Switch
              value={language.retainOriginal}
              onChange={on => setLanguage(current => ({ ...current, retainOriginal: on }))}
              label="Retain original language"
            />
          </div>
        </div>
        <div className="modal-preview-card">
          <h3>
            Room label preview · {languageCode(language.primary)}
            {language.secondary !== "None" ? ` + ${languageCode(language.secondary)}` : ""}
          </h3>
          <p>
            The selected language context is carried into Chatoner Class and eligible AI Lab handoffs.
            Production translation should preserve the original and its approval status.
          </p>
        </div>
      </ModalShell>
    );
  if (modal.type === "assignment-brief")
    return (
      <ModalShell
        icon="clipboard"
        title="Accessible checkout assignment brief"
        description="A complete instructor view of the outcome, requirements, connected Class, Lab, Build Room and credential evidence."
        close={close}
        wide
        actions={
          <>
            <button
              type="button"
              className="secondary-btn"
              onClick={() =>
                downloadTextFile(
                  "Chatoner-Academy-Accessible-Checkout-Brief.md",
                  `# Accessible, Trustworthy Checkout\n\nDue: 28 November 2026\n\n## Requirements\n- UX-04: Clear and recoverable validation\n- ACC-02: Keyboard and screen-reader operation\n- SEC-06: Test data and human merge control\n- EVAL-03: Automated plus human evaluation\n\nConnected Class: Accessible Product Decisions\nConnected Lab: AI-App Builder\nBuild Room: group-build-checkout\n`,
                )
              }
            >
              <Icon name="download" size="sm" />
              Download brief
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                navigate("assessment", "assignment-accessible-checkout");
                close();
              }}
            >
              Full assignment
            </button>
            <button type="button" className="primary-btn" onClick={close}>
              Return to room
            </button>
          </>
        }
      >
        <div className="brief-overview">
          <div className="brief-block">
            <span>Outcome</span>
            <strong>Build and defend an accessible, trustworthy checkout change</strong>
            <p>
              Learners must show the problem, smallest safe change, tests, human decision and residual risk.
            </p>
          </div>
          <div className="brief-block">
            <span>Due and submission</span>
            <strong>28 Nov 2026 · 17:00 EAT</strong>
            <p>
              Repository branch, working preview, evaluation set, reflection and selected credential evidence.
            </p>
          </div>
        </div>
        <div className="brief-requirements">
          {[
            [
              "UX-04",
              "Clear validation",
              "Coupon and delivery errors are specific, recoverable and placed near the affected control.",
            ],
            [
              "ACC-02",
              "Keyboard and screen-reader operation",
              "Focus order, visible focus, announcement and return focus are verified by a person.",
            ],
            [
              "SEC-06",
              "Safe test environment",
              "Synthetic data only; AI agents cannot merge or access external services without named approval.",
            ],
            [
              "EVAL-03",
              "Evidence-based evaluation",
              "Automated checks and human observation are linked to the requirement and remaining risk.",
            ],
          ].map(item => (
            <div className="brief-requirement" key={item[0]}>
              <code>{item[0]}</code>
              <div>
                <strong>{item[1]}</strong>
                <p>{item[2]}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="content-grid" style={{ marginTop: 10 }}>
          <AcademyPanel icon="video" title="Connected Class" meta="class.chatoner.com">
            <p className="settings-intro">
              {sessions[0]
                ? `${sessions[0].title} · one-time handoff after authority check`
                : "No current Academy Class session is assigned."}
            </p>
            <button
              type="button"
              className="secondary-btn button-sm"
              disabled={!sessions[0]}
              onClick={() => {
                if (!sessions[0]) return;
                navigate("class", `session-${sessions[0].id}`);
                close();
              }}
            >
              Open host studio
              <Icon name="external" size="sm" />
            </button>
          </AcademyPanel>
          <AcademyPanel icon="flask" title="Connected AI Lab" meta="Independent · labs.chatoner.com">
            <p className="settings-intro">
              AI-App Builder · Academy carries the intended Lab and entitlement into the independent
              Instructor workspace.
            </p>
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() =>
                openExternalHandoff(
                  instructorLabHandoffUrl(
                    LABS[0],
                    "builder-oct-2026",
                    "accessible-checkout",
                    "facilitator",
                    "dashboard",
                    language,
                  ),
                )
              }
            >
              Open in Labs
              <Icon name="external" size="sm" />
            </button>
          </AcademyPanel>
        </div>
      </ModalShell>
    );
  if (modal.type === "huddle")
    return (
      <ModalShell
        icon="video"
        title="Start a Build Room huddle"
        description="A Build Room huddle requires a canonical Academy Class session assigned to this subgroup."
        close={close}
        actions={
          <button type="button" className="secondary-btn" onClick={close}>
            Close
          </button>
        }
      >
        <div className="class-trust-line">
          <Icon name="shield" size="sm" />
          <p>
            Academy will not manufacture a provider code or host destination from browser-only Build Room
            data. Create the subgroup session through the owning learning workflow first.
          </p>
        </div>
      </ModalShell>
    );
  if (modal.type === "new-channel")
    return (
      <ModalShell
        icon="hash"
        title="Create Build Room channel"
        description="Add a scoped collaboration channel to the Kijani project room."
        close={close}
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!name.trim()}
              onClick={() => {
                const value = name
                  .trim()
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "");
                addChannel(value || `channel-${Date.now()}`);
                notify("Channel created", `#${value}`);
                close();
              }}
            >
              <Icon name="plus" size="sm" />
              Create channel
            </button>
          </>
        }
      >
        <label className="field-label">
          <span>Channel name</span>
          <input
            className="text-field"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="for example: staging-verification"
          />
        </label>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Purpose</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Explain what belongs in this channel and who should have access."
          />
        </label>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Access</span>
            <select>
              <option>Project members and assigned staff</option>
              <option>Core team only</option>
              <option>Instructor review only</option>
            </select>
          </label>
          <label className="field-label">
            <span>Evidence retention</span>
            <select>
              <option>Retain selected decisions and artifacts</option>
              <option>Conversation only</option>
            </select>
          </label>
        </div>
      </ModalShell>
    );
  if (modal.type === "create-class")
    return (
      <ModalShell
        icon="video"
        title="Create Chatoner Class session"
        description="Schedule a session in Academy, generate a unique Class code and prepare separate instructor and learner handoffs."
        close={close}
        wide
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!title.trim()}
              onClick={() => {
                const id = `custom-${Date.now()}`;
                const selected = COHORTS.find(item => item.id === cohort) || COHORTS[0];
                createSession({
                  id,
                  title: title.trim(),
                  code: createClassCode(),
                  dateLabel: new Date(`${date}T${time}`).toLocaleDateString(undefined, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }),
                  timeLabel: `${time} EAT`,
                  localTime: `${time} EAT`,
                  duration: "60 min",
                  instructor: "Amina Mutesi",
                  program: selected.program,
                  cohort: selected.title,
                  mode: "Interactive live class",
                  status: "upcoming",
                  preparation: [
                    "Connected lesson selected",
                    "Question queue reviewed",
                    "Device and caption rehearsal completed",
                  ],
                  agenda: [
                    "Connect to prior learning",
                    "Instructor demonstration",
                    "Learner practice",
                    "Reflection and evidence",
                  ],
                  learnerCount: selected.learners,
                  questions: 0,
                  recording: "scheduled",
                  language: language.primary,
                });
                close();
              }}
            >
              <Icon name="calendar" size="sm" />
              Create session
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Class title</span>
            <input
              className="text-field"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="for example: Evidence and Verification Clinic"
            />
          </label>
          <label className="field-label">
            <span>Cohort</span>
            <select value={cohort} onChange={event => setCohort(event.target.value)}>
              {COHORTS.filter(item => item.status !== "completed").map(item => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Date</span>
            <input
              className="text-field"
              type="date"
              value={date}
              onChange={event => setDate(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Start time</span>
            <input
              className="text-field"
              type="time"
              value={time}
              onChange={event => setTime(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Duration</span>
            <select>
              <option>60 minutes</option>
              <option>45 minutes</option>
              <option>75 minutes</option>
              <option>90 minutes</option>
            </select>
          </label>
        </div>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Connected lesson or objective</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="State what learners should be able to do after this Class."
          />
        </label>
        <div className="modal-form-note">
          <Icon name="link" size="sm" />
          After creation, Academy generates the Class code, instructor host handoff, learner invitation and
          calendar file. Production tokens are signed server-side.
        </div>
      </ModalShell>
    );
  if (modal.type === "class-invite") {
    const session = sessions.find(item => item.id === modal.payload) || sessions[0];
    if (!session)
      return (
        <ModalShell
          icon="share"
          title="Learner Class invitation"
          description="No current Academy session is available."
          close={close}
          actions={
            <button type="button" className="secondary-btn" onClick={close}>
              Close
            </button>
          }
        >
          <p>Create an Academy session for an assigned Cohort before sharing Class access.</p>
        </ModalShell>
      );
    return (
      <ModalShell
        icon="share"
        title="Learner Class invitation"
        description="Learners open this assigned session from their Academy Class schedule. D17 issues each learner a separate one-time join destination after sign-in."
        close={close}
        actions={
          <button
            type="button"
            className="secondary-btn"
            onClick={() => {
              copyToClipboard(
                `${session.title}\n${session.dateLabel} · ${session.timeLabel}\nOpen your Academy Class schedule to join.`,
              );
              notify("Academy session notice copied");
            }}
          >
            Copy Academy session notice
          </button>
        }
      >
        <div className="modal-preview-card">
          <h3>{session.title}</h3>
          <p>
            {session.dateLabel} · {session.timeLabel} · {session.cohort}
          </p>
        </div>
        <div className="invite-code-box">
          <div className="invite-code-value">One-time handoff after sign-in</div>
        </div>
        <div className="class-trust-line">
          <Icon name="users" size="sm" />
          <p>
            Academy rechecks current participation when the learner requests access. No reusable provider code
            or host-capable link is exposed through this invitation.
          </p>
        </div>
      </ModalShell>
    );
  }
  if (modal.type === "sync-attendance") {
    const session = sessions.find(item => item.id === modal.payload) || sessions[0];
    return (
      <ModalShell
        icon="refresh"
        title="Sync Chatoner Class attendance"
        description={`Reconcile the signed attendance record for ${session.title}, including corrections and approved equivalent participation.`}
        close={close}
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Not now
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                notify(
                  "Attendance synced",
                  `${session.learnerCount - 3} present · 2 equivalent routes · 1 correction pending`,
                );
                close();
              }}
            >
              <Icon name="refresh" size="sm" />
              Sync record
            </button>
          </>
        }
      >
        <div className="stat-grid" style={{ marginTop: 0 }}>
          <StatTile icon="users" label="Expected" value={session.learnerCount} detail="Enrolled learners" />
          <StatTile
            icon="check"
            label="Present"
            value={session.learnerCount - 3}
            detail="Signed Class attendance"
            tone="cyan"
          />
          <StatTile icon="play" label="Equivalent" value="2" detail="Recording + reflection" tone="blue" />
          <StatTile icon="alert" label="Corrections" value="1" detail="Needs human decision" tone="orange" />
        </div>
        <div className="modal-form-note">
          <Icon name="shield" size="sm" />
          Attendance is not used as a hidden behavioural score. Correction requests and accessible
          alternatives remain visible and appealable.
        </div>
      </ModalShell>
    );
  }
  if (modal.type === "new-cohort")
    return (
      <ModalShell
        icon="users"
        title="Create cohort"
        description="Prepare a programme delivery group with capacity, dates, instructors, language and connected Class/Lab entitlements."
        close={close}
        wide
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!title.trim()}
              onClick={() => {
                notify(
                  "Cohort created",
                  `${title.trim()} is now in planning with ${name || "Amina Mutesi"} as lead instructor.`,
                );
                navigate("cohorts", "upcoming");
                close();
              }}
            >
              <Icon name="plus" size="sm" />
              Create cohort
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Cohort name</span>
            <input
              className="text-field"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="AI Product Builder · January 2027"
            />
          </label>
          <label className="field-label">
            <span>Programme</span>
            <select>
              <option>AI Product Builder</option>
              <option>Prompt Systems Practitioner</option>
              <option>AI Leadership & Governance</option>
              <option>Context Systems Practitioner</option>
            </select>
          </label>
        </div>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Start date</span>
            <input
              className="text-field"
              type="date"
              value={date}
              onChange={event => setDate(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Capacity</span>
            <input className="text-field" type="number" defaultValue="36" min="1" />
          </label>
          <label className="field-label">
            <span>Lead instructor</span>
            <input
              className="text-field"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="Amina Mutesi"
            />
          </label>
        </div>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Primary language</span>
            <select defaultValue={language.primary}>
              {[language.primary, ...LANGUAGE_NAMES]
                .filter((item, index, list) => list.indexOf(item) === index)
                .map(item => (
                  <option key={item}>{item}</option>
                ))}
            </select>
          </label>
          <label className="field-label">
            <span>Delivery mode</span>
            <select>
              <option>Blended · Class + Labs + Projects</option>
              <option>Live online</option>
              <option>Hybrid</option>
            </select>
          </label>
        </div>
      </ModalShell>
    );
  if (modal.type === "announcement")
    return (
      <ModalShell
        icon="message"
        title="Create cohort announcement"
        description="Send a clear, multilingual update linked to the exact Class, Lab, assignment, project or deadline."
        close={close}
        wide
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Save draft
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!title.trim() || !notes.trim()}
              onClick={() => {
                notify("Announcement sent", `${title} · Builder October 2026`);
                close();
              }}
            >
              <Icon name="send" size="sm" />
              Send announcement
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Audience</span>
            <select>
              <option>AI Product Builder · October 2026</option>
              <option>All assigned learners</option>
              <option>Prompt Systems · September 2026</option>
            </select>
          </label>
          <label className="field-label">
            <span>Language view</span>
            <select>
              <option>
                {language.primary}
                {language.secondary !== "None" ? ` + ${language.secondary}` : ""}
              </option>
              <option>Primary only</option>
            </select>
          </label>
        </div>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Title</span>
          <input
            className="text-field"
            value={title}
            onChange={event => setTitle(event.target.value)}
            placeholder="Week 6 · From claims to evidence"
          />
        </label>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Message</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Write a concise learner-facing update…"
          />
        </label>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Connected destination</span>
            <select>
              <option>Build Room · group-build-checkout</option>
              <option>Class · Accessible Product Decisions</option>
              <option>Lab · AI-App Builder</option>
              <option>Assignment · Accessible checkout</option>
            </select>
          </label>
          <label className="field-label">
            <span>Delivery</span>
            <select>
              <option>Academy + email + push</option>
              <option>Academy only</option>
            </select>
          </label>
        </div>
      </ModalShell>
    );
  if (modal.type === "create-lesson")
    return <AcademicCreateLessonModal close={close} navigate={navigate} notify={notify} />;
  if (modal.type === "create-assignment")
    return (
      <ModalShell
        icon="clipboard"
        title="Create assignment"
        description="Define an outcome, requirements, evidence, rubric, due date and connected Class, Lab or Build Room."
        close={close}
        wide
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!title.trim()}
              onClick={() => {
                notify("Assignment draft created", title);
                navigate("assessment", "assignments");
                close();
              }}
            >
              <Icon name="clipboard" size="sm" />
              Create draft
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Assignment title</span>
            <input
              className="text-field"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Build an accessible, trustworthy checkout"
            />
          </label>
          <label className="field-label">
            <span>Cohort</span>
            <select>
              <option>AI Product Builder · October 2026</option>
              <option>Prompt Systems · September 2026</option>
            </select>
          </label>
        </div>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Outcome</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Describe the working result, learner decision and proof required."
          />
        </label>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Rubric</span>
            <select>
              <option>Product Evidence Rubric v3.0</option>
              <option>Context Systems Rubric v2.2</option>
            </select>
          </label>
          <label className="field-label">
            <span>Due date</span>
            <input
              className="text-field"
              type="date"
              value={date}
              onChange={event => setDate(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Weight</span>
            <input className="text-field" type="number" defaultValue="25" />
          </label>
        </div>
      </ModalShell>
    );
  if (modal.type === "review-decision")
    return (
      <ModalShell
        icon="shield"
        title="Record human review decision"
        description="The reviewer owns the decision. AI output and automated tests may support, but cannot issue the final grade, milestone or credential recommendation."
        close={close}
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={!notes.trim()}
              onClick={() => {
                notify("Human decision recorded", `${decision} · attributable to Amina Mutesi`);
                close();
              }}
            >
              <Icon name="check" size="sm" />
              Record decision
            </button>
          </>
        }
      >
        <label className="field-label">
          <span>Decision</span>
          <select value={decision} onChange={event => setDecision(event.target.value)}>
            <option>Approve with conditions</option>
            <option>Approve</option>
            <option>Request revision</option>
            <option>Refer to moderation</option>
            <option>Hold for safety review</option>
          </select>
        </label>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Rationale and learner action</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Explain the evidence considered, condition, learner action, remaining risk and next checkpoint."
          />
        </label>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Follow-up due</span>
            <input
              className="text-field"
              type="date"
              value={date}
              onChange={event => setDate(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Notify</span>
            <select>
              <option>Learner and assigned team</option>
              <option>Learner only</option>
              <option>Moderation panel</option>
            </select>
          </label>
        </div>
      </ModalShell>
    );
  if (modal.type === "assign-lab")
    return (
      <ModalShell
        icon="flask"
        title="Assign connected AI Lab"
        description="Map an Academy outcome to the exact independent Lab, eligible cohort, entitlement and evidence return path. Activity authoring continues on labs.chatoner.com."
        close={close}
        wide
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                notify(
                  "Lab connection assigned",
                  "Eligible learners will open the exact independent Lab on labs.chatoner.com after server-side entitlement validation.",
                );
                navigate("labs", "assignments");
                close();
              }}
            >
              <Icon name="link" size="sm" />
              Assign Lab
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>AI Lab</span>
            <select defaultValue={modal.payload || LABS[0].id}>
              {LABS.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title} · {item.access}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            <span>Cohort</span>
            <select>
              {COHORTS.filter(item => item.status !== "completed").map(item => (
                <option key={item.id}>{item.title}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Connected lesson</span>
            <select>
              <option>Accessible Product Decisions</option>
              <option>APIs, Data Boundaries and Server Actions</option>
              <option>Evaluation Clinic</option>
            </select>
          </label>
          <label className="field-label">
            <span>Entitlement</span>
            <select>
              <option>AIPB-CORE · Included</option>
              <option>ADVANCED-LABS · Approval required</option>
            </select>
          </label>
        </div>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Expected evidence</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Selected run, configuration, outputs, tests, reflection and human decision."
          />
        </label>
        <div className="modal-form-note">
          <Icon name="shield" size="sm" />
          The handoff must reject learners who are not enrolled, not entitled or who alter the intended Lab
          identifier.
        </div>
      </ModalShell>
    );
  if (modal.type === "office-hours")
    return (
      <ModalShell
        icon="calendar"
        title="Add office-hour availability"
        description="Create an Academy booking window with capacity, appointment type and a linked Chatoner Class room."
        close={close}
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                notify("Office-hour availability saved", `${date} · ${time} EAT`);
                close();
              }}
            >
              <Icon name="calendar" size="sm" />
              Save availability
            </button>
          </>
        }
      >
        <div className="form-row">
          <label className="field-label">
            <span>Day or date</span>
            <input
              className="text-field"
              type="date"
              value={date}
              onChange={event => setDate(event.target.value)}
            />
          </label>
          <label className="field-label">
            <span>Start time</span>
            <input
              className="text-field"
              type="time"
              value={time}
              onChange={event => setTime(event.target.value)}
            />
          </label>
        </div>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Appointment type</span>
            <select>
              <option>Individual support</option>
              <option>Project clinic</option>
              <option>Open drop-in</option>
            </select>
          </label>
          <label className="field-label">
            <span>Capacity</span>
            <input className="text-field" type="number" defaultValue="4" min="1" />
          </label>
        </div>
        <label className="field-label" style={{ marginTop: 10 }}>
          <span>Booking guidance</span>
          <textarea
            className="text-area"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            placeholder="Ask learners to bring the relevant requirement, evidence or question."
          />
        </label>
      </ModalShell>
    );
  if (modal.type === "export")
    return (
      <ModalShell
        icon="download"
        title="Create privacy-aware export"
        description="Choose a purpose, scope, format and expiry. Every export is attributable and auditable."
        close={close}
        actions={
          <>
            <button type="button" className="secondary-btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                const payload = modal.payload || "instructor-report";
                downloadTextFile(
                  `Chatoner-${String(payload).replace(/[^a-z0-9]+/gi, "-")}.csv`,
                  `Report,Value\nScope,${payload}\nGenerated by,Amina Mutesi\nGenerated at,${new Date().toISOString()}\nRetention,30 days\nLearners on track,82%\nReview median,18.4 hours\nCredential readiness,71%\n`,
                  "text/csv",
                );
                notify("Export generated", `${payload} · expires in 30 days`);
                close();
              }}
            >
              <Icon name="download" size="sm" />
              Generate export
            </button>
          </>
        }
      >
        <label className="field-label">
          <span>Purpose</span>
          <input
            className="text-field"
            value={title}
            onChange={event => setTitle(event.target.value)}
            placeholder="Weekly delivery review with Academic Operations"
          />
        </label>
        <div className="form-row" style={{ marginTop: 10 }}>
          <label className="field-label">
            <span>Format</span>
            <select>
              <option>CSV</option>
              <option>PDF summary</option>
              <option>Markdown report</option>
            </select>
          </label>
          <label className="field-label">
            <span>Scope</span>
            <select defaultValue={modal.payload || "Current view"}>
              <option>{modal.payload || "Current view"}</option>
              <option>Assigned cohorts only</option>
              <option>Aggregate indicators only</option>
            </select>
          </label>
          <label className="field-label">
            <span>Expiry</span>
            <select>
              <option>30 days</option>
              <option>7 days</option>
              <option>90 days</option>
            </select>
          </label>
        </div>
        <div className="toggle-list">
          <div className="toggle-line">
            <span>
              <strong>Remove direct learner identifiers</strong>
              <small>Use aggregate or pseudonymous values unless names are necessary.</small>
            </span>
            <Switch
              value={true}
              onChange={() => notify("Identifier setting changed")}
              label="Remove direct learner identifiers"
            />
          </div>
          <div className="toggle-line">
            <span>
              <strong>Include interpretation notes</strong>
              <small>Explain indicator limits and human decision boundaries.</small>
            </span>
            <Switch
              value={true}
              onChange={() => notify("Interpretation setting changed")}
              label="Include interpretation notes"
            />
          </div>
        </div>
      </ModalShell>
    );
  if (modal.type === "command")
    return (
      <ModalShell
        icon="command"
        title="Instructor quick actions"
        description="Jump directly to common teaching, review, support and collaboration tasks."
        close={close}
        actions={
          <button type="button" className="secondary-btn" onClick={close}>
            Close
          </button>
        }
      >
        <div className="command-grid">
          {[
            ["video", "Host today’s Class", "Open the live instructor studio", "class", "schedule"],
            [
              "clipboard",
              "Review next submission",
              "Maya Chen · Context Pack v2",
              "assessment",
              "submission-maya-context",
            ],
            [
              "message",
              "Open Build Room",
              "Kijani checkout · live build",
              "buildRooms",
              "group-build-checkout",
            ],
            [
              "flask",
              "Open Instructor Lab workspace",
              "Create, guide and grade on labs.chatoner.com",
              "labs",
              "workspace",
            ],
            [
              "grid",
              "Manage small groups",
              "Six persistent teams inside one class",
              "buildRooms",
              "small-groups",
            ],
            ["alert", "Support Jamal", "Open learner support profile", "cohorts", "learner-jamal-thompson"],
            ["plus", "Create a Class", "Schedule and generate code", "class", "create"],
          ].map(item => (
            <button
              type="button"
              className="command-card"
              key={item[1]}
              onClick={() => {
                navigate(item[3] as InstructorArea, item[4]);
                close();
              }}
            >
              <Icon name={item[0]} />
              <span>
                <strong>{item[1]}</strong>
                <span>{item[2]}</span>
              </span>
              <Icon name="arrow-right" size="sm" />
            </button>
          ))}
        </div>
      </ModalShell>
    );
  if (modal.type === "profile")
    return (
      <ModalShell
        icon="users"
        title={identity.name}
        description={`${identity.role || "No assigned role"} · Chatoner AI Academy`}
        close={close}
        actions={
          <>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                close();
                window.setTimeout(() => setRoleId(roleId), 0);
                notify(
                  "Open the role preview from any route ribbon",
                  "The active role is shown beside the operational model.",
                );
              }}
            >
              Role: {INSTRUCTOR_ROLES[roleId].shortName}
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                navigate("settings", "profile");
                close();
              }}
            >
              Open profile settings
            </button>
            <button type="button" className="primary-btn" onClick={close}>
              Done
            </button>
          </>
        }
      >
        <div className="profile-row">
          <div className="profile-photo-large">{identity.initials}</div>
          <div>
            <h3>{identity.name}</h3>
            <p>{identity.email}</p>
            <TonePill tone={identity.verified ? "green" : "orange"}>
              {identity.verified ? "Email verified" : "Email not verified"}
            </TonePill>
          </div>
        </div>
        <div className="evidence-stack">
          <div className="evidence-card">
            <span>
              <Icon name="calendar" />
            </span>
            <div>
              <strong>Next Class</strong>
              <p>Accessible Product Decisions · today at 3:00 PM EAT</p>
            </div>
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() => {
                navigate("class", sessions[0] ? `session-${sessions[0].id}` : "schedule");
                close();
              }}
            >
              Open
            </button>
          </div>
          <div className="evidence-card">
            <span>
              <Icon name="clipboard" />
            </span>
            <div>
              <strong>Review queue</strong>
              <p>11 assigned · 4 older than 24 hours</p>
            </div>
            <button
              type="button"
              className="secondary-btn button-sm"
              onClick={() => {
                navigate("assessment", "grading");
                close();
              }}
            >
              Review
            </button>
          </div>
        </div>
      </ModalShell>
    );
  return (
    <ModalShell
      icon="alert"
      title="Confirm action"
      description={
        modal.type === "confirmation" ? modal.payload || "Confirm this sensitive action" : "Confirm action"
      }
      close={close}
      actions={
        <>
          <button type="button" className="secondary-btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => {
              notify("Request recorded", "Academic Operations will review this protected action.");
              close();
            }}
          >
            Confirm request
          </button>
        </>
      }
    >
      <div className="modal-form-note">
        <Icon name="shield" size="sm" />
        This protected action is never completed silently. The request, decision owner and outcome remain
        auditable.
      </div>
    </ModalShell>
  );
}

function parseInstructorRoute(): { area: InstructorArea; page: string } {
  if (typeof window === "undefined") return { area: "command", page: "dashboard" };
  const parts = window.location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const index = parts[0] === "instructor" ? 1 : 0;
  const area = parts[index] as InstructorArea;
  if (AREA_CONFIG[area]) return { area, page: parts[index + 1] || AREA_CONFIG[area].defaultPage };
  return { area: "command", page: "dashboard" };
}

export default function ChatonerAIInstructorPlatform() {
  const coreInbox = useCoreNotifications();
  const initial = useMemo(() => parseInstructorRoute(), []);
  const [area, setArea] = useState<InstructorArea>(initial.area);
  const [page, setPage] = useState(initial.page);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modal, setModal] = useState<InstructorModal>(null);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [notices, setNotices] = useState<InstructorNotice[]>([]);
  const notifications: NotificationRecord[] = coreInbox.notifications.map(item => ({
    id: item.id,
    icon: "bell",
    title: item.title,
    detail: item.body,
    time: new Date(item.createdAt).toLocaleString(),
    category:
      item.category === "ASSESSMENT"
        ? "review"
        : item.category === "LEARNING"
          ? "class"
          : item.category === "ENROLLMENT"
            ? "cohort"
            : "system",
    unread: !item.readAt,
    area: "notifications",
    page: "all",
    deepLink: item.deepLink,
  }));
  const setNotifications: React.Dispatch<React.SetStateAction<NotificationRecord[]>> = action => {
    const next = typeof action === "function" ? action(notifications) : action;
    const newlyRead = notifications.filter(
      item => item.unread && !next.find(row => row.id === item.id)?.unread,
    );
    if (!newlyRead.length) return;
    if (newlyRead.length === notifications.filter(item => item.unread).length)
      void coreInbox.markAllRead().catch(() => {});
    else for (const item of newlyRead) void coreInbox.markRead(item.id).catch(() => {});
  };
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecord | null>(null);
  const [roleId, setRoleId] = useState<TeachingRoleId>(() => {
    const saved = readStorage("chatoner-instructor-role") as TeachingRoleId | null;
    if (saved && INSTRUCTOR_ROLES[saved]) return saved;
    return "instructor-lead";
  });
  const [language, setLanguage] = useState<LanguageSettings>(() => {
    try {
      const saved = readStorage("chatoner-instructor-language");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { primary: "English", secondary: "Luganda", showTranslation: true, retainOriginal: true };
  });
  const classSessions = useInstructorClassSessions();
  const sessions = classSessions.items;
  const [customChannels, setCustomChannels] = useState<string[]>(() => {
    try {
      return JSON.parse(readStorage("chatoner-instructor-channels") || "[]");
    } catch {
      return [];
    }
  });
  const [subgroups, setSubgroups] = useState<BuildRoomSubgroup[]>(() => {
    try {
      const saved = readStorage("chatoner-instructor-build-room-subgroups");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch {}
    return INITIAL_BUILD_ROOM_SUBGROUPS;
  });
  const [roomTab, setRoomTab] = useState<RoomTab>("chat");
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("details");
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop");
  const [composer, setComposer] = useState("");
  const [dynamicMessages, setDynamicMessages] = useState<DynamicMessage[]>([]);
  const [agentStopped, setAgentStopped] = useState(false);

  const notify = useCallback(
    (
      title: string,
      detail = "Action completed in this interactive Instructor Platform demonstration.",
      tone: InstructorNotice["tone"] = "green",
    ) => {
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setNotices(current => [...current, { id, title, detail, tone }]);
      window.setTimeout(() => setNotices(current => current.filter(item => item.id !== id)), 4200);
    },
    [],
  );
  const navigate = useCallback((nextArea: InstructorArea, nextPage = AREA_CONFIG[nextArea].defaultPage) => {
    setArea(nextArea);
    setPage(nextPage);
    setSidebarOpen(false);
    setGlobalSearchOpen(false);
    if (typeof window !== "undefined") {
      const hash = `#/instructor/${nextArea}/${nextPage}`;
      if (window.location.hash !== hash) window.location.hash = hash;
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const route = parseInstructorRoute();
      setArea(route.area);
      setPage(route.page);
      setSidebarOpen(false);
    };
    window.addEventListener("hashchange", handler);
    if (!window.location.hash) navigate("command", "dashboard");
    return () => window.removeEventListener("hashchange", handler);
  }, [navigate]);
  useEffect(() => {
    const testWindow = window as Window & {
      __CHATONER_INSTRUCTOR_NAVIGATE__?: (nextArea: InstructorArea, nextPage: string) => void;
    };
    testWindow.__CHATONER_INSTRUCTOR_NAVIGATE__ = navigate;
    return () => {
      delete testWindow.__CHATONER_INSTRUCTOR_NAVIGATE__;
    };
  }, [navigate]);
  useEffect(() => {
    safeLocalStorageSet("chatoner-instructor-language", JSON.stringify(language));
  }, [language]);
  useEffect(() => {
    safeLocalStorageSet("chatoner-instructor-role", roleId);
  }, [roleId]);
  useEffect(() => {
    safeLocalStorageSet("chatoner-instructor-channels", JSON.stringify(customChannels));
  }, [customChannels]);
  useEffect(() => {
    safeLocalStorageSet("chatoner-instructor-build-room-subgroups", JSON.stringify(subgroups));
  }, [subgroups]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setGlobalSearchOpen(true);
      }
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "p") {
        event.preventDefault();
        setModal({ type: "command" });
      }
      if (event.key === "Escape") {
        setGlobalSearchOpen(false);
        setModal(null);
        setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let wasCompact = window.innerWidth <= 980;
    if (wasCompact) setInspectorOpen(false);
    const handleResize = () => {
      const compact = window.innerWidth <= 980;
      if (compact && !wasCompact) setInspectorOpen(false);
      wasCompact = compact;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const unread = coreInbox.unreadCount;
  const openNotification = useCallback((item: NotificationRecord) => {
    setSelectedNotification(item);
  }, []);
  const createSession = (session: ClassSession) => {
    notify(
      "Use Academy session creation",
      `${session.title} was not saved. Create it in the canonical Attendance workspace before requesting a Class handoff.`,
      "orange",
    );
    navigate("class", "attendance");
  };
  const addChannel = (channel: string) => {
    setCustomChannels(current => (current.includes(channel) ? current : [...current, channel]));
    navigate("buildRooms", channel);
  };
  const sendRoomMessage = () => {
    const text = composer.trim();
    if (!text) return;
    const kind: DynamicMessage["kind"] = text.toLowerCase().includes("@ai builder") ? "agent" : "instructor";
    setDynamicMessages(current => [
      ...current,
      { id: Date.now(), kind, text, createdAt: nowTime(), scopeApproved: false },
    ]);
    setComposer("");
    notify(
      kind === "agent" ? "Agent task proposed" : "Message sent",
      kind === "agent"
        ? "Approve the visible permission scope before the AI Builder can begin."
        : "The room message and original language are retained.",
    );
  };
  const approveScope = (id: number) => {
    setDynamicMessages(current =>
      current.map(item => (item.id === id ? { ...item, scopeApproved: true } : item)),
    );
    notify(
      "AI Builder scope approved",
      "The task can run only inside the displayed branch, files and sandbox permissions.",
    );
  };
  const stopAgent = () => {
    setAgentStopped(true);
    notify(
      "AI Builder stopped",
      "The stop action, requester and partial output are retained in the room record.",
      "orange",
    );
  };
  const languageLabel = `${languageCode(language.primary)}${language.secondary !== "None" ? ` + ${languageCode(language.secondary)}` : ""}`;
  const isFullBuildRoom = area === "buildRooms" && page === "group-build-checkout";
  const routeSpec = useMemo(
    () =>
      resolveStaticInstructorRouteSpec(area, page) ||
      (area === "teaching" && academicLessonIdentity(page)
        ? {
            ...resolveStaticInstructorRouteSpec("teaching", "lesson-api-boundaries")!,
            id: `teaching/${page}`,
            page,
            title: "Lesson Authoring",
            purpose: "Edit the exact lesson in its assigned academic curriculum.",
          }
        : null) ||
      (area === "teaching" && academicProgramIdentity(page)
        ? {
            ...resolveStaticInstructorRouteSpec("teaching", "program-ai-product-builder")!,
            id: `teaching/${page}`,
            page,
            title: "Program Curriculum",
            purpose: "Read the exact academic version in the assigned Program.",
          }
        : null) ||
      (area === "assessment" && page.startsWith("assignment-")
        ? {
            ...resolveStaticInstructorRouteSpec("assessment", "assignments")!,
            id: `assessment/${page}`,
            page,
            title: "Assessment Assignment",
            purpose: "Read the exact published assessment assignment in current lead or assessor scope.",
          }
        : null) ||
      (area === "assessment" && page.startsWith("submission-")
        ? {
            ...resolveStaticInstructorRouteSpec("assessment", "grading")!,
            id: `assessment/${page}`,
            page,
            title: "Assessment Evaluation",
            purpose: "Evaluate one exact submitted attempt currently assigned to this assessor.",
          }
        : null) ||
      (area === "assessment" &&
      (page === "policy-reviews" || page.startsWith("policy-review-") || page.startsWith("rubric-"))
        ? {
            ...resolveStaticInstructorRouteSpec("assessment", "rubrics")!,
            id: `assessment/${page}`,
            page,
            title: page.startsWith("policy-review-")
              ? "Review Scoring Policy"
              : page === "policy-reviews"
                ? "Independent Scoring-Policy Reviews"
                : "Scoring Policy",
            purpose: "Author or independently review the exact governed scoring policy in current scope.",
          }
        : null) ||
      (area === "buildRooms" &&
      (customChannels.includes(page) || subgroups.some(group => group.channel === page))
        ? createDynamicInstructorRouteSpec(area, page)
        : null) ||
      (area === "class" &&
      page.startsWith("session-") &&
      sessions.some(session => `session-${session.id}` === page)
        ? createDynamicInstructorRouteSpec(area, page)
        : null),
    [area, page, customChannels, subgroups, sessions],
  );
  const routeAccessible = routeSpec ? roleCanAccessInstructorRoute(roleId, routeSpec) : false;

  let standardView: React.ReactNode;
  if (area === "command")
    standardView = (
      <CommandView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
        openSearch={() => setGlobalSearchOpen(true)}
      />
    );
  else if (area === "cohorts")
    standardView = (
      <CohortsView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
        sessions={sessions}
        sessionsLoading={classSessions.loading}
        sessionsError={classSessions.error}
      />
    );
  else if (area === "teaching")
    standardView = (
      <TeachingView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "class")
    standardView = (
      <ClassView
        key={`${area}-${page}`}
        page={page}
        sessions={sessions}
        sessionsLoading={classSessions.loading}
        sessionsError={classSessions.error}
        language={language}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
        createSession={createSession}
      />
    );
  else if (area === "buildRooms")
    standardView = (
      <BuildRoomsOverviewView
        key={`${area}-${page}`}
        page={page}
        language={language}
        subgroups={subgroups}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "labs")
    standardView = (
      <LabsInstructorView
        key={`${area}-${page}`}
        page={page}
        language={language}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "assessment")
    standardView = (
      <AssessmentView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "projects")
    standardView = (
      <ProjectsInstructorView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "engagement")
    standardView = (
      <EngagementView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "analytics")
    standardView = (
      <AnalyticsView
        key={`${area}-${page}`}
        page={page}
        navigate={navigate}
        notify={notify}
        openModal={setModal}
      />
    );
  else if (area === "search")
    standardView = <SearchInstructorView key={`${area}-${page}`} page={page} navigate={navigate} />;
  else if (area === "notifications")
    standardView = (
      <NotificationsInstructorView
        key={`${area}-${page}`}
        page={page}
        notifications={notifications}
        setNotifications={setNotifications}
        openNotification={openNotification}
        notify={notify}
      />
    );
  else
    standardView = (
      <SettingsInstructorView
        key={`${area}-${page}`}
        page={page}
        language={language}
        setLanguage={setLanguage}
        notify={notify}
        openModal={setModal}
      />
    );

  if (!isFullBuildRoom) {
    const content = routeSpec ? (
      routeAccessible ? (
        standardView
      ) : (
        <InstructorAccessBoundary
          spec={routeSpec}
          roleId={roleId}
          openRoleSwitch={() => setModal({ type: "role-switch" })}
          navigate={navigate}
        />
      )
    ) : (
      <UnknownInstructorRoute area={area} page={page} navigate={navigate} />
    );
    standardView = routeSpec ? (
      <InstructorRouteFrame
        spec={routeSpec}
        roleId={roleId}
        openRoleSwitch={() => setModal({ type: "role-switch" })}
      >
        {content}
      </InstructorRouteFrame>
    ) : (
      content
    );
  }

  const roomView =
    roomTab === "chat" ? (
      <ChatView
        selectTab={setRoomTab}
        notify={notify}
        composer={composer}
        setComposer={setComposer}
        sendMessage={sendRoomMessage}
        dynamicMessages={dynamicMessages}
        approveScope={approveScope}
        languageLabel={languageLabel}
      />
    ) : roomTab === "plan" ? (
      <PlanView notify={notify} />
    ) : roomTab === "artifacts" ? (
      <ArtifactsView selectTab={setRoomTab} notify={notify} />
    ) : roomTab === "preview" ? (
      <PreviewView device={previewDevice} setDevice={setPreviewDevice} notify={notify} />
    ) : roomTab === "tests" ? (
      <TestsView selectTab={setRoomTab} notify={notify} />
    ) : (
      <EvidenceView notify={notify} />
    );

  return (
    <>
      <style>
        {completeStyles +
          integrationStyles +
          instructorStyles +
          instructorExtensionStyles +
          instructorFinalStyles +
          instructorV2Styles +
          instructorV3Styles +
          dropdownThemeStyles}
      </style>
      <svg className="sprite" aria-hidden="true" dangerouslySetInnerHTML={{ __html: completeIconSprite }} />
      <div
        className={`app-shell ${isFullBuildRoom ? `build-room-shell ${inspectorOpen ? "" : "inspector-closed"}` : "standard-shell"}`}
      >
        <InstructorRail
          area={area}
          navigate={navigate}
          unread={unread}
          openProfile={() => setModal({ type: "profile" })}
        />
        <InstructorSidebar
          open={sidebarOpen}
          area={area}
          page={page}
          navigate={navigate}
          close={() => setSidebarOpen(false)}
          openSearch={() => setGlobalSearchOpen(true)}
          openModal={setModal}
          customChannels={customChannels}
          subgroups={subgroups}
          sessions={sessions}
          sessionsLoading={classSessions.loading}
          sessionsError={classSessions.error}
        />
        <button
          type="button"
          className={`sidebar-mobile-scrim ${sidebarOpen ? "open" : ""}`}
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
        {isFullBuildRoom ? (
          <main
            className="main build-main"
            data-route-id={routeSpec?.id}
            data-route-title={routeSpec?.title}
            data-operational-model={routeSpec?.model}
            data-layout-family={routeSpec?.layout}
            data-route-signature={routeSpec?.signature}
            data-required-authority={routeSpec?.authority}
            data-active-role={roleId}
          >
            <RoomHeader
              openSidebar={() => setSidebarOpen(true)}
              openInspector={() => setInspectorOpen(current => !current)}
              openSearch={() => setGlobalSearchOpen(true)}
              openMembers={() => setModal({ type: "members" })}
              manageGroups={() => setModal({ type: "manage-groups" })}
              inspectorOpen={inspectorOpen}
              notify={notify}
              startHuddle={() => setModal({ type: "huddle" })}
              subgroupCount={subgroups.length}
            />
            <ContextStrip
              openBrief={() => setModal({ type: "assignment-brief" })}
              openLanguage={() => setModal({ type: "language" })}
              languageLabel={languageLabel}
            />
            {routeSpec ? (
              <InstructorRouteFrame
                spec={routeSpec}
                roleId={roleId}
                openRoleSwitch={() => setModal({ type: "role-switch" })}
              >
                <></>
              </InstructorRouteFrame>
            ) : null}
            <RoomTabs tab={roomTab} selectTab={setRoomTab} />
            <div className="room-view-host">{roomView}</div>
            <MobileBottom
              tab={roomTab}
              selectTab={setRoomTab}
              openSidebar={() => setSidebarOpen(true)}
              openInspector={() => setInspectorOpen(true)}
            />
          </main>
        ) : (
          <main className="main standard-main">
            <InstructorHeader
              area={area}
              page={page}
              openSidebar={() => setSidebarOpen(true)}
              openSearch={() => setGlobalSearchOpen(true)}
              openNotifications={() => navigate("notifications", "all")}
              openProfile={() => setModal({ type: "profile" })}
              unread={unread}
              primaryAction={
                <button type="button" className="pill-btn" onClick={() => setModal({ type: "command" })}>
                  <Icon name="command" size="sm" />
                  Quick actions
                </button>
              }
            />
            <section className="academy-scroll">{standardView}</section>
            <InstructorMobileBottom
              area={area}
              navigate={navigate}
              openSidebar={() => setSidebarOpen(true)}
            />
          </main>
        )}
        {isFullBuildRoom ? (
          <Inspector
            open={inspectorOpen}
            tab={inspectorTab}
            setTab={setInspectorTab}
            close={() => setInspectorOpen(false)}
            notify={notify}
            agentStopped={agentStopped}
            stopAgent={stopAgent}
            openMembers={() => setModal({ type: "members" })}
            manageGroups={() => setModal({ type: "manage-groups" })}
            openLab={() =>
              openExternalHandoff(
                instructorLabHandoffUrl(
                  LABS[0],
                  "builder-oct-2026",
                  "accessible-checkout",
                  "facilitator",
                  "dashboard",
                  language,
                ),
              )
            }
            openClass={() => navigate("class", sessions[0] ? `session-${sessions[0].id}` : "schedule")}
            subgroups={subgroups}
          />
        ) : null}
      </div>
      <GlobalInstructorSearch
        open={globalSearchOpen}
        close={() => setGlobalSearchOpen(false)}
        navigate={navigate}
      />
      <InstructorModalLayer
        modal={modal}
        close={() => setModal(null)}
        navigate={navigate}
        notify={notify}
        language={language}
        setLanguage={setLanguage}
        sessions={sessions}
        createSession={createSession}
        addChannel={addChannel}
        subgroups={subgroups}
        setSubgroups={setSubgroups}
        roleId={roleId}
        setRoleId={setRoleId}
      />
      <InstructorNotificationDetailDrawer
        item={selectedNotification}
        close={() => setSelectedNotification(null)}
        navigate={navigate}
      />
      <InstructorToastStack notices={notices} />
    </>
  );
}
